import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import {
  Armchair, Backpack, BarChart3, Briefcase,
  CalendarDays, Clock3, CreditCard, Dumbbell,
  Handshake, Info, Lock, Luggage, MapPin, MonitorCheck,
  Plane, PlaneLanding, PlaneTakeoff, PlaneTakeoffIcon,
  PlusCircle, ShieldCheck, Star, Sunrise, Sun, Sunset, Users,
  Wind, Zap, Coffee, Wifi, UtensilsCrossed, ChevronLeft, ChevronRight,
  AlertCircle, CheckCircle2, Eye, EyeOff, ChevronDown, ArrowRight,
  Check,
} from "lucide-react";
import { Fragment, createElement, isValidElement, useEffect, useLayoutEffect, useState, useMemo, useCallback, useRef } from "react";
import { useAuth } from "../../../AuthProvider/AuthContext";
import Login from "../../Main/Login/Login";
import Signup from "../../Main/Signup/Signup";
import TravelLoader from "../../Loader/Loader";
import { useLocation, useNavigate } from "react-router-dom";
import "./flights.css";
import usePageMeta from "../../../hooks/usePageMeta";
let __bookingInProgress = false;
const COMPARE_STORAGE_KEY = "compareFlights";
const COMPARE_META_KEY = "compareFlightsMeta";
const EMPTY_ITINERARY_LEGS = [];
const COMPANY_EMAIL = "holidays@makebeetrip.com";


/**
 * Creates a stable comparison object without changing the
 * original supplier/backend flight object.
 *
 * IMPORTANT:
 * - Backend/supplier flight data remains the source of truth.
 * - localStorage is only a temporary comparison snapshot.
 * - Do not calculate taxes, baggage, meals, etc. here.
 */
const buildCompareItem = ({
  tripType,
  flight,
  meta,
  searchContext = {},
}) => {
  if (!flight) return null;

  return {
    ...flight,

    // Explicit compare contract
    compareId:
      flight.compareId ||
      flight.id ||
      flight.flightId ||
      flight.key ||
      `${flight.airline || "flight"}-${flight.flightNumbers || flight.flightNumber || ""}-${flight.departTime || ""}-${flight.arriveTime || ""}`,

    tripType,

    // Keep the original backend/supplier object intact.
    sourceFlight: flight,

    // Explicit search context so Compare.jsx knows
    // which search produced this flight.
    searchContext: {
      from: meta?.from || "",
      fromCode: meta?.fromCode || "",
      to: meta?.to || "",
      toCode: meta?.toCode || "",
      departDate: meta?.departDate || "",
      returnDate: meta?.returnDate || "",
      adults: Number(meta?.adults || 1),
      children: Number(meta?.children || 0),
      infants: Number(meta?.infants || 0),
      classType: meta?.classType || "",
      fare: searchContext.fare || "",
    },

    // Preserve selected fare exactly as supplied by Flights.jsx.
    selectedFare:
      flight.selectedFare ||
      flight.fareOptions?.[flight.selectedFareIndex ?? 0] ||
      null,

    selectedFareIndex:
      flight.selectedFareIndex ?? 0,
  };
};

// ── AOS init ──────────────────────────────────────────────────────────────────
function useAOS() {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://unpkg.com/aos@2.3.4/dist/aos.css";
    document.head.appendChild(link);
    const script = document.createElement("script");
    script.src = "https://unpkg.com/aos@2.3.4/dist/aos.js";
    script.onload = () => {
      window.AOS?.init({ duration: 400, easing: "ease-out-quad", once: true, offset: 40 });
    };
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(link);
      document.head.removeChild(script);
    };
  }, []);
}

const renderIconNode = (icon, props = {}) => {
  if (!icon) return null;
  if (isValidElement(icon)) return icon;
  if (typeof icon === "function" || typeof icon === "object") return createElement(icon, props);
  return icon;
};

// ── Constants ─────────────────────────────────────────────────────────────────
const API_BASE =
  (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api";

const clearStoredAuth = () => {
  try {
    localStorage.removeItem("mbt_token");
    localStorage.removeItem("mbt_user");
  } catch (_) {
    // Ignore storage errors; the UI will still ask for login.
  }
};

// ─── Saved passenger details (persist across future bookings) ───────────────
const PASSENGER_INFO_KEY = "mbt_savedPassengerDetails";

function loadSavedPassengerDetails() {
  try {
    const saved = JSON.parse(localStorage.getItem(PASSENGER_INFO_KEY) || "{}");
    return saved && typeof saved === "object" ? saved : {};
  } catch {
    return {};
  }
}

function savePassengerDetails(details) {
  try {
    localStorage.setItem(PASSENGER_INFO_KEY, JSON.stringify(details));
  } catch {
    // Ignore storage errors (private browsing, quota, etc.)
  }
}
// ─── Saved passenger profiles (for the "fill from saved passenger" dropdown) ───
const SAVED_PASSENGERS_KEY = "mbt_savedPassengersList";

function loadSavedPassengersList() {
  try {
    const saved = JSON.parse(localStorage.getItem(SAVED_PASSENGERS_KEY) || "[]");
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
}

// Adds/updates a passenger profile, de-duped by first+last name (case-insensitive).
// Most-recently-used passenger appears first in the dropdown.
function mergeSavedPassenger(passenger) {
  const firstName = (passenger.firstName || "").trim();
  const lastName = (passenger.lastName || "").trim();
  if (!firstName && !lastName) return;
  try {
    const key = `${firstName.toLowerCase()}|${lastName.toLowerCase()}`;
    const list = loadSavedPassengersList();
    const next = list.filter(
      (p) => `${(p.firstName || "").trim().toLowerCase()}|${(p.lastName || "").trim().toLowerCase()}` !== key
    );
    next.unshift({ ...passenger, firstName, lastName, savedAt: Date.now() });
    localStorage.setItem(SAVED_PASSENGERS_KEY, JSON.stringify(next.slice(0, 30)));
  } catch {
    // Ignore storage errors; dropdown just won't have this entry.
  }
}
const fareTypes = [
  { id: "regular", label: "Regular" },
  { id: "student", label: "Student" },
  { id: "armed", label: "Armed Forces" },
  { id: "senior", label: "Senior Citizen" },
  { id: "doctor", label: "Doctor and Nurses" },
];

const popularFilterDefs = [
  { id: "nonstop", label: "Non Stop" },
  { id: "nearby", label: "Hide Nearby Airports" },
  { id: "refundable", label: "Refundable Fares" },
  { id: "onestop", label: "1 Stop" },
  { id: "airindiaexpress", label: "Air India Express" },
  { id: "morning", label: "Morning Departures (before 12pm)" },
  { id: "afternoon", label: "Afternoon Departure (12pm–5pm)" },
  { id: "evening", label: "Evening Departure (after 5pm)" },
];



const sortOptions = [
  { id: "cheapest", label: "CHEAPEST", detail: "Lowest fare first", icon: <MonitorCheck size={17} /> },
  { id: "nonstop", label: "NON STOP FIRST", detail: "Fewest stops", icon: <PlaneTakeoff size={17} /> },
  { id: "prefer", label: "YOU MAY PREFER", detail: "Best value", icon: <Star size={17} /> },
  { id: "other", label: "Earliest Dep", detail: "By departure time", icon: "↑" },
];


// Passenger type labels for price display
const PAX_TYPE_LABELS = {
  regular: "adult",
  student: "student",
  armed: "armed forces",
  senior: "senior citizen",
  doctor: "medical staff",
};
const PAX_AGE_RULES = {
  0: { min: 12, max: 130, label: "Adult", hint: "must be 12 years or older" },
  1: { min: 2, max: 12, label: "Child", hint: "must be between 2 and 11 years old" },
  2: { min: 0, max: 2, label: "Infant", hint: "must be under 2 years old" },
};
const FARE_AGE_OVERRIDES = {
  senior: { min: 60, max: 130, hint: "must be 60 years or older for a Senior Citizen fare" },
  student: { min: 12, max: 32, hint: "must be between 12 and 31 years old for a Student fare" },
  armed: { min: 18, max: 58, hint: "must be between 18 and 57 years old for an Armed Forces fare" },
  doctor: { min: 21, max: 70, hint: "must be between 21 and 69 years old for a Doctor/Nurses fare" },
};
function ageAtDate(dobIso, refIso) {
  if (!dobIso || !refIso) return null;
  const dob = new Date(`${dobIso}T00:00:00`);
  const ref = new Date(`${refIso}T00:00:00`);
  if (Number.isNaN(dob.getTime()) || Number.isNaN(ref.getTime())) return null;
  let age = ref.getFullYear() - dob.getFullYear();
  const m = ref.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < dob.getDate())) age--;
  return age;
}

function validatePaxAge(paxType, dobIso, travelDateIso, activeFare) {
  let rule = PAX_AGE_RULES[Number(paxType)];
  if (!rule) return null;
  if (Number(paxType) === 0 && activeFare && FARE_AGE_OVERRIDES[activeFare]) {
    rule = { ...rule, ...FARE_AGE_OVERRIDES[activeFare] };
  }
  if (!dobIso) return `Date of birth is required for ${rule.label.toLowerCase()}s.`;
  const age = ageAtDate(dobIso, travelDateIso);
  if (age === null) return "Enter a valid date of birth.";
  if (age < rule.min || age >= rule.max) {
    return `${rule.label} ${rule.hint} (on the travel date).`;
  }
  return null;
}
const AIRPORT_FILTER_CODES = {
  igi: "DEL", hindon: "GZB", csia: "BOM", navimumbai: "NMB",
}

const airportLabel = (airport) =>
  airport?.label || [airport?.city, airport?.country].filter(Boolean).join(", ") || airport?.code || "";

function isInternationalRoute(fromCode, toCode, airports) {
  if (!fromCode || !toCode) return false;
  const findCountry = (code) => {
    const match = airports.find((a) => String(a.code).toUpperCase() === String(code).toUpperCase());
    if (match?.countryCode) return match.countryCode;
if (match?.country) return match.country;

return null;
  };
  const normaliseCountry = (country) => String(country || "").trim().toUpperCase();
  const fromCountry = normaliseCountry(findCountry(fromCode));
  const toCountry = normaliseCountry(findCountry(toCode));
  // A passport is required only where the known origin and destination
  // countries differ. Same-country travel is domestic regardless of country.

  return Boolean(fromCountry && toCountry && fromCountry !== toCountry);
}
function formatFlightDate(value) {
  if (!value) return null;

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;

  return {
    day: date.getDate(),
    month: date.toLocaleDateString("en-US", { month: "short" }),
    year: date.getFullYear().toString().slice(-2),
    weekday: date.toLocaleDateString("en-US", { weekday: "long" }),
  };
}

const FLIGHT_WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const toLocalDate = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDateIso = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const addMonths = (date, amount) => new Date(date.getFullYear(), date.getMonth() + amount, 1);

const buildCalendarDays = (monthDate) => {
  const monthStart = startOfMonth(monthDate);
  const gridStart = new Date(monthStart);
  gridStart.setDate(monthStart.getDate() - monthStart.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + index);
    return {
      date: day,
      iso: toDateIso(day),
      inMonth: day.getMonth() === monthDate.getMonth(),
    };
  });
};

function FlightDateField({ label, value, min, onChange, disabled = false, compact = false }) {
  const fieldRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    const base = toLocalDate(value) || toLocalDate(min) || new Date();
    return startOfMonth(base);
  });
  const parts = formatFlightDate(value);
  const minDate = useMemo(() => toLocalDate(min), [min]);
  const todayIso = useMemo(() => toDateIso(startOfDay(new Date())), []);

  useEffect(() => {
    const nextValue = toLocalDate(value);
    if (nextValue) setViewMonth(startOfMonth(nextValue));
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;
    const updateDirection = () => {
      const rect = fieldRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const needed = 380;
      setOpenUp(spaceAbove > needed && spaceAbove > spaceBelow);
    };
    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);
    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [isOpen]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!fieldRef.current?.contains(event.target)) setIsOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const isBlocked = (date) => {
    if (!minDate) return false;
    return startOfDay(date).getTime() < startOfDay(minDate).getTime();
  };

  const selectDate = (date) => {
    const iso = toDateIso(date);
    if (!iso || isBlocked(date)) return;
    onChange(iso);
    setIsOpen(false);
  };

  const days = useMemo(() => buildCalendarDays(viewMonth), [viewMonth]);
  const monthLabel = useMemo(
    () => viewMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
    [viewMonth]
  );

  return (
    <div
      ref={fieldRef}
      className={`flight-search-field flight-search-field--date flight-date-field${compact ? " flight-date-field--compact" : ""}${disabled ? " is-disabled" : ""}${isOpen ? " is-open" : ""}${openUp ? " is-open-up" : ""}`}
      aria-label={label}
    >
      <button
  type="button"
  className={`flight-date-card${compact ? " flight-date-card--compact" : ""}`}
  onClick={() => {
    if (disabled) return;
    setViewMonth(startOfMonth(toLocalDate(value) || toLocalDate(min) || new Date()));
    setIsOpen((prev) => !prev);
  }}
  disabled={disabled}
  aria-haspopup="dialog"
  aria-expanded={isOpen}
>
  <span className="flight-date-top">
    <span>{label}</span>
    <ChevronDown size={13} />
  </span>

  <span className="flight-date-main">
    <span className="flight-field-icon">
      <CalendarDays size={16} />
    </span>

    <span className="flight-date-day">
      {parts?.day || "–"}
    </span>

    <span className="flight-date-copy">
      <strong>
        {parts ? `${parts.month}'${parts.year}` : "Select date"}
      </strong>
      <span>{parts?.weekday || "Choose travel date"}</span>
    </span>
  </span>
</button>

      {isOpen && !disabled && (
        <div className="flight-date-popover" role="dialog" aria-label={`${label} calendar`}>
          <div className="flight-date-popover__header">
            <button type="button" className="flight-date-popover__nav" onClick={() => setViewMonth((prev) => addMonths(prev, -1))} aria-label="Previous month">
              <ChevronLeft size={16} />
            </button>
            <div className="flight-date-popover__title">
              <strong>{monthLabel}</strong>
              <span>{parts ? `Selected ${parts.weekday}, ${parts.day} ${parts.month} ${parts.year}` : "Pick a travel date"}</span>
            </div>
            <button type="button" className="flight-date-popover__nav" onClick={() => setViewMonth((prev) => addMonths(prev, 1))} aria-label="Next month">
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="flight-date-popover__weekdays" aria-hidden="true">
            {FLIGHT_WEEKDAYS.map((day) => <span key={day}>{day}</span>)}
          </div>

          <div className="flight-date-popover__grid">
            {days.map(({ date, iso, inMonth }) => {
              const blocked = isBlocked(date);
              const isToday = iso === todayIso;
              const isSelected = value && iso === value;
              return (
                <button
                  key={iso}
                  type="button"
                  className={`flight-date-popover__day${inMonth ? "" : " is-muted"}${isToday ? " is-today" : ""}${isSelected ? " is-selected" : ""}`}
                  onClick={() => selectDate(date)}
                  disabled={blocked}
                  aria-pressed={isSelected}
                  aria-label={date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                >
                  <span>{date.getDate()}</span>
                </button>
              );
            })}
          </div>

          <div className="flight-date-popover__footer">
            <button type="button" className="flight-date-popover__chip" onClick={() => selectDate(new Date())}>
              Today
            </button>
            <button type="button" className="flight-date-popover__chip flight-date-popover__chip--ghost" onClick={() => setIsOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const CLASS_OPTIONS = [
  "Economy/Premium Economy", "Premium Economy", "Business", "First Class",
];
// ── Airline code → full name fallback ──────────────────────────────────────
// Used only when the supplier response doesn't include a usable Airline_Name.
const AIRLINE_CODE_TO_NAME = {
  "6E": "IndiGo",
  "SG": "SpiceJet",
  "AI": "Air India",
  "IX": "Air India Express",
  "UK": "Vistara",
  "QP": "Akasa Air",
  "G8": "GoAir",
  "I5": "AirAsia India",
  "9W": "Jet Airways",
};

const resolveAirlineName = (code, suppliedName) => {
  const upperCode = String(code || "").toUpperCase();
  const trimmedName = String(suppliedName || "").trim();

  // If the supplier's "name" field is empty, or is literally just the code
  // again, prefer our known full airline name.
  if (!trimmedName || trimmedName.toUpperCase() === upperCode) {
    return AIRLINE_CODE_TO_NAME[upperCode] || trimmedName || upperCode || "Airline";
  }
  if (trimmedName.toUpperCase() === "AIRINDIA EXPRESS") {
    return AIRLINE_CODE_TO_NAME.IX;
  }
  return trimmedName;
};
// UI shows friendly labels (with "/" etc.), but the backend/supplier API
// expects a clean enum. Map before sending in any API request body.
const CLASS_TYPE_API_MAP = {
  "Economy/Premium Economy": "Economy",
  "Premium Economy": "Premium Economy",
  "Business": "Business",
  "First Class": "First Class",
};
const toApiClassType = (label) => CLASS_TYPE_API_MAP[label] || "Economy";
const pickFirstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const asFiniteNumberOrNull = (...values) => {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const readFareSeats = (fare = {}, fareDetail = {}) =>
  asFiniteNumberOrNull(
    fare.SeatsAvailable,
    fare.Seats_Available,
    fare.AvailableSeats,
    fare.Available_Seats,
    fare.SeatAvailability,
    fare.Seat_Availability,
    fare.LastFewSeats,
    fareDetail.SeatsAvailable,
    fareDetail.Seats_Available,
    fareDetail.AvailableSeats,
    fareDetail.Available_Seats
  );

const readFlightSeats = (flight = {}) => {
  const fares = Array.isArray(flight.Fares) ? flight.Fares : [];
  const direct = asFiniteNumberOrNull(
    flight.SeatsAvailable,
    flight.Seats_Available,
    flight.AvailableSeats,
    flight.Available_Seats,
    flight.SeatAvailability,
    flight.Seat_Availability,
    flight.LastFewSeats
  );
  if (direct !== null) return direct;

  const fareSeats = fares
    .map((fare) => readFareSeats(fare, fare.FareDetails?.find((d) => d.PAXType === "0" || d.PAXType === 0) || fare.FareDetails?.[0] || {}))
    .filter((n) => Number.isFinite(n));
  return fareSeats.length ? Math.max(...fareSeats) : null;
};

const isSpecialRoundTripFlight = (flight = {}) =>
  flight?.specialRoundTrip === true ||
  Number(flight?.bookingType ?? flight?.Booking_Type ?? flight?.BookingType) === 2 ||
  isSpecialRoundTripType(flight?.tripType);

const countFlightNumberStops = (group) => {
  const numbers = Array.isArray(group)
    ? group
    : String(group || "").split(",");
  return Math.max(0, numbers.map((item) => String(item).trim()).filter(Boolean).length - 1);
};

const cleanFlightNumberDisplay = (value) =>
  String(value || "")
    .replace(/\^[A-Za-z0-9]+/g, "")
    .trim();

const matchesStopFilter = (flight = {}, filterId) => {
  if (!filterId) return true;
  if (isSpecialRoundTripFlight(flight)) {
    const groups = Array.isArray(flight.specialRoundTripFlightNumberGroups)
      ? flight.specialRoundTripFlightNumberGroups.slice(0, 2)
      : [];
    if (groups.length >= 2) {
      const directionStops = groups.map(countFlightNumberStops);
      if (filterId === "nonstop") return directionStops.every((stops) => stops === 0);
      if (filterId === "onestop") return directionStops.every((stops) => stops <= 1) && directionStops.some((stops) => stops === 1);
      if (filterId === "twostop") return directionStops.some((stops) => stops >= 2);
    }
  }
  const stops = Number(flight.stops || 0);
  if (filterId === "nonstop") return stops === 0;
  if (filterId === "onestop") return stops === 1;
  if (filterId === "twostop") return stops >= 2;
  return true;
};

const splitSpecialRoundTripSegments = (flight = {}) => {
  const sourceSegments = Array.isArray(flight?.segments)
    ? flight.segments
    : Array.isArray(flight?.Segments)
      ? flight.Segments
      : [];
  const segments = sourceSegments.map((s) => ({
    segmentId: s.segmentId ?? s.Segment_Id ?? s.SegmentId ?? null,
    legIndex: s.legIndex ?? s.Leg_Index ?? s.LegIndex ?? null,
    returnFlight: s.returnFlight ?? s.Return_Flight ?? s.ReturnFlight ?? false,
    origin: s.origin || s.Origin || null,
    destination: s.destination || s.Destination || null,
    departureDateTime: s.departureDateTime || s.DepartureDateTime || s.Departure_DateTime || null,
    arrivalDateTime: s.arrivalDateTime || s.ArrivalDateTime || s.Arrival_DateTime || null,
    duration: s.duration || s.Duration || null,
    flightNo: s.flightNo || `${s.Airline_Code || s.AirlineCode || ""}${s.Flight_Number || s.FlightNumber || ""}`,
  }));
  if (segments.length < 2) return { outbound: [], returnSegments: [] };

  let outbound = segments.filter((segment) =>
    segment.returnFlight === false ||
    String(segment.returnFlight).toLowerCase() === "false"
  );
  let returnSegments = segments.filter((segment) =>
    segment.returnFlight === true ||
    String(segment.returnFlight).toLowerCase() === "true"
  );

  if (!outbound.length || !returnSegments.length) {
    outbound = segments.filter((segment) => Number(segment.legIndex) === 0);
    returnSegments = segments.filter((segment) => Number(segment.legIndex) === 1);
  }

  return { outbound, returnSegments };
};

const durationTextToMinutes = (value) => {
  if (value === undefined || value === null || value === "") return 0;
  if (Number.isFinite(Number(value))) return Number(value);
  const text = String(value).trim();
  const colon = text.match(/^(\d{1,2}):(\d{2})$/);
  if (colon) return Number(colon[1]) * 60 + Number(colon[2]);
  const hours = text.match(/(\d+)\s*h/i);
  const mins = text.match(/(\d+)\s*m/i);
  return (Number(hours?.[1] || 0) * 60) + Number(mins?.[1] || 0);
};

const summarizeSegmentDirection = (segments = [], label = "Outbound", fallbackDate = null) => {
  const first = segments[0] || {};
  const last = segments[segments.length - 1] || first;
  const minutes = segments.reduce((sum, segment) => sum + durationTextToMinutes(segment.duration), 0);
  const stops = Math.max(0, segments.length - 1);
  return {
    label,
    origin: first.origin,
    destination: last.destination,
    departTime: sliceTime(first.departureDateTime),
    arriveTime: sliceTime(last.arrivalDateTime),
    duration: minutes > 0 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : "",
    stops,
    stopText: stops === 0 ? "Non Stop" : `${stops} Stop${stops === 1 ? "" : "s"}`,
    flightNumbers: segments.map((segment) => segment.flightNo).filter(Boolean).join(", "),
    date: first.departureDateTime ? String(first.departureDateTime).slice(0, 10) : fallbackDate,
  };
};

const compactBookingLeg = (leg = {}, fallbackIndex = 0) => {
  const segments = (Array.isArray(leg.segments) ? leg.segments : [])
    .map((segment, index) => ({
      segmentId: segment.segmentId ?? segment.Segment_Id ?? segment.SegmentId ?? null,
      legIndex: segment.legIndex ?? segment.Leg_Index ?? segment.LegIndex ?? index,
      returnFlight: Boolean(segment.returnFlight ?? segment.Return_Flight ?? segment.ReturnFlight ?? false),
      origin: segment.origin || segment.Origin || null,
      destination: segment.destination || segment.Destination || null,
      departureDateTime: segment.departureDateTime || segment.DepartureDateTime || segment.Departure_DateTime || null,
      arrivalDateTime: segment.arrivalDateTime || segment.ArrivalDateTime || segment.Arrival_DateTime || null,
      duration: segment.duration || segment.Duration || null,
      flightNo: segment.flightNo || segment.flightNumber || segment.FlightNumber || segment.Flight_Number || null,
    }));
  const first = segments[0] || {};
  const last = segments[segments.length - 1] || first;
  const direction =
    leg.label ||
    leg.direction ||
    (fallbackIndex === 1 ? "Return" : "Outbound");

  const totalDurationMins =
    segments.reduce(
      (sum, segment) =>
        sum +
        durationTextToMinutes(
          segment.duration
        ),
      0
    );

  const stops =
    Math.max(
      0,
      segments.length - 1
    );

  const segmentFlightNumbers =
    segments
      .map((segment) => segment.flightNo)
      .filter(Boolean)
      .join(", ");
  return {
    direction,
    legIndex: leg.legIndex ?? fallbackIndex,
    departCode: first.origin || leg.departCode || null,
    arriveCode: last.destination || leg.arriveCode || null,
    departTime: sliceTime(first.departureDateTime) || leg.departTime || null,
    arriveTime: sliceTime(last.arrivalDateTime) || leg.arriveTime || null,
    travelDate: first.departureDateTime ? String(first.departureDateTime).slice(0, 10) : leg.travelDate || null,
    flightNumbers:
      segmentFlightNumbers ||
      leg.flightNumbers ||
      null,

    duration:
      totalDurationMins > 0
        ? `${Math.floor(totalDurationMins / 60)}h ${String(
          totalDurationMins % 60
        ).padStart(2, "0")}m`
        : leg.duration || null,

    stops,

    stopDetail:
      stops === 0
        ? "Non Stop"
        : `${stops} Stop${stops === 1 ? "" : "s"}`,
    flightId:
      leg.flightId ||
      null,
    segmentId: leg.segmentId ?? first.segmentId ?? null,
    segments,
    supplierRequestId: leg.selectedFare?.supplierRequestId || leg.supplierRequestId || null,
    supplierBookingRef: leg.supplierBookingRef || null,
    airlinePnr: leg.airlinePnr || null,
    recordLocator: leg.recordLocator || null,
    crsPnr: leg.crsPnr || null,
    crsCode: leg.crsCode || null,
  };
};

const buildSpecialRoundTripLegsFromReprice = (flight = {}, repriced = null, fallbackLegs = []) => {
  const repricedResult = repriced?.results?.[0] || repriced?.result || null;
  const repricedSegments = Array.isArray(repricedResult?.segments)
    ? repricedResult.segments
    : Array.isArray(repriced?.segments)
      ? repriced.segments
      : [];
  const { outbound, returnSegments } = splitSpecialRoundTripSegments({
    segments: repricedSegments.length ? repricedSegments : flight.segments,
  });
  const sourceLegs = outbound.length && returnSegments.length
    ? [
      { ...(fallbackLegs[0] || {}), label: "Outbound", flightNumbers: null, segments: outbound },
      { ...(fallbackLegs[1] || {}), label: "Return", flightNumbers: null, segments: returnSegments },
    ]
    : fallbackLegs;
  return sourceLegs.map((leg, index) => compactBookingLeg(leg, index));
};

const mapApiFlight = (f) => {
  // Map FareDetails per PAX type
  const cheapestFare = (f.Fares || []).reduce((best, fare) => {
    const ad = fare.FareDetails?.find(d => d.PAXType === "0" || d.PAXType === 0) || fare.FareDetails?.[0] || {};
    const p = Number(ad.TotalAmount || 0);
    return (!best || (p > 0 && p < best.price)) ? { fare, detail: ad, price: p } : best;
  }, null);
  const adultFare = cheapestFare?.detail || {};
  const fareDetails = cheapestFare?.fare?.FareDetails || f.Fares?.[0]?.FareDetails || [];
  const childFare = fareDetails.find((d) => d.PAXType === "1" || d.PAXType === 1) || {};
  const infantFare = fareDetails.find((d) => d.PAXType === "2" || d.PAXType === 2) || {};

  // Map segments
  const segments = f.Segments || [];
  const firstSeg = segments[0] || {};
  const lastSeg = segments[segments.length - 1] || {};

  const depDateTime = firstSeg.DepartureDateTime || "";
  const arrDateTime = lastSeg.ArrivalDateTime || "";
  const depTime = depDateTime ? depDateTime.slice(11, 16) : "";
  const arrTime = arrDateTime ? arrDateTime.slice(11, 16) : "";

  // Calculate total duration from segments
  const totalDurationMins = segments.reduce((sum, seg) => {
    const [h, m] = (seg.Duration || "0:00").split(":").map(Number);
    return sum + (h * 60 + (m || 0));
  }, 0);
  const durH = Math.floor(totalDurationMins / 60);
  const durM = totalDurationMins % 60;
  const duration = `${durH}h ${durM}m`;

  const stops = Math.max(0, segments.length - 1);
  const stopDetail = stops === 0
    ? "Non Stop"
    : stops === 1
      ? `1 Stop via ${segments[0].DestinationCity || segments[0].Destination}`
      : `${stops} Stops`;

  const seatsAvailable = readFlightSeats(f);
  const mappedSegments = segments.map((s) => ({
    segmentId:
      s.Segment_Id ??
      s.SegmentId ??
      null,

    legIndex:
      s.Leg_Index ??
      s.LegIndex ??
      s.legIndex ??
      null,

    returnFlight:
      s.Return_Flight ??
      s.ReturnFlight ??
      s.returnFlight ??
      false,

    origin:
      s.Origin ||
      s.origin ||
      null,

    destination:
      s.Destination ||
      s.destination ||
      null,

    departureDateTime:
      s.DepartureDateTime ||
      s.Departure_DateTime ||
      null,

    arrivalDateTime:
      s.ArrivalDateTime ||
      s.Arrival_DateTime ||
      null,

    duration:
      s.Duration ||
      s.duration ||
      null,

    flightNo: `${s.Airline_Code ||
      s.AirlineCode ||
      ""
      }${s.Flight_Number ||
      s.FlightNumber ||
      ""
      }`,
  }));
  const bookingType = f.Booking_Type ?? f.BookingType ?? f.bookingType ?? null;
  const specialSplit = Number(bookingType) === 2
    ? splitSpecialRoundTripSegments({ segments: mappedSegments })
    : { outbound: [], returnSegments: [] };
  const specialOutboundSummary = specialSplit.outbound.length
    ? summarizeSegmentDirection(specialSplit.outbound, "Outbound")
    : null;
  const specialReturnSummary = specialSplit.returnSegments.length
    ? summarizeSegmentDirection(specialSplit.returnSegments, "Return")
    : null;
  const displayFirst = specialOutboundSummary || {
    origin: firstSeg.Origin,
    destination: lastSeg.Destination,
    departTime: depTime,
    arriveTime: arrTime,
    duration,
    stops,
    stopText: stopDetail,
  };
  return {
    id: f.Flight_Id || f.Flight_Key,
    flightId: f.Flight_Id || f.FlightId || null,
    segmentId:
      segments[0]?.Segment_Id ??
      segments[0]?.SegmentId ??
      null,

    bookingType,
    segments: mappedSegments,
    specialRoundTrip: Number(bookingType) === 2,
    specialRoundTripSummaries: [specialOutboundSummary, specialReturnSummary].filter(Boolean),
    specialRoundTripFlightNumberGroups:
      Array.isArray(
        f.specialRoundTripFlightNumberGroups
      )
        ? f.specialRoundTripFlightNumberGroups
        : [],
    specialRoundTripRouteGroups:
      Array.isArray(
        f.specialRoundTripRouteGroups
      )
        ? f.specialRoundTripRouteGroups
        : [],
    resultIndex: f.Flight_Key,
    flightKey: f.Flight_Key || null,
    supplierRequestId: f.supplierRequestId || null,
    airline: resolveAirlineName(
      firstSeg.AirlineCode || f.Airline_Code,
      firstSeg.AirlineName || firstSeg.Airline_Name || f.Airline_Name
    ),
    airlineLogo: firstSeg.AirlineCode || f.Airline_Code,
    flightNumbers:
      Array.isArray(
        f.specialRoundTripFlightNumberGroups
      ) &&
        f.specialRoundTripFlightNumberGroups.length
        ? f.specialRoundTripFlightNumberGroups
          .flat()
          .map(cleanFlightNumberDisplay)
          .filter(Boolean)
          .join(", ")
        : segments
          .map(
            (s) =>
              `${s.AirlineCode}${s.FlightNumber}`
          )
          .join(", "),
    departAirport: displayFirst.origin,
    departCode: displayFirst.origin,
    arriveAirport: displayFirst.destination,
    arriveCode: displayFirst.destination,
    departTime: displayFirst.departTime,
    arriveTime: displayFirst.arriveTime,
    arriveNextDay: arrDateTime.slice(0, 10) > depDateTime.slice(0, 10),
    duration: displayFirst.duration,
    stops: displayFirst.stops,
    stopDetail: displayFirst.stopText,
    seatsAvailable,
    price: adultFare.TotalAmount || 0,
    refundable: !!cheapestFare?.fare?.Refundable,
    cancelFeeText: cheapestFare?.fare?.Refundable
      ? "Free"
      : (cheapestFare?.fare?.CancellationCharges?.length ? "Check policy" : "Non-refundable"),
    baggageCheckin: baggageValue(adultFare.FreeBaggage?.CheckInBaggage),
    baggageCabin: baggageValue(adultFare.FreeBaggage?.CabinBaggage),
    travelDate: f.TravelDate || firstSeg.DepartureDateTime?.slice(0, 10),
    fareOptions: (() => {
      const rawFares = f.Fares || [];

      const built = rawFares.map((fare, i) => {
        const ad = fare.FareDetails?.find((d) => d.PAXType === "0" || d.PAXType === 0) || fare.FareDetails?.[0] || {};
        const ch = fare.FareDetails?.find((d) => d.PAXType === "1" || d.PAXType === 1) || {};
        const inf = fare.FareDetails?.find((d) => d.PAXType === "2" || d.PAXType === 2) || {};

        const checkIn = ad.FreeBaggage?.CheckInBaggage || null;
        const cabin = ad.FreeBaggage?.CabinBaggage || null;
        const seatsLeft = readFareSeats(fare, ad);

        return {
          fareId: fare.FareId || fare.FareKey || null,
          supplierSearchKey: fare._searchKey || f._searchKey || null,
          supplierFlightKey: fare._flightKey || f.Flight_Key || null,
          supplierRequestId: fare.supplierRequestId || f.supplierRequestId || null,
          source: fare._source || f._source || null,
          name: fare.FareName || fare.FareTypeName || fare.Fare_Type_Name || `Fare ${i + 1}`,
          isUpfront: Boolean(fare.IsUpfront || fare.Is_Upfront || /upfront/i.test(fare.FareName || "")),
          price: ad.TotalAmount || 0,
          childPrice: ch.TotalAmount || null,
          infantPrice: inf.TotalAmount || null,
          baggage: (checkIn || cabin)
            ? [checkIn && `${checkIn} check-in`, cabin && `${cabin} cabin`].filter(Boolean).join(", ")
            : null,
          baggageCheckin: baggageValue(checkIn),
          baggageCabin: baggageValue(cabin),
          seat: Number.isFinite(seatsLeft) ? `${seatsLeft} seat${seatsLeft === 1 ? "" : "s"} left` : null,
          seatsAvailable: seatsLeft,
          refundable: !!fare.Refundable,
          cancellation: fare.CancellationCharges?.length
            ? null
            : (fare.Refundable ? "Refundable" : "Non-refundable"),
          meal: fare.Foodonboard || null,
          recommended: i === 0,
          cancellationCharges: fare.CancellationCharges || [],
          rescheduleCharges: fare.RescheduleCharges || [],
          holdAllowed: readBlockTicketAllowed(fare, f),
        };
      });

      const seen = new Set();
      return built.filter((fare) => {
        const key = fare.fareId
          ? `fare-id|${fare.fareId}`
          : [fare.name, fare.price, fare.baggage, fare.refundable, fare.isUpfront, fare.source, fare.supplierFlightKey].join("|");
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    })(),
    cancellationCharges: f.Fares?.[0]?.CancellationCharges || [],
    rescheduleCharges: f.Fares?.[0]?.RescheduleCharges || [],
    // True if ANY fare option on this flight supports hold-and-pay-later —
    // used to show the "Hold Payment Available" tag on the flight card.
    holdAllowed: aggregateFlightHoldAllowed(f.Fares || [], f),
  };
};
const buildChargeRows = (chargesArr) => {
  if (!Array.isArray(chargesArr) || chargesArr.length === 0) return [];

  // Group by PassengerType so we can show Adult / Child separately
  const byPax = {};
  chargesArr.forEach((c) => {
    const paxKey = c.PassengerType ?? 0;
    if (!byPax[paxKey]) byPax[paxKey] = [];
    byPax[paxKey].push(c);
  });

  const rows = [];
  Object.entries(byPax).forEach(([paxKey, charges]) => {
    const paxLabel = PAX_TYPE_NAME[Number(paxKey)] || "Adult";
    charges.forEach((c) => {
      // Build a human-readable time-window label
      const fromNum = c.DurationFrom ?? 0;
      const toNum = c.DurationTo ?? 0;
      const fromType = DURATION_TYPE[c.DurationTypeFrom] || "hour";
      const toType = DURATION_TYPE[c.DurationTypeTo] || "hour";

      let label;
      if (fromNum === 0 && toNum === 0) {
        label = `No-show`;
      } else if (fromNum === 0) {
        label = `Within ${toNum} ${toType}${toNum !== 1 ? "s" : ""} of departure`;
      } else if (toNum === 9999 || toNum === 0) {
        label = `More than ${fromNum} ${fromType}${fromNum !== 1 ? "s" : ""} before`;
      } else {
        label = `${fromNum}–${toNum} ${toType}${toNum !== 1 ? "s" : ""} before`;
      }
      if (Object.keys(byPax).length > 1) label += ` (${paxLabel})`;

      // Format the fee value
      const val = String(c.Value || "").trim();
      let fee;
      if (!val || val === "0") {
        fee = "Free";
      } else if (Number(c.ValueType) === 1) {
        fee = `${val}% of fare`;
      } else {
        const num = Number(val);
        fee = Number.isFinite(num) && num > 0 ? `₹${num.toLocaleString("en-IN")}` : val;
      }

      // Add offline service fee if present
      const offline = Number(c.OfflineServiceFee || 0);
      const online = Number(c.OnlineServiceFee || 0);
      if (online > 0) fee += ` + ₹${online.toLocaleString("en-IN")} svc fee`;
      else if (offline > 0) fee += ` + ₹${offline.toLocaleString("en-IN")} svc fee`;

      rows.push({ label, fee });
    });
  });

  return rows;
};
const DURATION_TYPE = { 0: "hour", 1: "day" };
const PAX_TYPE_NAME = { 0: "Adult", 1: "Child", 2: "Infant" };
// ── AIRCRAFT CONFIGURATIONS ──────────────────────────────────────────────────
const AIRCRAFT_CONFIGS = {
  "6E": {
    cols: ["A", "B", "C", "D", "E", "F"],
    rows: 30,
    exitRows: [12, 13],
    extraLegroomRows: [1, 2, 12, 13],
    seatPrices: { window: 400, aisle: 300, middle: 0, extraLegroom: 800 },
  },
  IX: {
    cols: ["A", "B", "C", "D", "E", "F"],
    rows: 29,
    exitRows: [11, 12],
    extraLegroomRows: [1, 2, 11, 12],
    seatPrices: { window: 350, aisle: 250, middle: 0, extraLegroom: 700 },
  },
  SG: {
    cols: ["A", "B", "C", "D", "E", "F"],
    rows: 31,
    exitRows: [13, 14],
    extraLegroomRows: [1, 2, 13, 14],
    seatPrices: { window: 300, aisle: 200, middle: 0, extraLegroom: 600 },
  },
  QP: {
    cols: ["A", "B", "C", "D", "E", "F"],
    rows: 28,
    exitRows: [11, 12],
    extraLegroomRows: [1, 2, 11, 12],
    seatPrices: { window: 350, aisle: 250, middle: 0, extraLegroom: 700 },
  },
  AI: {
    cols: ["A", "B", "C", "D", "E", "F"],
    rows: 30,
    exitRows: [12, 13],
    extraLegroomRows: [1, 2, 12, 13],
    seatPrices: { window: 400, aisle: 300, middle: 0, extraLegroom: 900 },
  },
  DEFAULT: {
    cols: ["A", "B", "C", "D", "E", "F"],
    rows: 30,
    exitRows: [12, 13],
    extraLegroomRows: [1, 2, 12, 13],
    seatPrices: { window: 350, aisle: 250, middle: 0, extraLegroom: 700 },
  },
};

// ── Seat Map Generator (fallback when API returns no data) ───────────────────
// Deterministic seed per airline so occupancy is stable across re-renders.
const generateSeatMap = (airlineCode, occupancyRate = 0.45) => {
  const config = AIRCRAFT_CONFIGS[airlineCode] || AIRCRAFT_CONFIGS.DEFAULT;
  const { cols, rows, exitRows, extraLegroomRows, seatPrices } = config;

  const seed = (airlineCode || "XX")
    .split("")
    .reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const seededRand = (i) => {
    const x = Math.sin(seed * 9301 + i * 49297 + 233720) * 10000;
    return x - Math.floor(x);
  };

  return Array.from({ length: rows }, (_, rowIdx) => {
    const rowNum = rowIdx + 1;
    const isExitRow = exitRows.includes(rowNum);
    const isExtraLegroom = extraLegroomRows.includes(rowNum);

    const seats = cols.map((col, colIdx) => {
      const seatId = `${rowNum}${col}`;
      const seatType = getSeatType(col);
      const extraCost = isExtraLegroom
        ? seatPrices.extraLegroom
        : seatType === "window"
          ? seatPrices.window
          : seatType === "aisle"
            ? seatPrices.aisle
            : 0;

      // Premium/exit rows tend to fill earlier
      const effectiveRate = isExtraLegroom
        ? Math.min(occupancyRate + 0.2, 0.9)
        : occupancyRate;
      const r = seededRand(rowIdx * 10 + colIdx);
      const status =
        r < effectiveRate * 0.60 ? "booked" :
          r < effectiveRate * 0.75 ? "occupied" :
            r < effectiveRate * 0.85 ? "blocked" :
              "available";

      return { id: seatId, col, row: rowNum, seatType, status, isExtraLegroom, isExitRow, extraCost };
    });

    return { row: rowNum, isExtraLegroom, isExitRow, seats };
  });
};

// Use today's local date instead of a hardcoded date that can become outdated.
const DEFAULT_DEPART_DATE = (() => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
})();
const defaultSearchMeta = {
  from: "New Delhi, India", fromCode: "DEL",
  to: "Mumbai, India", toCode: "BOM",
  departDate: DEFAULT_DEPART_DATE,
  tripType: "One Way",
  classType: "Economy/Premium Economy", // ADD
};


const normalizeTripType = (tripType) =>
  String(tripType || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const isSpecialRoundTripType = (tripType) => {
  const normalized = normalizeTripType(tripType);

  return (
    normalized === "special_round_trip" ||
    normalized === "special_roundtrip"
  );
};

const isReturnTripType = (tripType) => {
  const normalized = normalizeTripType(tripType);

  return (
    normalized === "round_trip" ||
    normalized === "roundtrip" ||
    isSpecialRoundTripType(normalized)
  );
};

// Canonical values stored by the backend.
const getCanonicalTripType = (tripType, isSpecialSupplierRoundTrip = false) => {
  const normalized = normalizeTripType(tripType);

  if (isSpecialSupplierRoundTrip || isSpecialRoundTripType(normalized)) {
    return "special_round_trip";
  }

  if (["multi_city", "multicity"].includes(normalized)) {
    return "multi_city";
  }

  if (["round_trip", "roundtrip", "return"].includes(normalized)) {
    return "round_trip";
  }

  return "one_way";
};

// Supplier Booking_Type enum:
// 0 = ONE_WAY, 1 = ROUNDTRIP,
// 2 = SPECIALROUNDTRIP, 3 = MULTICITY.
const getSearchBookingType = (tripType) => {
  const canonicalType = getCanonicalTripType(tripType);

  if (canonicalType === "special_round_trip") return 2;
  if (canonicalType === "round_trip") return 1;
  if (canonicalType === "multi_city") return 3;

  return 0;
};

const getSupplierBookingType = (canonicalType) => {
  switch (canonicalType) {
    case "round_trip":
      return 1;
    case "special_round_trip":
      return 2;
    case "multi_city":
      return 3;
    default:
      return 0;
  }
};


// ── SEAT CONFIGURATION ────────────────────────────────────────────────────────
// Real aircraft cabin layouts
const getSeatType = (col) => {
  const value = String(col || "").toUpperCase();
  if (["A", "F", "K"].includes(value)) return "window";
  if (["C", "D", "G", "H"].includes(value)) return "aisle";
  return "middle";
};

const getSeatTypeIcon = (type) => {
  if (type === "window") return "🪟";
  if (type === "aisle") return "🚶";
  return "💺";
};

const SEAT_TYPE_LABELS = {
  window: "Window",
  aisle: "Aisle",
  middle: "Middle",
};

// ── Seat Map Generator (realistic, with occupancy patterns) ──────────────────
const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");
const normalizeHoldAllowedValue = (value) => {
  if (value === undefined || value === null) return null;
  if (value === true || value === 1 || String(value).toLowerCase() === "true") return true;
  if (value === false || value === 0 || String(value).toLowerCase() === "false") return false;
  return null;
};

const aggregateFlightHoldAllowed = (fares = [], flightLike = null) => {
  const values = fares.map((fare) => readBlockTicketAllowed(fare, flightLike));
  if (values.some((value) => value === true)) return true;
  if (values.some((value) => value === false)) return false;
  return null;
};

const aggregateCombinedHoldAllowed = (legs = []) => {
  const values = legs.map((leg) => resolveHoldAllowed(leg, leg.selectedFare));
  if (values.some((value) => value === false)) return false;
  if (values.length > 0 && values.every((value) => value === true)) return true;
  return null;
};
// Reads Block_Ticket_Allowed from an Air_Reprice response, tolerating
// whichever casing your backend forwards it under.
const readBlockTicketAllowed = (result, data) => {
  const raw = firstDefined(
    result?.blockTicketAllowed,
    result?.Block_Ticket_Allowed,
    result?.BlockTicketAllowed,
    result?.holdAllowed,
    result?.HoldAllowed,
    result?.Flight?.Block_Ticket_Allowed,
    result?.Flight?.BlockTicketAllowed,
    result?.Flight?.blockTicketAllowed,
    data?.blockTicketAllowed,
    data?.holdAllowed
  );
  return normalizeHoldAllowedValue(raw);
};

const resolveHoldAllowed = (flightLike, fareLike) => {
  const value = firstDefined(
    fareLike?.holdAllowed,
    fareLike?.blockTicketAllowed,
    fareLike?.Block_Ticket_Allowed,
    fareLike?.BlockTicketAllowed,
    flightLike?.holdAllowed,
    flightLike?.blockTicketAllowed,
    flightLike?.Block_Ticket_Allowed,
    flightLike?.BlockTicketAllowed
  );

  return normalizeHoldAllowedValue(value);
};
const parseSeatAvailability = (seat) => {
  const statusCode = firstDefined(
    seat.SSR_Status,
    seat.SSRStatus,
    seat.statusCode,
    seat.StatusCode,
    seat.SeatStatusCode,
    seat.Seat_Status_Code,
    seat.AvailabilityType,
    seat.Availability_Type
  );
  if (statusCode !== undefined && statusCode !== null && statusCode !== "") {
    const code = Number(statusCode);
    if (code === 1) return "available";
    if (code === 2) return "blocked";
    if (code === 3) return "booked";
    if (code === 0) return "occupied";
  }

  const occupiedFlag = firstDefined(seat.Occupied, seat.IsOccupied);
  if (occupiedFlag === true || String(occupiedFlag).toLowerCase() === "true") {
    return "occupied";
  }

  const raw = firstDefined(
    seat.status,
    seat.Status,
    seat.Seat_Status,
    seat.SeatStatus,
    seat.Availability,
    seat.Available,
    seat.IsAvailable,
    seat.Is_Available,
    seat.Occupied,
    seat.IsOccupied
  );
  const text = String(raw ?? "").toLowerCase();
  if (raw === 0 || text === "0") return "occupied";
  if (raw === 1 || text === "1") return "available";
  if (raw === 2 || text === "2") return "blocked";
  if (raw === 3 || text === "3") return "booked";
  if (text.includes("booked")) return "booked";
  if (text.includes("blocked")) return "blocked";
  if (raw === false || text === "false" || text.includes("occupied") || text.includes("unavailable")) {
    return "occupied";
  }
  return "available";
};

const isSeatUnavailable = (seat) =>
  ["occupied", "booked", "blocked", "unavailable"].includes(String(seat?.status || "").toLowerCase());

const buildSeatSegmentMeta = (segment = {}, seatMap = {}, segmentIndex = null) => {
  const origin = firstDefined(segment.origin, segment.Origin, segment.From, segment.Departure, "");
  const destination = firstDefined(segment.destination, segment.Destination, segment.To, segment.Arrival, "");
  const route = firstDefined(
    segment.route,
    segment.Route,
    segment.Sector,
    origin && destination ? `${origin}-${destination}` : ""
  );
  const flightNo = firstDefined(segment.flightNo, segment.FlightNo, segment.Flight_No, segment.FlightNumber, segment.Flight_Number, "");
  const segmentId = firstDefined(segment.segmentId, segment.Segment_Id, segment.SegmentId, null);
  const supplierLegIndex = firstDefined(segment.legIndex, segment.Leg_Index, segment.LegIndex, segmentIndex);
  const flightId = firstDefined(segment.flightId, segment.Flight_ID, segment.FlightID, seatMap.flightId, seatMap.Flight_Id, seatMap.FlightId, null);
  const segmentKey = [segmentId, supplierLegIndex, flightId, route, segmentIndex !== null && segmentIndex !== undefined ? `idx-${segmentIndex}` : ""]
    .filter((value) => value !== null && value !== undefined && String(value) !== "")
    .join("::");

  return {
    segmentId,
    supplierLegIndex,
    flightId,
    origin: origin || null,
    destination: destination || null,
    route: route || null,
    flightNo: flightNo || null,
    segmentKey: segmentKey || null,
  };
};

const formatSegmentLabel = (meta = {}) => {
  const route = meta.origin && meta.destination
    ? `${meta.origin} → ${meta.destination}`
    : String(meta.route || "").replace(/\s*-\s*/g, " → ");
  return [route, meta.flightNo].filter(Boolean).join(" · ");
};

const normalizeApiSeat = (seat, rowNum, seatIdx = 0, segmentMeta = {}) => {
  const seatCode = String(firstDefined(
    seat.id,
    seat.seatCode,
    seat.SeatCode,
    seat.SSR_Code,
    seat.SSR_TypeDesc,
    seat.seatDesc,
    seat.SeatNo,
    seat.Seat_No,
    seat.SeatNumber,
    seat.Seat_Number,
    seat.Code,
    seat.Name,
    ""
  )).trim();
  const col = String(firstDefined(
    seat.col,
    seat.Column,
    seat.ColumnNo,
    seatCode.match(/[A-Z]$/i)?.[0],
    ""
  )).toUpperCase();
  const seatType = String(firstDefined(seat.seatType, seat.SeatType, seat.Seat_Type, "")).toLowerCase() || getSeatType(col);
  const extraCost = Number(firstDefined(
    seat.extraCost,
    seat.amount,
    seat.Amount,
    seat.Price,
    seat.TotalAmount,
    seat.totalAmount,
    seat.Seat_Amount,
    seat.SeatCharge,
    seat.Seat_Charge,
    0
  )) || 0;
  const isExtraLegroom = Boolean(firstDefined(seat.isExtraLegroom, seat.ExtraLegroom, seat.IsExtraLegroom, false));
  const isExitRow = Boolean(firstDefined(seat.isExitRow, seat.ExitRow, seat.IsExitRow, false));

  const segmentKey = firstDefined(seat.segmentKey, segmentMeta.segmentKey, null);
  const safeSeatCode = seatCode || `${rowNum}-${seatIdx + 1}`;

  return {
    id: segmentKey ? `${segmentKey}::${safeSeatCode}` : safeSeatCode,
    seatCode: safeSeatCode,
    ssrKey: firstDefined(seat.ssrKey, seat.SSR_Key, null),
    segmentId: firstDefined(seat.segmentId, seat.Segment_Id, seat.SegmentId, segmentMeta.segmentId, null),
    supplierLegIndex: firstDefined(seat.supplierLegIndex, seat.legIndex, seat.Leg_Index, seat.LegIndex, segmentMeta.supplierLegIndex, null),
    flightId: firstDefined(seat.flightId, seat.Flight_ID, seat.FlightID, segmentMeta.flightId, null),
    origin: firstDefined(seat.origin, seat.Origin, segmentMeta.origin, null),
    destination: firstDefined(seat.destination, seat.Destination, segmentMeta.destination, null),
    route: firstDefined(seat.route, seat.Route, seat.Sector, segmentMeta.route, null),
    flightNo: firstDefined(seat.flightNo, seat.FlightNo, seat.Flight_No, seat.FlightNumber, seat.Flight_Number, segmentMeta.flightNo, null),
    segmentKey,
    col,
    row: rowNum,
    seatType: ["window", "aisle", "middle"].includes(seatType) ? seatType : getSeatType(col),
    status: parseSeatAvailability(seat),
    isExtraLegroom,
    isExitRow,
    extraCost,
    applicablePaxTypes: Array.isArray(seat.ApplicablePaxTypes)
      ? seat.ApplicablePaxTypes
      : Array.isArray(seat.Applicable_PaxTypes)
        ? seat.Applicable_PaxTypes
        : Array.isArray(seat.applicablePaxTypes)
          ? seat.applicablePaxTypes
          : [],
    raw: seat,
  };
};

const findSeatRows = (value, seen = new Set()) => {
  if (!value || typeof value !== "object" || seen.has(value)) return [];
  seen.add(value);
  if (Array.isArray(value)) {
    const looksLikeRows = value.some((row) => {
      if (!row || typeof row !== "object") return false;
      const seats = firstDefined(row.seats, row.Seats, row.SeatInfo, row.Seat_Info, row.SeatDetails, row.Seat_Details);
      return Array.isArray(seats);
    });
    return looksLikeRows ? value : value.flatMap((item) => findSeatRows(item, seen));
  }
  return Object.values(value).flatMap((item) => findSeatRows(item, seen));
};

export const normalizeSeatMap = (data) => {
  if (Array.isArray(data?.seats)) return data.seats;
  if (Array.isArray(data?.seatMap)) return data.seatMap;
  if (Array.isArray(data?.seatMaps) && data.seatMaps.length) {
    const rows = [];
    data.seatMaps.forEach((seatMap) => {
      (seatMap.segments || []).forEach((segment, segmentIndex) => {
        const segmentMeta = buildSeatSegmentMeta(segment, seatMap, segmentIndex);
        (segment.rows || []).forEach((row, rowIdx) => {
          const rawSeats = row.seats || [];
          const rowNum = Number(firstDefined(row.rowNumber, row.row, row.RowNumber, row.RowNo)) || rowIdx + 1;
          const seats = rawSeats
            .filter((seat) => seat?.isAisle !== true && String(seat?.status || "").toLowerCase() !== "aisle")
            .map((seat, seatIdx) => normalizeApiSeat(seat, rowNum, seatIdx, segmentMeta))
            .filter((seat) => seat.id);
          if (seats.length) {
            rows.push({
              row: rowNum,
              ...segmentMeta,
              isExtraLegroom: seats.some((seat) => seat.isExtraLegroom),
              isExitRow: seats.some((seat) => seat.isExitRow),
              seats,
            });
          }
        });
      });
    });
    if (rows.length) return rows;
  }
  const airSeatMaps = data?.AirSeatMaps || data?.airSeatMaps;
  if (Array.isArray(airSeatMaps) && airSeatMaps.length) {
    const allRows = [];
    airSeatMaps.forEach((seatMap) => {
      const segments = seatMap.Seat_Segments || [];
      segments.forEach((seg, segmentIndex) => {
        const segmentMeta = buildSeatSegmentMeta(seg, seatMap, segmentIndex);
        const seatRows = seg.Seat_Row || [];
        seatRows.forEach((row, rowIdx) => {
          const rawSeats = row.Seat_Details || [];
          const rowNum = row.RowNumber || row.Row_Number || row.RowNo || rowIdx + 1;
          const seats = rawSeats
            .filter((s) => s.SSR_Type === "3" || s.SSR_Type === 3 || s.SSR_TypeName?.toLowerCase().includes("seat"))
            .map((seat) => {
              const seatCode = String(seat.SSR_TypeDesc || seat.Code || seat.SeatNo || "");
              const col = seatCode.replace(/\d+/, "") || "";
              const seatType = getSeatType(col);
              const extraCost = Number(seat.Amount || seat.Price || seat.TotalAmount || 0);
              return {
                id: segmentMeta.segmentKey ? `${segmentMeta.segmentKey}::${seatCode}` : seatCode,
                seatCode,
                ...segmentMeta,
                col,
                row: Number(rowNum),
                seatType,
                status: parseSeatAvailability(seat),
                isExtraLegroom: false,
                isExitRow: false,
                extraCost,
                ssrKey: firstDefined(seat.SSR_Key, seat.SSRKey, seat.ssrKey, null),
                applicablePaxTypes: Array.isArray(seat.ApplicablePaxTypes)
                  ? seat.ApplicablePaxTypes
                  : Array.isArray(seat.Applicable_PaxTypes)
                    ? seat.Applicable_PaxTypes
                    : [],
              };
            })
            .filter((s) => s.id);
          if (seats.length) {
            allRows.push({ row: Number(rowNum), ...segmentMeta, isExtraLegroom: false, isExitRow: false, seats });
          }
        });
      });
    });
    if (allRows.length) return allRows;
  }
  const sourceRows = findSeatRows(data)[0];
  if (!Array.isArray(sourceRows) || !sourceRows.length) return null;

  const rows = sourceRows.map((row, rowIdx) => {
    const rawSeats = firstDefined(row.seats, row.Seats, row.SeatInfo, row.Seat_Info, row.SeatDetails, row.Seat_Details) || [];
    const rowNum = Number(firstDefined(row.row, row.Row, row.RowNumber, row.Row_Number, row.RowNo, rawSeats[0]?.RowNumber)) || rowIdx + 1;
    const seats = rawSeats
      .map((seat, seatIdx) => normalizeApiSeat(seat, rowNum, seatIdx))
      .filter((seat) => seat.id);
    return {
      row: rowNum,
      isExtraLegroom: seats.some((seat) => seat.isExtraLegroom),
      isExitRow: seats.some((seat) => seat.isExitRow),
      seats,
    };
  }).filter((row) => row.seats.length);

  return rows.length ? rows : null;
};

const groupSeatRowsBySegment = (rows = []) => {
  if (!Array.isArray(rows) || !rows.length) return [];
  const groups = [];
  rows.forEach((row) => {
    const firstSeat = row.seats?.[0] || {};
    const segmentKey =
      row.segmentKey ||
      firstSeat.segmentKey ||
      [row.segmentId ?? firstSeat.segmentId, row.supplierLegIndex ?? firstSeat.supplierLegIndex, row.flightId ?? firstSeat.flightId, row.route ?? firstSeat.route]
        .filter((value) => value !== null && value !== undefined && String(value) !== "")
        .join("::") ||
      "journey";
    const existing = groups.find((group) => group.segmentKey === segmentKey);
    const meta = {
      segmentKey,
      segmentId: row.segmentId ?? firstSeat.segmentId ?? null,
      supplierLegIndex: row.supplierLegIndex ?? firstSeat.supplierLegIndex ?? null,
      flightId: row.flightId ?? firstSeat.flightId ?? null,
      origin: row.origin || firstSeat.origin || null,
      destination: row.destination || firstSeat.destination || null,
      route: row.route || firstSeat.route || null,
      flightNo: row.flightNo || firstSeat.flightNo || null,
    };
    if (existing) {
      existing.rows.push(row);
    } else {
      groups.push({ ...meta, rows: [row] });
    }
  });
  return groups;
};

const getSeatLabel = (seat) => seat?.seatCode || seat?.seatId || seat?.id || "";

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) => {
  const value = Number(n);
  return Number.isFinite(value) && value > 0
    ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(value)
    : null;
};

const toIsoDate = (v, fallback = DEFAULT_DEPART_DATE) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(v || "")) return v;
  const d = new Date(v);
  return isNaN(d) ? fallback : d.toISOString().slice(0, 10);
};

const addDays = (v, n) => {
  const d = new Date(`${toIsoDate(v)}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const sliceTime = (dt) => {
  if (!dt) return null;
  const clean = String(dt).replace("T", " ").trim();
  const match = clean.match(/(\d{1,2}):(\d{2})/);
  return match ? `${match[1].padStart(2, "0")}:${match[2]}` : null;
};
const formatStripDate = (isoDate) =>
  new Date(`${toIsoDate(isoDate)}T00:00:00`).toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
  });

const getHour = (t) => {
  if (!t || typeof t !== "string") return 0;
  return Number(t.split(":")[0]) || 0;
};

const extractLayover = (s) => { const m = s?.match(/via (.+)/i); return m ? m[1] : null; };

const estimateLegs = (duration) => {
  const [h, m] = duration.split("h");
  const total = (parseInt(h) || 0) * 60 + (parseInt(m) || 0);
  const leg1 = Math.round(total * 0.45);
  const layover = 75;
  const leg2 = total - leg1 - layover;
  const toHM = (mins) => `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return { leg1: toHM(leg1), layoverMins: layover, leg2: toHM(leg2) };
};

const addMinutesToTime = (timeStr, addMins) => {
  const [h, m] = timeStr.split(":").map(Number);
  const total = h * 60 + m + addMins;
  const nextDay = total >= 1440;
  const t = total % 1440;
  return {
    time: `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`,
    nextDay,
  };
};

const tagStyle = (bg, color) => ({
  fontSize: 10.5, padding: "2px 8px", borderRadius: 4, fontWeight: 600, background: bg, color,
});

// ── Price label helper ────────────────────────────────────────────────────────
// Returns contextual "/adult" text based on fare type and passenger mix
const baggageValue = (value) => {
  const text = String(value || "").trim();
  return text || null;
};

const baggageDisplay = (value) => baggageValue(value) || "Not provided by airline";
const TITLE_LABELS = {
  MR: "Mr",
  MRS: "Mrs",
  MS: "Ms",
  MSTR: "Master",
  MISS: "Miss",
};

const formatPassengerTitle = (title) => {
  const key = String(title || "").trim().toUpperCase();
  return TITLE_LABELS[key] || title || "";
};

const fareBaggageDisplay = (fare, flight) => {
  if (baggageValue(fare?.baggage)) return fare.baggage;
  const checkin = baggageValue(fare?.baggageCheckin || flight?.baggageCheckin);
  const cabin = baggageValue(fare?.baggageCabin || flight?.baggageCabin);
  const parts = [
    checkin && `${checkin} check-in`,
    cabin && `${cabin} cabin`,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "Not provided by airline";
};

const getPriceLabel = (activeFare, pax) => {
  if (!pax) return `/${PAX_TYPE_LABELS[activeFare] || "adult"}`;
  const { adults, children, infants } = pax;
  const parts = [];
  if (adults > 0) parts.push(`${adults} ${PAX_TYPE_LABELS[activeFare] || "adult"}${adults > 1 ? "s" : ""}`);
  if (children > 0) parts.push(`${children} child${children > 1 ? "ren" : ""}`);
  if (infants > 0) parts.push(`${infants} infant${infants > 1 ? "s" : ""}`);
  if (parts.length === 0) return `/${PAX_TYPE_LABELS[activeFare] || "adult"}`;
  if (parts.length === 1 && adults > 0 && children === 0 && infants === 0) {
    return `/${adults === 1 ? PAX_TYPE_LABELS[activeFare] || "adult" : `${adults} ${PAX_TYPE_LABELS[activeFare] || "adults"}`}`;
  }
  return `/${parts.join(" + ")}`;
};

// ── Skeleton ──────────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div style={{ background: "#fff", border: "1px solid rgba(34,197,94,0.12)", borderRadius: 18, padding: "18px 18px 14px", overflow: "hidden", position: "relative" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div style={sk(44, 44, 12)} />
          <div><div style={sk(110, 16, 6)} /><div style={{ ...sk(72, 11, 4), marginTop: 7 }} /></div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <div><div style={sk(52, 22, 6)} /><div style={{ ...sk(72, 11, 4), marginTop: 5 }} /></div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}><div style={sk(80, 2, 2)} /></div>
          <div><div style={sk(52, 22, 6)} /><div style={{ ...sk(72, 11, 4), marginTop: 5 }} /></div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: "#f0fdf4", borderRadius: 12, border: "1px solid rgba(34,197,94,0.12)" }}>
          <div style={sk(80, 22, 6)} /><div style={sk(100, 34, 10)} />
        </div>
      </div>
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.65) 50%, transparent 100%)", animation: "shimmer 1.4s infinite" }} />
      <style>{`@keyframes shimmer{0%{transform:translateX(-100%)}100%{transform:translateX(100%)}}`}</style>
    </div>
  );
}
const sk = (w, h, r) => ({
  width: w, height: h, borderRadius: r,
  background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.4s infinite",
});

/* ────────────────────────────────────────────────────────────────────────
   WHERE TO PASTE THIS
   In Flights.jsx, find the existing function:

       // ── CityCombobox ──────────────────────────────────────────────
       function CityCombobox({ label, valueCode, airports, loading, placeholder, onSelect }) {
         ... (old code) ...
       }

   Delete that ENTIRE function and paste this one in its place.
   Nothing else in the file needs to change — no backend/API changes.
   ──────────────────────────────────────────────────────────────────────── */

// ── CityCombobox ──────────────────────────────────────────────────────────────
function CityCombobox({
  label,
  valueCode,
  valueLabel,
  airports,
  loading,
  placeholder,
  onSelect
}) {
  const current = airports.find((a) => a.code === valueCode);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Reset the typed search text whenever the popover closes
  useEffect(() => {
    if (!open) setText("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const updateDirection = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const needed = 320;
      setOpenUp(spaceAbove > needed && spaceAbove > spaceBelow);
    };
    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);
    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [open]);

  // Auto-focus the input the moment it opens
  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  
  
  const matches = useMemo(() => {
    const q = text.trim().toLowerCase().replace(/\s+/g, " ");
    const source = Array.isArray(airports) ? airports : [];

    if (!q) return [];

    return source
      .filter((airport) => {
        const fields = [
          airport.city,
          airport.country,
          airport.countryCode,
          airport.code,
          airport.airport,
          airport.label,
          ...(Array.isArray(airport.aliases) ? airport.aliases : []),
        ];

        return fields.some((field) =>
          String(field || "")
            .toLowerCase()
            .split(/[\s,()-]+/)
            .filter(Boolean)
            .some((word) => word.startsWith(q))
        );
      })
      .slice(0, 10);
  }, [airports, text]);



  
const choose = (airport) => {
  if (!airport) return;

  setText("");
  setOpen(false);

  onSelect({
    label: airportLabel(airport),
    code: String(airport.code || "").trim().toUpperCase(),
  });
};


  // Short big city name, e.g. "Delhi" — falls back gracefully if `city` isn't provided by the API
  const cityShortName = (a) => {
    if (!a) return "";
    if (a.city) return a.city;
    const lbl = airportLabel(a);
    return lbl ? lbl.split(",")[0].trim() : a.code || "";
  };

  // Small sub-line, e.g. "DEL, Delhi Airport India"
  const airportSubLabel = (a) => {
    if (!a) return "";
    return `${a.code || ""}${a.airport ? `, ${a.airport}` : ""}`;
  };

  return (
    <div ref={wrapRef} className={`flight-city-combobox${openUp ? " is-open-up" : ""}${open ? " is-open" : ""}`}>
      {!open && (
        <button
          type="button"
          className="flight-city-display"
         
onClick={() => {
  setText("");
  setOpen(true);
}}

          aria-label={label}
        >
          <span className="flight-city-display-name">
            {valueLabel
              ? String(valueLabel).replace(/\s*\([A-Z0-9]{2,5}\)\s*$/i, "")
              : current
                ? cityShortName(current)
                : (placeholder || "Select city")}
          </span>

          <span className="flight-city-display-airport">
            {valueLabel
              ? (() => {
                const match = String(valueLabel).match(/\(([A-Z0-9]{2,5})\)/i);
                return match?.[1] ? match[1].toUpperCase() : "Tap to search";
              })()
              : current
                ? airportSubLabel(current)
                : "Tap to search"}
          </span>
        </button>
      )}

      {open && (
        <input
          ref={inputRef}
          type="text" value={text}
          placeholder={loading ? "Loading cities..." : (placeholder || "Type city or airport")}
          onChange={(e) => {
  const nextText = e.target.value;
  setText(nextText);
  onSelect({ label: nextText, code: "" });
}}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          aria-label={label} autoComplete="off"
        />
      )}

      {open && (
        <div
          className="flight-city-menu"
          style={{
            top: openUp ? "auto" : "calc(100% + 14px)",
            bottom: openUp ? "calc(100% + 14px)" : "auto",
            maxHeight: "min(300px, calc(100vh - 24px))",
          }}
        >
          {matches.length > 0 ? matches.map((a) => (
            <button
              type="button"
className="flight-city-option"
key={`${a.code}-${a.city || a.airport || ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(a)}
            >
              <span>{airportLabel(a)}</span>
              <small>{a.code}{a.airport ? ` — ${a.airport}` : ""}</small>
            </button>
          )) : <div className="flight-city-empty">No matching city found.</div>}
        </div>
      )}
    </div>
  );
}

// ── Passenger Selector ────────────────────────────────────────────────────────
function PassengerSelector({ adults, children, infants, classType, onChange, activeFare }) {
  const total = adults + children + infants;

  const update = (key, delta) => {
    const current = { adults, children, infants };
    const next = Math.max(key === "adults" ? 1 : 0, current[key] + delta);
    onChange({ ...current, [key]: next });
  };
  const fareLabel = PAX_TYPE_LABELS[activeFare] || "adult";

  return (
    <div className="pax-selector-card">
      <div className="pax-selector-title">
        <Users size={14} /> Passengers &amp; Class
      </div>

      {[
        { key: "adults", label: `${fareLabel.charAt(0).toUpperCase() + fareLabel.slice(1)}s`, sub: activeFare === "senior" ? "60+ years" : activeFare === "student" ? "12–25 years" : "12+ years" },
        { key: "children", label: "Children", sub: "2–12 years" },
        { key: "infants", label: "Infants", sub: "Under 2 years (lap)" },
      ].map(({ key, label, sub }) => {
        const val = key === "adults" ? adults : key === "children" ? children : infants;
        const min = key === "adults" ? 1 : 0;
        return (
          <div className="pax-selector-row" key={key}>
            <div className="pax-selector-info">
              <div className="pax-selector-label">{label}</div>
              <div className="pax-selector-sub">{sub}</div>
            </div>
            <div className="pax-selector-controls">
              <button
                type="button" className="pax-btn"
                onClick={() => update(key, -1)}
                disabled={val <= min}
              >−</button>
              <span className="pax-count">{val}</span>
              <button
                type="button" className="pax-btn"
                onClick={() => update(key, 1)}
              >+</button>
            </div>
          </div>
        );
      })}

      <select
        className="pax-class-select"
        value={classType}
        onChange={(e) => onChange({ adults, children, infants, classType: e.target.value })}
      >
        {CLASS_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>

      <div style={{ marginTop: 10, fontSize: 11.5, color: "#64748b", textAlign: "center" }}>
        {total} passenger{total > 1 ? "s" : ""} · {classType}
        {activeFare !== "regular" && (
          <span style={{ marginLeft: 6, color: "#10b981", fontWeight: 700 }}>
            ({fareTypes.find(f => f.id === activeFare)?.label} fare)
          </span>
        )}
      </div>
    </div>
  );
}
function SearchPaxClassField({ pax, onChange }) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const wrapRef = useRef(null);
  const { adults, children, infants, classType } = pax;
  const total = adults + children + infants;

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const updateDirection = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUp(spaceBelow < 300 && spaceAbove > spaceBelow);
    };
    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);
    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [open]);

  const update = (key, delta) => {
    const next = { adults, children, infants };
    const candidate = next[key] + delta;

    if (key === "adults") {
      const minAdults = Math.max(1, infants); // can't go below infants needing an adult
      next.adults = Math.max(minAdults, candidate);
    } else if (key === "infants") {
      // infants can never exceed adults (1 adult per infant minimum)
      next.infants = Math.max(0, Math.min(candidate, adults));
    } else {
      next.children = Math.max(0, candidate);
    }

    onChange(next);
  };

  const summaryLabel = `${total} Traveller${total > 1 ? "s" : ""}, ${classType.split("/")[0]}`;

  return (
    <div className={`flight-search-field flight-search-field--pax${open ? " pax-open" : ""}`} ref={wrapRef} style={{ position: "relative" }}>
      <span className="flight-field-icon"><Users size={13} /></span>
      <label>TRAVELLERS</label>
      <button
        type="button"
        className="flight-pax-summary"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <strong>{summaryLabel}</strong>
        {/* <span>Tap to edit travellers and cabin class</span> */}
      </button>

      {open && (
        <div className="pax-selector-card" style={{
          position: "absolute",
          top: openUp ? "auto" : "calc(100% + 8px)",
          bottom: openUp ? "calc(100% + 8px)" : "auto",
          left: "auto", right: 0,
          zIndex: 10000,   // ← match your city combobox / modal z-index tier
          width: "min(340px, calc(100vw - 24px))",
          maxHeight: "min(320px, calc(100vh - 24px))",
          overflowY: "auto",
          boxShadow: "0 16px 40px rgba(13,27,62,0.18)",
          marginBottom: 0,
        }}>
          {[
            { key: "adults", label: "Adults", sub: "12+ years", min: Math.max(1, infants) },
            { key: "children", label: "Children", sub: "2–12 years", min: 0 },
            { key: "infants", label: "Infants", sub: "Under 2 years (lap, needs an adult)", min: 0 },
          ].map(({ key, label, sub, min }) => {
            const val = pax[key];
            const atMax = key === "infants" ? val >= adults : false;
            return (
              <div className="pax-selector-row" key={key}>
                <div className="pax-selector-info">
                  <div className="pax-selector-label">{label}</div>
                  <div className="pax-selector-sub">{sub}</div>
                </div>
                <div className="pax-selector-controls">
                  <button type="button" className="pax-btn" onClick={() => update(key, -1)} disabled={val <= min}>−</button>
                  <span className="pax-count">{val}</span>
                  <button type="button" className="pax-btn" onClick={() => update(key, 1)} disabled={atMax}>+</button>
                </div>
              </div>
            );
          })}

          {infants > adults && (
            <div style={{ fontSize: 11, color: "#b91c1c", padding: "4px 2px" }}>
              Each infant must travel with an adult.
            </div>
          )}

          <select
            className="pax-class-select"
            value={classType}
            onChange={(e) => onChange({ ...pax, classType: e.target.value })}
          >
            {CLASS_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

          <button type="button" onClick={() => setOpen(false)} style={{ width: "100%", marginTop: 10, background: "linear-gradient(135deg,#10b981,#047857)", color: "#fff", border: "none", borderRadius: 10, padding: "9px 0", fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
            Done
          </button>
        </div>
      )}
    </div>
  );
}
// ── Enhanced Seat Map Component ───────────────────────────────────────────────
export function SeatMap({ seatMap, selectedSeats, onSeatToggle, requiredSeats, airlineCode, loading }) {
  const [preferredType, setPreferredType] = useState(null); // "window" | "aisle" | "middle" | null
  const [viewMode, setViewMode] = useState("full"); // "full" | "compact"
  const getTypeFee = (type) => {
    const costs = seatMap
      .flatMap((row) => row.seats)
      .filter((seat) => seat.seatType === type && !isSeatUnavailable(seat))
      .map((seat) => Number(seat.extraCost || 0))
      .filter((cost) => Number.isFinite(cost) && cost > 0);
    return costs.length ? Math.min(...costs) : null;
  };

  // Auto-suggest seats based on preference
  const suggestSeats = (type) => {
    setPreferredType(type);
    // Find first available seat group of this type
    const available = seatMap
      .flatMap(row => row.seats)
      .filter(s => s.status === "available" && s.seatType === type)
      .sort((a, b) => {
        const aPreferred = Number(a.row) >= 20 && Number(a.row) <= 30 ? 0 : 1;
        const bPreferred = Number(b.row) >= 20 && Number(b.row) <= 30 ? 0 : 1;
        return aPreferred - bPreferred || Number(a.row || 0) - Number(b.row || 0);
      });
    if (available.length >= requiredSeats) {
      // Pick seats in same row if possible
      const preferredRows = [...seatMap].sort((a, b) => {
        const aPreferred = Number(a.row) >= 20 && Number(a.row) <= 30 ? 0 : 1;
        const bPreferred = Number(b.row) >= 20 && Number(b.row) <= 30 ? 0 : 1;
        return aPreferred - bPreferred || Number(a.row || 0) - Number(b.row || 0);
      });
      for (const row of preferredRows) {
        const matches = row.seats.filter(s => s.status === "available" && s.seatType === type);
        if (matches.length >= requiredSeats) {
          const toSelect = matches.slice(0, requiredSeats).map(s => s.id);
          toSelect.forEach(id => {
            if (!selectedSeats.includes(id)) onSeatToggle(id);
          });
          return;
        }
      }
      // Fallback: pick first N available
      available.slice(0, requiredSeats).forEach(s => {
        if (!selectedSeats.includes(s.id)) onSeatToggle(s.id);
      });
    }
  };

  const totalExtraCost = selectedSeats.reduce((sum, seatId) => {
    const seat = seatMap.flatMap(r => r.seats).find(s => s.id === seatId);
    return sum + (seat?.extraCost || 0);
  }, 0);

  if (loading) {
    return (
      <div style={{ padding: "32px", textAlign: "center" }}>
        <div style={{ width: 36, height: 36, border: "3px solid #d1fae5", borderTopColor: "#10b981", borderRadius: "50%", animation: "spin 0.9s linear infinite", margin: "0 auto 12px" }} />
        <div style={{ fontSize: 13, color: "#64748b" }}>Loading seat map from airline…</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!seatMap.length) {
    return (
      <div className="seat-map-wrap">
        <div className="seat-map-title">Seat Map Unavailable</div>
        <div className="seat-map-sub">
          The airline did not return selectable seats for this fare. You can continue and choose seats at check-in.
        </div>
        <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px", fontSize: 12.5, color: "#64748b" }}>
          Seat selection is optional for this booking. No extra seat charges will be added.
        </div>
      </div>
    );
  }

  return (
    <div className="seat-map-wrap">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <div className="seat-map-title">Choose Your Seats</div>
          <div className="seat-map-sub">
            {selectedSeats.length}/{requiredSeats} seat{requiredSeats > 1 ? "s" : ""} selected
            {totalExtraCost > 0 && <span style={{ color: "#d97706", fontWeight: 700, marginLeft: 8 }}>+{fmt(totalExtraCost)} extra</span>}
          </div>
        </div>
        <button
          onClick={() => setViewMode(v => v === "full" ? "compact" : "full")}
          style={{ background: "#f0fdf4", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 8, padding: "5px 10px", fontSize: 11, color: "#047857", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}
        >
          {viewMode === "full" ? <><EyeOff size={12} /> Compact</> : <><Eye size={12} /> Full View</>}
        </button>
      </div>

      <div className="seat-quick-pick">
        <div className="seat-quick-pick-title">Quick Pick by Seat Type</div>
        <div className="seat-quick-pick-grid">
          {[
            { type: "window", label: "Window", desc: "Side row" },
            { type: "aisle", label: "Aisle", desc: "Easy access" },
            { type: "middle", label: "Middle", desc: "Center seat" },
          ].map(({ type, label, desc }) => {
            const isActive = preferredType === type;
            const availCount = seatMap.flatMap(r => r.seats).filter(s => s.seatType === type && s.status === "available").length;
            const priceAdd = getTypeFee(type);
            return (
              <button
                key={type}
                onClick={() => suggestSeats(type)}
                className={`seat-quick-card ${isActive ? "is-active" : ""}`}
              >
                <Armchair size={18} strokeWidth={2.4} />
                <div className="seat-quick-label">{label}</div>
                <div className="seat-quick-desc">{desc}</div>
                <div className={`seat-quick-price ${priceAdd > 0 ? "has-fee" : ""}`}>
                  {priceAdd > 0 ? `from +₹${priceAdd}` : "Airline priced"}
                </div>
                <div className="seat-quick-count">{availCount} avail.</div>
              </button>
            );
          })}
          <button
            onClick={() => { setPreferredType(null); selectedSeats.forEach(id => onSeatToggle(id)); }}
            className="seat-quick-card seat-quick-clear"
          >
            <span aria-hidden="true">×</span>
            <div>Clear</div>
            <div className="seat-quick-count">All seats</div>
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="seat-map-legend" style={{ flexWrap: "wrap", gap: 8 }}>
        {[
          { bg: "#eafff5", border: "1.5px solid #2dd4bf", label: "Available" },
          { bg: "linear-gradient(135deg,#2563eb,#1e3a8a)", label: "Selected" },
          { bg: "repeating-linear-gradient(135deg,#334155 0 5px,#475569 5px 10px)", border: "1.5px solid #0f172a", label: "Booked" },
          { bg: "repeating-linear-gradient(45deg,#fee2e2 0 5px,#fca5a5 5px 10px)", border: "1.5px solid #ef4444", label: "Blocked" },
          { bg: "linear-gradient(180deg,#fef3c7,#fde68a)", border: "1.5px solid #f59e0b", label: "Extra Legroom" },
          { bg: "linear-gradient(180deg,#dbeafe,#bfdbfe)", border: "1.5px solid #60a5fa", label: "Exit Row" },
        ].map(({ bg, border, label }) => (
          <div key={label} className="seat-legend-item">
            <div className="seat-legend-dot" style={{ background: bg, border: border || "none" }} />
            {label}
          </div>
        ))}
      </div>

      {/* Seat type price info */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, padding: "8px 10px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Seat fees:</span>
        {["window", "aisle", "middle"].map((type) => {
          const fee = getTypeFee(type);
          return (
            <span key={type} style={{ fontSize: 11 }}>
              {SEAT_TYPE_LABELS[type]} <strong style={{ color: fee ? "#d97706" : "#16a34a" }}>{fee ? `from +₹${fee}` : "as returned"}</strong>
            </span>
          );
        })}
      </div>


      <div className={`seat-cabin ${viewMode === "compact" ? "is-compact" : ""}`}>
        <div className="seat-cabin-nose">
          <Plane size={18} />
          <span>Front</span>
        </div>
        <div className="seat-wing-mark seat-wing-mark--left">Wing</div>
        <div className="seat-wing-mark seat-wing-mark--right">Wing</div>
        <div className="seat-map-grid" style={{ maxHeight: viewMode === "compact" ? 220 : 360 }}>
          {seatMap.map((rowData) => (
            <div className="seat-row" key={rowData.row}>
              <div className="seat-row-num">{rowData.row}</div>
              <div className="seat-bank seat-bank--left">
                {rowData.seats.slice(0, 3).map((seat) => {
                  const isSelected = selectedSeats.includes(seat.id);
                  const canSelect = selectedSeats.length < requiredSeats || isSelected;
                  let cls = isSeatUnavailable(seat)
                    ? seat.status
                    : isSelected
                      ? "selected"
                      : seat.isExtraLegroom
                        ? "extra-legroom"
                        : seat.isExitRow
                          ? "exit-row"
                          : "available";
                  const seatSize = viewMode === "compact" ? 25 : 34;

                  return (
                    <button
                      type="button"
                      key={seat.id}
                      className={`seat-btn ${cls} seat-type-${seat.seatType}`}
                      style={{ width: seatSize, height: seatSize }}
                      onClick={() => {
                        if (isSeatUnavailable(seat)) return;
                        if (!canSelect && !isSelected) return;
                        onSeatToggle(seat);
                      }}
                      title={`${seat.id} · ${SEAT_TYPE_LABELS[seat.seatType]}${seat.isExtraLegroom ? " · Extra Legroom" : ""}${seat.isExitRow ? " · Exit Row" : ""}${seat.extraCost > 0 ? ` · +₹${seat.extraCost}` : ""}`}
                    >
                      <span className="seat-chair-back" />
                      {isSeatUnavailable(seat)
                        ? <span className="seat-mark">{seat.status === "blocked" ? "X" : "B"}</span>
                        : isSelected
                          ? <Check size={12} strokeWidth={3} className="seat-mark" />
                          : viewMode === "compact"
                            ? null
                            : <span className="seat-label">{getSeatLabel(seat)}</span>
                      }
                    </button>
                  );
                })}
              </div>
              <div className="seat-aisle">
                <span />
              </div>
              <div className="seat-bank seat-bank--right">
                {rowData.seats.slice(3).map((seat) => {
                  const isSelected = selectedSeats.includes(seat.id);
                  const canSelect = selectedSeats.length < requiredSeats || isSelected;


                  let cls = isSeatUnavailable(seat)
                    ? seat.status
                    : isSelected
                      ? "selected"
                      : seat.isExtraLegroom
                        ? "extra-legroom"
                        : seat.isExitRow
                          ? "exit-row"
                          : "available";

                  const seatSize = viewMode === "compact" ? 25 : 34;

                  return (
                    <button
                      type="button"
                      key={seat.id}
                      className={`seat-btn ${cls} seat-type-${seat.seatType}`}
                      style={{ width: seatSize, height: seatSize }}
                      onClick={() => {
                        if (isSeatUnavailable(seat)) return;
                        if (!canSelect && !isSelected) return;
                        onSeatToggle(seat);
                      }}
                      title={`${seat.id} · ${SEAT_TYPE_LABELS[seat.seatType]}${seat.isExtraLegroom ? " · Extra Legroom" : ""}${seat.isExitRow ? " · Exit Row" : ""}${seat.extraCost > 0 ? ` · +₹${seat.extraCost}` : ""}`}
                    >
                      <span className="seat-chair-back" />
                      {isSeatUnavailable(seat)
                        ? <span className="seat-mark">{seat.status === "blocked" ? "X" : "B"}</span>
                        : isSelected
                          ? <Check size={12} strokeWidth={3} className="seat-mark" />
                          : viewMode === "compact"
                            ? null
                            : <span className="seat-label">{getSeatLabel(seat)}</span>
                      }
                    </button>
                  );
                })}
              </div>
              {(rowData.isExtraLegroom || rowData.isExitRow) && viewMode !== "compact" && (
                <div className={`seat-row-note ${rowData.isExitRow ? "is-exit" : "is-legroom"}`}>
                  {rowData.isExitRow ? "Exit" : "+Legroom"}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="seat-cabin-tail">Rear</div>
      </div>

      {selectedSeats.length > 0 && (
        <div style={{ marginTop: 12, background: "#f0fdf4", borderRadius: 10, padding: "10px 14px", border: "1px solid rgba(16,185,129,0.2)" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#047857", marginBottom: 6 }}>Selected Seats</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {selectedSeats.map(seatId => {
              const seat = seatMap.flatMap(r => r.seats).find(s => s.id === seatId);
              if (!seat) return null;
              return (
                <div key={seatId} style={{ display: "flex", alignItems: "center", gap: 5, background: "#fff", borderRadius: 6, padding: "4px 8px", border: "1px solid rgba(16,185,129,0.3)", fontSize: 12 }}>
                  <span>{getSeatTypeIcon(seat.seatType)}</span>
                  <span style={{ fontWeight: 700, color: "#047857" }}>{getSeatLabel(seat)}</span>
                  <span style={{ color: "#64748b" }}>{SEAT_TYPE_LABELS[seat.seatType]}</span>
                  {seat.extraCost > 0 && <span style={{ color: "#d97706", fontWeight: 600 }}>+₹{seat.extraCost}</span>}
                  <button onClick={() => onSeatToggle(seatId)} style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                </div>
              );
            })}
          </div>
          {totalExtraCost > 0 && (
            <div style={{ marginTop: 8, fontSize: 12.5, color: "#d97706", fontWeight: 700, textAlign: "right" }}>
              Seat charges: +{fmt(totalExtraCost)}
            </div>
          )}
        </div>
      )}

      <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", marginTop: 8 }}>
        Seat selection is optional · You can also select at check-in
      </div>
    </div>
  );
}

// ── EasyBuzz Payment Component ────────────────────────────────────────────────
function EasyBuzzPayment({
  bookingData,
  amount,
  token,
  guestAccessToken,
  onSuccess,
  onFailure,
  onClose,
  onAuthExpired
}) {
  const [status, setStatus] = useState("initiating");
  const [txnData, setTxnData] = useState(null);
  const [error, setError] = useState("");
  const paymentClickRef = useRef(false);

  useEffect(() => { initiatePayment(); }, []);

  const initiatePayment = async () => {
    setStatus("initiating"); setError("");
    try {
      const res = await fetch(`${API_BASE}/payment/easebuzz/initiate`, {
        method: "POST",
        headers: {
  "Content-Type": "application/json",
  ...(token
    ? { Authorization: `Bearer ${token}` }
    : guestAccessToken
      ? { "x-guest-access-token": guestAccessToken }
      : {}),
},
        body: JSON.stringify({
          service: "flight",
          amount: Number(amount.toFixed(2)),
          productinfo: `Flight ${bookingData.departCode} to ${bookingData.arriveCode}`,
          firstname: bookingData.passengerName || "Traveller",
          email:
  String(bookingData.passengerEmail || "").trim() ||
  COMPANY_EMAIL,
          phone: String(bookingData.passengerPhone || "").trim(),
          bookingReference: bookingData.bookingReference,
          udf1: bookingData.departDate || "",
          udf2: bookingData.airline || "",
          udf3: bookingData.fareName || "",

          status: "upcoming",
        }),

      });
      const data = await res.json();  // call ONCE only
      

      if (res.status === 401) {
  if (token) {
    onAuthExpired?.();
    throw new Error(
      "Your login session expired. Please log in again to continue payment."
    );
  }

  throw new Error(
    "Guest payment authorization failed. Please restart the booking."
  );
}
      if (!res.ok || !data.access_key) throw new Error(data.error || "Payment initiation failed");
      setTxnData(data);
      setStatus("ready");
    } catch (err) {
      setError(err.message || "Could not initiate payment. Please try again.");
      setStatus("failed");
    }
  };

  const handlePay = () => {
    if (!txnData?.access_key) return;
    if (paymentClickRef.current) return;
    paymentClickRef.current = true;
    setStatus("processing");
    const script = document.createElement("script");
    script.src = "https://ebz-static.s3.ap-south-1.amazonaws.com/easecheckout/easebuzz-checkout.js";
    script.onload = () => {
      const easebuzzCheckout = new window.EasebuzzCheckout(
        txnData.key,
        txnData.env || "test"
      );
      const options = {
        access_key: txnData.access_key,
        onResponse: async (response) => {
          if (response.status === "success") {
            setStatus("confirming");
            await onSuccess(response);
          }
          else {
            paymentClickRef.current = false;

            setStatus("failed");
            setError(
              `Payment ${response.status}: ${response.error_Message || "Please try again."
              }`
            );

            onFailure(response);
          }
        },
        theme: "#10b981",
      };
      easebuzzCheckout.initiatePayment(options);
    };
    script.onerror = () => {
      paymentClickRef.current = false;
      setStatus("failed");
      setError("Could not load payment gateway. Please try again.");
    };
    document.body.appendChild(script);
  };

  return (
    <div>
      <div className="easebuzz-amount-bar">
        <div>
          <div className="easebuzz-amount-label">Total Amount</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>
            {bookingData.departCode} → {bookingData.arriveCode} · {bookingData.fareName || "Economy"}
          </div>
        </div>
        <div className="easebuzz-amount-value">{fmt(amount)}</div>
      </div>

      {status === "initiating" && (
        <div className="easebuzz-loading">
          <div className="easebuzz-spinner" />
          <div style={{ fontSize: 13, color: "#64748b" }}>
            {status === "initiating" ? "Securing your payment session…" : "Opening payment gateway…"}
          </div>
        </div>
      )}

      {(
        status === "ready" ||
        status === "processing" ||
        status === "confirming"
      ) && txnData && (
          <div className="easebuzz-container">
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0d1b3e", marginBottom: 4 }}>Secure Checkout via EasyBuzz</div>
              <div style={{ fontSize: 11.5, color: "#64748b" }}>All major cards, UPI, net banking &amp; wallets accepted.</div>
            </div>
            <div style={{ background: "#fff", borderRadius: 10, padding: "12px 14px", marginBottom: 14, border: "1px solid #e2e8f0" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" }}>
                {[
                  { label: "Flight", value: bookingData.flightNumbers },
                  { label: "Route", value: `${bookingData.departCode} → ${bookingData.arriveCode}` },
                  { label: "Travel Date", value: bookingData.departDate },
                  { label: "Fare", value: bookingData.fareName },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>{label}</div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "#0d1b3e", marginTop: 1 }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={handlePay}
              disabled={
                status === "processing" ||
                status === "confirming"
              }
              style={{
                width: "100%",
                background: "linear-gradient(135deg,#10b981,#047857)",
                color: "#fff",
                border: "none",
                borderRadius: 12,
                padding: "14px 0",
                fontFamily: "'Syne',sans-serif",
                fontSize: 15,
                fontWeight: 700,

                cursor:
                  status === "processing" || status === "confirming"
                    ? "not-allowed"
                    : "pointer",

                opacity:
                  status === "processing" || status === "confirming"
                    ? 0.7
                    : 1,

                boxShadow: "0 8px 28px rgba(16,185,129,0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <Lock size={15} />

              {status === "processing"
                ? "Opening Payment Gateway…"
                : status === "confirming"
                  ? "Payment Processing…"
                  : `Pay ${fmt(amount)} Securely`}
            </button>
            <div className="payment-security-badge"><ShieldCheck size={15} />256-bit SSL encryption · PCI-DSS compliant · Powered by EasyBuzz</div>
          </div>
        )}

      {status === "failed" && (
        <div style={{ padding: "20px 0" }}>
          <div style={{ background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 12, padding: "16px 20px", marginBottom: 16, textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>❌</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#b91c1c", marginBottom: 4 }}>Payment Failed</div>
            <div style={{ fontSize: 12.5, color: "#64748b" }}>{error}</div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={initiatePayment} style={{ flex: 1, background: "linear-gradient(135deg,#10b981,#047857)", color: "#fff", border: "none", borderRadius: 10, padding: "12px 0", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>Retry Payment</button>
            <button onClick={onClose} style={{ flex: 1, background: "none", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "12px 0", fontWeight: 600, cursor: "pointer", fontSize: 13, color: "#64748b" }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
// ── Animated E-Ticket ──────────────────────────────────────────────────────
function FlightETicket({
  flight, fare, pax, paxLabel, activeFare, bookingRef, totalPrice,
  selectedSeatSummary, travelDate, bookingForm, ticketStatus, totalPassengers,
  extras = [], cancellationInfo = [], legs = null, selectedSeatGroups = [],
}) {
  const dateParts = formatFlightDate(travelDate || flight.travelDate);
  const statusMap = {
    null: { label: "Confirming…", bg: "#eff6ff", fg: "#1e40af", dot: "#3b82f6" },
    success: { label: "Confirmed", bg: "#ecfdf5", fg: "#047857", dot: "#10b981" },
    inprocess: { label: "Processing", bg: "#fffbeb", fg: "#92400e", dot: "#f59e0b" },
    failed: { label: "Action Needed", bg: "#fef2f2", fg: "#b91c1c", dot: "#ef4444" },
  };
  const status = statusMap[ticketStatus] || statusMap.null;
  const multiLeg = Array.isArray(legs) && legs.length > 1;
  const displayFlightNumbers =
    multiLeg
      ? legs
        .map((leg) => leg.flightNumbers)
        .filter(Boolean)
        .join(", ")
      : flight.flightNumbers;
  const passengerNames = Array.from(
    {
      length:
        (pax.adults || 0) +
        (pax.children || 0) +
        (pax.infants || 0) || 1,
    },
    (_, i) =>
      `${formatPassengerTitle(bookingForm[`title_${i}`])} ${bookingForm[`name_${i}`] || ""
        } ${bookingForm[`lname_${i}`] || ""}`.trim()
  ).filter(Boolean);

  const extraGroups = Object.values(
    (extras || []).reduce((groups, item) => {
      const legIndex = Number(item.itineraryLegIndex ?? 0);
      const outerLeg = Array.isArray(legs) && legs[legIndex] ? legs[legIndex] : flight;
      const outerRoute = [outerLeg?.departCode, outerLeg?.arriveCode].filter(Boolean).join(" → ");
      const route =
        item.route ||
        [item.origin, item.destination].filter(Boolean).join(" → ");
      const flightNo = item.flightNo || item.flightNumbers || "";
      const isJourneyWide =
        item.displayJourneyWide === true ||
        item.segmentWise === false;
      const hasSegmentMeta = !isJourneyWide && Boolean(
        route ||
        item.segmentKey ||
        item.segmentId ||
        item.supplierLegIndex
      );
      const key = hasSegmentMeta
        ? `${legIndex}::${item.segmentKey || item.segmentId || `${route}|${flightNo}`}`
        : `${legIndex}::journey`;

      if (!groups[key]) {
        groups[key] = {
          heading: hasSegmentMeta
            ? [route, flightNo].filter(Boolean).join(" · ")
            : [outerRoute, "Entire journey"].filter(Boolean).join(" · "),
          items: [],
        };
      }

      groups[key].items.push(item);
      return groups;
    }, {})
  );

  return (
    <div className="eticket-wrap">
      <style>{`
        @keyframes eticketIn {
          0% { opacity: 0; transform: translateY(24px) scale(.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes eticketPlane {
          0% { left: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { left: calc(100% - 18px); opacity: 0; }
        }
        @keyframes eticketPulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(16,185,129,.35); }
          50% { box-shadow: 0 0 0 6px rgba(16,185,129,0); }
        }
        @keyframes eticketShine {
          0% { transform: translateX(-120%) rotate(8deg); }
          100% { transform: translateX(220%) rotate(8deg); }
        }
        .eticket-card { animation: eticketIn .55s cubic-bezier(.22,1,.36,1) both; }
        .eticket-status-dot { animation: eticketPulse 1.8s ease-in-out infinite; }
        .eticket-shine {
          position: absolute; top: -40%; left: 0; width: 40%; height: 180%;
          background: linear-gradient(120deg, transparent, rgba(255,255,255,.28), transparent);
          animation: eticketShine 3.2s ease-in-out infinite; pointer-events: none;
        }
        .eticket-plane-track { position: relative; height: 18px; }
        .eticket-plane-track svg { position: absolute; top: -3px; animation: eticketPlane 2.6s linear infinite; }
      `}</style>

      <div
        className="eticket-card"
        style={{
          position: "relative", overflow: "hidden", borderRadius: 20,
          background: "#fff", boxShadow: "0 20px 50px rgba(13,27,62,0.18)",
          border: "1px solid rgba(15,23,42,0.06)", maxWidth: 640, margin: "0 auto",
          textAlign: "left",
        }}
      >
        {/* Header */}
        <div style={{
          position: "relative", background: "linear-gradient(120deg,#0d9488,#047857 55%,#10b981)",
          padding: "18px 22px", color: "#fff", overflow: "hidden",
        }}>
          <div className="eticket-shine" />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", zIndex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Plane size={18} />
              </div>
              <div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, lineHeight: 1.1 }}>{flight.airline}</div>
                <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>{displayFlightNumbers} · {fare?.name || "Economy"}{activeFare !== "regular" ? ` · ${fareTypes.find(f => f.id === activeFare)?.label}` : ""}</div>
              </div>
            </div>
            <div style={{
              display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.16)",
              borderRadius: 20, padding: "5px 11px", fontSize: 11, fontWeight: 700,
            }}>
              <span className="eticket-status-dot" style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", display: "inline-block" }} />
              {status.label === "Confirmed" ? "E-TICKET" : status.label.toUpperCase()}
            </div>
          </div>
        </div>

        {/* Route strip */}
        {!multiLeg && (
          <div style={{ padding: "22px 24px 14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ textAlign: "left", minWidth: 84 }}>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 26, fontWeight: 800, color: "#0d1b3e", lineHeight: 1 }}>{flight.departTime}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#047857", marginTop: 3 }}>{flight.departCode}</div>
                <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 1, maxWidth: 90 }}>{flight.departAirport}</div>
              </div>

              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "0 4px" }}>
                <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 700 }}>{flight.duration}</div>
                <div style={{ width: "100%", position: "relative" }}>
                  <div style={{ width: "100%", borderTop: "2px dashed #cbd5e1" }} />
                  <div className="eticket-plane-track" style={{ position: "absolute", top: -9, left: 0, width: "100%" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2 L4 20 L12 16 L20 20 Z" fill="#10b981" />
                    </svg>
                  </div>
                </div>
                {!multiLeg && (
                  <div style={{ fontSize: 10, fontWeight: 700, color: flight.stops > 0 ? "#f59e0b" : "#10b981" }}>{flight.stopDetail}</div>
                )}
              </div>

              <div style={{ textAlign: "right", minWidth: 84 }}>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 26, fontWeight: 800, color: "#0d1b3e", lineHeight: 1 }}>
                  {flight.arriveTime}{flight.arriveNextDay && <sup style={{ fontSize: 11, color: "#dc2626" }}>+1</sup>}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#047857", marginTop: 3 }}>{flight.arriveCode}</div>
                <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 1, maxWidth: 90, marginLeft: "auto" }}>{flight.arriveAirport}</div>
              </div>
            </div>

            {dateParts && (
              <div style={{ marginTop: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#0d1b3e", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "7px 0" }}>
                <CalendarDays size={13} color="#10b981" />
                {dateParts.weekday}, {dateParts.day} {dateParts.month} {`20${dateParts.year}`}
              </div>
            )}
          </div>
        )}

        {/* Perforated divider */}
        <div style={{ position: "relative", height: 20, margin: "0 24px" }}>
          <div style={{ position: "absolute", left: -34, top: -10, width: 20, height: 20, borderRadius: "50%", background: "#0f172a10", boxShadow: "inset 0 0 0 9999px #f4f6fa" }} />
          <div style={{ position: "absolute", right: -34, top: -10, width: 20, height: 20, borderRadius: "50%", background: "#0f172a10", boxShadow: "inset 0 0 0 9999px #f4f6fa" }} />
          <div style={{ borderTop: "2px dashed #d8dee8", position: "absolute", left: 0, right: 0, top: 0 }} />
        </div>

        {/* Details grid */}
        <div style={{ padding: "16px 24px 6px", display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "14px 10px" }}>
          {[
            { label: "Booking Reference", value: bookingRef, strong: true },
            {
              label: "Passengers",
              value: `${pax.adults || 0} Adult${pax.adults > 1 ? "s" : ""}${pax.children ? `, ${pax.children} Child${pax.children > 1 ? "ren" : ""}` : ""}${pax.infants ? `, ${pax.infants} Infant${pax.infants > 1 ? "s" : ""}` : ""}`
            },
            { label: "Seat(s)", value: selectedSeatGroups.length ? "Selected" : (selectedSeatSummary || "At check-in") },
            { label: "Baggage", value: fareBaggageDisplay(fare, flight) },
            { label: "Class", value: fare?.name || "Economy" },
            { label: "Fare Type", value: fareTypes.find(f => f.id === activeFare)?.label || "Regular" },
          ].map(({ label, value, strong }) => (
            <div key={label}>
              <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
              <div style={{ fontSize: strong ? 14 : 12.5, fontWeight: strong ? 800 : 600, color: strong ? "#047857" : "#0d1b3e", marginTop: 2, letterSpacing: strong ? 1 : 0, fontFamily: strong ? "monospace" : "inherit" }}>
                {value}
              </div>
            </div>
          ))}
        </div>

        {selectedSeatGroups.length > 0 && (
          <div style={{ padding: "10px 24px 4px" }}>
            <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Seat Details</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {selectedSeatGroups.map((group, i) => (
                <div key={`${group.heading || "seats"}-${i}`} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "7px 10px", textAlign: "left" }}>
                  {group.heading && (
                    <div style={{ fontSize: 11.5, fontWeight: 800, color: "#0d1b3e", marginBottom: 4 }}>{group.heading}</div>
                  )}
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#047857" }}>{group.seats}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {passengerNames.length > 0 && (
          <div style={{ padding: "10px 24px 4px" }}>
            <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Traveller{passengerNames.length > 1 ? "s" : ""}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {passengerNames.map((name, i) => (
                <span key={i} style={{ fontSize: 11.5, fontWeight: 600, color: "#0d1b3e", background: "#f0fdf4", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 8, padding: "4px 10px" }}>
                  {name}
                </span>
              ))}
            </div>
          </div>
        )}
        {multiLeg && (
          <div style={{ padding: "10px 24px 4px" }}>
            <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
              Flight Legs
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {legs.map((leg, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    fontSize: 11.5,
                    color: "#0d1b3e",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    padding: "7px 10px"
                  }}
                >
                  <div>
                    <div>
                      <strong>
                        {leg.direction ||
                          leg.label ||
                          /* Multi-City legs have no supplier direction — label them
                             "Flight N" instead of forcing an RT-only "Return" fallback
                             on every leg after the first. */
                          (String(flight?.tripType || "").toLowerCase().includes("multi")
                            ? `Flight ${i + 1}`
                            : (i === 1 ? "Return" : "Outbound"))}
                      </strong>
                      {" · "}
                      {leg.airline || flight.airline}{" "}
                      {leg.flightNumbers} ·{" "}
                      {leg.departCode} → {leg.arriveCode}
                    </div>

                    <div
                      style={{
                        marginTop: 3,
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: leg.stops > 0 ? "#f59e0b" : "#10b981",
                      }}
                    >
                      {leg.stopDetail || (leg.stops > 0 ? `${leg.stops} Stop` : "Non Stop")}
                    </div>
                  </div>

                  <span style={{ color: "#64748b", whiteSpace: "nowrap" }}>
                    {leg.travelDate} · {leg.departTime}–{leg.arriveTime}
                    {leg.arriveNextDay ? " (+1)" : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {extraGroups.length > 0 && (
          <div style={{ padding: "10px 24px 4px" }}>
            <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Extras Added</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {extraGroups.map((group, i) => (
                <div key={`${group.heading}-${i}`} style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "7px 10px", textAlign: "left" }}>
                  <div style={{ fontSize: 11.5, fontWeight: 800, color: "#92400e", marginBottom: 4 }}>{group.heading || "Flight add-ons"}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {group.items.map((e, itemIndex) => (
                      <div key={itemIndex} style={{ fontSize: 11.5, fontWeight: 600, color: "#78350f" }}>
                        {e.name} × {e.qty}{e.unitPrice > 0 ? ` · ₹${e.unitPrice}` : " · Free"}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {cancellationInfo.length > 0 && (
          <div style={{ padding: "10px 24px 4px" }}>
            <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Cancellation Policy</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {cancellationInfo.map((row, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#475569" }}>
                  <span>{row.label}</span>
                  <span style={{ fontWeight: 700, color: "#0d1b3e" }}>{row.fee}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer: barcode + total */}
        <div style={{
          margin: "16px 0 0", padding: "14px 24px", background: "#0d1b3e",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
        }}>
          <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
            {Array.from({ length: 28 }, (_, i) => (
              <div key={i} style={{ width: (i % 4 === 0) ? 2.5 : 1.3, height: 26, background: "rgba(255,255,255,0.85)" }} />
            ))}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.55)", fontWeight: 700, textTransform: "uppercase" }}>Total Paid</div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, color: "#fff" }}>₹{Number(totalPrice).toLocaleString("en-IN")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
// ── PricingModal ──────────────────────────────────────────────────────────────
export function PricingModal({ flight, travelDate, onClose, onLoginRequest, activeFare, isInternational = false, adults, children, infants, classType, itineraryLegs = EMPTY_ITINERARY_LEGS }) {
  useLayoutEffect(() => {
    __bookingInProgress = true;
    return () => { __bookingInProgress = false; };
  }, []);

  const { user, token, expireSession, login } = useAuth();
  const [selectedCountryCode, setSelectedCountryCode] = useState("+91");
  const isSpecialSupplierRoundTrip =
    flight?.specialRoundTrip === true ||
    Number(flight?.bookingType ?? flight?.Booking_Type ?? flight?.BookingType) === 2 ||
    isSpecialRoundTripType(flight?.tripType);
  const [step, setStep] = useState(
    isSpecialSupplierRoundTrip && flight?.selectedFare ? "passengers" : "fare"
  );
  const [selectedFare, setSelectedFare] = useState(() => {
    if (
      Number.isInteger(Number(flight?.selectedFareIndex)) &&
      Number(flight.selectedFareIndex) >= 0
    ) {
      return Number(flight.selectedFareIndex);
    }
    const idx = flight.fareOptions?.findIndex((f) => f.recommended) ?? -1;
    return idx >= 0 ? idx : 0;
  });
  const pollIntervalRef = useRef(null);
  const [pax, setPax] = useState({
    adults: adults || 1,
    children: children || 0,
    infants: infants || 0,
    classType: classType || "Economy/Premium Economy"
  });
  const [passengerPricingPreview, setPassengerPricingPreview] = useState(null);
const [passengerPricingLoading, setPassengerPricingLoading] = useState(false);
const [passengerPricingError, setPassengerPricingError] = useState("");
  const [paxDirty, setPaxDirty] = useState(false);
  const [paxSyncNotice, setPaxSyncNotice] = useState("");
  const [detailsError, setDetailsError] = useState("");
  const updatePaxCount = (key, delta) => {
    setPax((prev) => {
      const next = { ...prev };

      if (key === "adults") {
        const newAdults = Math.max(1, prev.adults + delta);

        next.adults = newAdults;

        // If adults are reduced, automatically adjust children/infants
        next.children = Math.min(prev.children, newAdults * 4);
        next.infants = Math.min(prev.infants, newAdults);

      } else if (key === "children") {
        const maxChildren = prev.adults * 4;

        next.children = Math.max(
          0,
          Math.min(prev.children + delta, maxChildren)
        );

      } else if (key === "infants") {
        const maxInfants = prev.adults;

        next.infants = Math.max(
          0,
          Math.min(prev.infants + delta, maxInfants)
        );
      }

      return next;
    });

    // Everything downstream (seat map, extras, held/priced key) was fetched
    // for the OLD passenger count — it's now invalid and must be refetched.
    setPaxDirty(true);
    setSeatMapData(null);
    setSeatMapsByLeg({});
    setSelectedSeats([]);
    setSelectedSeatsByLeg({});
    setSsrData(null);
    setSsrDataByLeg({});
    ssrCacheRef.current.clear();
    ssrInFlightRef.current.clear();
    addonKeyContextRef.current.clear();
    setSelectedSsr({});
    setFareHold(null);
    setFareHoldError("");
    setRepriced(null);
    setPaxSyncNotice("Passenger count changed — we'll fetch a fresh price and seat map for this.");
  };
  const [seatMapData, setSeatMapData] = useState(null);
  const [seatMapsByLeg, setSeatMapsByLeg] = useState({});
  const [seatMapLoading, setSeatMapLoading] = useState(false);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [selectedSeatsByLeg, setSelectedSeatsByLeg] = useState({});
  const [ssrData, setSsrData] = useState(null);

  // Round-trip / multi-city SSR data, stored separately for every itinerary leg.
  const [ssrDataByLeg, setSsrDataByLeg] = useState({});

  const [ssrLoading, setSsrLoading] = useState(false);
  const [ssrError, setSsrError] = useState("");
  const [ssrRetryTick, setSsrRetryTick] = useState(0);
  const ssrCacheRef = useRef(new Map());
  const ssrInFlightRef = useRef(new Map());
  const addonKeyContextRef = useRef(new Map());
  const addonKeyContextInFlightRef = useRef(new Map());
  const [selectedSsr, setSelectedSsr] = useState({});
  const [bookingForm, setBookingForm] = useState(() => {
    const saved = loadSavedPassengerDetails();
    return {
      ...saved,
      email: user?.email || saved.email || "",
      phone: user?.phone || saved.phone || "",
    };
  });
  const [savedPassengers] = useState(() => loadSavedPassengersList());
  const collectSupplierRequiredPaxDetails = (...legs) =>
    legs.flatMap((leg) => {
      if (!leg) return [];
      const legFare =
        leg.selectedFare ||
        leg.fareOptions?.[leg.selectedFareIndex ?? selectedFare] ||
        leg.fareOptions?.[selectedFare];
      return [
        ...(Array.isArray(leg.requiredPaxDetails) ? leg.requiredPaxDetails : []),
        ...(Array.isArray(legFare?.requiredPaxDetails) ? legFare.requiredPaxDetails : []),
      ];
    });
  const [supplierRequiredPaxDetails, setSupplierRequiredPaxDetails] = useState(
    () => collectSupplierRequiredPaxDetails(flight, ...itineraryLegs)
  );
  const supplierFieldRequiredFor = (idx, ...fieldNames) => {
    const paxType =
      idx < pax.adults
        ? "0"
        : idx < pax.adults + pax.children
          ? "1"
          : "2";

    return (supplierRequiredPaxDetails || []).some((detail) => {
      const detailPaxType = firstDefined(
        detail?.paxType,
        detail?.Pax_type,
        detail?.Pax_Type,
        detail?.PAXType,
        detail?.PAX_Type,
        detail?.Paxtype
      );

      const appliesToPax =
        detailPaxType !== null &&
        detailPaxType !== undefined &&
        String(detailPaxType) === String(paxType);

      if (!appliesToPax) return false;

      return fieldNames.some((fieldName) => detail?.[fieldName] === true);
    });
  };

  const isDobRequired = (idx) =>
    supplierFieldRequiredFor(
      idx,
      "dob",
      "DOB",
      "DateOfBirth",
      "Date_Of_Birth"
    );

  const isPassportNumberRequired = (idx) =>
    supplierFieldRequiredFor(
      idx,
      "passportNumber",
      "PassportNumber",
      "Passport_Number"
    );

  const isPassportExpiryRequired = (idx) =>
    supplierFieldRequiredFor(
      idx,
      "passportExpiry",
      "PassportExpiry",
      "Passport_Expiry"
    );

  const isPassportCountryRequired = (idx) =>
    supplierFieldRequiredFor(
      idx,
      "passportIssuingCountry",
      "PassportIssuingCountry",
      "Passport_Issuing_Country"
    );

  const isNationalityRequired = (idx) =>
    supplierFieldRequiredFor(
      idx,
      "nationality",
      "Nationality"
    );

  const hasPassportRequirements = (idx) =>
    isPassportNumberRequired(idx) ||
    isPassportExpiryRequired(idx) ||
    isPassportCountryRequired(idx) ||
    isNationalityRequired(idx);

  const [holdAllowed, setHoldAllowed] = useState(() => {
    return resolveHoldAllowed(flight, flight.fareOptions?.[selectedFare]);
  }); // null = not yet known
  const [holdEligibilityChecking, setHoldEligibilityChecking] = useState(false);
  const [savedPassengerOpen, setSavedPassengerOpen] = useState(null);
  // Remember passenger details so future bookings are pre-filled automatically.
  useEffect(() => {
    savePassengerDetails(bookingForm);
  }, [bookingForm]);
  const [agreed, setAgreed] = useState(false);
  const [holdTermsAccepted, setHoldTermsAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMode, setSavingMode] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [showSupplierEmail, setShowSupplierEmail] = useState(false);
  const [isStaleKey, setIsStaleKey] = useState(false);
  const [bookingRef, setBookingRef] = useState("");
  const [airlinePnr, setAirlinePnr] = useState("");
  const [bookingId, setBookingId] = useState(null);
  const [ticketStatus, setTicketStatus] = useState(null);
  const [confirmedBookingData, setConfirmedBookingData] = useState(null);
  // Guest credentials are retained in memory for this booking session.
const [guestAccessToken, setGuestAccessToken] = useState(null);
const guestAccessTokenRef = useRef(null);
  const bookingRequestRef = useRef(false);
  const [repriced, setRepriced] = useState(null);
  const [repriceLoading, setRepriceLoading] = useState(false);
  const repriceInFlightRef = useRef(null);
  const [detailsRequirementsChecking, setDetailsRequirementsChecking] = useState(true);
  const [repricedSchedule, setRepricedSchedule] = useState(null);
  const [fareHold, setFareHold] = useState(null);
  const [fareHoldLoading, setFareHoldLoading] = useState(false);
  const [fareHoldError, setFareHoldError] = useState("");
  
  const fare = useMemo(() => {
    const fareFromOptions = flight.fareOptions?.[selectedFare];

    return fareFromOptions
      ? {
          ...flight.selectedFare,
          ...fareFromOptions,
          displayPrice:
            fareFromOptions.displayPrice ??
            flight.selectedFare?.displayPrice ??
            flight.displayPrice,
          displayPricing:
            fareFromOptions.displayPricing ??
            flight.selectedFare?.displayPricing ??
            flight.displayPricing,
        }
      : flight.selectedFare || null;
  }, [
    flight.fareOptions,
    flight.selectedFare,
    flight.displayPrice,
    flight.displayPricing,
    selectedFare,
  ]);


  const isCombinedItinerary = itineraryLegs.length > 1 && !isSpecialSupplierRoundTrip;
  const hasDisplayItineraryLegs = isCombinedItinerary || (isSpecialSupplierRoundTrip && itineraryLegs.length > 1);
  const apiFlight = isCombinedItinerary ? (itineraryLegs[0] || flight) : flight;
  const apiFare = apiFlight.fareOptions?.[selectedFare] || apiFlight.fareOptions?.[0] || fare;
  const effectiveItineraryLegs =
    isSpecialSupplierRoundTrip
      ? buildSpecialRoundTripLegsFromReprice(
        flight,
        repriced,
        itineraryLegs
      )
      : itineraryLegs;

  const paymentDisplayFlightNumbers =
    isSpecialSupplierRoundTrip &&
      effectiveItineraryLegs.length
      ? effectiveItineraryLegs
        .map((leg) => leg.flightNumbers)
        .filter(Boolean)
        .join(", ")
      : flight.flightNumbers;

  const paymentDisplayDepartCode =
    isSpecialSupplierRoundTrip
      ? effectiveItineraryLegs[0]?.departCode ||
      flight.departCode
      : flight.departCode;

  const paymentDisplayArriveCode =
    isSpecialSupplierRoundTrip
      ? effectiveItineraryLegs[
        effectiveItineraryLegs.length - 1
      ]?.arriveCode ||
      flight.arriveCode
      : flight.arriveCode;


  const seatLegs =
    effectiveItineraryLegs.length > 1
      ? effectiveItineraryLegs
      : [flight];
  
  useEffect(() => {
    if (step !== "details") {
      const nextRequiredDetails =
        collectSupplierRequiredPaxDetails(flight, ...itineraryLegs);

      setSupplierRequiredPaxDetails((previous) => {
        if (
          JSON.stringify(previous) === JSON.stringify(nextRequiredDetails)
        ) {
          return previous;
        }

        return nextRequiredDetails;
      });
    }

    const nextHoldAllowed = resolveHoldAllowed(flight, fare);

    setHoldAllowed((previous) =>
      Object.is(previous, nextHoldAllowed)
        ? previous
        : nextHoldAllowed
    );
  }, [flight, fare, itineraryLegs, step]);

  useEffect(() => {
    if (step !== "details") return;
    
  }, [step, holdAllowed, isCombinedItinerary]);
  const gstAllowed = Boolean(flight.gstEntryAllowed || fare?.gstMandatory);
  
  const gstRequired = Boolean(fare?.gstMandatory);
  const gstEnabled = Boolean(bookingForm.gstEnabled);
  const gstDetails = gstEnabled ? {
    enabled: true,
    gstin: bookingForm.gstin || "",
    holderName: bookingForm.gstHolderName || "",
    address: bookingForm.gstAddress || "",
    phone: bookingForm.gstPhone || "",
    email: bookingForm.gstEmail || "",
  } : { enabled: false };
  const gstValidationError = () => {
    if (gstRequired && !gstEnabled) return "GST details are required for this fare.";
    if (!gstEnabled) return "";
    if (!/^[0-9A-Z]{15}$/.test(String(gstDetails.gstin).trim().toUpperCase())) {
      return "Enter a valid 15-character GSTIN.";
    }
    if (
      !String(gstDetails.holderName).trim() ||
      !String(gstDetails.address).trim() ||
      !String(gstDetails.phone).trim() ||
      !String(gstDetails.email).trim()
    ) {
      return "Enter the GST legal name, billing address, phone, and email.";
    }
    return "";
  };
  const validateDetailsStep = ({ requireHoldTerms = false } = {}) => {
    for (const { idx, typeLabel } of passengerFields) {
      if (!bookingForm[`name_${idx}`]?.trim() || !bookingForm[`lname_${idx}`]?.trim()) {
        return `Please enter first and last name for Passenger ${idx + 1} (${typeLabel}).`;
      }
    }
    for (const { idx } of passengerFields) {
      const isInfant =
        idx >= pax.adults + pax.children;

      if (!isInfant) continue;

      const associatedAdultPassengerId =
        Number(
          bookingForm[
          `associatedAdultPassengerId_${idx}`
          ]
        );

      if (
        !Number.isInteger(
          associatedAdultPassengerId
        ) ||
        associatedAdultPassengerId < 1 ||
        associatedAdultPassengerId > pax.adults
      ) {
        return `Please select the accompanying adult for Infant ${idx - pax.adults - pax.children + 1
          }.`;
      }
    }
    if (!String(bookingForm.phone || "").trim()) {
  return "Please enter a contact phone number.";
}

if (showSupplierEmail) {
  const email = String(bookingForm.email || "").trim();

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return "This airline requires a valid contact email address.";
  }
} else if (String(bookingForm.email || "").trim()) {
  const email = String(bookingForm.email).trim();

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return "Please enter a valid email address or leave it blank.";
  }
}

    for (const { idx, typeLabel } of passengerFields) {
      if (!isDobRequired(idx)) continue;
      const paxType = idx < pax.adults ? 0 : idx < pax.adults + pax.children ? 1 : 2;
      const err = validatePaxAge(paxType, bookingForm[`dob_${idx}`], travelDate || flight.travelDate, activeFare);
      if (err) return `Passenger ${idx + 1} (${typeLabel}): ${err}`;
    }

    for (const { idx, typeLabel } of passengerFields) {
      if (
        isPassportNumberRequired(idx) &&
        !String(bookingForm[`passport_${idx}`] || "").trim()
      ) {
        return `Please enter passport number for Passenger ${idx + 1} (${typeLabel}).`;
      }

      if (
        isPassportExpiryRequired(idx) &&
        !String(bookingForm[`passportExpiry_${idx}`] || "").trim()
      ) {
        return `Please enter passport expiry for Passenger ${idx + 1} (${typeLabel}).`;
      }

      if (
        isPassportCountryRequired(idx) &&
        !String(bookingForm[`passportCountry_${idx}`] || "").trim()
      ) {
        return `Please enter passport issuing country for Passenger ${idx + 1} (${typeLabel}).`;
      }

      if (
        isNationalityRequired(idx) &&
        !String(bookingForm[`nationality_${idx}`] || "").trim()
      ) {
        return `Please enter nationality for Passenger ${idx + 1} (${typeLabel}).`;
      }
    }

    const gstError = gstValidationError();
    if (gstError) return gstError;

    if (!agreed) return "Please agree to the fare rules, baggage policy, and terms & conditions.";
    if (requireHoldTerms && !holdTermsAccepted) return "Please confirm you understand the hold and payment conditions.";

    return "";
  };
  const getFreshSearchKey = async (targetFlight = apiFlight, targetFare = apiFare) => {
    if (isCombinedItinerary) {
      const known = getKnownSupplierKeys(targetFlight, targetFare);
      if (!known?.searchKey || !known?.flightKey || !known?.fareId) {
        throw new Error("Could not locate supplier keys for this roundtrip itinerary. Please search again.");
      }
      return {
        ...known,
        requiredPaxDetails: collectSupplierRequiredPaxDetails(targetFlight, { selectedFare: known.fare || targetFare }),
      };
    }
    const authToken = token || (() => { try { return localStorage.getItem("mbt_token"); } catch { return null; } })();
    const res = await fetch(`${API_BASE}/flight-search`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
      body: JSON.stringify({
        fromCode: targetFlight.departCode,
        toCode: targetFlight.arriveCode,
        departDate: targetFlight.travelDate || travelDate || flight.travelDate,
        tripType: targetFlight.tripType || flight.tripType || "One Way",
        fare: activeFare,
        adults: pax.adults,
        children: pax.children,
        infants: pax.infants,
        classType: toApiClassType(pax.classType),
      }),
    });
    const data = await res.json();
    const norm = (value) => String(value || "").trim().toUpperCase();
    const match = (data.flights || []).find(f =>
      norm(f.flightNumbers) === norm(targetFlight.flightNumbers) &&
      f.departTime === targetFlight.departTime &&
      f.arriveTime === targetFlight.arriveTime &&
      norm(f.departCode) === norm(targetFlight.departCode) &&
      norm(f.arriveCode) === norm(targetFlight.arriveCode) &&
      Number(f.stops || 0) === Number(targetFlight.stops || 0) &&
      norm(f.airlineLogo || f.airline) === norm(targetFlight.airlineLogo || targetFlight.airline)
    );
    if (!match) throw new Error("Could not re-locate this flight — please search again.");
    // A FareId identifies the exact supplier offer (and therefore its cabin,
    // price and inclusions).  Match it before using display names, which are
    // often repeated across different cabin searches.
    const matchedFare = (match.fareOptions || []).find((option) =>
      option.fareId && option.fareId === targetFare?.fareId
    ) || (match.fareOptions || []).find((option) =>
      option.source === targetFare?.source &&
      option.name === targetFare?.name &&
      Number(option.price || 0) === Number(targetFare?.price || 0)
    ) || (match.fareOptions || []).find((option) =>
      option.source === targetFare?.source &&
      Number(option.price || 0) === Number(targetFare?.price || 0)
    );
    if (!matchedFare?.fareId) {
      throw new Error("Could not re-locate this fare — please search again.");
    }
    return {
      searchKey: matchedFare?.supplierSearchKey || match.searchKey,
      flightKey: matchedFare?.supplierFlightKey || match.flightKey || match.resultIndex || match.id,
      fareId: matchedFare.fareId,
      fare: matchedFare,
      requiredPaxDetails: collectSupplierRequiredPaxDetails(match, { selectedFare: matchedFare }),
    };
  };
  const getKnownSupplierKeys = (targetFlight = apiFlight, targetFare = apiFare) => {
    const searchKey =
      targetFare?.supplierSearchKey ||
      targetFare?.searchKey ||
      targetFlight?.supplierSearchKey ||
      targetFlight?.searchKey;
    const flightKey =
      targetFare?.supplierFlightKey ||
      targetFare?.flightKey ||
      targetFlight?.supplierFlightKey ||
      targetFlight?.flightKey ||
      targetFlight?.resultIndex ||
      targetFlight?.id;
    if (!searchKey || !flightKey) return null;
    return {
      searchKey,
      flightKey,
      fareId: targetFare?.fareId || targetFlight?.fareId,
      supplierRequestId: targetFare?.supplierRequestId || targetFlight?.supplierRequestId || null,
      fare: targetFare,
    };
  };
  const getFlightRequestIdentity = (
    targetFlight = apiFlight,
    targetFare = apiFare,
    itineraryLegIndex = 0
  ) => [
    flight.tripType ||
    (isCombinedItinerary ? "Itinerary" : "One Way"),

    itineraryLegIndex,

    // Supplier lifecycle identity.
    // Prevent cached add-on keys from another Search session being reused.
    targetFare?.supplierRequestId ||
    targetFlight?.supplierRequestId ||
    flight?.supplierRequestId ||
    "",

    targetFare?.supplierSearchKey ||
    targetFlight?.supplierSearchKey ||
    targetFlight?.searchKey ||
    flight?.searchKey ||
    "",

    targetFlight?.id ||
    targetFlight?.flightId ||
    targetFlight?.flightKey ||
    targetFlight?.resultIndex ||
    targetFlight?.flightNumbers ||
    "",

    targetFare?.fareId ||
    targetFlight?.fareId ||
    "",

    targetFlight?.travelDate ||
    travelDate ||
    flight.travelDate ||
    "",

    pax.adults,
    pax.children,
    pax.infants,
  ].join("|");
  const getAddonKeyContext = async (targetFlight = apiFlight, targetFare = apiFare, itineraryLegIndex = 0) => {
    const requestKey = getFlightRequestIdentity(targetFlight, targetFare, itineraryLegIndex);
    const cached = addonKeyContextRef.current.get(requestKey);

    
    const currentSupplierRequestId =
      targetFare?.supplierRequestId ||
      targetFlight?.supplierRequestId ||
      flight?.supplierRequestId ||
      null;

    if (
      cached?.searchKey &&
      cached?.flightKey &&
      (
        !currentSupplierRequestId ||
        !cached?.supplierRequestId ||
        String(cached.supplierRequestId) ===
        String(currentSupplierRequestId)
      )
    ) {
      return cached;
    }

    // Never reuse a Reprice/SSR context from another supplier Search lifecycle.
    if (cached) {
      addonKeyContextRef.current.delete(requestKey);
    }
    if (isSpecialSupplierRoundTrip) {
      const combined =
        repriced?.searchKey && repriced?.flightKey
          ? repriced
          : await repriceFlight(false);

      if (!combined?.searchKey || !combined?.flightKey) {
        throw new Error(
          "Air_Reprice did not return a valid Special Round Trip add-on key"
        );
      }

      const context = {
        requestKey,

        searchKey:
          combined.searchKey,

        flightKey:
          combined.flightKey,

        fareId:
          combined.fareId ||
          targetFare?.fareId ||
          fare?.fareId ||
          null,

        supplierRequestId:
          combined.supplierRequestId ||
          targetFare?.supplierRequestId ||
          targetFlight?.supplierRequestId ||
          flight?.supplierRequestId ||
          null,
      };

      

      addonKeyContextRef.current.set(
        requestKey,
        context
      );

      return context;
    }
    if (isCombinedItinerary) {
  const combined = await repriceFlight(true);

  const returnedLegs = Array.isArray(combined?.legs)
    ? combined.legs
    : [];

  const normaliseCode = (value) =>
    String(value || "").trim().toUpperCase();

  const targetOrigin = normaliseCode(targetFlight?.departCode);
  const targetDestination = normaliseCode(targetFlight?.arriveCode);

  // Prefer the requested itinerary index when its supplier keys are valid.
  let legContext = returnedLegs[itineraryLegIndex];

  // If the index does not resolve, find the matching route without
  // changing the normal One-Way or Special Round Trip branches.
  if (!legContext?.searchKey || !legContext?.flightKey) {
    const matchingLegs = returnedLegs.filter((leg) => {
      const segments = Array.isArray(leg?.segments) ? leg.segments : [];

      const firstSegment = segments[0];
      const lastSegment = segments[segments.length - 1];

      const origin = normaliseCode(
        firstSegment?.origin || leg?.result?.origin
      );
      const destination = normaliseCode(
        lastSegment?.destination || leg?.result?.destination
      );

      return (
        targetOrigin &&
        targetDestination &&
        origin === targetOrigin &&
        destination === targetDestination &&
        Boolean(leg?.searchKey) &&
        Boolean(leg?.flightKey)
      );
    });

    if (matchingLegs.length === 1) {
      legContext = matchingLegs[0];
    }
  }

  if (!legContext?.searchKey || !legContext?.flightKey) {
    console.error("[MULTI-CITY ADDON KEY MISSING]", {
      itineraryLegIndex,
      targetRoute: `${targetOrigin} → ${targetDestination}`,
      returnedLegCount: returnedLegs.length,
      returnedLegs: returnedLegs.map((leg, index) => ({
        index,
        hasSearchKey: Boolean(leg?.searchKey),
        hasFlightKey: Boolean(leg?.flightKey),
        fareId: leg?.fareId || null,
        segments: (leg?.segments || []).map((segment) => ({
          origin: segment?.origin,
          destination: segment?.destination,
        })),
      })),
    });

    throw new Error(
      "Could not resolve supplier add-on keys for this Multi-City leg. Please search again."
    );
  }

  const context = {
    requestKey,
    searchKey: legContext.searchKey,
    flightKey: legContext.flightKey,
    fareId:
      legContext.fareId ||
      targetFare?.fareId ||
      null,
    supplierRequestId:
      legContext.supplierRequestId ||
      targetFare?.supplierRequestId ||
      targetFlight?.supplierRequestId ||
      flight?.supplierRequestId ||
      null,
  };

  addonKeyContextRef.current.set(requestKey, context);
  return context;
}
    const known = getKnownSupplierKeys(targetFlight, targetFare);
    if (!known?.searchKey || !known?.flightKey || !known?.fareId) {
      throw new Error("Missing supplier keys for add-ons. Please search again.");
    }
    const authToken = token || localStorage.getItem("mbt_token");
    const res = await fetch(`${API_BASE}/flight-search/reprice`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
      body: JSON.stringify({
        Search_Key: known.searchKey,
        AirRepriceRequests: [{ Flight_Key: known.flightKey, Fare_Id: known.fareId }],
        CustomerMobile: String(bookingForm.phone || "").trim(),
        GSTInput: gstDetails?.enabled
          ? {
            GST: true,
            GST_Number: String(gstDetails.gstin || "").trim().toUpperCase(),
            GST_HolderName: String(gstDetails.holderName || "").trim(),
            GST_Address: String(gstDetails.address || "").trim(),
          }
          : null,
        supplierRequestId: known.supplierRequestId || null,
      }),
    });
    const data = await res.json();
    if (!res.ok || data?.success === false) {
      throw new Error(data?.errorDesc || data?.error || "Air_Reprice did not return a valid add-on key");
    }
    const context = {
      requestKey,
      searchKey: data.searchKey,
      flightKey: data.results?.[0]?.flightKey,
      fareId: known.fareId,
      supplierRequestId:
        data.supplierRequestId ||
        known.supplierRequestId ||
        targetFare?.supplierRequestId ||
        targetFlight?.supplierRequestId ||
        flight?.supplierRequestId ||
        null,
    };
    if (!context.searchKey || !context.flightKey) {
      throw new Error("Air_Reprice did not return a valid add-on key");
    }
    const previousContext =
      addonKeyContextRef.current.get(requestKey);

    const contextChanged =
      previousContext &&
      (
        previousContext.searchKey !== context.searchKey ||
        previousContext.flightKey !== context.flightKey
      );

    if (contextChanged) {
      setSelectedSeats([]);
      setSelectedSeatsByLeg({});
      setSelectedSsr({});

      setSeatMapData(null);
      setSeatMapsByLeg({});

      setSsrData(null);
      setSsrDataByLeg({});
      ssrCacheRef.current.delete(requestKey);
    }
    addonKeyContextRef.current.set(requestKey, context);
    return context;
  };

  const getCurrentAddonContext = (
    targetFlight = flight,
    targetFare = fare,
    itineraryLegIndex = 0
  ) => {
    const requestKey = getFlightRequestIdentity(
      targetFlight,
      targetFare,
      itineraryLegIndex
    );

    return addonKeyContextRef.current.get(requestKey) || null;
  };

  const getFreshKeysForSeatLegs = async () => {
    const keys = await Promise.all(
      seatLegs.map(async (leg) => {
        const legFare = leg.selectedFare || leg.fareOptions?.[leg.selectedFareIndex ?? selectedFare] || leg.fareOptions?.[0] || fare;
        const fresh = await getFreshSearchKey(leg, legFare);
        return { ...fresh, leg };
      })
    );
    setSupplierRequiredPaxDetails(keys.flatMap((item) => item.requiredPaxDetails || []));
    return keys;
  };
  const repriceFlight = useCallback(async (force = false) => {
    if (!force && repriced) return repriced;
    if (repriceInFlightRef.current) return repriceInFlightRef.current;

    setRepriceLoading(true);
    setRepriceError("");

    const promise = (async () => {
      try {
        const authToken = token || (() => { try { return localStorage.getItem("mbt_token"); } catch { return null; } })();
        const repriceLegs = isCombinedItinerary
          ? seatLegs.map((leg) => {
            const legFare = leg.selectedFare || leg.fareOptions?.[leg.selectedFareIndex ?? selectedFare] || leg.fareOptions?.[0] || fare;
            const known = getKnownSupplierKeys(leg, legFare);
            return { leg, fare: legFare, known };
          })
          : [{
            leg: flight,
            fare,
            known: getKnownSupplierKeys(flight, fare),
          }];
          
        if (
          repriceLegs.some((item) => !item.known?.searchKey || !item.known?.flightKey || !item.known?.fareId)
        ) {
          setRepriceError("Could not refresh this itinerary. Please search again.");
          return null;
        }
        const supplierSearchKeys = [...new Set(repriceLegs.map((item) => item.known.searchKey))];
        if (supplierSearchKeys.length !== 1) {
          setRepriceError("This roundtrip has mismatched supplier search keys. Please search again.");
          return null;
        }
        const res = await fetch(`${API_BASE}/flight-search/reprice`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
          body: JSON.stringify({
            Search_Key: supplierSearchKeys[0],
            AirRepriceRequests: repriceLegs.map((item) => ({
              Flight_Key: item.known.flightKey,
              Fare_Id: item.known.fareId,
            })),
            CustomerMobile: String(bookingForm.phone || "").trim(),
            GSTInput: gstDetails?.enabled
              ? {
                GST: true,
                GST_Number: String(gstDetails.gstin || "").trim().toUpperCase(),
                GST_HolderName: String(gstDetails.holderName || "").trim(),
                GST_Address: String(gstDetails.address || "").trim(),
              }
              : null,
            SinglePricing: isCombinedItinerary,
            supplierRequestId: repriceLegs[0]?.known?.supplierRequestId || null,
          }),
        });
        const data = await res.json();
        

        if (!res.ok || data?.success === false) {
          const reason = data?.errorDesc || data?.error || `Error ${data?.errorCode || res.status}`;
          console.error("[reprice] failed:", reason, data);
          setRepriceError(`Could not refresh this flight's price: ${reason}`);
          return null;
        }

        const result = data.results?.[0];
        if (!result?.flightKey || !data.searchKey) {
          console.error("[reprice] missing searchKey in response — refusing to pair with stale key", data);
          setRepriceError("Could not refresh this flight's price (missing search key). Please search again.");
          return null;
        }
        const freshRequiredPaxDetails =
          Array.isArray(result?.requiredPaxDetails)
            ? result.requiredPaxDetails
            : [];

        

        setSupplierRequiredPaxDetails(freshRequiredPaxDetails);
        const allowed = readBlockTicketAllowed(result, data);
        
        if (allowed !== null) setHoldAllowed(allowed);

        // NEW — if search never gave us real times, pull them from the reprice response
        if (flight.departTime === "--:--" || flight.arriveTime === "--:--") {
          const depTime = sliceTime(result.departureDateTime);
          const arrTime = sliceTime(result.arrivalDateTime);
          if (depTime || arrTime) {
            const depDateOnly = result.departureDateTime ? String(result.departureDateTime).slice(0, 10) : null;
            const arrDateOnly = result.arrivalDateTime ? String(result.arrivalDateTime).slice(0, 10) : null;
            setRepricedSchedule({
              departTime: depTime || flight.departTime,
              arriveTime: arrTime || flight.arriveTime,
              arriveNextDay: depDateOnly && arrDateOnly ? arrDateOnly > depDateOnly : flight.arriveNextDay,
            });
          }
        }

        const next = {
          searchKey: data.searchKey,
          flightKey: result.flightKey,
          fareId: repriceLegs[0]?.known?.fareId || fare?.fareId,
          supplierRequestId: repriceLegs[0]?.known?.supplierRequestId || null,
          result,
          results: data.results || [],
          segments: result.segments || [],
          flightNumbers: result.flightNumbers || null,
          legs: (data.results || []).map((item, index) => ({
            searchKey: data.searchKey,
            flightKey: item.flightKey,
            fareId: repriceLegs[index]?.known?.fareId,
            supplierRequestId: repriceLegs[index]?.known?.supplierRequestId || repriceLegs[0]?.known?.supplierRequestId || null,
            result: item,
            segments: item.segments || [],
            flightNumbers: item.flightNumbers || null,
          })),
        };
        setRepriced(next);
        return next;
      } catch (err) {
        setRepriceError("Could not refresh this flight. Please try again.");
        return null;
      } finally {
        setRepriceLoading(false);
        repriceInFlightRef.current = null;
      }
    })();

    repriceInFlightRef.current = promise;
    return promise;
  }, [flight, fare, bookingForm.phone, repriced, token, isCombinedItinerary, seatLegs, selectedFare]);

  const checkHoldEligibility = async () => {
    setHoldEligibilityChecking(true);
    try {
      const fresh = await getFreshSearchKey();
      const authToken = token || (() => { try { return localStorage.getItem("mbt_token"); } catch { return null; } })();
      const res = await fetch(`${API_BASE}/flight-search/reprice`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
        body: JSON.stringify({
          Search_Key: fresh.searchKey,
          AirRepriceRequests: [{ Flight_Key: fresh.flightKey, Fare_Id: fresh.fareId }],
          CustomerMobile: String(bookingForm.phone || "").trim(),
          GSTInput: gstDetails?.enabled
            ? {
              GST: true,
              GST_Number: String(gstDetails.gstin || "").trim().toUpperCase(),
              GST_HolderName: String(gstDetails.holderName || "").trim(),
              GST_Address: String(gstDetails.address || "").trim(),
            }
            : null,
          supplierRequestId: fresh.supplierRequestId || null,
        }),
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) return null;
      const result = data.results?.[0];

      if (Array.isArray(result.requiredPaxDetails)) {
        

        setSupplierRequiredPaxDetails(result.requiredPaxDetails);
      }
      const allowed = readBlockTicketAllowed(result, data);
      
      if (allowed !== null) setHoldAllowed(allowed);
      if (result?.flightKey && data.searchKey) {
        setRepriced({ searchKey: data.searchKey, flightKey: result.flightKey, fareId: fresh.fareId });
      }
      return allowed;
    } catch (err) {
      console.error("[HoldEligibility] check failed:", err.message);
      return null;
    } finally {
      setHoldEligibilityChecking(false);
    }
  };
  const [repriceError, setRepriceError] = useState("");
  // Load seat map when reaching seats step
  useEffect(() => {
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, []);

  // NEW — if the search result didn't include real times, fetch them immediately
  // so the modal header shows correct timing right away instead of waiting for Seats.
  useEffect(() => {
    if (flight.departTime === "--:--" || flight.arriveTime === "--:--") {
      repriceFlight(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (step !== "details") return;

    let cancelled = false;

    // Seats/SSR already use the authoritative Reprice context.
    // Reuse it instead of issuing another Air_Reprice.
    if (
      repriced?.searchKey &&
      repriced?.flightKey
    ) {
      const repricedResult =
        repriced?.results?.[0] ||
        repriced?.result ||
        null;

      setSupplierRequiredPaxDetails(
        Array.isArray(
          repricedResult?.requiredPaxDetails
        )
          ? repricedResult.requiredPaxDetails
          : []
      );

      setDetailsRequirementsChecking(false);

      return () => {
        cancelled = true;
      };
    }

    // Fallback only when no successful Reprice exists yet.
    setSupplierRequiredPaxDetails([]);
    setDetailsRequirementsChecking(true);

    Promise.resolve(
      repriceFlight(true)
    ).finally(() => {
      if (!cancelled) {
        setDetailsRequirementsChecking(false);
      }
    });

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  useEffect(() => {
    if (step !== "seats") return;
    if (isCombinedItinerary && seatLegs.every((_, index) => seatMapsByLeg[index])) return;
    if (!isCombinedItinerary && seatMapData) return;

    let cancelled = false;
    setSeatMapLoading(true);

    const fallbackSeatMap = (targetFlight = flight) => {
      const seatCount = Number(targetFlight?.seatsAvailable);
      const requiredSeats = Math.max(1, Number(pax.adults || 0) + Number(pax.children || 0));
      const visibleAvailability = Number.isFinite(seatCount) && seatCount > 0
        ? Math.max(requiredSeats, seatCount)
        : Math.max(requiredSeats + 8, 24);
      const config = AIRCRAFT_CONFIGS[targetFlight?.airlineLogo] || AIRCRAFT_CONFIGS.DEFAULT;
      const totalSeats = config.rows * config.cols.length;
      const occupancyRate = Math.min(0.72, Math.max(0.18, 1 - (visibleAvailability / totalSeats)));
      return generateSeatMap(targetFlight?.airlineLogo || targetFlight?.airline || "DEFAULT", occupancyRate);
    };

    const loadSeatMapForLeg = async (targetFlight, itineraryLegIndex = 0) => {
      const targetFare = targetFlight.selectedFare || targetFlight.fareOptions?.[targetFlight.selectedFareIndex ?? selectedFare] || targetFlight.fareOptions?.[0] || fare;
      const key = await getAddonKeyContext(targetFlight, targetFare, itineraryLegIndex).catch((err) => {
        console.error("[SeatMap Client] Could not obtain repriced key:", err.message);
        return null;
      });
      if (!key?.searchKey || !key?.flightKey || cancelled) {
        if (isSpecialSupplierRoundTrip) return null;
        return fallbackSeatMap(targetFlight);
      }

      try {
        const authToken = token || (() => { try { return localStorage.getItem("mbt_token"); } catch { return null; } })();
        const res = await fetch(`${API_BASE}/flight-search/seat-map`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
          body: JSON.stringify({
            Search_Key: key.searchKey,
            Flight_Key: [key.flightKey],
            supplierRequestId: key.supplierRequestId || null,
            // Air_GetSeatMap must receive the same passenger identity shape
            // used for repricing/TempBooking. "ADT"/"CHD" and a hard-coded
            // MR title happen to work for some domestic inventory, but are
            // rejected by international carriers which validate the manifest.
            PAXDetails: Array.from({ length: pax.adults + pax.children }, (_, i) => {
              const paxType = i < pax.adults ? "0" : "1";
              const paxDetail = buildPaxPayload(bookingForm, i, paxType, {
                DOB: isDobRequired(i),
                PassportNumber: isInternational,
                PassportIssuingCountry: isInternational,
                PassportExpiry: isInternational,
                Nationality: isInternational,
              });
              return {
                ...paxDetail,
                PassportIssuingCountry: isInternational ? bookingForm[`passportCountry_${i}`] || null : undefined,
                PassportExpiry: isInternational ? bookingForm[`passportExpiry_${i}`] || null : undefined,
              };
            }),
          }),
        });
        if (res.ok) {
          const data = await res.json();
          
          if (data.fallback) {
            console.warn("[SeatMap Client] Backend returned fallback — reason:", data.error);
            console.warn("[SeatMap Client] Full detail:", JSON.stringify(data.detail, null, 2));
          } else {
            const normalized = normalizeSeatMap(data);
            
            if (normalized && normalized.length > 0) {
              const enriched = normalized.map((row) => ({
                ...row,
                seats: row.seats.map((seat) => {
                  const seatType = getSeatType(seat.col || seat.id?.replace(/\d+/, ""));
                  const isExtraLegroom = Boolean(seat.isExtraLegroom);
                  const isExitRow = Boolean(seat.isExitRow);
                  const extraCost = Number(seat.extraCost || 0) || 0;
                  return {
                    ...seat,
                    seatType,
                    isExtraLegroom,
                    isExitRow,
                    extraCost,
                    ssrSearchKey: key.searchKey,
                    ssrFlightKey: key.flightKey,
                    ssrFareId: key.fareId,
                    ssrContextKey: key.requestKey,
                  };
                }),
              }));
              return enriched;
            }
          }
        } else {
          console.error("[SeatMap Client] non-OK response:", res.status);
        }
      } catch (err) {
        console.error("[SeatMap Client] fetch threw:", err.message);
      }

      return isSpecialSupplierRoundTrip ? null : fallbackSeatMap(targetFlight);
    };

    const loadSeatMaps = async () => {
      if (isCombinedItinerary) {
        const entries = await Promise.all(seatLegs.map(async (leg, index) => [index, await loadSeatMapForLeg(leg, index)]));
        if (!cancelled) setSeatMapsByLeg(Object.fromEntries(entries));
      } else {
        const map = await loadSeatMapForLeg(flight, 0);
        if (!cancelled) setSeatMapData(map);
      }
    };

    loadSeatMaps().finally(() => { if (!cancelled) setSeatMapLoading(false); });
    return () => { cancelled = true; };
  }, [step, flight, seatMapData, isCombinedItinerary, itineraryLegs, selectedFare]);

  useEffect(() => {
    if (!["meals", "baggage", "extras"].includes(step)) return;

    const allLoaded =
      isCombinedItinerary
        ? seatLegs.every((_, index) => ssrDataByLeg[index])
        : Boolean(ssrData);

    if (allLoaded && !ssrError) return;

    let cancelled = false;
    setSsrLoading(true);
    setSsrError("");

    const loadSsrForLeg = (
      targetFlight,
      itineraryLegIndex
    ) => {
      const authToken =
        token ||
        (() => {
          try {
            return localStorage.getItem("mbt_token");
          } catch {
            return null;
          }
        })();

      const targetFare =
        targetFlight.selectedFare ||
        targetFlight.fareOptions?.[
        targetFlight.selectedFareIndex ?? selectedFare
        ] ||
        targetFlight.fareOptions?.[0] ||
        fare;

      const requestKey = getFlightRequestIdentity(
        targetFlight,
        targetFare,
        itineraryLegIndex
      );

      if (ssrCacheRef.current.has(requestKey)) {
        return Promise.resolve(ssrCacheRef.current.get(requestKey));
      }

      if (ssrInFlightRef.current.has(requestKey)) {
        return ssrInFlightRef.current.get(requestKey);
      }

      const promise = (async () => {
        const keyContext = await getAddonKeyContext(targetFlight, targetFare, itineraryLegIndex);

        const res = await fetch(
          `${API_BASE}/flight-search/ssr`,
          {
            method: "POST",

            headers: {
              "Content-Type": "application/json",
              ...(authToken
                ? { Authorization: `Bearer ${authToken}` }
                : {}),
            },

            body: JSON.stringify({
              Search_Key: keyContext.searchKey,

              AirSSRRequestDetails: [
                {
                  Flight_Key: keyContext.flightKey,
                },
              ],

              supplierRequestId:
                keyContext.supplierRequestId ||
                flight?.supplierRequestId ||
                null,
            }),
          }
        );

        const data = await res.json();

        if (!res.ok) {
          throw new Error(
            data?.error ||
            data?.errorDesc ||
            `Unable to load extras for Flight ${itineraryLegIndex + 1
            }`
          );
        }

        const rawByType =
          data?.ssrByType &&
            Object.keys(data.ssrByType).length
            ? data.ssrByType
            : {};

        const normalized = Object.fromEntries(
          Object.entries(rawByType).map(
            ([typeName, items]) => [
              typeName,

              (items || []).map(
                (item) => ({
                  ...item,

                  itineraryLegIndex,

                  itineraryDepartCode:
                    targetFlight.departCode,

                  itineraryArriveCode:
                    targetFlight.arriveCode,

                  itineraryFlightNumbers:
                    targetFlight.flightNumbers,

                  ssrSearchKey:
                    keyContext.searchKey,

                  ssrFlightKey:
                    keyContext.flightKey,

                  ssrFareId:
                    keyContext.fareId,

                  ssrContextKey:
                    keyContext.requestKey,
                })
              ),
            ]
          )
        );
        ssrCacheRef.current.set(requestKey, normalized);
        return normalized;
      })().finally(() => {
        ssrInFlightRef.current.delete(requestKey);
      });

      ssrInFlightRef.current.set(requestKey, promise);
      return promise;
    };

    const loadSsr = async () => {
      try {
        if (isCombinedItinerary) {
          const entries =
            await Promise.all(
              seatLegs.map(
                async (
                  leg,
                  itineraryLegIndex
                ) => [
                    itineraryLegIndex,

                    await loadSsrForLeg(
                      leg,
                      itineraryLegIndex
                    ),
                  ]
              )
            );

          if (!cancelled) {
            setSsrDataByLeg(
              Object.fromEntries(entries)
            );
          }
        } else {
          const data =
            await loadSsrForLeg(
              flight,
              0
            );

          if (!cancelled) {
            setSsrData(data);
          }
        }
      } catch (err) {
        console.error(
          "[SSR Client]",
          err.message
        );

        if (!cancelled) {
          if (isCombinedItinerary) {
            setSsrDataByLeg({});
          } else {
            setSsrData({});
          }
          setSsrError(err.message || "Unable to load extras. Please try again.");
        }
      }
    };

    loadSsr().finally(() => {
      if (!cancelled) {
        setSsrLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    step,
    flight,
    fare?.fareId,
    selectedFare,
    isCombinedItinerary,
    itineraryLegs,
    token,
    pax.adults,
    pax.children,
    pax.infants,
    bookingForm.phone,
    ssrRetryTick,
  ]);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  const resolvedToken = token || (() => { try { return localStorage.getItem("mbt_token"); } catch { return null; } })();
  const displayItineraryLegs =
    hasDisplayItineraryLegs
      ? (
        isSpecialSupplierRoundTrip
          ? buildSpecialRoundTripLegsFromReprice(
            flight,
            repriced,
            itineraryLegs
          )
          : itineraryLegs
      )
      : [];
  const specialRtSummary = (() => {
    if (!isSpecialSupplierRoundTrip) return [];
    const legs =
      displayItineraryLegs;
    return legs.slice(0, 2).map((leg, index) => ({
      label: leg.label || (index === 0 ? "Outbound" : "Return"),
      route: [leg.departCode, leg.arriveCode].filter(Boolean).join(" → "),
    })).filter((item) => item.route);
  })();
  const modalSubtitle = isSpecialSupplierRoundTrip
    ? `${flight.airline} · Special Round Trip`
    : `${flight.airline} · ${flight.departCode} → ${flight.arriveCode}`;

  const totalPassengers = pax.adults + pax.children + pax.infants;
  const singleSeatSegmentGroups = groupSeatRowsBySegment(seatMapData || []);
  const hasSingleSegmentedSeatMap = !isCombinedItinerary && singleSeatSegmentGroups.length > 1;
  const selectedSeatCount = (isCombinedItinerary || hasSingleSegmentedSeatMap)
    ? Object.values(selectedSeatsByLeg).reduce((sum, seats) => sum + seats.length, 0)
    : selectedSeats.length;
  const selectedSeatSummary = (isCombinedItinerary || hasSingleSegmentedSeatMap)
    ? Object.entries(selectedSeatsByLeg)
      .filter(([, seats]) => seats.length)
      .map(([selectionKey, seats]) => {
        const { legIndex, segmentKey } = getSeatSelectionParts(selectionKey);
        const legSeatMap = seatMapsByLeg[legIndex] || [];
        const rows = isCombinedItinerary ? legSeatMap : seatMapData || [];

        const seatObjects = seats
          .map((id) => rows.flatMap((r) => r.seats || []).find((s) => s.id === id))
          .filter(Boolean);

        const segmentMeta =
          groupSeatRowsBySegment(rows).find((group) => group.segmentKey === segmentKey);
        const segmentIndexFromKey =
          Number(
            String(segmentKey || "").match(/idx-(\d+)/)?.[1] ??
            String(segmentKey || "").split("::").find((part) => /^\d+$/.test(part)) ??
            -1
          );
        const specialPhysicalSegments =
          isSpecialSupplierRoundTrip
            ? displayItineraryLegs.flatMap(
              (leg) =>
                Array.isArray(leg?.segments)
                  ? leg.segments
                  : []
            )
            : [];

        const authoritativeSeatSegment =
          isSpecialSupplierRoundTrip &&
            Number.isInteger(segmentIndexFromKey) &&
            segmentIndexFromKey >= 0
            ? specialPhysicalSegments[
            segmentIndexFromKey
            ] || null
            : null;

        const outerLegForSeats =
          isCombinedItinerary
            ? itineraryLegs[legIndex]
            : isSpecialSupplierRoundTrip
              ? {
                ...flight,
                segments:
                  specialPhysicalSegments.length
                    ? specialPhysicalSegments
                    : flight.segments,
              }
              : flight;

        const fallbackSegment =
          authoritativeSeatSegment ||
          (
            Number.isInteger(
              segmentIndexFromKey
            ) &&
              segmentIndexFromKey >= 0
              ? outerLegForSeats
                ?.segments?.[
              segmentIndexFromKey
              ]
              : null
          );

        const segmentHeading =
          (
            isSpecialSupplierRoundTrip &&
              authoritativeSeatSegment
              ? getVisibleSeatSegmentLabel(
                authoritativeSeatSegment,
                segmentIndexFromKey,
                outerLegForSeats
              )
              : null
          ) ||
          seatObjects
            .map((s) => formatSegmentLabel(s))
            .find(Boolean) ||
          formatSegmentLabel(segmentMeta) ||
          getVisibleSeatSegmentLabel(
            fallbackSegment,
            segmentIndexFromKey,
            outerLegForSeats
          ) ||
          `Flight ${Number(legIndex) + 1}`;

        const seatText = seatObjects.length
          ? seatObjects
            .map((s) => {
              const type = SEAT_TYPE_LABELS[s.seatType];
              return type ? `${getSeatLabel(s)} (${type})` : getSeatLabel(s);
            })
            .join(", ")
          : seats.join(", ");

        return `${segmentHeading}: ${seatText}`;
      })
      .join(" | ") || "At check-in"
    : selectedSeats.length
      ? selectedSeats.map(id => {
        const s = seatMapData?.flatMap(r => r.seats).find(x => x.id === id);
        return s ? [
          `${getSeatLabel(s)} (${SEAT_TYPE_LABELS[s.seatType]})`,
          formatSegmentLabel(s),
        ].filter(Boolean).join(" · ") : id;
      }).join(", ")
      : "At check-in";

  const selectedSeatGroupsForDisplay =
    selectedSeatSummary === "At check-in"
      ? []
      : String(selectedSeatSummary)
        .split(" | ")
        .map((line) => {
          const [heading, seats] = line.split(/:\s(.+)/);
          return {
            heading: seats ? heading : "",
            seats: seats || heading,
          };
        })
        .filter((group) => group.seats);

  // Seat extra cost
  const seatExtraCost = (isCombinedItinerary || hasSingleSegmentedSeatMap)
    ? Object.entries(selectedSeatsByLeg).reduce((total, [selectionKey, seats]) => {
      const { legIndex } = getSeatSelectionParts(selectionKey);
      const rows = isCombinedItinerary ? seatMapsByLeg[legIndex] || [] : seatMapData || [];
      return total + seats.reduce((sum, seatId) => {
        const seat = rows.flatMap(r => r.seats).find(s => s.id === seatId);
        return sum + (seat?.extraCost || 0);
      }, 0);
    }, 0)
    : seatMapData
      ? selectedSeats.reduce((sum, seatId) => {
        const seat = seatMapData.flatMap(r => r.seats).find(s => s.id === seatId);
        return sum + (seat?.extraCost || 0);
      }, 0)
      : 0;
  const allSsrServices =
    ssrData
      ? Object.values(ssrData).flat()
      : [];

  const ssrExtraCost = Object.values(selectedSsr).reduce(
    (sum, selected) => {
      return (
        sum +
        Number(selected.unitPrice || 0) *
        Number(selected.qty || 0)
      );
    },
    0
  );

  // Real, per-passenger extras the user actually chose — shown on the
  // ticket instead of being silently dropped.
  const rawSelectedExtrasList =
    Object.values(selectedSsr).map(
      (selected) => ({
        ssrKey:
          selected.ssrKey,

        name:
          selected.name ||
          "Extra",

        ssrType:
          selected.ssrType ?? null,

        qty:
          Number(
            selected.qty || 0
          ),

        unitPrice:
          Number(
            selected.unitPrice || 0
          ),

        itineraryLegIndex:
          Number(
            selected.itineraryLegIndex ??
            0
          ),

        segmentWise:
          Boolean(
            selected.segmentWise
          ),

        segmentId:
          selected.segmentId ??
          null,

        supplierLegIndex:
          selected.supplierLegIndex ??
          null,

        flightId:
          selected.flightId ??
          null,

        route:
          selected.route || null,

        origin:
          selected.origin || null,

        destination:
          selected.destination || null,

        flightNo:
          selected.flightNo || null,
      })
    );

  const selectedExtrasList = (() => {
    const wheelchairByLeg = new Map();
    const displayItems = [];

    rawSelectedExtrasList.forEach((item) => {
      const isWheelchair =
        Number(item.ssrType) === 10 ||
        String(item.name || "")
          .toUpperCase()
          .includes("WHEELCHAIR");

      if (!isWheelchair) {
        displayItems.push(item);
        return;
      }

      const serviceKey = [
        Number(item.itineraryLegIndex ?? 0),
        Number(item.ssrType ?? 10),
        String(item.name || "").trim().toUpperCase(),
        Number(item.unitPrice || 0),
      ].join("::");
      const existingIndex = wheelchairByLeg.get(serviceKey);

      if (existingIndex !== undefined) {
        displayItems[existingIndex] = {
          ...displayItems[existingIndex],
          displayJourneyWide: true,
          route: null,
          origin: null,
          destination: null,
          flightNo: null,
          segmentId: null,
          supplierLegIndex: null,
        };
        return;
      }

      wheelchairByLeg.set(serviceKey, displayItems.length);
      displayItems.push(item);
    });

    return displayItems;
  })();

  // Real cancellation fee schedule from the supplier's fare data — used on
  // the ticket instead of a generic "charges apply" sentence.
  const cancellationInfo = buildChargeRows(fare?.cancellationCharges || flight.cancellationCharges || []);

  
  
const calcTotal = (f) => {
  // Keep the supplier fare separate from the admin-adjusted display price.
  const adultPrice = Number(
    f?.basePrice ??
    f?.price ??
    flight?.basePrice ??
    flight?.price ??
    0
  ) || 0;

  const childPrice = Number(
    f?.childBasePrice ??
    f?.childPrice ??
    flight?.childBasePrice ??
    flight?.childPrice
  ) || Math.round(adultPrice * 0.75);

  const infantPrice = Number(
    f?.infantBasePrice ??
    f?.infantPrice ??
    flight?.infantBasePrice ??
    flight?.infantPrice
  ) || Math.round(adultPrice * 0.10);

  return (
    adultPrice * pax.adults +
    childPrice * pax.children +
    infantPrice * pax.infants +
    seatExtraCost +
    ssrExtraCost
  );
};


  const totalPrice = calcTotal(fare);

  
  // Use primitive pricing inputs so recreated flight/fare objects
  // don't trigger another quote when their actual values are unchanged.
  const pricingPreviewLegs =
  (isCombinedItinerary || isSpecialSupplierRoundTrip) &&
  Array.isArray(effectiveItineraryLegs) &&
  effectiveItineraryLegs.length
    ? effectiveItineraryLegs
    : [flight];

const pricingPerLegSegments = pricingPreviewLegs.map((leg) =>
  Array.isArray(leg?.segments) && leg.segments.length
    ? leg.segments.length
    : 1
);

const pricingPerLegSegmentsKey = pricingPerLegSegments.join(",");

if (
  isSpecialSupplierRoundTrip &&
  pricingPerLegSegments.length !== 2
) {
  console.error("[SPECIAL ROUND TRIP PRICING LEGS INVALID]", {
    legCount: pricingPerLegSegments.length,
    perLegSegments: pricingPerLegSegments,
    effectiveItineraryLegCount: effectiveItineraryLegs?.length,
  });
}

  const pricingSearchKey = flight?.searchKey || null;
  const pricingFareId = fare?.fareId || flight?.fareId || null;
  const pricingTravelDate = flight?.travelDate || travelDate || null;
  const pricingReturnDate = flight?.returnDate || null;

  const pricingTripType = getCanonicalTripType(
    flight?.tripType,
    isSpecialSupplierRoundTrip
  );

  const pricingSupplierFareTotal = Math.max(
    0,
    Number(totalPrice || 0) -
      Number(seatExtraCost || 0) -
      Number(ssrExtraCost || 0)
  );

  useEffect(() => {
    let cancelled = false;

    const calculatePassengerTotal = async () => {
      const authToken =
        token ||
        (() => {
          try {
            return localStorage.getItem("mbt_token");
          } catch {
            return null;
          }
        })();


      if (!(pricingSupplierFareTotal > 0)) {
        setPassengerPricingError(
          "The supplier fare is unavailable. Please select the flight again."
        );
        return;
      }

      setPassengerPricingLoading(true);
      setPassengerPricingError("");

      try {
        const perLegSegments = pricingPerLegSegmentsKey
          .split(",")
          .map(Number);

        const response = await fetch(
          `${API_BASE}/flight-search/pricing-quote`,
          {
            method: "POST",
            headers: {
  "Content-Type": "application/json",
  ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
},
            body: JSON.stringify({
              supplierFareTotal: pricingSupplierFareTotal,
              seatExtraCost: 0,
              ssrExtraCost: 0,
              supplierTaxTotal: 0,
              adults: pax.adults,
              children: pax.children,
              infants: pax.infants,
              perLegSegments,
              tripType: pricingTripType,
              searchKey: pricingSearchKey,
              fareId: pricingFareId,
              travelDate: pricingTravelDate,
              returnDate: pricingReturnDate,
            }),
          }
        );

        const data = await response.json();

        if (!response.ok || !data?.quoteId) {
          throw new Error(
            data?.error || "Unable to calculate the passenger total."
          );
        }

        if (!cancelled) {
          setPassengerPricingPreview(data);
        }
      } catch (error) {
        if (!cancelled) {
          setPassengerPricingError(
            error.message || "Unable to calculate the passenger total."
          );
        }
      } finally {
        if (!cancelled) {
          setPassengerPricingLoading(false);
        }
      }
    };

    calculatePassengerTotal();

    return () => {
      cancelled = true;
    };
  }, [
    pricingSupplierFareTotal,
    pax.adults,
    pax.children,
    pax.infants,
    pricingPerLegSegmentsKey,
    pricingTripType,
    pricingSearchKey,
    pricingFareId,
    pricingTravelDate,
    pricingReturnDate,
    token,
  ]);

  // Passenger type label
  const paxLabel = PAX_TYPE_LABELS[activeFare] || "adult";
  const pricePerLabel = activeFare !== "regular"
    ? `/${paxLabel}`
    : "/adult";

  const passengerFields = Array.from({ length: totalPassengers }, (_, idx) => {
    const isAdult = idx < pax.adults;
    const isChild = !isAdult && idx < pax.adults + pax.children;
    const typeLabel = isAdult ? (activeFare !== "regular" ? fareTypes.find(f => f.id === activeFare)?.label || "Adult" : "Adult") : isChild ? "Child" : "Infant";
    const typeIdx = isAdult ? idx + 1 : isChild ? idx - pax.adults + 1 : idx - pax.adults - pax.children + 1;
    return { idx, typeLabel, typeIdx };
  });

  function getSeatSegmentSelectionKey(legIndex = 0, segmentKey = "journey") {
    return `${Number(legIndex) || 0}::${segmentKey || "journey"}`;
  }

  function getSeatSelectionParts(selectionKey) {
    const [legIndexText, ...segmentParts] = String(selectionKey).split("::");
    return {
      legIndex: Number(legIndexText) || 0,
      segmentKey: segmentParts.length ? segmentParts.join("::") : "journey",
    };
  }

  function getVisibleSeatSegmentLabel(segment, segmentIndex = 0, outerLeg = flight) {
    const fallback = Array.isArray(outerLeg?.segments) ? outerLeg.segments[segmentIndex] : null;
    const origin = segment?.origin || fallback?.origin || fallback?.departCode || fallback?.from || "";
    const destination = segment?.destination || fallback?.destination || fallback?.arriveCode || fallback?.to || "";
    const flightNo = segment?.flightNo || fallback?.flightNo || fallback?.flightNumbers || fallback?.flightNumber || "";
    const route = origin && destination ? `${origin} → ${destination}` : "";
    return [`Segment ${segmentIndex + 1}`, route, flightNo].filter(Boolean).join(" · ");
  }

  const toggleSeat = (seat) => {
    if (!seat?.id) return;

    

    const seatId = seat.id;

    setSelectedSeats((prev) =>
      prev.includes(seatId)
        ? prev.filter((s) => s !== seatId)
        : [...prev, seatId]
    );
  };
  const toggleSeatForLeg = (selectionKey, seat) => {
    if (!seat?.id) return;

    

    const seatId = seat.id;

    setSelectedSeatsByLeg((prev) => {
      const current = prev[selectionKey] || [];

      return {
        ...prev,
        [selectionKey]: current.includes(seatId)
          ? current.filter((s) => s !== seatId)
          : [...current, seatId],
      };
    });
  };
  const getSsrSelectionKey = (
    item,
    itineraryLegIndex = 0
  ) => {
    if (!item?.ssrKey) return "";

    return [
      itineraryLegIndex,
      item.segmentWise ? "segment" : "journey",
      item.segmentWise
        ? item.segmentId ??
        item.supplierLegIndex ??
        item.legIndex ??
        item.flightId ??
        item.route ??
        "unknown"
        : "journey",
      item.ssrKey,
    ].join("::");
  };


  const getSsrSegment = (
    item,
    itineraryLegIndex = 0
  ) => {
    if (!item?.segmentWise) {
      return null;
    }

    const targetFlight =
      seatLegs[itineraryLegIndex] ||
      flight;

    const segments =
      Array.isArray(
        targetFlight?.segments
      )
        ? targetFlight.segments
        : [];

    if (
      item.segmentId !== null &&
      item.segmentId !== undefined
    ) {
      const byId =
        segments.find(
          (segment) =>
            String(
              segment.segmentId
            ) ===
            String(item.segmentId)
        );

      if (byId) return byId;
    }

        const supplierLegIndex = Number(
      item.supplierLegIndex ??
      item.legIndex ??
      item.Leg_Index ??
      item.LegIndex
    );

    if (
      Number.isInteger(supplierLegIndex) &&
      supplierLegIndex >= 0
    ) {
      const bySupplierIndex = segments.find((segment) => {
        const segmentIndex = Number(
          segment.supplierLegIndex ??
          segment.legIndex ??
          segment.Leg_Index ??
          segment.LegIndex
        );

        return (
          Number.isInteger(segmentIndex) &&
          segmentIndex === supplierLegIndex
        );
      });

      if (bySupplierIndex) return bySupplierIndex;
    }

    return null;
  };

  const classifySsrItem = (typeName, item) => {
    const text = [
      typeName,
      item?.ssrTypeName,
      item?.ssrTypeDesc,
      item?.code,
      item?.ssrCode,
    ].filter(Boolean).join(" ").toLowerCase();

    if (item?.isMeal || /\bmeal\b|food|snack|breakfast|lunch|dinner/.test(text)) return "meals";
    if (item?.isBaggage || /baggage|bag|kgs?\b|kilogram/.test(text)) return "baggage";
    if (item?.isSeat || /\bseat\b/.test(text)) return "seat";
    return "extras";
  };

  const filterSsrDataForStep = (data, activeStep) => Object.fromEntries(
    Object.entries(data || {})
      .map(([typeName, items]) => [
        typeName,
        (items || []).filter((item) => classifySsrItem(typeName, item) === activeStep),
      ])
      .filter(([, items]) => items.length > 0)
  );

  const filterSsrDataForItineraryLeg = (
    data,
    activeStep,
    leg,
    itineraryLegIndex
  ) => {
    const legSegments =
      Array.isArray(leg?.segments)
        ? leg.segments
        : [];

    const legSegmentIds = new Set(
      legSegments
        .map((segment) =>
          String(
            segment.segmentId ??
            segment.Segment_Id ??
            segment.SegmentId ??
            ""
          )
        )
        .filter(Boolean)
    );

    return Object.fromEntries(
      Object.entries(
        filterSsrDataForStep(data, activeStep) || {}
      )
        .map(([typeName, items]) => [
          typeName,

          (items || []).filter((item) => {
            const itemSegmentId = String(
              item.segmentId ??
              item.Segment_Id ??
              item.SegmentId ??
              ""
            );

            /*
             * 1. Strongest match:
             * supplier Segment_Id belongs to this displayed leg.
             */
            if (itemSegmentId) {
              return legSegmentIds.has(itemSegmentId);
            }

            /*
             * 2. Special Round Trip direction.
             *
             * Supplier may return no Segment_Id for a
             * journey-level SSR but still identify the
             * direction using Return_Flight.
             */
            const itemReturnFlight =
              item.returnFlight ??
              item.Return_Flight ??
              item.ReturnFlight;

            if (
              isSpecialSupplierRoundTrip &&
              itemReturnFlight !== null &&
              itemReturnFlight !== undefined
            ) {
              const isReturn =
                itemReturnFlight === true ||
                itemReturnFlight === 1 ||
                itemReturnFlight === "1" ||
                String(itemReturnFlight).toLowerCase() === "true";

              return isReturn
                ? Number(itineraryLegIndex) === 1
                : Number(itineraryLegIndex) === 0;
            }

                        /*
             * 3. Journey-wide SSR.
             * Show journey-level items only once.
             */
            const segmentWise =
              item.segmentWise ??
              item.Segment_Wise ??
              item.SegmentWise ??
              false;

            if (!segmentWise) {
              return Number(itineraryLegIndex) === 0;
            }

            /*
             * Do not compare supplier Leg_Index directly
             * with the displayed itinerary leg index.
             * They may represent different indexing systems.
             *
             * Without a matching Segment_Id or Return_Flight,
             * do not guess which displayed leg owns this SSR.
             */
            return false;
          }),
        ])
        .filter(([, items]) => items.length > 0)
    );
  };

  const getSsrGroupKey = (item) => (
    item?.segmentWise
      ? [
        "segment",
        item.segmentId ?? item.supplierLegIndex ?? item.legIndex ?? item.flightId ?? item.route ?? "unknown",
      ].join("::")
      : "journey"
  );

  const groupSsrItemsForDisplay = (items, itineraryLegIndex = 0) => {
    const groups = new Map();
    (items || []).forEach((item) => {
      const key = getSsrGroupKey(item);
      if (!groups.has(key)) {
        const segment = getSsrSegment(item, itineraryLegIndex);
        const label = item.segmentWise
          ? segment
            ? `${segment.origin} → ${segment.destination}${segment.flightNo ? ` · ${segment.flightNo}` : ""}`
            : `Segment ${Number(item.legIndex ?? item.supplierLegIndex ?? 0) + 1}`
          : "Entire flight journey";
        groups.set(key, { key, label, items: [] });
      }
      groups.get(key).items.push(item);
    });
    return Array.from(groups.values());
  };


  const setSsrQuantity = (
    item,
    itineraryLegIndex,
    quantity,
    maxQty
  ) => {
    const selectionKey =
      getSsrSelectionKey(
        item,
        itineraryLegIndex
      );

    if (!selectionKey) return;

    setSelectedSsr((prev) => {
      const next = { ...prev };

      const normalized =
        Math.max(
          0,
          Math.min(
            Number(quantity) || 0,
            maxQty
          )
        );

      if (normalized === 0) {
        delete next[selectionKey];
        return next;
      }

      const segment =
        getSsrSegment(
          item,
          itineraryLegIndex
        );

      const targetFlight =
        seatLegs[
        itineraryLegIndex
        ] || flight;

      next[selectionKey] = {
        qty: normalized,

        ssrKey:
          item.ssrKey,

        name:
          item.ssrTypeDesc ||
          item.ssrTypeName ||
          "Extra",

        unitPrice:
          Number(
            item.totalAmount || 0
          ),

        ssrType:
          item.ssrType ?? null,

        ssrTypeName:
          item.ssrTypeName || null,

        applicablePaxTypes:
          Array.isArray(
            item.applicablePaxTypes
          )
            ? item.applicablePaxTypes
            : [],

        itineraryLegIndex,

        segmentWise:
          Boolean(item.segmentWise),

        segmentId:
          item.segmentId ?? null,

        legIndex:
          item.legIndex ??
          item.supplierLegIndex ??
          null,

        supplierLegIndex:
          item.legIndex ??
          item.supplierLegIndex ??
          null,

        flightId:
          item.flightId ?? null,

        route: segment
          ? `${segment.origin} → ${segment.destination}`
          : `${targetFlight.departCode} → ${targetFlight.arriveCode}`,

        origin:
          segment?.origin ||
          targetFlight.departCode ||
          null,

        destination:
          segment?.destination ||
          targetFlight.arriveCode ||
          null,

        flightNo:
          segment?.flightNo ||
          targetFlight.flightNumbers ||
          null,

        ssrSearchKey:
          item.ssrSearchKey || null,

        ssrFlightKey:
          item.ssrFlightKey || null,

        ssrFareId:
          item.ssrFareId || null,

        ssrContextKey:
          item.ssrContextKey || null,
      };

      return next;
    });
  };
  const buildPaxPayload = (formData, idx, paxType, requiredFields) => {
    const base = {
      Pax_Id: idx + 1,
      Pax_type: paxType, // "0"=ADT, "1"=CHD, "2"=INF
      Title: formData[`title_${idx}`] || (paxType === "0" ? "MR" : "MSTR"),
      FirstName: formData[`name_${idx}`] || "",
      LastName: formData[`lname_${idx}`] || "",
      Gender: formData[`gender_${idx}`] || "0",
    };
    if (requiredFields?.DOB)
      base.DOB = formData[`dob_${idx}`] || null;
    if (requiredFields?.PassportNumber)
      base.PassportNumber = formData[`passport_${idx}`] || null;
    if (requiredFields?.PassportIssuingCountry)
      base.PassportIssuingCountry = formData[`passportCountry_${idx}`] || null;
    if (requiredFields?.PassportExpiry)
      base.PassportExpiry = formData[`passportExpiry_${idx}`] || null;
    if (requiredFields?.Nationality)
      base.Nationality = formData[`nationality_${idx}`] || null;
    return base;
  };

  const holdFare = async (mode = "hold") => {
    if (fareHold || fareHoldLoading) return;
    setFareHoldError("");
    const contactPhone = String(bookingForm.phone || "").trim();

if (!contactPhone) {
  setFareHoldError(
    "Please enter your contact phone number to hold this fare."
  );
  return;
}
    let searchKey, flightKey, freshFareId;
    try {
      const fresh = await getFreshSearchKey(); // ALWAYS re-search with CURRENT pax counts — no stale reuse
      searchKey = fresh.searchKey;
      flightKey = fresh.flightKey;
      freshFareId = fresh.fareId;
    } catch (e) {
      setSaveError("This flight can no longer be booked. Please search again and pick a fresh result.");
      return;
    }
    if (!searchKey || !flightKey || !freshFareId) {
      setSaveError("This flight can no longer be booked. Please search again and pick a fresh result.");
      return;
    }
    setPaxDirty(false);

    setFareHoldLoading(true);
    try {
      const paxDetails = passengerFields.map(({ idx }) => {
        const paxType = idx < pax.adults ? "0" : idx < pax.adults + pax.children ? "1" : "2";
        return buildPaxPayload(bookingForm, idx, paxType, { DOB: isDobRequired(idx) });
      });
      const res = await fetch(`${API_BASE}/flight-search/${mode === "lock" ? "fare-lock" : "fare-hold"}`, {
        method: "POST",
        headers: {
  "Content-Type": "application/json",
  ...(user && token ? { Authorization: `Bearer ${token}` } : {}),
},
        body: JSON.stringify({
          Search_Key: searchKey,
          Flight_Key: [flightKey],
          Fare_Id: freshFareId,
          CustomerMobile: contactPhone,
          PAXDetails: paxDetails,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.errorDesc || data?.error || data?.note || "The airline could not hold this fare.");
      }
      setFareHold({ ...data, mode });
    } catch (err) {
      setFareHoldError(err.message || "Could not hold this fare. Please try again.");
    } finally {
      setFareHoldLoading(false);
    }
  };

const handleBookingAction = (mode) => {
  const contactPhone = String(bookingForm.phone || "").trim();

  if (mode === "hold" && !contactPhone) {
  setDetailsError("Please enter your contact phone number to hold this fare.");
  return;
}

  createPendingBooking(mode, user && token ? token : null);
};

const getFlightBookingHeaders = (authToken = null) => {
  const effectiveAuthToken =
    authToken || (user && token ? token : null);

  const headers = {
    "Content-Type": "application/json",
  };

  if (effectiveAuthToken) {
    headers.Authorization = `Bearer ${effectiveAuthToken}`;
  } else if (guestAccessTokenRef.current) {
    headers["x-guest-access-token"] = guestAccessTokenRef.current;
  }

  return headers;
};

  const createPendingBooking = async (
  mode = "pay",
  verifiedToken = null
) => {
    
    if (bookingRequestRef.current) return;
    bookingRequestRef.current = true;
    setSaving(true);
    setSavingMode(mode);
    setSaveError("");
    setIsStaleKey(false);

    // Cache each passenger's details locally so future bookings (any trip type)
    // can offer them in the "fill from saved passenger" dropdown.
    passengerFields.forEach(({ idx }) => {
      mergeSavedPassenger({
        title: bookingForm[`title_${idx}`] || "MR",
        firstName: bookingForm[`name_${idx}`] || "",
        lastName: bookingForm[`lname_${idx}`] || "",
        gender: bookingForm[`gender_${idx}`] || "0",
        dob: bookingForm[`dob_${idx}`] || "",
        passportNumber: bookingForm[`passport_${idx}`] || "",
        passportIssuingCountry: bookingForm[`passportCountry_${idx}`] || "",
        passportExpiry: bookingForm[`passportExpiry_${idx}`] || "",
        nationality: bookingForm[`nationality_${idx}`] || "",
      });
    });

    try {
      const authToken =
  verifiedToken || (user && token ? token : null);

      let searchKey, flightKey, freshFareId, freshFare, bookingSupplierRequestId;
      let legKeys = [];

      if (isCombinedItinerary) {
        try {
          legKeys = await getFreshKeysForSeatLegs();
        } catch {
          setSaveError(
            "One or more flights in this itinerary can no longer be booked. Please search again."
          );
          return;
        }

        if (
          !legKeys.length ||
          legKeys.some(
            (item) =>
              !item.searchKey ||
              !item.flightKey ||
              !item.fareId
          )
        ) {
          setSaveError(
            "One or more flights in this itinerary are no longer available. Please search again."
          );
          return;
        }
      } else {
        if (isSpecialSupplierRoundTrip && repriced?.searchKey && repriced?.flightKey && repriced?.fareId) {
          searchKey = repriced.searchKey;
          flightKey = repriced.flightKey;
          freshFareId = repriced.fareId;
          freshFare = fare;
          bookingSupplierRequestId = repriced.supplierRequestId || fare?.supplierRequestId || flight.supplierRequestId || null;
        } else {
          try {
            const fresh = await getFreshSearchKey();

            searchKey = fresh.searchKey;
            flightKey = fresh.flightKey;
            freshFareId = fresh.fareId;
            freshFare = fresh.fare;
            bookingSupplierRequestId = fresh.supplierRequestId || null;
          } catch {
            setFareHoldError(
              "This fare can no longer be held. Please search again and choose a fresh flight."
            );
            return;
          }
        }

        if (!searchKey || !flightKey || !freshFareId) {
          setSaveError(
            "This flight can no longer be booked. Please search again and pick a fresh result."
          );
          return;
        }

        if (mode === "hold" && repriced?.searchKey && repriced?.flightKey) {
          searchKey = repriced.searchKey;
          flightKey = repriced.flightKey;
          freshFareId = repriced.fareId || freshFareId;
        }
      }
      // Payment must use the just-searched API fare, not a stale value from
      // the card. This also keeps Business/Premium/First class totals tied to
      // the Fare_Id submitted to Air_Reprice and TempBooking.
      const paymentFare = freshFare
  ? {
      ...freshFare,
      displayPrice:
        fare?.displayPrice ??
        flight.selectedFare?.displayPrice ??
        flight.displayPrice ??
        freshFare.displayPrice ??
        freshFare.price,
      childDisplayPrice:
        fare?.childDisplayPrice ?? freshFare.childDisplayPrice,
      infantDisplayPrice:
        fare?.infantDisplayPrice ?? freshFare.infantDisplayPrice,
    }
  : fare;

const paymentTotal = calcTotal(paymentFare);
      const bookingDisplayItineraryLegs =
        isSpecialSupplierRoundTrip
          ? buildSpecialRoundTripLegsFromReprice(flight, repriced, itineraryLegs)
          : itineraryLegs.map((leg, index) => compactBookingLeg(leg, index));

      const authoritativePhysicalSegments =
        isSpecialSupplierRoundTrip
          ? bookingDisplayItineraryLegs.flatMap(
            (leg) =>
              Array.isArray(leg?.segments)
                ? leg.segments
                : []
          )
          : [];


      const primaryDisplayLeg =
        bookingDisplayItineraryLegs[0] ||
        null;

      const returnDisplayLeg =
        bookingDisplayItineraryLegs[1] ||
        null;

      const storedFlightNumbers =
        isSpecialSupplierRoundTrip
          ? bookingDisplayItineraryLegs
            .map((leg) => leg.flightNumbers)
            .filter(Boolean)
            .join(", ")
          : flight.flightNumbers;
      const buildSeatDetailsForRows = (rows, selectedSeatIds, legIndex = 0, leg = null) =>
        groupSeatRowsBySegment(rows || []).flatMap((segment, segmentIndex) => {
          const seatsInSegment = segment.rows.flatMap((row) => row.seats || []);
          const flightSegment =
            isSpecialSupplierRoundTrip
              ? (
                (
                  segment.segmentId !== null &&
                  segment.segmentId !== undefined
                )
                  ? authoritativePhysicalSegments.find(
                    (candidate) =>
                      String(
                        candidate?.segmentId
                      ) ===
                      String(
                        segment.segmentId
                      )
                  )
                  : null
              ) ||
              authoritativePhysicalSegments[
              segmentIndex
              ] ||
              null
              : Array.isArray(leg?.segments)
                ? leg.segments[segmentIndex]
                : null;

          const segmentOrigin =
            segment.origin ||
            flightSegment?.origin ||
            flightSegment?.Origin ||
            flightSegment?.from ||
            flightSegment?.departCode ||
            null;

          const segmentDestination =
            segment.destination ||
            flightSegment?.destination ||
            flightSegment?.Destination ||
            flightSegment?.to ||
            flightSegment?.arriveCode ||
            null;

          const segmentRoute =
            segment.route ||
            (
              segmentOrigin && segmentDestination
                ? `${segmentOrigin} → ${segmentDestination}`
                : ""
            );

          const segmentFlightNo =
            segment.flightNo ||
            flightSegment?.flightNo ||
            flightSegment?.flightNumber ||
            flightSegment?.Flight_No ||
            flightSegment?.FlightNumber ||
            null;
          return selectedSeatIds
            .map((seatId) => seatsInSegment.find((seat) => seat.id === seatId))
            .filter(Boolean)
            .map((seat, index) => ({
              legIndex: Number(legIndex),
              paxId: (index % Math.max(1, pax.adults + pax.children)) + 1,
              route:
                seat.route ||
                segmentRoute ||
                (leg ? `${leg.departCode} → ${leg.arriveCode}` : ""),

              origin:
                seat.origin ||
                segmentOrigin ||
                null,

              destination:
                seat.destination ||
                segmentDestination ||
                null,

              flightNo:
                seat.flightNo ||
                segmentFlightNo ||
                leg?.flightNumbers ||
                null,
              flightNumbers:
                seat.flightNo ||
                segmentFlightNo ||
                segment.flightNo ||
                leg?.flightNumbers ||
                "",
              seatId: seat.id,
              seatCode: getSeatLabel(seat),
              segmentId: seat.segmentId ?? segment.segmentId ?? null,
              segmentKey: seat.segmentKey || segment.segmentKey || null,
              supplierLegIndex: seat.supplierLegIndex ?? segment.supplierLegIndex ?? null,
              flightId: seat.flightId ?? segment.flightId ?? null,
              ssrKey: seat.ssrKey || null,
              ssrSearchKey: seat.ssrSearchKey || null,
              ssrFlightKey: seat.ssrFlightKey || null,
              ssrFareId: seat.ssrFareId || null,
              ssrContextKey: seat.ssrContextKey || null,
              applicablePaxTypes: Array.isArray(seat.applicablePaxTypes)
                ? seat.applicablePaxTypes
                : [],
              seatType: seat.seatType || "unknown",
              extraCost: seat.extraCost || 0,
            }));
        });

      const seatDetails = hasDisplayItineraryLegs
        ? Object.entries(selectedSeatsByLeg).flatMap(
          ([selectionKey, seats]) => {
            const { legIndex } =
              getSeatSelectionParts(selectionKey);

            const sourceRows =
              isSpecialSupplierRoundTrip
                ? seatMapData || []
                : seatMapsByLeg[legIndex] || [];

            return buildSeatDetailsForRows(
              sourceRows,
              seats,
              Number(legIndex),
              effectiveItineraryLegs[
              Number(legIndex)
              ] ||
              itineraryLegs[
              Number(legIndex)
              ]
            );
          }
        )
        : buildSeatDetailsForRows(seatMapData || [], hasSingleSegmentedSeatMap ? Object.values(selectedSeatsByLeg).flat() : selectedSeats, 0, flight);
      
      const selectedSeatsTextByLeg = seatDetails.reduce((acc, seat) => {
        const key = Number.isFinite(Number(seat.legIndex)) ? Number(seat.legIndex) : 0;
        acc[key] = [...(acc[key] || []), seat.seatCode || seat.seatId].filter(Boolean);
        return acc;
      }, {});
      const seatSsrDetails = Object.values(
        seatDetails.reduce((groups, seat) => {
          if (!seat.ssrKey) return groups;
          const groupKey = [
            Number.isFinite(Number(seat.legIndex)) ? Number(seat.legIndex) : 0,
            seat.segmentKey || seat.segmentId || seat.route || "journey",
          ].join("::");
          groups[groupKey] = [...(groups[groupKey] || []), seat];
          return groups;
        }, {})
      ).flatMap((seatsInSegment) =>
        seatsInSegment.map((seat, index) => ({
          paxId: index + 1,
          ssrKey: seat.ssrKey,
          itineraryLegIndex: seat.legIndex,
          ssrSearchKey: seat.ssrSearchKey || null,
          ssrFlightKey: seat.ssrFlightKey || null,
          ssrFareId: seat.ssrFareId || null,
          ssrContextKey: seat.ssrContextKey || null,
        }))
      );

      
      // SSRs are booked per passenger. Assign each selected meal/add-on to the
      // first N eligible passengers, where N is the selected quantity.
      const paxManifest = passengerFields.map((_, index) => ({
        paxId: index + 1,
        paxType:
          index < pax.adults
            ? 0
            : index < pax.adults + pax.children
              ? 1
              : 2,
      }));

      const normalizeApplicablePaxTypes = (value) => {
        if (!Array.isArray(value)) return [];

        return value
          .map((entry) => {
            if (entry === null || entry === undefined) return null;

            if (typeof entry === "object") {
              const raw = firstDefined(
                entry.Pax_type,
                entry.Pax_Type,
                entry.PAXType,
                entry.PAX_Type,
                entry.paxType,
                entry.type,
                entry.value,
                entry.id,
                null
              );

              return raw === null || raw === undefined
                ? null
                : Number(raw);
            }

            return Number(entry);
          })
          .filter((type) => Number.isInteger(type) && type >= 0);
      };

      const eligiblePassengersFor = (applicablePaxTypes) => {
        const normalizedTypes = normalizeApplicablePaxTypes(applicablePaxTypes);
        const eligibleTypes =
          normalizedTypes.length > 0
            ? new Set(normalizedTypes)
            : new Set([0, 1]);

        return paxManifest.filter((manifestPax) =>
          eligibleTypes.has(manifestPax.paxType)
        );
      };

      const seatPaxCursorBySegment = new Map();

      const specialRoundTripSeatSsrDetails = isSpecialSupplierRoundTrip
        ? Object.values(
          seatDetails.reduce((groups, seat) => {
            if (!seat.ssrKey) return groups;
            const groupKey = [
              Number.isFinite(Number(seat.legIndex)) ? Number(seat.legIndex) : 0,
              seat.segmentKey || seat.segmentId || seat.route || "journey",
            ].join("::");
            groups[groupKey] = [...(groups[groupKey] || []), seat];
            return groups;
          }, {})
        ).flatMap((seatsInSegment) =>
          seatsInSegment.flatMap((seat) => {
            const groupKey = [
              Number.isFinite(Number(seat.legIndex)) ? Number(seat.legIndex) : 0,
              seat.segmentKey || seat.segmentId || seat.route || "journey",
            ].join("::");
            const eligiblePassengers = eligiblePassengersFor(seat.applicablePaxTypes);
            const cursor = seatPaxCursorBySegment.get(groupKey) || 0;
            const manifestPax = eligiblePassengers[cursor];

            if (!manifestPax) return [];

            seatPaxCursorBySegment.set(groupKey, cursor + 1);

            return [{
              paxId: manifestPax.paxId,
              paxType: manifestPax.paxType,
              ssrKey: seat.ssrKey,
              itineraryLegIndex: seat.legIndex,
              ssrSearchKey: seat.ssrSearchKey || null,
              ssrFlightKey: seat.ssrFlightKey || null,
              ssrFareId: seat.ssrFareId || null,
              ssrContextKey: seat.ssrContextKey || null,
              unitPrice: Number(seat.extraCost || 0),
              amount: Number(seat.extraCost || 0),
              source: "initial_booking_seat",
              applied: true,
              segmentId: seat.segmentId ?? null,
              supplierLegIndex: seat.supplierLegIndex ?? null,
              flightId: seat.flightId ?? null,
              applicablePaxTypes: normalizeApplicablePaxTypes(seat.applicablePaxTypes),
            }];
          })
        )
        : seatSsrDetails;

      const assignedWheelchairByLegPax = new Set();

      const mealSsrDetailsRaw = Object.values(selectedSsr).flatMap((selected) => {
        const applicablePaxTypes =
          normalizeApplicablePaxTypes(selected.applicablePaxTypes);

        const isWheelchairSsr =
          Number(selected.ssrType) === 10 ||
          String(
            selected.ssrTypeName ||
            selected.name ||
            ""
          )
            .toUpperCase()
            .includes("WHEELCHAIR");

        const eligiblePassengers = eligiblePassengersFor(selected.applicablePaxTypes);

        const requestedQty =
          Number(selected.qty) || 0;

        const assignedPassengers =
          eligiblePassengers.slice(
            0,
            Math.min(requestedQty, eligiblePassengers.length)
          ).filter((manifestPax) => {
            if (!isWheelchairSsr) return true;

            const dedupeKey = [
              Number(selected.itineraryLegIndex ?? 0),
              manifestPax.paxId,
            ].join("::");

            if (assignedWheelchairByLegPax.has(dedupeKey)) {
              return false;
            }

            assignedWheelchairByLegPax.add(dedupeKey);
            return true;
          });

        

        return assignedPassengers.map((manifestPax) => ({
          paxId:
            manifestPax.paxId,

          ssrKey:
            selected.ssrKey,

          name:
            selected.name ||
            selected.ssrTypeName ||
            "Extra",

          ssrType:
            selected.ssrType ?? null,

          ssrTypeName:
            selected.ssrTypeName || null,

          unitPrice:
            Number(selected.unitPrice || 0),

          amount:
            Number(selected.unitPrice || 0),

          source:
            "initial_booking",

          applied:
            true,

          itineraryLegIndex:
            Number(
              selected.itineraryLegIndex ??
              0
            ),

          segmentWise:
            Boolean(
              selected.segmentWise
            ),

          segmentId:
            selected.segmentId ??
            null,

          supplierLegIndex:
            selected.supplierLegIndex ??
            null,

          flightId:
            selected.flightId ??
            null,

          ssrSearchKey:
            selected.ssrSearchKey ||
            null,

          ssrFlightKey:
            selected.ssrFlightKey ||
            null,

          ssrFareId:
            selected.ssrFareId ||
            null,

          ssrContextKey:
            selected.ssrContextKey ||
            null,

          applicablePaxTypes,
        }));
      });

      const seenMealByPaxSegment = new Set();
      const mealSsrDetails = mealSsrDetailsRaw.filter((item) => {
        if (!isSpecialSupplierRoundTrip) return true;

        const isMealSsr =
          [1, 2, "1", "2"].includes(item.ssrType) ||
          String(item.ssrTypeName || item.name || "")
            .toUpperCase()
            .includes("MEAL");

        if (!isMealSsr) return true;

        const mealKey = [
          item.paxId,
          Number(item.itineraryLegIndex ?? 0),
          item.segmentId ?? item.supplierLegIndex ?? item.flightId ?? "journey",
        ].join("::");

        if (seenMealByPaxSegment.has(mealKey)) return false;
        seenMealByPaxSegment.add(mealKey);
        return true;
      });

      const bookingSsrDetails = [
        ...specialRoundTripSeatSsrDetails,
        ...mealSsrDetails,
      ].filter((item) => {
        if (!isSpecialSupplierRoundTrip) return true;
        const manifestPax = paxManifest.find((paxItem) => paxItem.paxId === item.paxId);
        if (!manifestPax) return false;
        const applicablePaxTypes =
          normalizeApplicablePaxTypes(item.applicablePaxTypes);
        if (!applicablePaxTypes.length) return manifestPax.paxType !== 2;
        return applicablePaxTypes.includes(manifestPax.paxType);
      });

      if (isSpecialSupplierRoundTrip) {


        const missingSeatSsrKey = specialRoundTripSeatSsrDetails.find((item) => !item.ssrKey);
        if (missingSeatSsrKey) {
          throw new Error(
            "Selected seat is missing current supplier SSR data. Please reopen the seat map and choose an available seat again."
          );
        }
      }

      const finalSeatExtraCost =
        isSpecialSupplierRoundTrip
          ? specialRoundTripSeatSsrDetails.reduce(
            (sum, item) => sum + Number(item.amount || 0),
            0
          )
          : seatExtraCost;

      const finalSsrExtraCost =
        isSpecialSupplierRoundTrip
          ? mealSsrDetails.reduce(
            (sum, item) => sum + Number(item.amount || 0),
            0
          )
          : ssrExtraCost;

      

const finalPaymentTotal = isSpecialSupplierRoundTrip
  ? (
      (
        Number(
          paymentFare?.basePrice ??
          paymentFare?.price ??
          flight?.basePrice ??
          flight?.price ??
          0
        ) || 0
      ) * pax.adults
      + (
        Number(
          paymentFare?.childBasePrice ??
          paymentFare?.childPrice ??
          flight?.childBasePrice ??
          flight?.childPrice
        ) || Math.round(
          (
            Number(
              paymentFare?.basePrice ??
              paymentFare?.price ??
              flight?.basePrice ??
              flight?.price ??
              0
            ) || 0
          ) * 0.75
        )
      ) * pax.children
      + (
        Number(
          paymentFare?.infantBasePrice ??
          paymentFare?.infantPrice ??
          flight?.infantBasePrice ??
          flight?.infantPrice
        ) || Math.round(
          (
            Number(
              paymentFare?.basePrice ??
              paymentFare?.price ??
              flight?.basePrice ??
              flight?.price ??
              0
            ) || 0
          ) * 0.10
        )
      ) * pax.infants
      + finalSeatExtraCost
      + finalSsrExtraCost
    )
  : paymentTotal;



      const staleAddonContext = bookingSsrDetails.some((item) => {
        const legIndex = Number(item.itineraryLegIndex ?? 0);

        const targetFlight =
          hasDisplayItineraryLegs
            ? seatLegs[legIndex]
            : flight;

        const targetFare =
          targetFlight?.selectedFare ||
          targetFlight?.fareOptions?.[
          targetFlight?.selectedFareIndex ?? selectedFare
          ] ||
          targetFlight?.fareOptions?.[0] ||
          fare;

        const currentContext = getCurrentAddonContext(
          targetFlight,
          targetFare,
          legIndex
        );

        if (!currentContext) return false;

        return (
          item.ssrContextKey &&
          item.ssrContextKey !== currentContext.requestKey
        );
      });

      if (staleAddonContext) {
        setSaveError(
          "Your selected add-ons are from an older fare refresh. Please reopen Seats and choose the add-ons again."
        );
        return;
      }
      

      const ssrContextByLeg = bookingSsrDetails.reduce((map, item) => {
        const legIndex = Number(item.itineraryLegIndex ?? 0);
        if (!item.ssrSearchKey || !item.ssrFlightKey) return map;
        const current = map.get(legIndex);
        const next = {
          searchKey: item.ssrSearchKey,
          flightKey: item.ssrFlightKey,
          fareId: item.ssrFareId || freshFareId,
          contextKey: item.ssrContextKey,
        };
        if (current && (current.searchKey !== next.searchKey || current.flightKey !== next.flightKey)) {
          map.set(legIndex, { ...current, mismatch: true });
        } else {
          map.set(legIndex, next);
        }
        return map;
      }, new Map());
      if ([...ssrContextByLeg.values()].some((context) => context.mismatch)) {
        setSaveError("Selected add-ons were loaded from different fare refreshes. Please reopen Seats/Extras and try again.");
        return;
      }
      const singleSsrContext = ssrContextByLeg.get(0);
      if (!isCombinedItinerary && bookingSsrDetails.length && singleSsrContext?.searchKey && singleSsrContext?.flightKey) {
        
        searchKey = singleSsrContext.searchKey;
        flightKey = singleSsrContext.flightKey;
        freshFareId = singleSsrContext.fareId || freshFareId;
      }

      
      const bookingStatus = mode === "hold" ? "held" : "pending_payment";
      

      

      

      

      // ── Server-authoritative customer price quote (flight pricing) ──
      // The backend (not this UI) applies admin-configured charges to the
      // supplier fare total and returns the payable amount plus an itemised
      // breakdown. quoteId is sent with the booking; the persisted booking
      // total and the actual Easebuzz amount are then derived server-side.
      


      

const quoteBookingTripType = getCanonicalTripType(
  flight?.tripType,
  isSpecialSupplierRoundTrip
);

const quoteLegs = bookingDisplayItineraryLegs?.length
  ? bookingDisplayItineraryLegs
  : itineraryLegs?.length
    ? itineraryLegs
    : [];

const quotePerLegSegments = quoteLegs.length
  ? quoteLegs.map((leg) =>
      Array.isArray(leg?.segments) && leg.segments.length
        ? leg.segments.length
        : 1
    )
  : [
      Array.isArray(flight?.segments) && flight.segments.length
        ? flight.segments.length
        : 1
    ];


 // Round-trip: calculate supplier fare from the original fare-bearing legs.
 // bookingDisplayItineraryLegs contains compact display data and may not
 // contain price, basePrice, selectedFare, or fareOptions.
const quoteFareLegs =
  Array.isArray(itineraryLegs) && itineraryLegs.length
    ? itineraryLegs
    : quoteLegs;


const quoteFareBase =
  quoteBookingTripType === "round_trip" &&
  quoteFareLegs.length >= 2
    ? quoteFareLegs.reduce((total, leg) => {
        const legFare =
          leg?.selectedFare ||
          leg?.fareOptions?.[
            leg?.selectedFareIndex ?? selectedFare
          ] ||
          leg?.fareOptions?.[0] ||
          {};

        const adultBase = Number(
          legFare.basePrice ??
          legFare.price ??
          leg.basePrice ??
          leg.price ??
          0
        ) || 0;

        const childBase = Number(
          legFare.childBasePrice ??
          legFare.childPrice ??
          leg.childBasePrice ??
          leg.childPrice ??
          Math.round(adultBase * 0.75)
        ) || 0;

        const infantBase = Number(
          legFare.infantBasePrice ??
          legFare.infantPrice ??
          leg.infantBasePrice ??
          leg.infantPrice ??
          Math.round(adultBase * 0.10)
        ) || 0;

        return (
          total +
          adultBase * pax.adults +
          childBase * pax.children +
          infantBase * pax.infants
        );
      }, 0)
    : (() => {
        const adultBase = Number(
          paymentFare?.basePrice ??
          paymentFare?.price ??
          flight?.basePrice ??
          flight?.price ??
          0
        ) || 0;

        const childBase = Number(
          paymentFare?.childBasePrice ??
          paymentFare?.childPrice ??
          flight?.childBasePrice ??
          flight?.childPrice ??
          Math.round(adultBase * 0.75)
        ) || 0;

        const infantBase = Number(
          paymentFare?.infantBasePrice ??
          paymentFare?.infantPrice ??
          flight?.infantBasePrice ??
          flight?.infantPrice ??
          Math.round(adultBase * 0.10)
        ) || 0;

        return (
          adultBase * pax.adults +
          childBase * pax.children +
          infantBase * pax.infants
        );
      })();


      let flightQuote = null;
      try {
        const quoteRes = await fetch(`${API_BASE}/flight-search/pricing-quote`, {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  },
  body: JSON.stringify({
            supplierFareTotal: quoteFareBase,
            seatExtraCost: finalSeatExtraCost || 0,
            ssrExtraCost: finalSsrExtraCost || 0,
            adults: pax.adults,
            children: pax.children,
            infants: pax.infants,
            perLegSegments: quotePerLegSegments,
            tripType: quoteBookingTripType,
            searchKey: searchKey || flight.searchKey || null,
            fareId: freshFareId || fare?.fareId || flight.fareId || null,
            travelDate:
              isSpecialSupplierRoundTrip
                ? primaryDisplayLeg?.travelDate || travelDate || flight.travelDate
                : travelDate || flight.travelDate,
            returnDate:
              isSpecialSupplierRoundTrip
                ? returnDisplayLeg?.travelDate || null
                : flight.returnDate || null,
          }),
        });
        const quoteData = await quoteRes.json();
        if (!quoteRes.ok || !quoteData?.quoteId) {
          throw new Error(quoteData?.error || "Could not price this booking");
        }
        flightQuote = quoteData;
        
      } catch (quoteErr) {
        console.error("[FLIGHT PRICING QUOTE] failed:", quoteErr);
        setSaveError(quoteErr.message || "Could not price this booking. Please try again.");
        return;
      }

      const res = await fetch(`${API_BASE}/flight-bookings`, {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  },
        body: JSON.stringify({
          fareName: paymentFare?.name || fare?.name || "Economy",
          airline:
            flight.airline,
          travelType: isInternational ? "international" : "domestic",

          
tripType: quoteBookingTripType,
Booking_Type: getSupplierBookingType(quoteBookingTripType),


          flightNumbers:
            storedFlightNumbers ||
            flight.flightNumbers,

          departAirport:
            isSpecialSupplierRoundTrip
              ? primaryDisplayLeg?.departCode || flight.departAirport
              : flight.departAirport,

          departCode:
            isSpecialSupplierRoundTrip
              ? primaryDisplayLeg?.departCode || flight.departCode
              : flight.departCode,

          arriveAirport:
            isSpecialSupplierRoundTrip
              ? primaryDisplayLeg?.arriveCode || flight.arriveAirport
              : flight.arriveAirport,

          arriveCode:
            isSpecialSupplierRoundTrip
              ? primaryDisplayLeg?.arriveCode || flight.arriveCode
              : flight.arriveCode,

          departTime:
            isSpecialSupplierRoundTrip
              ? primaryDisplayLeg?.departTime || flight.departTime
              : repricedSchedule?.departTime || flight.departTime,

          arriveTime:
            isSpecialSupplierRoundTrip
              ? primaryDisplayLeg?.arriveTime || flight.arriveTime
              : repricedSchedule?.arriveTime || flight.arriveTime,

          arriveNextDay:
            isSpecialSupplierRoundTrip
              ? false
              : repricedSchedule?.arriveNextDay ??
              (flight.arriveNextDay || false),

          duration:
            isSpecialSupplierRoundTrip
              ? primaryDisplayLeg?.duration || null
              : flight.duration,

          stops:
            isSpecialSupplierRoundTrip
              ? Number(primaryDisplayLeg?.stops || 0)
              : flight.stops,

          stopDetail:
            isSpecialSupplierRoundTrip
              ? primaryDisplayLeg?.stopDetail || null
              : flight.stopDetail,

          travelDate:
            isSpecialSupplierRoundTrip
              ? primaryDisplayLeg?.travelDate ||
              travelDate ||
              flight.travelDate
              : travelDate || flight.travelDate,

          returnDate:
            isSpecialSupplierRoundTrip
              ? returnDisplayLeg?.travelDate || null
              : flight.returnDate || null,
          numPassengers: totalPassengers,
          adults: pax.adults, children: pax.children, infants: pax.infants,
          classType: toApiClassType(pax.classType),
          fareType: activeFare,
          pricePerAdult: paymentFare?.price || flight.price,
          totalPrice: flightQuote ? flightQuote.payableTotal : finalPaymentTotal,
          quoteId: flightQuote ? flightQuote.quoteId : undefined,
          seatExtraCost: finalSeatExtraCost,
          ssrExtraCost: finalSsrExtraCost,
          selectedExtras: JSON.stringify(
            selectedExtrasList
          ),
          ssrDetails: JSON.stringify(
            mealSsrDetails
          ),
          refundable: paymentFare?.refundable ?? flight.refundable ?? false,
          baggageCheckin: baggageValue(paymentFare?.baggageCheckin || flight.baggageCheckin),
          baggageCabin: baggageValue(paymentFare?.baggageCabin || flight.baggageCabin),
          cancellationCharges: JSON.stringify(paymentFare?.cancellationCharges || flight.cancellationCharges || []),
          rescheduleCharges: JSON.stringify(paymentFare?.rescheduleCharges || flight.rescheduleCharges || []),
          selectedSeats: hasDisplayItineraryLegs
            ? Object.entries(selectedSeatsTextByLeg)
              .map(([legIndex, seats]) => `Leg ${Number(legIndex) + 1}: ${seats.join(",")}`)
              .join(" | ")
            : seatDetails.map((seat) => seat.seatCode || seat.seatId).filter(Boolean).join(","),
          seatDetails: JSON.stringify(seatDetails),
          passengerName: bookingForm["name_0"] || "",
          passengerEmail: bookingForm.email || "",
          passengerPhone: bookingForm.phone || "",
          passengerDob: bookingForm["dob_0"] || null,
          passengers: passengerFields.map(({ idx, typeLabel, typeIdx }) => {
            const isAdult = idx < pax.adults;
            const isChild = !isAdult && idx < pax.adults + pax.children;
            return {
              passengerId: idx + 1,

              paxType:
                isAdult
                  ? "0"
                  : isChild
                    ? "1"
                    : "2",

              title:
                bookingForm[`title_${idx}`] ||
                "MR",

              firstName:
                bookingForm[`name_${idx}`] ||
                "",

              lastName:
                bookingForm[`lname_${idx}`] ||
                "",

              gender:
                bookingForm[`gender_${idx}`] ||
                "0",

              nationality:
                bookingForm[`nationality_${idx}`] ||
                null,

              dob:
                bookingForm[`dob_${idx}`] ||
                null,

              passportNumber:
                bookingForm[`passport_${idx}`] ||
                null,

              passportIssuingCountry:
                bookingForm[
                `passportCountry_${idx}`
                ] || null,

              passportExpiry:
                bookingForm[
                `passportExpiry_${idx}`
                ] || null,

              associatedAdultPassengerId:
                !isAdult && !isChild
                  ? Number(
                    bookingForm[
                    `associatedAdultPassengerId_${idx}`
                    ]
                  ) || null
                  : null,
            };
          }),
          status: bookingStatus,
          gstDetails,
          itineraryLegs: hasDisplayItineraryLegs
            ? bookingDisplayItineraryLegs.map((leg, index) => ({
              ...leg,
              supplierRequestId: leg.supplierRequestId || bookingSupplierRequestId || flight.supplierRequestId || null,
              supplierBookingMode: isSpecialSupplierRoundTrip ? "special-round-trip" : undefined,
            }))
            : [{
              legIndex: 0,
              flightId: flight.flightId || flight.id || null,
              segmentId:
                flight.segments?.[0]?.segmentId ??
                flight.segmentId ??
                null,
              segments: (flight.segments || []).filter(
                (s) => Number.isFinite(Number(s.segmentId))
              ),
              departCode: flight.departCode,
              arriveCode: flight.arriveCode,
              flightNumbers: flight.flightNumbers,
              travelDate: travelDate || flight.travelDate,
              supplierRequestId: bookingSupplierRequestId || fare?.supplierRequestId || flight.supplierRequestId || null,
            }],
        }),
      });
      const data = await res.json();
      if (res.status === 401) {
  if (authToken) {
    expireSession?.();
    onLoginRequest();
    throw new Error(
      "Your login session expired. Please log in again before booking."
    );
  }

  throw new Error(
    data?.error ||
      "Guest booking authorization failed. Please try again."
  );
}
      if (!res.ok) {
        console.error("[FlightBooking Client] POST /flight-bookings failed:", {
          status: res.status,
          error: data?.error,
          code: data?.code,
        });
        throw new Error(data.error || "Booking creation failed");
      }
      const resolvedBookingId = data.booking?.id ?? data.id;

const resolvedBookingReference =
  data.bookingReference ||
  data.booking?.booking_reference ||
  data.booking_reference ||
  "";

if (!resolvedBookingId || !resolvedBookingReference) {
  throw new Error(
    data.error || "The server did not return a valid booking reference."
  );
}

// A guest booking must receive its own backend-issued access token.
if (!authToken) {
  if (
    typeof data.guestAccessToken !== "string" ||
    !/^[a-f0-9]{64}$/i.test(data.guestAccessToken)
  ) {
    throw new Error(
      "The guest booking token was not returned correctly. Please contact support before retrying."
    );
  }

  guestAccessTokenRef.current = data.guestAccessToken;
  setGuestAccessToken(data.guestAccessToken);
}

setBookingRef(resolvedBookingReference);
setBookingId(resolvedBookingId);
      setConfirmedBookingData({
        fare: paymentFare || fare,
        // Server-authoritative total (booking row). With a quote this is the
        // payable amount incl. admin charges; legacy is the client total.
        totalPrice: Number(
  data.pricing?.payableTotal ??
  data.booking?.total_price ??
  flightQuote?.payableTotal ??
  finalPaymentTotal ??
  totalPrice
),
        pricing: data.pricing || null,
        selectedSeatGroups: selectedSeatGroupsForDisplay,
        selectedSeatSummary,
        extras: selectedExtrasList,
      });
      
      if (isCombinedItinerary) {
        try {
          const combinedReprice = repriced?.legs?.length
            ? repriced
            : await repriceFlight(true);
          if (!combinedReprice?.legs?.length) {
            setSaveError("Could not refresh this roundtrip itinerary. Please search again.");
            return;
          }
          const legsPayload = legKeys.map(
            (
              {
                searchKey,
                flightKey,
                fareId,
                supplierRequestId,
                leg
              },
              index
            ) => {
              const legAdults =
                pax.adults;

              const legChildren =
                pax.children;

              const legInfants =
                pax.infants;

              const legMealSsrDetails =
                bookingSsrDetails
                  .filter(
                    (item) =>
                      Number(
                        item.itineraryLegIndex
                      ) === index
                  )
                  .map(
                    (item) => ({
                      paxId:
                        item.paxId,

                      ssrKey:
                        item.ssrKey,
                    })
                  );
              const legSsrContext = ssrContextByLeg.get(index);
              const repricedLegContext = combinedReprice.legs[index] || null;
              const finalSearchKey = legSsrContext?.searchKey || repricedLegContext?.searchKey || searchKey;
              const finalFlightKey = legSsrContext?.flightKey || repricedLegContext?.flightKey || flightKey;
              const finalFareId = legSsrContext?.fareId || repricedLegContext?.fareId || fareId;
              if (legSsrContext) {
                
              }

              return {
                legIndex: index,
                flightId: leg.flightId || leg.id || null,
                segmentId:
                  leg.segments?.[0]?.segmentId ??
                  leg.segmentId ??
                  null,
                segments: (leg.segments || []).filter(
                  (s) => Number.isFinite(Number(s.segmentId))
                ),
                searchKey: finalSearchKey,
                flightKey: finalFlightKey,
                fareId: finalFareId,
                supplierRequestId: legSsrContext?.supplierRequestId || repricedLegContext?.supplierRequestId || supplierRequestId || null,
                departCode: leg.departCode, arriveCode: leg.arriveCode,
                customerMobile: String(bookingForm.phone || "").trim(),
passengerMobile: String(bookingForm.phone || "").trim(),
                passengerEmail:
  String(bookingForm.email || "").trim() ||
  COMPANY_EMAIL,
                adults: legAdults, children: legChildren, infants: legInfants,
                isInternational,
                gstDetails,
                ssrDetails: legMealSsrDetails,
                useProvidedRepricedKeys: true,
                passengers: passengerFields.map(({ idx }) => {
                  const isAdult = idx < pax.adults;
                  const isChild = !isAdult && idx < pax.adults + pax.children;
                  return {
                    paxType: isAdult ? "0" : isChild ? "1" : "2",
                    title: bookingForm[`title_${idx}`] || "MR",
                    firstName: bookingForm[`name_${idx}`] || "",
                    lastName: bookingForm[`lname_${idx}`] || "",
                    gender: bookingForm[`gender_${idx}`] || "0",

                    dob: isDobRequired(idx)
                      ? bookingForm[`dob_${idx}`] || null
                      : null,

                    nationality: isNationalityRequired(idx)
                      ? bookingForm[`nationality_${idx}`] || null
                      : null,

                    passportNumber: isPassportNumberRequired(idx)
                      ? bookingForm[`passport_${idx}`] || null
                      : null,

                    passportIssuingCountry: isPassportCountryRequired(idx)
                      ? bookingForm[`passportCountry_${idx}`] || null
                      : null,

                    passportExpiry: isPassportExpiryRequired(idx)
                      ? bookingForm[`passportExpiry_${idx}`] || null
                      : null,
                  };
                }),
              };
            });

          const authToken = verifiedToken || (user && token ? token : null);
          
          const itinRes = await fetch(`${API_BASE}/flight-ticketing/temp-booking-itinerary`, {
            method: "POST",
            headers: getFlightBookingHeaders(authToken),
            body: JSON.stringify({
              bookingId: resolvedBookingId,
              legs: legsPayload,
              bookingMode: mode,
            }),
          });
          const itinData = await itinRes.json();
          if (!itinRes.ok || !itinData.success) {
            console.error("[TempBookingItinerary Client] failed:", {
              status: itinRes.status,
              error: itinData?.error,
              legIndex: itinData?.legIndex,
            });
            throw new Error(itinData.error || "Could not initiate booking with the airline for one or more flights.");
          }
          setStep(mode === "hold" ? "held" : "payment");
        } catch (err) {
  const message =
    err.message ||
    "Something went wrong preparing this itinerary. Please try again.";

  if (/passenger.*email.*required|email.*required/i.test(message)) {
    setShowSupplierEmail(true);
    setSaveError(
      "The airline requires a contact email. Enter your email and continue again."
    );
  } else {
    setSaveError(message);
  }
}
        return;
      }


      
      const holdUsesFrontendReprice =
        mode === "hold" &&
        Boolean(repriced?.searchKey && repriced?.flightKey);
      const specialRoundTripUsesFrontendReprice =
        isSpecialSupplierRoundTrip &&
        Boolean(repriced?.searchKey && repriced?.flightKey && repriced?.fareId);
      
      const tempRes = await fetch(`${API_BASE}/flight-ticketing/temp-booking`, {
        method: "POST",
        headers: getFlightBookingHeaders(authToken),
        body: JSON.stringify({
          bookingId: resolvedBookingId,
          bookingMode: mode,
          searchKey,
          flightKey,
          inventoryType: flight.inventoryType ?? 0,
          isLCC: flight.isLCC ?? false,
          fareId: freshFareId,
          supplierRequestId: repriced?.supplierRequestId || freshFare?.supplierRequestId || fare?.supplierRequestId || flight.supplierRequestId || null,
          gstDetails,
          ssrDetails: bookingSsrDetails,
          useProvidedRepricedKeys: holdUsesFrontendReprice || specialRoundTripUsesFrontendReprice || Boolean(singleSsrContext),
          customerMobile: String(bookingForm.phone || "").trim(),
passengerMobile: String(bookingForm.phone || "").trim(),
          passengerEmail:
  String(bookingForm.email || "").trim() ||
  COMPANY_EMAIL,
          adults: pax.adults,
          children: pax.children,
          infants: pax.infants,
          isInternational,
          passengers: passengerFields.map(({ idx, typeLabel, typeIdx }) => {
            const isAdult = idx < pax.adults;
            const isChild = !isAdult && idx < pax.adults + pax.children;
            return {
              paxType: isAdult ? "0" : isChild ? "1" : "2",  // ← use index, not label
              title: bookingForm[`title_${idx}`] || "MR",
              firstName: bookingForm[`name_${idx}`] || "",
              lastName: bookingForm[`lname_${idx}`] || "",
              gender: bookingForm[`gender_${idx}`] || "0",
              dob: isDobRequired(idx)
                ? bookingForm[`dob_${idx}`] || null
                : null,

              nationality: isNationalityRequired(idx)
                ? bookingForm[`nationality_${idx}`] || null
                : null,

              passportNumber: isPassportNumberRequired(idx)
                ? bookingForm[`passport_${idx}`] || null
                : null,

              passportIssuingCountry: isPassportCountryRequired(idx)
                ? bookingForm[`passportCountry_${idx}`] || null
                : null,

              passportExpiry: isPassportExpiryRequired(idx)
                ? bookingForm[`passportExpiry_${idx}`] || null
                : null,
            };
          }),
        }),
      });
      const tempData = await tempRes.json();
      if (!tempRes.ok) {
        console.error("[TempBooking Client] supplier failure:", tempData);
        const stale = tempRes.status === 409;
        const supplierMsg = tempData?.detail?.Response_Header?.Error_InnerException
          || tempData?.detail?.Response_Header?.Error_Desc
          || tempData?.detail?.ResponseHeader?.Error_InnerException
          || tempData?.detail?.ResponseHeader?.Error_Desc
          || (stale
            ? "This flight search has expired or was already used to start a booking. Please close this window and search again to get a fresh price."
            : null)
          || tempData?.error
          || "Could not initiate booking with airline.";
        if (stale) setIsStaleKey(true);
        throw new Error(supplierMsg);
      }

      if (mode === "hold") {
        setStep("held");
      } else {
        setStep("payment");
      }
   } catch (err) {
  const message =
    err.message || "Something went wrong. Please try again.";

  if (/passenger.*email.*required|email.*required/i.test(message)) {
    setShowSupplierEmail(true);
    setSaveError(
      "The airline requires a contact email. Enter your email and continue again."
    );
  } else {
    setSaveError(message);
  }
} finally {
  bookingRequestRef.current = false;
  setSaving(false);
  setSavingMode(null);
}
  };


  const handlePaymentSuccess = async (paymentResponse) => {
  const authToken = user && token ? token : null;
    try {
      if (bookingId && isCombinedItinerary) {
        try {
          const addPaymentRes = await fetch(`${API_BASE}/flight-ticketing/add-payment-itinerary`, {
            method: "POST",
            headers: getFlightBookingHeaders(authToken),
            body: JSON.stringify({
              bookingId, transactionType: 0,
              clientRefNo: paymentResponse.easepayid || paymentResponse.txnid || "",
              gatewayResponse: paymentResponse,
            }),
          });
          const addPaymentData = await addPaymentRes.json();
          if (!addPaymentRes.ok || !addPaymentData.success) {
            throw new Error("Could not confirm payment with the airline for one or more flights.");
          }

          const ticketRes = await fetch(`${API_BASE}/flight-ticketing/ticket-itinerary`, {
            method: "POST",
            headers: getFlightBookingHeaders(authToken),
            body: JSON.stringify({ bookingId, ticketingType: "1" }),
          });
          const ticketData = await ticketRes.json();
          const initialStatus = ticketData.status || "failed";
          setTicketStatus(initialStatus);

          if (initialStatus === "inprocess") {
            pollIntervalRef.current = setInterval(async () => {
              try {
                const statusRes = await fetch(
  `${API_BASE}/flight-ticketing/status/${bookingId}`,
  {
    headers: getFlightBookingHeaders(authToken),
  }
);
                const statusData = await statusRes.json();
                if (["success", "failed", "cancelled", "blocked"].includes(statusData.ticketing_status)) {
                  clearInterval(pollIntervalRef.current);
                  setTicketStatus(statusData.ticketing_status || "failed");
                }
              } catch (_) { /* keep polling */ }
            }, 5 * 60 * 1000);
          }
        } catch (e) {
          console.error("Itinerary booking confirm error:", e);
          setTicketStatus("failed");
        }
        setStep("confirm");
        return;
      }
      if (bookingId) {
        // 1. First notify supplier that payment was completed.
        const addPaymentRes = await fetch(`${API_BASE}/flight-ticketing/add-payment`, {
          method: "POST",
          headers: getFlightBookingHeaders(authToken),
          body: JSON.stringify({
            bookingId,
            transactionType: 0,
            clientRefNo: paymentResponse.easepayid || paymentResponse.txnid || "",
            gatewayResponse: paymentResponse,
          }),
        });
        const addPaymentData = await addPaymentRes.json();
        if (!addPaymentRes.ok || !addPaymentData.success) {
          throw new Error(addPaymentData.errorDesc || addPaymentData.error || "Could not add payment with airline.");
        }

        // 2. Then trigger Air_Ticketing.
        const ticketRes = await fetch(`${API_BASE}/flight-ticketing/ticket`, {
          method: "POST",
          headers: getFlightBookingHeaders(authToken),
          body: JSON.stringify({ bookingId, ticketingType: "1" }),
        });
        const ticketData = await ticketRes.json();
        if (!ticketRes.ok || !ticketData.success) {
          throw new Error(ticketData.error || ticketData.pnr?.failureRemark || "Airline ticketing could not be completed.");
        }
        const initialStatus = ticketData.status || "failed";
        setTicketStatus(initialStatus);

        // Safe to include status: this PATCH runs after ticketData has already
        // resolved (sequential await), so initialStatus is final — no race here.
        await fetch(`${API_BASE}/flight-bookings/${bookingId}`, {
          method: "PATCH",
          headers: getFlightBookingHeaders(authToken),
          body: JSON.stringify({
            status: initialStatus === "success" ? "upcoming" : "pending_ticket",
            paymentId: paymentResponse.easepayid || paymentResponse.txnid || "",
            paymentMethod: paymentResponse.mode || "easebuzz",
            paymentStatus: "success",
            gatewayResponse: paymentResponse,
          }),
        });

        // 4. For a pending supplier response, Air_Ticketing documentation
        // requires Air_Reprint checks every five minutes until final status.
        if (initialStatus === "inprocess") {
          pollIntervalRef.current = setInterval(async () => {
            try {
              const statusRes = await fetch(
                `${API_BASE}/flight-ticketing/status/${bookingId}`,
                { headers: getFlightBookingHeaders(authToken), }
              );
              const statusData = await statusRes.json();
              if (["success", "failed", "cancelled", "blocked"].includes(statusData.ticketing_status)) {
                clearInterval(pollIntervalRef.current);
                setTicketStatus(statusData.ticketing_status || "failed");
              }
            } catch (_) {
              // Keep polling: the supplier can be temporarily unavailable.
            }
          }, 5 * 60 * 1000);
        }
      }
    } catch (e) {
      console.error("Booking confirm error:", e);
      setTicketStatus("failed");
    }
    setStep("confirm");
  };

  const markCurrentBookingHeld = async (message = "Payment was not completed. Your airline booking has been held so you can resume payment from My Bookings.") => {
    if (!bookingId) {
      onClose?.();
      return;
    }

    const authToken = user && token ? token : null;

    try {
      await fetch(`${API_BASE}/flight-bookings/${bookingId}`, {
        method: "PATCH",
        headers: getFlightBookingHeaders(authToken),
        body: JSON.stringify({
          status: "held",
          paymentStatus: null,
        }),
      });
    } catch (err) {
      console.warn("[Flight Payment] Could not mark booking held after payment close:", err.message);
    }

    setSaveError(message);
    setStep("held");
  };

  const stepOrder = ["fare", "passengers", "seats", "meals", "baggage", "extras", "details", "payment", "confirm"];
  const stepLabels = ["Fare", "Passengers", "Seats", "Meals", "Baggage", "Extras", "Details", "Payment", "Done"];
  const currentStepIdx = stepOrder.indexOf(step);
  const addonStepMeta = {
    meals: {
      title: "Meals",
      subtitle: "Optional meal choices, grouped by flight segment when the airline provides segment-wise options.",
      empty: "No meal options available for this flight.",
      optional: "Meals are optional",
      next: "baggage",
      selectedText: "Continue with meals →",
      skipText: "Skip Meals →",
    },
    baggage: {
      title: "Baggage",
      subtitle: "Optional extra baggage, grouped by flight segment when the airline provides segment-wise options.",
      empty: "No additional baggage available for this flight.",
      optional: "Baggage is optional",
      next: "extras",
      selectedText: "Continue with baggage →",
      skipText: "Skip Baggage →",
    },
    extras: {
      title: "Other Extras",
      subtitle: "Optional add-ons such as wheelchair assistance and other airline services.",
      empty: "No other extras available for this flight. You can continue without adding any.",
      optional: "Extras are optional",
      next: "details",
      selectedText: "Continue with extras →",
      skipText: "Skip & Fill Details →",
    },
  };

  const ssrDisplayLegs =
    isCombinedItinerary
      ? seatLegs.map(
        (
          leg,
          itineraryLegIndex
        ) => ({
          leg,

          itineraryLegIndex,

          data:
            filterSsrDataForStep(
              ssrDataByLeg[
              itineraryLegIndex
              ] || {},
              step
            ),
        })
      )
      : isSpecialSupplierRoundTrip &&
        effectiveItineraryLegs.length > 1
        ? effectiveItineraryLegs.map(
          (leg, itineraryLegIndex) => ({
            leg,
            itineraryLegIndex,
            data:
              filterSsrDataForItineraryLeg(
                ssrData || {},
                step,
                leg,
                itineraryLegIndex
              ),
          })
        )
        : [
          {
            leg: flight,
            itineraryLegIndex: 0,
            data: filterSsrDataForStep(ssrData || {}, step),
          },
        ];

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div className="flight-pricing-modal" style={styles.pricingModal} onClick={(e) => e.stopPropagation()}>
 
        {/* Header */}
        <div style={styles.pricingHeader}>
          <div>
            <div style={styles.pricingTitle}>
              {step === "confirm"
                ? ticketStatus === "success"
                  ? "Booking Confirmed! 🎉"
                  : "Payment Received!"
                : "Book Your Flight"}
            </div>
            <div style={styles.pricingSubtitle}>
              {modalSubtitle}
              {!isSpecialSupplierRoundTrip && (
                <>
                  {" "}· {repricedSchedule?.departTime || flight.departTime} – {repricedSchedule?.arriveTime || flight.arriveTime}
                  {(flight.departTime === "--:--" || flight.arriveTime === "--:--") && !repricedSchedule && (
                    <span style={{ marginLeft: 6, fontSize: 10.5, opacity: 0.8 }}>(loading exact timing…)</span>
                  )}
                </>
              )}
              {activeFare !== "regular" && (
                <span style={{ marginLeft: 8, background: "rgba(255,255,255,0.2)", borderRadius: 4, padding: "1px 6px", fontSize: 10 }}>
                  {fareTypes.find(f => f.id === activeFare)?.label} Fare
                </span>
              )}
              {specialRtSummary.length > 0 && (
                <span style={{ display: "block", marginTop: 4, fontSize: 11 }}>
                  {specialRtSummary.map((item) => `${item.label}: ${item.route}`).join(" · ")}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        {/* Step indicator */}
        {step !== "confirm" && (
          <div
            className="flight-booking-steps"
            style={{
              display: "flex",
              alignItems: "center",
              padding: "14px 28px 0",
              gap: 4
            }}
          >
            {stepOrder.filter(s => s !== "confirm").map((s, i) => {
              const done = currentStepIdx > i;
              const active = currentStepIdx === i;
              return (
                <div key={s} style={{ display: "flex", alignItems: "center", flex: i < stepOrder.length - 2 ? 1 : "none" }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0,
                    background: done ? "rgba(16,185,129,0.15)" : active ? "linear-gradient(135deg,#10b981,#047857)" : "#f1f5f9",
                    color: done ? "#10b981" : active ? "#fff" : "#94a3b8",
                    boxShadow: active ? "0 2px 8px rgba(16,185,129,0.3)" : "none",
                  }}>
                    {i + 1}
                  </div>
                  <div style={{ fontSize: 9.5, color: active ? "#10b981" : done ? "#10b981" : "#94a3b8", fontWeight: active || done ? 700 : 400, marginLeft: 4, whiteSpace: "nowrap" }}>
                    {stepLabels[i]}
                  </div>
                  {i < stepOrder.length - 2 && <div style={{ flex: 1, height: 2, background: done ? "rgba(16,185,129,0.3)" : "#f1f5f9", margin: "0 8px" }} />}
                </div>
              );
            })}
          </div>
        )}

        {/* Step: Fare */}
        {step === "fare" && (
          <div className="flight-pricing-body flight-pricing-body--details" style={styles.pricingBody}>
            <div className="flight-fare-grid" style={styles.fareGrid}>
              {flight.fareOptions?.map((f, i) => (
                <FareOptionCard
                  key={i}
                  fare={f}
                  selected={selectedFare === i}
                  pricePerLabel={pricePerLabel}
                  onClick={() => {
                    if (i === selectedFare) return;
                    setSelectedFare(i);
                    setRepriced(null);
                    setSeatMapData(null);
                    setSelectedSeats([]);
                    setSsrData(null);
                    setSsrDataByLeg({});
                    ssrCacheRef.current.clear();
                    ssrInFlightRef.current.clear();
                    addonKeyContextRef.current.clear();
                    setSelectedSsr({});
                    setFareHold(null);
                    setFareHoldError("");
                  }}
                />
              ))}
            </div>
            <div className="flight-fare-summary" style={styles.pricingSummary}>
              <div>
                <div style={styles.summaryLabel}>BASE FARE ({paxLabel.toUpperCase()})</div>
                <div style={styles.summaryPrice}>
  ₹{Math.round(
    Number(
      fare?.displayPrice ??
      flight.displayPrice ??
      fare?.price ??
      flight.price ??
      0
    )
  ).toLocaleString("en-IN")}
</div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>Incl. taxes &amp; fees · {fare?.name}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, color: "#64748b" }}>Next: choose passengers</div>
              </div>
            </div>
          </div>
        )}

        {/* Step: Passengers */}
        {step === "passengers" && (
          <div style={styles.pricingBody}>
            <div style={{ padding: 20, background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0d1b3e", marginBottom: 4 }}>
                Passengers &amp; Class
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>
                Adjust counts if needed — we'll refresh the fare for you automatically.
              </div>

              {[
                { key: "adults", label: "Adults", sub: "12+ years", min: Math.max(1, pax.infants) },
                { key: "children", label: "Children", sub: "2–11 years", min: 0 },
                { key: "infants", label: "Infants", sub: "Under 2 years (needs an adult)", min: 0 },
              ].map(({ key, label, sub, min }) => {
                const val = pax[key];
                const atInfantMax = key === "infants" ? val >= pax.adults : false;
                return (
                  <div key={key} className="pax-selector-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderBottom: "1px solid #eef2f7" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#0d1b3e" }}>{label}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8" }}>{sub}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <button type="button" className="pax-btn"
                        onClick={() => updatePaxCount(key, -1)} disabled={val <= min}>−</button>
                      <span style={{ minWidth: 20, textAlign: "center", fontWeight: 700 }}>{val}</span>
                      <button type="button" className="pax-btn"
                        onClick={() => updatePaxCount(key, 1)} disabled={atInfantMax}>+</button>
                    </div>
                  </div>
                );
              })}

              {pax.infants > pax.adults && (
                <div style={{ fontSize: 11, color: "#b91c1c", padding: "6px 0" }}>
                  Each infant must travel with an adult.
                </div>
              )}

              {paxSyncNotice && (
                <div style={{ marginTop: 10, fontSize: 11.5, color: "#047857", background: "#f0fdf4", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 8, padding: "8px 10px" }}>
                  {paxSyncNotice}
                </div>
              )}
            </div>
            <div style={styles.pricingSummary}>
  <div>
    <div style={styles.summaryLabel}>
      TOTAL FOR {totalPassengers} PASSENGER
      {totalPassengers > 1 ? "S" : ""}
    </div>

    <div style={styles.summaryPrice}>
      {passengerPricingLoading
        ? "Calculating..."
        : passengerPricingError
          ? "Price unavailable"
          : passengerPricingPreview
            ? `₹${Math.round(
                Number(passengerPricingPreview.payableTotal) || 0
              ).toLocaleString("en-IN")}`
            : "—"}
    </div>

    {passengerPricingError && (
      <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 6 }}>
        {passengerPricingError}
      </div>
    )}
  </div>
</div>
          </div>
        )}

        {/* Step: Seats */}
        {step === "seats" && (
          <div style={styles.pricingBody}>
            {isCombinedItinerary ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                {seatLegs.map((leg, legIndex) => {
                  const legRows = seatMapsByLeg[legIndex] || [];
                  const segmentGroups = groupSeatRowsBySegment(legRows);
                  const showSegmentGroups = segmentGroups.length > 1;
                  return (
                    <div key={`${leg.id || leg.flightNumbers || legIndex}-${legIndex}`} style={{ border: "1px solid rgba(16,185,129,0.16)", borderRadius: 14, overflow: "hidden", background: "#fff" }}>
                      <div style={{ padding: "12px 16px", background: "#ecfdf5", borderBottom: "1px solid rgba(16,185,129,0.16)", display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                        <div>
                          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 13, fontWeight: 800, color: "#047857" }}>Flight {legIndex + 1}</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#0d1b3e" }}>{leg.departCode} → {leg.arriveCode} · {leg.flightNumbers}</div>
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>{leg.travelDate || travelDate}</div>
                      </div>
                      {showSegmentGroups ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 12 }}>
                          {segmentGroups.map((segment, segmentIndex) => {
                            const segmentSeatIds = new Set(segment.rows.flatMap((row) => row.seats.map((seat) => seat.id)));
                            const selectionLegIndex =
                              isSpecialSupplierRoundTrip &&
                                Number.isInteger(Number(segment.supplierLegIndex))
                                ? Number(segment.supplierLegIndex)
                                : 0;
                            const selectionKey = getSeatSegmentSelectionKey(legIndex, segment.segmentKey);
                            const selectedForSegment = (selectedSeatsByLeg[selectionKey] || []).filter((seatId) => segmentSeatIds.has(seatId));
                            return (
                              <div key={`${legIndex}-${segment.segmentKey}`} style={{ border: "1px solid rgba(16,185,129,0.16)", borderRadius: 12, overflow: "hidden" }}>
                                <div style={{ padding: "8px 12px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontSize: 12, fontWeight: 800, color: "#0d1b3e", display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                                  <span>{getVisibleSeatSegmentLabel(segment, segmentIndex, leg)}</span>
                                  <span style={{ color: "#047857" }}>{selectedForSegment.length}/{pax.adults + pax.children} selected</span>
                                </div>
                                <SeatMap
                                  seatMap={segment.rows}
                                  selectedSeats={selectedForSegment}
                                  onSeatToggle={(seat) => toggleSeatForLeg(selectionKey, seat)}
                                  requiredSeats={pax.adults + pax.children}
                                  airlineCode={leg.airlineLogo}
                                  loading={seatMapLoading && !seatMapsByLeg[legIndex]}
                                />
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <SeatMap
                          seatMap={legRows}
                          selectedSeats={selectedSeatsByLeg[getSeatSegmentSelectionKey(legIndex, "journey")] || []}
                          onSeatToggle={(seat) =>
                            toggleSeatForLeg(getSeatSegmentSelectionKey(legIndex, "journey"), seat)
                          }
                          requiredSeats={pax.adults + pax.children}
                          airlineCode={leg.airlineLogo}
                          loading={seatMapLoading && !seatMapsByLeg[legIndex]}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              (() => {
                const rows = seatMapData || [];
                const segmentGroups = groupSeatRowsBySegment(rows);
                if (segmentGroups.length <= 1) {
                  return (
                    <SeatMap
                      seatMap={rows}
                      selectedSeats={selectedSeats}
                      onSeatToggle={toggleSeat}
                      requiredSeats={pax.adults + pax.children}
                      airlineCode={flight.airlineLogo}
                      loading={seatMapLoading}
                    />
                  );
                }
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {segmentGroups.map((segment, segmentIndex) => {
                      const specialPhysicalSegments =
                        isSpecialSupplierRoundTrip
                          ? effectiveItineraryLegs.flatMap(
                            (leg) =>
                              Array.isArray(leg?.segments)
                                ? leg.segments
                                : []
                          )
                          : [];

                      const authoritativeSegment =
                        specialPhysicalSegments[segmentIndex] ||
                        segment;
                      const segmentSeatIds = new Set(segment.rows.flatMap((row) => row.seats.map((seat) => seat.id)));
                      const selectionLegIndex =
                        Number.isFinite(
                          Number(
                            authoritativeSegment?.legIndex ??
                            authoritativeSegment?.supplierLegIndex
                          )
                        )
                          ? Number(
                            authoritativeSegment?.legIndex ??
                            authoritativeSegment?.supplierLegIndex
                          )
                          : 0;

                      const selectionKey =
                        getSeatSegmentSelectionKey(
                          selectionLegIndex,
                          segment.segmentKey
                        );
                      const selectedForSegment = (selectedSeatsByLeg[selectionKey] || []).filter((seatId) => segmentSeatIds.has(seatId));
                      return (
                        <div key={segment.segmentKey} style={{ border: "1px solid rgba(16,185,129,0.16)", borderRadius: 12, overflow: "hidden" }}>
                          <div style={{ padding: "8px 12px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontSize: 12, fontWeight: 800, color: "#0d1b3e", display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                            <span>
                              {getVisibleSeatSegmentLabel(
                                authoritativeSegment,
                                segmentIndex,
                                {
                                  ...flight,
                                  segments:
                                    specialPhysicalSegments.length
                                      ? specialPhysicalSegments
                                      : flight.segments,
                                }
                              )}
                            </span>
                            <span style={{ color: "#047857" }}>{selectedForSegment.length}/{pax.adults + pax.children} selected</span>
                          </div>
                          <SeatMap
                            seatMap={segment.rows}
                            selectedSeats={selectedForSegment}
                            onSeatToggle={(seat) => toggleSeatForLeg(selectionKey, seat)}
                            requiredSeats={pax.adults + pax.children}
                            airlineCode={flight.airlineLogo}
                            loading={seatMapLoading}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            )}
          </div>
        )}
        {["meals", "baggage", "extras"].includes(step) && (
          <div style={styles.pricingBody}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 700, color: "#0d1b3e" }}>
                {addonStepMeta[step].title}
              </div>
              <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 3 }}>
                {addonStepMeta[step].subtitle}
              </div>
            </div>

            {ssrLoading && (
              <div style={{ textAlign: "center", padding: "32px 0" }}>
                <div style={{ width: 32, height: 32, border: "3px solid #d1fae5", borderTopColor: "#10b981", borderRadius: "50%", animation: "spin 0.9s linear infinite", margin: "0 auto 10px" }} />
                <div style={{ fontSize: 12.5, color: "#64748b" }}>Loading available add-ons...</div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            {!ssrLoading &&
              ssrError && (
                <div style={{ textAlign: "center", padding: "28px 0", color: "#b91c1c", fontSize: 13 }}>
                  <div style={{ marginBottom: 12 }}>{ssrError}</div>
                  <button
                    type="button"
                    onClick={() => {
                      setSsrError("");
                      setSsrRetryTick((tick) => tick + 1);
                    }}
                    style={{ ...styles.bookBtn, minWidth: 120 }}
                  >
                    Retry
                  </button>
                </div>
              )}

            {!ssrLoading &&
              !ssrError &&
              ssrDisplayLegs.every(
                ({ data }) =>
                  Object.keys(
                    data || {}
                  ).length === 0
              ) && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "32px 0",
                    color: "#94a3b8",
                    fontSize: 13,
                  }}
                >
                  {addonStepMeta[step].empty}
                </div>
              )}

            {!ssrLoading &&
              !ssrError &&
              ssrDisplayLegs.map(
                ({
                  leg,
                  itineraryLegIndex,
                  data,
                }) => (
                  <div
                    key={`ssr-leg-${itineraryLegIndex}`}
                    style={{
                      marginBottom: 22,
                    }}
                  >
                    {(isCombinedItinerary || isSpecialSupplierRoundTrip) && (
                      <div
                        style={{
                          background: "#f8fafc",
                          border:
                            "1px solid #e2e8f0",
                          borderRadius: 10,
                          padding: "9px 12px",
                          marginBottom: 10,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 800,
                            color: "#0d1b3e",
                          }}
                        >
                          {isSpecialSupplierRoundTrip
                            ? (leg.label || leg.direction || (itineraryLegIndex === 1 ? "Return" : "Outbound"))
                            : `Flight ${itineraryLegIndex + 1}`}
                          {" · "}
                          {leg.departCode} →{" "}
                          {leg.arriveCode}
                        </div>

                        <div
                          style={{
                            fontSize: 10.5,
                            color: "#64748b",
                            marginTop: 2,
                          }}
                        >
                          {leg.airline}{" "}
                          {leg.flightNumbers}
                        </div>
                      </div>
                    )}

                    {Object.entries(
                      data || {}
                    ).map(
                      ([typeName, items]) => (
                        <div
                          key={`${itineraryLegIndex}-${typeName}`}
                          style={{
                            marginBottom: 18,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#10b981",
                              textTransform:
                                "uppercase",
                              letterSpacing:
                                "0.05em",
                              marginBottom: 8,
                            }}
                          >
                            {typeName}
                          </div>

                          <div
                            style={{
                              display: "flex",
                              flexDirection:
                                "column",
                              gap: 8,
                            }}
                          >
                            {groupSsrItemsForDisplay(items, itineraryLegIndex).map((group) => (
                              <div key={`${itineraryLegIndex}-${typeName}-${group.key}`} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800 }}>
                                  {group.label}
                                </div>
                                {(group.items || []).map(
                                  (item) => {
                                    const selectionKey =
                                      getSsrSelectionKey(
                                        item,
                                        itineraryLegIndex
                                      );

                                    const selected =
                                      selectedSsr[
                                      selectionKey
                                      ];

                                    const qty =
                                      Number(
                                        selected?.qty ||
                                        0
                                      );

                                    const maxQty =
                                      pax.adults +
                                      pax.children;

                                    const isSelected =
                                      qty > 0;

                                    const isWheelchair =
                                      item.isWheelchair ||
                                      Number(
                                        item.ssrType
                                      ) === 10 ||
                                      /wheelchair|wheel chair/i.test(
                                        `${typeName} ${item.ssrTypeDesc}`
                                      );

                                    const isFree =
                                      Number(
                                        item.totalAmount ||
                                        0
                                      ) === 0;

                                    const segment =
                                      getSsrSegment(
                                        item,
                                        itineraryLegIndex
                                      );

                                    const segmentLabel =
                                      item.segmentWise
                                        ? segment
                                          ? `${segment.origin
                                          } → ${segment.destination
                                          }${segment.flightNo
                                            ? ` · ${segment.flightNo}`
                                            : ""
                                          }`
                                          : `Segment ${Number(
                                            item.legIndex ??
                                            0
                                          ) + 1
                                          }`
                                        : "Entire flight journey";

                                    return (
                                      <div
                                        key={
                                          selectionKey
                                        }
                                        style={{
                                          display:
                                            "flex",
                                          alignItems:
                                            "center",
                                          justifyContent:
                                            "space-between",
                                          padding:
                                            "12px 14px",
                                          borderRadius: 12,
                                          border:
                                            `1.5px solid ${isSelected
                                              ? "#10b981"
                                              : "#e2e8f0"
                                            }`,
                                          background:
                                            isSelected
                                              ? "#f0fdf4"
                                              : "#fff",
                                        }}
                                      >
                                        <div>
                                          <div
                                            style={{
                                              fontSize: 13,
                                              fontWeight: 600,
                                              color:
                                                "#0d1b3e",
                                            }}
                                          >
                                            {
                                              item.ssrTypeDesc
                                            }
                                            {isWheelchair
                                              ? " · Wheelchair assistance"
                                              : ""}
                                          </div>

                                          <div
                                            style={{
                                              fontSize: 11,
                                              color:
                                                "#64748b",
                                              marginTop: 2,
                                            }}
                                          >
                                            {isFree
                                              ? "Free"
                                              : `₹${Number(
                                                item.totalAmount ||
                                                0
                                              ).toLocaleString(
                                                "en-IN"
                                              )} per passenger`}
                                          </div>
                                        </div>

                                        <div
                                          style={{
                                            display:
                                              "flex",
                                            alignItems:
                                              "center",
                                            gap: 9,
                                          }}
                                        >
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setSsrQuantity(
                                                item,
                                                itineraryLegIndex,
                                                qty - 1,
                                                maxQty
                                              )
                                            }
                                            disabled={
                                              qty === 0
                                            }
                                          >
                                            −
                                          </button>

                                          <span>
                                            {qty}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setSsrQuantity(
                                                item,
                                                itineraryLegIndex,
                                                qty + 1,
                                                maxQty
                                              )
                                            }
                                            disabled={qty >= maxQty}
                                            style={{
                                              width: 27,
                                              height: 27,
                                              borderRadius: "50%",
                                              border: "none",
                                              background:
                                                qty >= maxQty
                                                  ? "#d1d5db"
                                                  : "#10b981",
                                              color:
                                                qty >= maxQty
                                                  ? "#9ca3af"
                                                  : "#fff",
                                              cursor:
                                                qty >= maxQty
                                                  ? "not-allowed"
                                                  : "pointer",
                                            }}
                                          >
                                            +
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )
              )}


            {ssrExtraCost > 0 && (
              <div style={{ marginTop: 16, background: "#f0fdf4", borderRadius: 10, padding: "10px 14px", border: "1px solid rgba(16,185,129,0.2)", display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, color: "#047857" }}>
                <span>Extras total</span>
                <span>+₹{ssrExtraCost.toLocaleString("en-IN")}</span>
              </div>
            )}
          </div>
        )}

        {step === "details" && (
          detailsRequirementsChecking ? (
            <div
              className="flight-pricing-body flight-details-step"
              style={{
                ...styles.pricingBody,
                minHeight: 360,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    width: 34,
                    height: 34,
                    border: "3px solid #e2e8f0",
                    borderTopColor: "#0d1b3e",
                    borderRadius: "50%",
                    margin: "0 auto",
                    animation: "detailsSpinner 0.8s linear infinite",
                  }}
                />

                <div
                  style={{
                    marginTop: 16,
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#0d1b3e",
                  }}
                >
                  Preparing passenger details…
                </div>

                <div
                  style={{
                    marginTop: 5,
                    fontSize: 12,
                    color: "#64748b",
                  }}
                >
                  Checking required information for this fare
                </div>

                <style>
                  {`
      @keyframes detailsSpinner {
        to {
          transform: rotate(360deg);
        }
      }
    `}
                </style>
              </div>
            </div>
          ) : repriceError ? (
            <div
              className="flight-pricing-body flight-details-step"
              style={{
                ...styles.pricingBody,
                minHeight: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  maxWidth: 440,
                  textAlign: "center",
                  padding: 20,
                  color: "#b91c1c",
                }}
              >
                <AlertCircle size={28} />

                <div
                  style={{
                    marginTop: 10,
                    fontWeight: 700,
                  }}
                >
                  Could not load passenger requirements
                </div>

                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12.5,
                  }}
                >
                  {repriceError}
                </div>
              </div>
            </div>
          ) : (
            <div
              className="flight-pricing-body flight-details-step"
              style={styles.pricingBody}
            >
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 700, color: "#0d1b3e" }}>
                  Passenger Details
                </div>
                <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 3 }}>
                  Enter details exactly as they appear on government ID.
                </div>
              </div>

              {passengerFields.map(({ idx, typeLabel }) => (
                <div
                  key={idx}
                  className="flight-booking-passenger-card"
                  style={{
                    marginBottom: 18,
                    padding: 14,
                    border: "1px solid #e2e8f0",
                    borderRadius: 12
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      marginBottom: 14,
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#047857" }}>
                      Passenger {idx + 1} · {typeLabel}
                    </div>

                    <div
                      className="flight-saved-passenger-row"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginLeft: "auto"
                      }}
                    >
                      <label style={{ ...styles.formLabel, whiteSpace: "nowrap", marginBottom: 0 }}>
                        Fill from saved
                      </label>
                      <div className="flight-saved-passenger-picker">
                        <button
                          type="button"
                          className="flight-saved-passenger-trigger"
                          disabled={savedPassengers.length === 0}
                          onClick={() =>
                            setSavedPassengerOpen((current) =>
                              current === idx ? null : idx
                            )
                          }
                        >
                          <span>
                            {savedPassengers.length === 0
                              ? "No saved passengers yet"
                              : "Select a saved passenger"}
                          </span>

                          <ChevronDown size={15} />
                        </button>

                        {savedPassengerOpen === idx && savedPassengers.length > 0 && (
                          <div className="flight-saved-passenger-menu">
                            {savedPassengers.map((p, i) => (
                              <button
                                key={i}
                                type="button"
                                className="flight-saved-passenger-option"
                                onClick={() => {
                                  setBookingForm((f) => ({
                                    ...f,
                                    [`title_${idx}`]:
                                      p.title || f[`title_${idx}`] || "MR",
                                    [`name_${idx}`]:
                                      p.firstName || "",
                                    [`lname_${idx}`]:
                                      p.lastName || "",
                                    [`gender_${idx}`]:
                                      p.gender || f[`gender_${idx}`] || "0",
                                    [`dob_${idx}`]:
                                      isDobRequired(idx)
                                        ? p.dob || ""
                                        : "",
                                    [`passport_${idx}`]:
                                      p.passportNumber || "",
                                    [`passportCountry_${idx}`]:
                                      p.passportIssuingCountry || "",
                                    [`passportExpiry_${idx}`]:
                                      p.passportExpiry || "",
                                    [`nationality_${idx}`]:
                                      p.nationality || "",
                                  }));

                                  setSavedPassengerOpen(null);
                                }}
                              >
                                <span className="flight-saved-passenger-name">
                                  {`${p.firstName || ""} ${p.lastName || ""}`.trim() ||
                                    "Unnamed passenger"}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div
                    className="flight-booking-form-grid"
                    style={styles.formGrid}
                  >
                    
                    <div
                      className="flight-booking-form-field"
                      style={styles.formField}
                    >
                      <label style={styles.formLabel}>Title</label>
                      <select
                        value={bookingForm[`title_${idx}`] || "MR"}
                        onChange={(e) => setBookingForm((f) => ({ ...f, [`title_${idx}`]: e.target.value }))}
                        style={styles.formInput}
                      >
                        <option value="MR">Mr</option>
                        <option value="MRS">Mrs</option>
                        <option value="MS">Ms</option>
                        <option value="MSTR">Master</option>
                        <option value="MISS">Miss</option>
                      </select>
                    </div>
                    <div
                      className="flight-booking-form-field"
                      style={styles.formField}
                    >
                      <label style={styles.formLabel}>Gender</label>
                      <select
                        value={bookingForm[`gender_${idx}`] || "0"}
                        onChange={(e) => setBookingForm((f) => ({ ...f, [`gender_${idx}`]: e.target.value }))}
                        style={styles.formInput}
                      >
                        <option value="0">Male</option>
                        <option value="1">Female</option>
                      </select>
                    </div>
                    <div
                      className="flight-booking-form-field"
                      style={styles.formField}
                    >
                      <label style={styles.formLabel}>First Name *</label>
                      <input
                        type="text"
                        value={bookingForm[`name_${idx}`] || ""}
                        onChange={(e) => setBookingForm((f) => ({ ...f, [`name_${idx}`]: e.target.value }))}
                        style={styles.formInput}
                      />
                    </div>
                    <div
                      className="flight-booking-form-field"
                      style={styles.formField}
                    >
                      <label style={styles.formLabel}>Last Name *</label>
                      <input
                        type="text"
                        value={bookingForm[`lname_${idx}`] || ""}
                        onChange={(e) => setBookingForm((f) => ({ ...f, [`lname_${idx}`]: e.target.value }))}
                        style={styles.formInput}
                      />
                    </div>
                    {isDobRequired(idx) && (
                      <div
                        className="flight-booking-form-field"
                        style={styles.formField}
                      >
                        <label style={styles.formLabel}>Date of Birth *</label>
                        <input
                          type="date"
                          value={bookingForm[`dob_${idx}`] || ""}
                          onChange={(e) => setBookingForm((f) => ({ ...f, [`dob_${idx}`]: e.target.value }))}
                          style={styles.formInput}
                        />
                        {(() => {
                          const paxType = idx < pax.adults ? 0 : idx < pax.adults + pax.children ? 1 : 2;
                          const err = validatePaxAge(paxType, bookingForm[`dob_${idx}`], travelDate || flight.travelDate, activeFare);
                          return err ? <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 3 }}>{err}</div> : null;
                        })()}
                      </div>
                    )}

                    {idx >= pax.adults + pax.children && (
                      <div
                        className="flight-booking-form-field"
                        style={styles.formField}
                      >
                        <label style={styles.formLabel}>
                          Travelling With *
                        </label>

                        <select
                          value={
                            bookingForm[
                            `associatedAdultPassengerId_${idx}`
                            ] || ""
                          }
                          onChange={(e) =>
                            setBookingForm((f) => ({
                              ...f,
                              [`associatedAdultPassengerId_${idx}`]:
                                e.target.value,
                            }))
                          }
                          style={styles.formInput}
                        >
                          <option value="">
                            Select accompanying adult
                          </option>

                          {Array.from(
                            { length: pax.adults },
                            (_, adultIndex) => (
                              <option
                                key={adultIndex}
                                value={adultIndex + 1}
                              >
                                {bookingForm[`name_${adultIndex}`]
                                  ? `${bookingForm[`name_${adultIndex}`]} ${bookingForm[`lname_${adultIndex}`] || ""
                                    }`.trim()
                                  : `Adult ${adultIndex + 1}`}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    )}

                    {hasPassportRequirements(idx) && (
                      <>
                        {isPassportNumberRequired(idx) && (
                          <div
                            className="flight-booking-form-field"
                            style={styles.formField}
                          >
                            <label style={styles.formLabel}>Passport Number *</label>
                            <input
                              type="text"
                              value={bookingForm[`passport_${idx}`] || ""}
                              onChange={(e) =>
                                setBookingForm((f) => ({
                                  ...f,
                                  [`passport_${idx}`]: e.target.value,
                                }))
                              }
                              style={styles.formInput}
                            />
                          </div>
                        )}

                        {isPassportExpiryRequired(idx) && (
                          <div
                            className="flight-booking-form-field"
                            style={styles.formField}
                          >
                            <label style={styles.formLabel}>Passport Expiry *</label>
                            <input
                              type="date"
                              value={bookingForm[`passportExpiry_${idx}`] || ""}
                              onChange={(e) =>
                                setBookingForm((f) => ({
                                  ...f,
                                  [`passportExpiry_${idx}`]: e.target.value,
                                }))
                              }
                              style={styles.formInput}
                            />
                          </div>
                        )}

                        {isPassportCountryRequired(idx) && (
                          <div
                            className="flight-booking-form-field"
                            style={styles.formField}
                          >
                            <label style={styles.formLabel}>
                              Passport Issuing Country *
                            </label>
                            <input
                              type="text"
                              value={bookingForm[`passportCountry_${idx}`] || ""}
                              onChange={(e) =>
                                setBookingForm((f) => ({
                                  ...f,
                                  [`passportCountry_${idx}`]: e.target.value,
                                }))
                              }
                              style={styles.formInput}
                            />
                          </div>
                        )}

                        {isNationalityRequired(idx) && (
                          <div
                            className="flight-booking-form-field"
                            style={styles.formField}
                          >
                            <label style={styles.formLabel}>Nationality *</label>
                            <input
                              type="text"
                              value={bookingForm[`nationality_${idx}`] || ""}
                              onChange={(e) =>
                                setBookingForm((f) => ({
                                  ...f,
                                  [`nationality_${idx}`]: e.target.value,
                                }))
                              }
                              style={styles.formInput}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}

              <div style={{ marginBottom: 14 }}>
                <div
                  className="flight-booking-form-grid"
                  style={styles.formGrid}
                >
  <div
    className="flight-booking-form-field"
    style={styles.formField}
  >
    <label style={styles.formLabel}>
  Contact Email {showSupplierEmail ? "*" : "(Optional)"}
</label>

    <input
      type="email"
      value={bookingForm.email || ""}
      onChange={(e) =>
        setBookingForm((form) => ({
          ...form,
          email: e.target.value,
        }))
      }
      placeholder={
  showSupplierEmail
    ? "Enter contact email"
    : "Enter email address (optional)"
}
      required
      style={styles.formInput}
    />
  </div>

                
                  <div
  className="flight-booking-form-field"
  style={styles.formField}
>
  <label style={styles.formLabel}>Contact Phone *</label>

  <PhoneInput
    defaultCountry="in"
    value={`${selectedCountryCode}${(bookingForm.phone || "").replace(/\D/g, "")}`}
    onChange={(phone, meta) => {
      const dialCode = meta?.country?.dialCode;

      const allDigits = phone.replace(/\D/g, "");
      const nationalDigits =
        dialCode && allDigits.startsWith(dialCode)
          ? allDigits.slice(dialCode.length)
          : allDigits;

      if (dialCode) {
        setSelectedCountryCode(`+${dialCode}`);
      }

      setBookingForm((f) => ({
        ...f,
        phone: nationalDigits,
      }));
    }}
    preferredCountries={["in", "us", "gb", "ae", "au", "sg"]}
    style={{ width: "100%" }}
    inputStyle={{
      width: "100%",
      minWidth: 0,
      height: "44px",
      fontSize: "14px",
      borderRadius: "6px",
    }}
    countrySelectorStyleProps={{
      buttonStyle: {
        height: "44px",
        borderRadius: "6px 0 0 6px",
      },
    }}
  />
</div>

                </div>
              </div>

              {gstAllowed && (
                <div
                  className="booking-consent-card booking-consent-card--optional"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch"
                  }}
                >
                  <label className="booking-consent-label">
                    <input
                      className="booking-checkbox"
                      type="checkbox"
                      checked={gstEnabled}
                      onChange={(e) =>
                        setBookingForm((form) => ({
                          ...form,
                          gstEnabled: e.target.checked,
                        }))
                      }
                    />

                    <span className="booking-consent-text">
                      <span className="booking-consent-title">
                        Add GST details
                        <span className="booking-consent-badge">
                          {gstRequired ? "REQUIRED" : "OPTIONAL"}
                        </span>
                      </span>

                      <span className="booking-consent-description">
                        Add GST information if you need it on your invoice.
                      </span>
                    </span>
                  </label>

                  {gstEnabled && (
                    <div
                      className="flight-booking-form-grid"
                      style={{
                        ...styles.formGrid,
                        marginTop: 12,
                        marginBottom: 0,
                        width: "100%",
                      }}
                    >
                      <div
                        className="flight-booking-form-field"
                        style={styles.formField}
                      >
                        <label style={styles.formLabel}>GSTIN *</label>
                        <input type="text" maxLength={15} placeholder="22AAAAA0000A1Z5" value={bookingForm.gstin || ""}
                          onChange={(e) => setBookingForm((form) => ({ ...form, gstin: e.target.value.toUpperCase() }))} style={styles.formInput} />
                      </div>
                      <div
                        className="flight-booking-form-field"
                        style={styles.formField}
                      >
                        <label style={styles.formLabel}>Legal Name *</label>
                        <input type="text" value={bookingForm.gstHolderName || ""}
                          onChange={(e) => setBookingForm((form) => ({ ...form, gstHolderName: e.target.value }))} style={styles.formInput} />
                      </div>
                      <div style={{ ...styles.formField, gridColumn: "1 / -1" }}>
                        <label style={styles.formLabel}>Billing Address *</label>
                        <input type="text" value={bookingForm.gstAddress || ""}
                          onChange={(e) => setBookingForm((form) => ({ ...form, gstAddress: e.target.value }))} style={styles.formInput} />
                      </div>
                      <div
                        className="flight-booking-form-field"
                        style={styles.formField}
                      >
                        <label style={styles.formLabel}>GST Phone *</label>
                        <input
                          type="tel"
                          value={bookingForm.gstPhone || ""}
                          onChange={(e) =>
                            setBookingForm((form) => ({
                              ...form,
                              gstPhone: e.target.value
                            }))
                          }
                          style={styles.formInput}
                        />
                      </div>

                      <div
                        className="flight-booking-form-field"
                        style={styles.formField}
                      >
                        <label style={styles.formLabel}>GST Email *</label>
                        <input
                          type="email"
                          value={bookingForm.gstEmail || ""}
                          onChange={(e) =>
                            setBookingForm((form) => ({
                              ...form,
                              gstEmail: e.target.value
                            }))
                          }
                          style={styles.formInput}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              {detailsError && (
                <div style={{ background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 12.5, color: "#b91c1c", display: "flex", alignItems: "center", gap: 8 }}>
                  <AlertCircle size={15} style={{ flexShrink: 0 }} />
                  {detailsError}
                </div>
              )}
              {saveError && (
                <div style={{ background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 12.5, color: "#b91c1c" }}>
                  ⚠️ {saveError}
                </div>
              )}

              <label className="booking-consent-card booking-consent-card--required">
                <input
                  className="booking-checkbox"
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />

                <span className="booking-consent-text">
                  <span className="booking-consent-title">
                    Terms &amp; Conditions
                    <span className="booking-consent-badge booking-consent-badge--required">
                      REQUIRED
                    </span>
                  </span>

                  <span className="booking-consent-description">
                    I agree to the fare rules, baggage policy and booking terms.
                  </span>
                </span>
              </label>

              {holdAllowed === true && (
                <div className="booking-consent-card booking-consent-card--hold">
                  <label className="booking-consent-label">
                    <input
                      className="booking-checkbox"
                      type="checkbox"
                      checked={holdTermsAccepted}
                      onChange={(e) => setHoldTermsAccepted(e.target.checked)}
                    />

                    <span className="booking-consent-text">
                      <span className="booking-consent-title">
                        Hold Fare confirmation
                        <span className="booking-consent-badge booking-consent-badge--hold">
                          FOR HOLD ONLY
                        </span>
                      </span>

                      <span className="booking-consent-description">
                        I understand that holding creates a temporary booking, not an issued ticket,
                        and payment must be completed before the airline hold expires.
                      </span>
                    </span>
                  </label>
                </div>
              )}
            </div>
          )
        )}

        {/* Step: Payment */}
        {step === "payment" && (
          <div className="flight-pricing-body" style={styles.pricingBody}>
            {(confirmedBookingData?.pricing?.lines || []).length > 0 && (
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 16px", marginBottom: 14, textAlign: "left" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#0d1b3e", marginBottom: 8, fontFamily: "'Syne',sans-serif" }}>
                  Price Breakdown
                </div>
                {[
                  {
                    label:
                      confirmedBookingData.pricing.supplierFareTotal > 0
                        ? "Supplier fare"
                        : "Fare",
                    value: confirmedBookingData.pricing.supplierFareTotal || 0,
                  },
                  ...(Number(confirmedBookingData.pricing.addOnTotal || 0) > 0
                    ? [{ label: "Seats & add-ons", value: confirmedBookingData.pricing.addOnTotal }]
                    : []),
                  ...(Array.isArray(confirmedBookingData.pricing.lines)
                    ? confirmedBookingData.pricing.lines.map((line) => ({
                      label: `${line.isTax ? "Tax" : "Fee"} · ${line.label || line.code}`,
                      value: Number(line.amount || 0),
                    }))
                    : []),
                ].map(({ label, value }, idx) => (
                  <div key={`${label}-${idx}`} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 12, color: "#475569" }}>
                    <span>{label}</span>
                    <span style={{ fontWeight: 600 }}>₹{Number(value || 0).toLocaleString("en-IN")}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #e2e8f0", marginTop: 8, paddingTop: 8, fontSize: 13, fontWeight: 800, color: "#0d1b3e" }}>
                  <span>Payable total</span>
                  <span>₹{Number(confirmedBookingData.pricing.payableTotal || 0).toLocaleString("en-IN")}</span>
                </div>
              </div>
            )}
            <EasyBuzzPayment
              bookingData={{
                bookingReference: bookingRef,
                flightNumbers: paymentDisplayFlightNumbers,
                departCode: paymentDisplayDepartCode,
                arriveCode: paymentDisplayArriveCode,
                airline: flight.airline,
                fareName: fare?.name || "Economy",
                departDate: travelDate || flight.travelDate,
                passengerName: bookingForm["name_0"] || "",
                passengerEmail:
  String(bookingForm.email || "").trim() ||
  COMPANY_EMAIL,
                passengerPhone: bookingForm.phone || "",
              }}
              amount={
  confirmedBookingData?.pricing?.payableTotal ??
  confirmedBookingData?.totalPrice ??
  totalPrice
}
              token={token}
              guestAccessToken={guestAccessToken}
              onSuccess={handlePaymentSuccess}
              onFailure={() => { }}
              onClose={onClose}
              onAuthExpired={() => {
                expireSession?.();
                onLoginRequest();
              }}
            />
          </div>
        )}
        {step === "held" && (
          <div style={{ ...styles.pricingBody, textAlign: "center", padding: "40px 28px" }}>
            <div style={{ fontSize: 56, marginBottom: 14 }}>🔒</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#0d1b3e", marginBottom: 8, fontFamily: "'Syne',sans-serif" }}>
              Price Held
            </div>
            <div style={{ fontSize: 13.5, color: "#64748b", marginBottom: 20 }}>
              Reference: <strong>{bookingRef}</strong> — complete payment from My Bookings before the airline&apos;s blocked expiry time.
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#047857" }}>
              ₹{Number(confirmedBookingData?.totalPrice || totalPrice).toLocaleString("en-IN")}
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 24, flexWrap: "wrap" }}>
              <a href="/profile?tab=flights&filter=held" style={{ ...styles.bookBtn, textDecoration: "none", display: "inline-flex" }}>View Held Booking</a>
              <button onClick={onClose} style={styles.backBtn}>Close</button>
            </div>
          </div>
        )}
        {/* Step: Confirm */}
        {step === "confirm" && (
          <div className="flight-pricing-body flight-pricing-body--confirm" style={{ ...styles.pricingBody, textAlign: "center", padding: "40px 28px" }}>
            <div style={{ fontSize: 68, marginBottom: 14 }}>✅</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: "#0d1b3e", marginBottom: 8, fontFamily: "'Syne',sans-serif" }}>
              {ticketStatus === "success" ? "Booking Confirmed!" : "Payment Received!"}
            </div>
            <div style={{ fontSize: 14, color: "#64748b", marginBottom: 20 }}>
              {ticketStatus === "success"
                ? <>Confirmation sent to <strong>{bookingForm.email}</strong></>
                : "Ticket pending. Booking status will update shortly."}
            </div>
            <div style={{ background: "#f0fdf4", borderRadius: 14, padding: "18px 28px", display: "inline-block", marginBottom: 20, border: "1px solid rgba(16,185,129,0.2)" }}>
              <div style={{ fontSize: 11, color: "#047857", fontWeight: 700, marginBottom: 4 }}>BOOKING REFERENCE</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#047857", letterSpacing: 3, fontFamily: "monospace" }}>{bookingRef}</div>
            </div>
            <FlightETicket
              flight={flight}
              fare={confirmedBookingData?.fare || fare}
              pax={pax}
              paxLabel={paxLabel}
              activeFare={activeFare}
              bookingRef={bookingRef}
              totalPrice={confirmedBookingData?.totalPrice || totalPrice}
              selectedSeatSummary={confirmedBookingData?.selectedSeatSummary || selectedSeatSummary}
              selectedSeatGroups={confirmedBookingData?.selectedSeatGroups || selectedSeatGroupsForDisplay}
              travelDate={travelDate}
              bookingForm={bookingForm}
              ticketStatus={ticketStatus}
              totalPassengers={totalPassengers}
              extras={confirmedBookingData?.extras || selectedExtrasList}
              cancellationInfo={cancellationInfo}
              legs={
                displayItineraryLegs.length > 1
                  ? displayItineraryLegs
                  : null
              }
            />
            <div style={{ fontSize: 12.5, color: "#64748b" }}>
              🗂 View booking in{" "}
              <a href="/profile?tab=flights" style={{ color: "#10b981", fontWeight: 600, textDecoration: "none" }}>My Profile → Flight Bookings</a>
              {ticketStatus === null && (
                <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "10px 14px", fontSize: 12.5, color: "#1e40af", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 14, height: 14, border: "2px solid #bfdbfe", borderTopColor: "#2563eb", borderRadius: "50%", animation: "spin 0.9s linear infinite" }} />
                  Confirming ticket with airline…
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              )}
              {ticketStatus === "inprocess" && (
                <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 10, padding: "10px 14px", fontSize: 12.5, color: "#92400e", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 14, height: 14, border: "2px solid #fde68a", borderTopColor: "#d97706", borderRadius: "50%", animation: "spin 0.9s linear infinite" }} />
                  Ticket being issued — check My Bookings shortly.
                </div>
              )}
              {ticketStatus === "failed" && (
                <div style={{ background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 10, padding: "10px 14px", fontSize: 12.5, color: "#b91c1c", marginBottom: 14 }}>
                  Ticket pending — check My Bookings for updates.
                </div>
              )}
            </div>

          </div>
        )}

        {/* Footer */}
        {step !== "confirm" && step !== "payment" && step !== "held" && (
          <div className="flight-pricing-footer" style={styles.pricingFooter}>
            {step !== "fare" && (
              <button onClick={() => setStep(stepOrder[currentStepIdx - 1])} style={styles.backBtn}>← Back</button>
            )}
            {step === "fare" && (
              <button onClick={() => setStep("passengers")} style={{ ...styles.bookBtn, minWidth: 180 }}>Select Passengers →</button>
            )}
            {step === "passengers" && (
              <button onClick={() => setStep("seats")} style={{ ...styles.bookBtn, minWidth: 180 }}>Choose Seats →</button>
            )}
            {step === "seats" && (
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, justifyContent: "flex-end" }}>
                {selectedSeatCount === 0 && (
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>Seat selection is optional</span>
                )}
                <button onClick={() => setStep("meals")} style={{ ...styles.bookBtn, minWidth: 200 }}>
                  {selectedSeatCount > 0 ? `Continue with ${selectedSeatCount} seat${selectedSeatCount > 1 ? "s" : ""} →` : "Skip & Add Meals →"}
                </button>
              </div>
            )}
            {["meals", "baggage", "extras"].includes(step) && (
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, justifyContent: "flex-end" }}>
                {Object.keys(selectedSsr).length === 0 && (
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>{addonStepMeta[step].optional}</span>
                )}
                <button onClick={() => setStep(addonStepMeta[step].next)} style={{ ...styles.bookBtn, minWidth: 200 }}>
                  {Object.keys(selectedSsr).length > 0 ? addonStepMeta[step].selectedText : addonStepMeta[step].skipText}
                </button>
              </div>
            )}
            {step === "details" && !detailsRequirementsChecking && !repriceError && (
              isStaleKey ? (
                <button
                  type="button"
                  onClick={onClose}
                  style={{ ...styles.bookBtn, minWidth: 220, background: "linear-gradient(135deg,#f59e0b,#d97706)" }}
                >
                  🔄 Close &amp; Search Again
                </button>
              ) : (
                <div style={{ display: "flex", gap: 10, flex: 1, justifyContent: "flex-end" }}>
                  {holdAllowed === true && (
                    <button
                      type="button"
                      onClick={() => {
                        const err = validateDetailsStep({ requireHoldTerms: true });
                        if (err) { setDetailsError(err); return; }
                        setDetailsError("");
                        
                        handleBookingAction("hold");
                      }}
                      disabled={saving}
                      style={{ ...styles.backBtn, opacity: saving ? 0.7 : 1, minWidth: 160, fontWeight: 700 }}
                    >
                      {saving && savingMode === "hold" ? "⏳ Holding…" : "🔒 Hold Fare"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      
                      const err = validateDetailsStep();
                      

                      if (err) {
                        setDetailsError(err);
                        return;
                      }

                      setDetailsError("");
                      handleBookingAction("pay");
                    }}
                   disabled={saving}
style={{
  ...styles.bookBtn,
  opacity: saving ? 0.5 : 1,
minWidth: 160,
cursor: saving ? "not-allowed" : "pointer",
}}
                  >
                    {saving && savingMode === "pay" ? "⏳ Processing…" : "Pay Now →"}
                  </button>
                </div>
              )
            )}
            {step === "held" && (
              <div className="flight-pricing-footer" style={styles.pricingFooter}>
                <a href="/profile?tab=flights&filter=held" style={{ ...styles.bookBtn, textDecoration: "none", display: "inline-flex" }}>📋 View Bookings</a>
                <button onClick={onClose} style={styles.backBtn}>Close</button>
              </div>
            )}
          </div>
        )}
        {step === "confirm" && (
          <div className="flight-pricing-footer" style={styles.pricingFooter}>
            <a href="/profile?tab=flights" style={{ ...styles.bookBtn, textDecoration: "none", display: "inline-flex" }}>📋 View Bookings</a>
            <button onClick={onClose} style={styles.backBtn}>Close</button>
          </div>
        )}

      </div>
    </div>
  );
}

function GroupBookingModal({ flight, travelDate, passengerCount, onClose }) {
  const { user, token } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    passengers: passengerCount || 10,
    remark: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.phone || !form.passengers) {
      setError("Please fill all required fields.");
      return;
    }
    if (Number(form.passengers) < 10) {
      setError("Group bookings are for 10 or more passengers.");
      return;
    }
    setSubmitting(true);
    setError("");

    // Open the tab SYNCHRONOUSLY, right inside the click handler, before any
    // `await`. This preserves the browser's "user gesture" trust so the tab
    // isn't blocked as a popup. We navigate it to the real WhatsApp URL once
    // the API responds — a blank tab opening first is expected/normal here.
    const whatsappTab = window.open("", "_blank", "noopener,noreferrer");

    try {
      const authToken = token || (() => { try { return localStorage.getItem("mbt_token"); } catch { return null; } })();
      const res = await fetch(`${API_BASE}/flight-bookings/group-request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          passengers: Number(form.passengers),
          remark: form.remark,
          airline: flight.airline,
          flightNumbers: flight.flightNumbers,
          departAirport: flight.departAirport,
          departCode: flight.departCode,
          arriveAirport: flight.arriveAirport,
          arriveCode: flight.arriveCode,
          departTime: flight.departTime,
          arriveTime: flight.arriveTime,
          travelDate: travelDate || flight.travelDate,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Could not submit group booking request.");
      }
      if (data.whatsappUrl && whatsappTab) {
        whatsappTab.location.href = data.whatsappUrl;
      } else if (data.whatsappUrl) {
        // The pre-opened tab reference is gone (e.g. blocked anyway, or the
        // user closed it) — fall back to a normal open attempt.
        window.open(data.whatsappUrl, "_blank", "noopener,noreferrer");
      } else if (whatsappTab) {
        // No WhatsApp URL configured server-side — don't leave a dangling
        // blank tab open.
        whatsappTab.close();
      }
      setSubmitted(true);
    } catch (err) {
      if (whatsappTab) whatsappTab.close();
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div
        className="flight-pricing-modal flight-pricing-modal--compact"
        style={{ ...styles.pricingModal, maxWidth: 520 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.pricingHeader}>
          <div>
            <div style={styles.pricingTitle}>{submitted ? "Request Sent! 🎉" : "Group Booking Request"}</div>
            <div style={styles.pricingSubtitle}>{flight.airline} · {flight.departCode} → {flight.arriveCode}</div>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        {!submitted ? (
          <>
            <div className="flight-pricing-body" style={styles.pricingBody}>
              <div style={{ background: "#f0fdf4", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 10, padding: "12px 14px", marginBottom: 18, fontSize: 12.5, color: "#047857", lineHeight: 1.6 }}>
                For 10 or more passengers, our team will reach out with a customized group fare. Fill in your details below.
              </div>

              {error && (
                <div style={{ background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#b91c1c" }}>
                  ⚠️ {error}
                </div>
              )}

              <div
                className="flight-booking-form-grid"
                style={styles.formGrid}
              >
                <div
                  className="flight-booking-form-field"
                  style={styles.formField}
                >
                  <label style={styles.formLabel}>Full Name *</label>
                  <input type="text" placeholder="Your name" value={form.name} onChange={(e) => update("name", e.target.value)} style={styles.formInput} />
                </div>
                <div
                  className="flight-booking-form-field"
                  style={styles.formField}
                >
                  <label style={styles.formLabel}>Email *</label>
                  <input type="email" placeholder="you@email.com" value={form.email} onChange={(e) => update("email", e.target.value)} style={styles.formInput} />
                </div>
                <div
                  className="flight-booking-form-field"
                  style={styles.formField}
                >
                  <label style={styles.formLabel}>Phone *</label>
                  <input type="tel" placeholder="+91 XXXXX XXXXX" value={form.phone} onChange={(e) => update("phone", e.target.value)} style={styles.formInput} />
                </div>
                <div
                  className="flight-booking-form-field"
                  style={styles.formField}
                >
                  <label style={styles.formLabel}>Passengers *</label>
                  <input type="number" min={10} value={form.passengers} onChange={(e) => update("passengers", e.target.value)} style={styles.formInput} />
                </div>
              </div>

              <div style={{ ...styles.formField, marginTop: 4 }}>
                <label style={styles.formLabel}>Remark (optional)</label>
                <textarea
                  placeholder="Any special requirements..."
                  value={form.remark}
                  onChange={(e) => update("remark", e.target.value)}
                  rows={4}
                  style={{ ...styles.formInput, resize: "vertical", fontFamily: "inherit" }}
                />
              </div>
            </div>

            <div className="flight-pricing-footer" style={styles.pricingFooter}>
              <button onClick={onClose} style={styles.backBtn}>Cancel</button>
              <button onClick={handleSubmit} disabled={submitting} style={{ ...styles.bookBtn, opacity: submitting ? 0.7 : 1 }}>
                {submitting ? "⏳ Submitting…" : "Submit Group Booking"}
              </button>
            </div>
          </>
        ) : (
          <div style={{ ...styles.pricingBody, textAlign: "center", padding: "40px 28px" }}>
            <div style={{ fontSize: 56, marginBottom: 14 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0d1b3e", marginBottom: 8, fontFamily: "'Syne',sans-serif" }}>
              Thanks, {form.name}!
            </div>
            <div style={{ fontSize: 13.5, color: "#64748b", marginBottom: 20 }}>
              Our team will contact you at <strong>{form.email}</strong> or <strong>{form.phone}</strong> shortly with a group fare quote for {form.passengers} passengers.
            </div>
            <button onClick={onClose} style={styles.backBtn}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}



// ── Flight Details Modal ──────────────────────────────────────────────────────
function DetailTabBtn({ label, icon, active, onClick }) {
  return (
    <button onClick={onClick} style={{ padding: "12px 14px", fontSize: 12.5, fontWeight: active ? 700 : 400, color: active ? "#0d1b3e" : "#64748b", background: "none", border: "none", borderBottom: `2px solid ${active ? "#10b981" : "transparent"}`, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, transition: "all .15s", whiteSpace: "nowrap", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      <span style={{ fontSize: 14 }}>{renderIconNode(icon)}</span>{label}
    </button>
  );
}
function InfoCard({ icon, label, value, sub }) {
  return (
    <div style={{ background: "#f8fafc", borderRadius: 10, padding: "11px 13px", border: "1px solid #e2e8f0" }}>
      <div style={{ fontSize: 10.5, color: "#94a3b8", marginBottom: 3, display: "flex", alignItems: "center", gap: 5 }}><span>{renderIconNode(icon)}</span>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#0d1b3e" }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
function BaggageRow({ icon, name, desc, value, valueColor }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0, color: "#16a34a" }}>{renderIconNode(icon)}</div>
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "#0d1b3e" }}>{name}</div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>{desc}</div>
        </div>
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: valueColor || "#0d1b3e" }}>{value}</div>
    </div>
  );
}
function TimelineStop({ time, nextDay, title, sub, connector, isLayover, layoverLabel }) {
  return (
    <div style={{ display: "flex", gap: 12, position: "relative" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 14, flexShrink: 0 }}>
        <div style={{ width: isLayover ? 10 : 13, height: isLayover ? 10 : 13, borderRadius: "50%", background: isLayover ? "#f59e0b" : "#10b981", border: `2px solid ${isLayover ? "#fde68a" : "#bbf7d0"}`, flexShrink: 0, marginTop: 3, zIndex: 1 }} />
        {connector && <div style={{ flex: 1, width: 2, background: "#e2e8f0", margin: "3px 0", minHeight: 36 }} />}
      </div>
      <div style={{ paddingBottom: connector ? 18 : 0, flex: 1 }}>
        <div style={{ fontSize: 10.5, color: "#94a3b8", marginBottom: 2 }}>{time}{nextDay && <sup style={{ color: "#f87171", fontWeight: 400 }}> +1</sup>}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#0d1b3e" }}>{title}</div>
        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2, lineHeight: 1.6 }}>{sub}</div>
        {isLayover && layoverLabel && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#fef3c7", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#92400e", fontWeight: 600, marginTop: 5 }}>⏱ Layover · {layoverLabel}</div>
        )}
      </div>
    </div>
  );
}

function FlightDetailsModal({
  flight, onClose, onLoginRequest, onBookNow, activeFare, isGroupBooking, cabinClass,
  selectionMode = false, isSelectedLeg = false, selectedFareOption = null, onSelectFare,
}) {
  const [activeTab, setActiveTab] = useState(0);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);
  const [fareRules, setFareRules] = useState([]);
  const [fareRulesLoading, setFareRulesLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== 2 || fareRules.length) return;
    setFareRulesLoading(true);
    fetch(`${API_BASE}/flight-search/fare-rule`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ResultIndex: flight.resultIndex || flight.id,
        FareId: flight.fareOptions?.[0]?.fareId ?? "",
      }),
    })
      .then(r => r.json())
      .then(data => setFareRules(data?.fareRules || []))
      .catch(() => { })
      .finally(() => setFareRulesLoading(false));
  }, [activeTab]);

  const aircraft = flight.aircraftType || "Not available";
  const depTerminal = flight.departTerminal || "Not specified";
  const arrTerminal = flight.arriveTerminal || "Not specified";
  const depAirportFull = flight.departAirportFull || flight.departAirport;
  const arrAirportFull = flight.arriveAirportFull || flight.arriveAirport;
  const amenities = flight.amenities || [];
  const ontime = flight.onTimePerformance; // may be null now
  const layoverCity = extractLayover(flight.stopDetail);
  const legs = flight.stops > 0 ? estimateLegs(flight.duration) : null;
  const price = flight.displayPrice || flight.price;
  const available = flight.available !== false && !flight.priceUnavailable;
  const refundText = Number(flight.refundableAmount) > 0 ? fmt(flight.refundableAmount) : null;
  const paxLabel = PAX_TYPE_LABELS[activeFare] || "adult";

  let layoverArr = null, layoverDep = null;
  if (flight.stops > 0 && legs) {
    const [lh, lm] = legs.leg1.split("h");
    const leg1Mins = (parseInt(lh) || 0) * 60 + (parseInt(lm) || 0);
    layoverArr = addMinutesToTime(flight.departTime, leg1Mins);
    layoverDep = addMinutesToTime(layoverArr.time, legs.layoverMins);
  }

  const tabs = [
    { label: "Overview", icon: <Info size={14} /> },
    { label: "Baggage", icon: <Briefcase size={14} /> },
    { label: "Cancellation", icon: <ShieldCheck size={14} /> },
    { label: "Journey", icon: <MapPin size={14} /> },
  ];
  const sectionTitle = { fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: "#94a3b8", marginBottom: 10 };

  return (
    <div onClick={onClose} style={{ ...styles.modalOverlay, zIndex: 10000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 800, maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 32px 80px rgba(0,0,0,0.4)" }}>
        <div style={{ background: "linear-gradient(145deg, #10b981, #047857)", padding: "18px 26px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", fontFamily: "'Syne',sans-serif" }}>{flight.airline}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>{flight.flightNumbers} · Economy Class</div>
          </div>
          <button onClick={onClose} style={{ ...styles.closeBtn }}>✕</button>
        </div>

        <div style={{ background: "#1e293b", padding: "14px 26px", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <div style={{ textAlign: "center", minWidth: 72 }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#fff", lineHeight: 1, fontFamily: "'Syne',sans-serif" }}>{flight.departTime}</div>
            <div style={{ fontSize: 11, color: "#10b981", marginTop: 2, fontWeight: 700 }}>{flight.departCode}</div>
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 1 }}>{flight.departAirport}</div>
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>{flight.duration}</div>
            <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 3 }}>
              <div style={{ flex: 1, height: 1.5, background: "#334155" }} />
              {flight.stops > 0 && <><div style={{ width: 7, height: 7, borderRadius: "50%", background: "#f59e0b", flexShrink: 0 }} /><div style={{ flex: 1, height: 1.5, background: "#334155" }} /></>}
              <Plane size={16} style={{ color: "#10b981" }} />
              <div style={{ flex: 1, height: 1.5, background: "#334155" }} />
            </div>
            <div style={{ fontSize: 10.5, color: flight.stops > 0 ? "#f59e0b" : "#10b981", fontWeight: 600 }}>{flight.stopDetail}</div>
          </div>
          <div style={{ textAlign: "center", minWidth: 72 }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#fff", lineHeight: 1, fontFamily: "'Syne',sans-serif" }}>
              {flight.arriveTime}{flight.arriveNextDay && <sup style={{ fontSize: 10, color: "#f87171", fontWeight: 400 }}>+1</sup>}
            </div>
            <div style={{ fontSize: 11, color: "#10b981", marginTop: 2, fontWeight: 700 }}>{flight.arriveCode}</div>
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 1 }}>{flight.arriveAirport}</div>
          </div>
        </div>

        <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", padding: "0 26px", overflowX: "auto", flexShrink: 0 }}>
          {tabs.map((t, i) => <DetailTabBtn key={i} label={t.label} icon={t.icon} active={activeTab === i} onClick={() => setActiveTab(i)} />)}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px 26px" }}>
          {activeTab === 0 && (
            <div>
              <div style={sectionTitle}>Flight information</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 9, marginBottom: 20 }}>
                <InfoCard icon={<PlaneTakeoffIcon size={16} />} label="Departure" value={flight.departTime} sub={`${depAirportFull} · ${depTerminal}`} />
                <InfoCard icon={<PlaneLanding size={16} />} label="Arrival" value={`${flight.arriveTime}${flight.arriveNextDay ? " (+1)" : ""}`} sub={`${arrAirportFull} · ${arrTerminal}`} />
                <InfoCard icon={<Clock3 size={16} />} label="Duration" value={flight.duration} sub={flight.stopDetail} />
                <InfoCard icon={<Plane size={16} />} label="Aircraft" value={aircraft} sub="Narrow-body jet" />
                <InfoCard icon={<Armchair size={16} />} label="Class" value={flight.cabinClass ?? cabinClass ?? "As selected"} sub="Supplier-selected cabin" />
                <InfoCard icon={<BarChart3 size={16} />} label="On-Time Rate" value={`${ontime}%`} sub="Last 30 days avg" />
              </div>
              <div style={sectionTitle}>Amenities</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 20 }}>
                {amenities.length ? amenities.map((a, i) => (
                  <span key={i} style={{ background: "#f0fdf4", borderRadius: 7, padding: "5px 11px", fontSize: 12, color: "#047857", border: "1px solid rgba(16,185,129,0.18)" }}>{a}</span>
                )) : <span style={{ fontSize: 12.5, color: "#94a3b8" }}>Amenity details not available.</span>}
              </div>
              {flight.discount && (
                <>
                  <div style={sectionTitle}>Active offers</div>
                  <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 10, padding: "10px 14px", fontSize: 12.5, color: "#92400e" }}>🏷️ {flight.discount}</div>
                </>
              )}
            </div>
          )}
          {activeTab === 1 && (
            <div>
              <div style={sectionTitle}>Baggage allowance</div>
              <BaggageRow icon={<Luggage size={16} />} name="Check-in baggage" desc="From airline fare data" value={baggageDisplay(flight.baggageCheckin)} />
              <BaggageRow icon={<Backpack size={16} />} name="Cabin baggage" desc="From airline fare data" value={baggageDisplay(flight.baggageCabin)} />
              <div style={{ marginTop: 14, background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: "11px 14px", fontSize: 12, color: "#075985", lineHeight: 1.7 }}>
                <Info size={14} style={{ marginTop: 2, flexShrink: 0, display: "inline", marginRight: 7 }} />
                <strong>Note:</strong> Extra baggage, sports equipment, and airport excess charges depend on airline fare rules and SSR availability.
              </div>
            </div>
          )}
          {activeTab === 2 && (() => {
            // Use the first fare option's charge arrays when available
            const firstFare = flight.fareOptions?.[0] || {};
            const cancelRows = buildChargeRows(firstFare.cancellationCharges || flight.cancellationCharges || []);
            const rescheduleRows = buildChargeRows(firstFare.rescheduleCharges || flight.rescheduleCharges || []);

            return (
              <div>
                <div style={sectionTitle}>Cancellation charges (per passenger)</div>
                <div style={{ background: "#fef9ec", border: "1px solid #fde68a", borderRadius: 12, padding: "12px 14px", marginBottom: 12 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: "#92400e", marginBottom: 8 }}>❌ Cancellation fees</div>
                  {cancelRows.length === 0 && !fareRulesLoading && (
                    <div style={{ fontSize: 12, color: "#94a3b8", padding: "6px 0" }}>Cancellation charges not provided by the airline for this fare.</div>
                  )}
                  {cancelRows.map((p, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "5px 0", color: "#78350f", borderBottom: "1px solid rgba(234,179,8,0.15)" }}>
                      <span>{p.label}</span>
                      <span style={{ fontWeight: 700, color: p.fee === "No refund" || p.fee === "Free" ? (p.fee === "Free" ? "#16a34a" : "#dc2626") : "#92400e" }}>{p.fee}</span>
                    </div>
                  ))}
                  {(firstFare.cancellationCharges?.length > 0 || flight.cancellationCharges?.length > 0) && (
                    <div style={{ fontSize: 10.5, color: "#b45309", marginTop: 8, fontStyle: "italic" }}>
                      Source: Airline cancellation policy · charges are per passenger
                    </div>
                  )}
                  {fareRulesLoading && <div style={{ fontSize: 12, color: "#94a3b8", padding: "8px 0" }}>Loading fare rules…</div>}
                  {fareRules.map((rule, i) => (
                    <div key={i} style={{ marginTop: 10, background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #e2e8f0" }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: "#0d1b3e", marginBottom: 4 }}>
                        {rule.ruleName} <span style={{ fontSize: 10, color: "#94a3b8" }}>· Seg {rule.segmentId}</span>
                      </div>
                      <div style={{ fontSize: 11.5, color: "#475569", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{rule.ruleDescText}</div>
                    </div>
                  ))}
                </div>
                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "12px 14px", marginBottom: 12 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: "#065f46", marginBottom: 8 }}>✏️ Date change / reschedule fees</div>
                  {rescheduleRows.length === 0 && (
                    <div style={{ fontSize: 12, color: "#94a3b8", padding: "6px 0" }}>Reschedule charges not provided by the airline for this fare.</div>
                  )}
                  {rescheduleRows.map((p, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "5px 0", color: "#065f46", borderBottom: "1px solid rgba(187,247,208,0.4)" }}>
                      <span>{p.label}</span>
                      <span style={{ fontWeight: 700 }}>{p.fee}</span>
                    </div>
                  ))}
                </div>
                {flight.refundable
                  ? <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "11px 14px", fontSize: 12, color: "#1e40af", lineHeight: 1.7 }}>
                    <strong>Refundable fare.</strong>
                    {flight.refundableAmount > 0 ? ` Refundable amount: ₹${Number(flight.refundableAmount).toLocaleString("en-IN")}.` : ""}
                  </div>
                  : <div style={{ background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 10, padding: "11px 14px", fontSize: 12, color: "#be123c", lineHeight: 1.7 }}>
                    <strong>Non-refundable fare.</strong> Only airport taxes (~₹1,200–1,800) are refundable.
                  </div>
                }
              </div>
            );
          })()}
          {activeTab === 3 && (
            <div>
              <div style={sectionTitle}>Flight itinerary</div>
              <div style={{ padding: "4px 0" }}>
                <TimelineStop time={flight.departTime} title={`Departs ${flight.departAirport} (${flight.departCode})`} sub={`${depAirportFull} · ${depTerminal} · ${flight.flightNumbers.split(",")[0].trim()} · ${aircraft}`} connector={flight.stops > 0} />
                {flight.stops > 0 && layoverCity && layoverArr && layoverDep && (
                  <>
                    <TimelineStop time={layoverArr.time} nextDay={layoverArr.nextDay} title={`Layover at ${layoverCity}`} sub="Connecting · Gate change may apply" connector isLayover layoverLabel={`${legs.layoverMins}m`} />
                    <TimelineStop time={layoverDep.time} nextDay={layoverDep.nextDay} title={`Departs ${layoverCity}`} sub={`Onward to ${flight.arriveAirport} (${flight.arriveCode})`} connector />
                  </>
                )}
                <TimelineStop time={flight.arriveTime} nextDay={flight.arriveNextDay} title={`Arrives ${flight.arriveAirport} (${flight.arriveCode})${flight.arriveNextDay ? " +1 day" : ""}`} sub={`${arrAirportFull} · ${arrTerminal} · Total: ${flight.duration}`} connector={false} />
              </div>
              <div style={{ marginTop: 20 }}>
                {isGroupBooking ? (
                  <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "12px 14px", fontSize: 12.5, color: "#047857", lineHeight: 1.6 }}>
                    Group fares are quoted separately for 10 or more passengers. Select this flight to request your quote.
                  </div>
                ) : (
                  <>
                    <div style={sectionTitle}>Fare options</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 9 }}>
                      {flight.fareOptions?.map((f, i) => (
                        <div key={i} style={{ borderRadius: 11, padding: "12px 14px", border: f.recommended ? "2px solid #10b981" : "1px solid #e2e8f0", background: f.recommended ? "#f0fdf4" : "#f8fafc", position: "relative" }}>
                          {f.recommended && <div style={{ position: "absolute", top: -9, left: "50%", transform: "translateX(-50%)", background: "#10b981", color: "#fff", fontSize: 8.5, fontWeight: 700, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap" }}>★ RECOMMENDED</div>}
                          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0d1b3e", marginBottom: 3 }}>{f.name}</div>
                          <div style={{ fontSize: 17, fontWeight: 800, color: "#0d1b3e", fontFamily: "'Syne',sans-serif" }}>{fmt(f.price)}</div>
                          <div style={{ fontSize: 10.5, color: "#047857", fontWeight: 600 }}>/{paxLabel}</div>
                          <div style={{ marginTop: 7, fontSize: 11, color: "#475569", lineHeight: 1.7 }}>🧳 {f.baggage}<br />🍽️ {f.meal}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: "12px 26px", borderTop: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafc", flexShrink: 0 }}>
          {!isGroupBooking && (
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#0d1b3e", fontFamily: "'Syne',sans-serif", lineHeight: 1 }}>
                {fmt(price)} <span style={{ fontSize: 12, fontWeight: 600, color: "#047857" }}>/{paxLabel}</span>
              </div>
              <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 2 }}>Incl. all taxes &amp; fees</div>
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} style={styles.backBtn}>Close</button>
            {available && (
              selectionMode ? (
                <button
                  onClick={isSelectedLeg && selectedFareOption ? onBookNow : onSelectFare}
                  style={{
                    ...styles.bookBtn,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {isSelectedLeg && selectedFareOption ? (
                    <>
                      <Check size={13} />
                      Select Passengers →
                    </>
                  ) : (
                    <>
                      <Lock size={13} />
                      Choose Fare
                    </>
                  )}
                </button>
              ) : (
                <button onClick={onBookNow} style={{ ...styles.bookBtn, display: "flex", alignItems: "center", gap: 6 }}>
                  <Lock size={13} /> {isGroupBooking ? "Select" : "Book Now"}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AirlineLogo({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return <div className="flight-card-logo"><Plane size={19} /></div>;
  }
  return (
    <div className="flight-card-logo">
      <img
        src={src}
        alt={alt}
        onError={() => setFailed(true)}
        style={{ width: 26, height: 26, objectFit: "contain" }}
      />
    </div>
  );
}
function StopInfoPopup({ flight }) {
  const [show, setShow] = useState(false);
  const hasLayovers = flight.layovers?.length > 0;

  return (
    <div
      style={{
        position: "relative",
        fontSize: 10,
        fontWeight: 700,
        color: flight.stops > 0 ? "#f59e0b" : "#16a34a",
        cursor: hasLayovers ? "pointer" : "default",
      }}
      onMouseEnter={() => hasLayovers && setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {flight.stopDetail}

      {show && hasLayovers && (
        <div
          style={{
            position: "absolute",
            bottom: "130%",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            padding: "10px 14px",
            minWidth: 220,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            zIndex: 30,
            textAlign: "left",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: "#0d1b3e", marginBottom: 6 }}>
            Plane change
          </div>
          {flight.layovers.map((l, i) => (
            <div key={i} style={{ fontSize: 11.5, color: "#475569", marginBottom: 2 }}>
              {l.airportName} ({l.airportCode}) · {l.durationText} layover
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function FareOptionCard({ fare, selected, onClick, pricePerLabel }) {
  return (
    <div
      className="flight-fare-option-card"
      onClick={onClick}
      style={{
        ...styles.fareCard,
        border: selected ? "2px solid #10b981" : "2px solid #e2e8f0",
        background: selected ? "#f0fdf4" : "#fff",
        transform: selected ? "scale(1.02)" : "scale(1)",
      }}
    >
      {fare.recommended && <div style={styles.recommendedBadge}>★ RECOMMENDED</div>}
      {fare.isUpfront && (
        <div style={{ ...styles.recommendedBadge, top: fare.recommended ? -22 : -9, background: "#7c3aed" }}>
          ⚡ INDIGO UPFRONT
        </div>
      )}
      <div style={styles.fareName}>{fare.name}</div>
      {fare.holdAllowed === true && (
        <div style={{ display: "inline-block", fontSize: 9.5, fontWeight: 700, color: "#0369a1", background: "#e0f2fe", borderRadius: 6, padding: "2px 7px", marginBottom: 5, width: "fit-content" }}>
          🔒 Hold Payment Available
        </div>
      )}
      <div style={styles.farePrice}>
  ₹{Math.round(
    Number(fare.displayPrice ?? fare.price) || 0
  ).toLocaleString("en-IN")}
</div>
      <div style={{ ...styles.farePricePer, color: "#047857", fontWeight: 600 }}>{pricePerLabel}</div>
      <div style={styles.fareDetails}>
        {fare.baggage && (
          <div style={styles.fareRow}><Luggage size={12} style={{ flexShrink: 0 }} /> {fare.baggage}</div>
        )}
        {fare.benefits?.seatIncluded !== null && (
          <div style={styles.fareRow}>
            <Armchair size={12} style={{ flexShrink: 0, color: fare.benefits?.seatIncluded ? "#16a34a" : "#94a3b8" }} />
            <span style={{ color: fare.benefits?.seatIncluded ? "#16a34a" : "#64748b" }}>
              {fare.benefits?.seatIncluded ? "Seat selection included" : "Paid seat selection"}
            </span>
          </div>
        )}
        {fare.meal && (
          <div style={styles.fareRow}>
            <UtensilsCrossed size={12} style={{ flexShrink: 0, color: fare.benefits?.mealIncluded ? "#16a34a" : "#94a3b8" }} />
            <span style={{ color: fare.benefits?.mealIncluded ? "#16a34a" : "#64748b" }}>{fare.meal}</span>
          </div>
        )}
        <div style={styles.fareRow}>
          <ShieldCheck size={12} style={{ flexShrink: 0, color: fare.benefits?.freeCancellation ? "#16a34a" : fare.benefits?.refundable ? "#d97706" : "#ef4444" }} />
          <span style={{ color: fare.benefits?.freeCancellation ? "#16a34a" : fare.benefits?.refundable ? "#d97706" : "#ef4444" }}>
            {fare.benefits?.freeCancellation
              ? "Free cancellation"
              : fare.benefits?.refundable
                ? `Cancellation fee: ${fare.benefits.cancellationFee}`
                : `Non-refundable${(fare.benefits?.cancellationFee || fare.cancellation) ? ` · ${fare.benefits?.cancellationFee || fare.cancellation}` : ""}`}
          </span>
        </div>
        <div style={styles.fareRow}>
          <CalendarDays size={12} style={{ flexShrink: 0, color: "#64748b" }} />
          <span style={{ color: "#64748b" }}>Change: {fare.benefits?.rescheduleFee || fare.changes || "As per fare rules"}</span>
        </div>
        {fare.benefits?.priorityBoarding && (
          <div style={styles.fareRow}><Zap size={12} style={{ flexShrink: 0, color: "#7c3aed" }} /><span style={{ color: "#7c3aed" }}>Priority boarding</span></div>
        )}
        {fare.benefits?.extraBaggage && (
          <div style={styles.fareRow}><Luggage size={12} style={{ flexShrink: 0, color: "#0369a1" }} /><span style={{ color: "#0369a1" }}>Extra baggage included</span></div>
        )}
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Flexibility</div>
          <div style={{ display: "flex", gap: 3 }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} style={{ flex: 1, height: 4, borderRadius: 2, background: n <= (fare.benefits?.flexibilityScore || 1) ? (n <= 2 ? "#ef4444" : n <= 3 ? "#f59e0b" : "#16a34a") : "#e2e8f0" }} />
            ))}
          </div>
          <div style={{ fontSize: 9.5, color: "#64748b", marginTop: 2 }}>
            {fare.benefits?.flexibilityScore <= 1 ? "Very rigid" :
              fare.benefits?.flexibilityScore <= 2 ? "Limited changes" :
                fare.benefits?.flexibilityScore <= 3 ? "Moderate flexibility" :
                  fare.benefits?.flexibilityScore <= 4 ? "Flexible" : "Fully flexible"}
          </div>
        </div>
      </div>
      {selected && (
        <div style={{ marginTop: 8, fontSize: 11, color: "#047857", fontWeight: 700, background: "rgba(16,185,129,0.1)", borderRadius: 6, padding: "3px 8px", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
          <Check size={12} strokeWidth={3} /> Selected
        </div>
      )}
    </div>
  );
}
// ── FlightCard ────────────────────────────────────────────────────────────────
function FlightCard({
  flight, travelDate, passengerCount = 1, adults, children, infants,
  isCompared, canCompare, onCompareToggle, onLoginRequest, activeFare,
  aosDelay = 0, isInternational = false, classType, selectionMode = false,
  isSelectedLeg = false, onSelectLeg,
  selectedFareOption = null,   // ← NEW
  itineraryLegs = EMPTY_ITINERARY_LEGS,
}) {
  const [activeModal, setActiveModal] = useState(null);
  const [showFareOptions, setShowFareOptions] = useState(false);

  if (!flight) return null;

  const hasBadge = !!flight.badge;

  
const defaultFare =
  flight.fareOptions?.find((fare) => fare.recommended) ||
  flight.fareOptions?.[0] ||
  null;

const effectivePrice =
  selectionMode && isSelectedLeg && selectedFareOption
    ? (
        selectedFareOption.displayPricing?.payableTotal ??
        selectedFareOption.displayPrice ??
        selectedFareOption.price
      )
    : (
        flight.displayPricing?.payableTotal ??
        defaultFare?.displayPricing?.payableTotal ??
        flight.displayPrice ??
        defaultFare?.displayPrice ??
        flight.price
      );

const price = effectivePrice;
  const available = flight.available !== false && !flight.priceUnavailable;
  const isGroupBooking = passengerCount > 9;
  const seatText = flight.seatsAvailable !== null && flight.seatsAvailable !== undefined && Number.isFinite(Number(flight.seatsAvailable))
    ? `${Number(flight.seatsAvailable)} seat${Number(flight.seatsAvailable) === 1 ? "" : "s"} left`
    : available ? "Seats available" : "Seats unavailable";

  const cardTravelDate = flight.travelDate || travelDate;
  const formattedTravelDate = cardTravelDate
    ? new Date(`${cardTravelDate}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
    : null;

  const openPricing = () => { if (!available) return; setActiveModal("pricing"); };
  const openDetails = () => setActiveModal("details");
  const openGroupBooking = () => { if (!available) return; setActiveModal("group"); };
  const closeAll = () => setActiveModal(null);
  const handleCardSelect = () => {
    if (!selectionMode || !available) return;

    if (isSpecialRoundTripFlight(flight)) {
      const fare =
        (isSelectedLeg && selectedFareOption) ||
        flight.fareOptions?.find((item) => item.recommended) ||
        flight.fareOptions?.[0] ||
        null;

      onSelectLeg?.({
        ...flight,
        // Keep the supplier fare for checkout; display the admin-adjusted price.
        price: fare
          ? (fare.basePrice ?? fare.price)
          : flight.price,
        displayPrice:
          fare?.displayPrice ??
          fare?.price ??
          flight.displayPrice ??
          flight.price,
        selectedFareIndex: fare
          ? flight.fareOptions?.findIndex((item) => item === fare)
          : 0,
        selectedFare: fare
          ? {
            ...fare,
            price: fare.basePrice ?? fare.price,
            displayPrice:
              fare.displayPrice ??
              fare.price,
          }
          : null,
      });
    }

    setShowFareOptions(true);
  };

  const paxLabel = PAX_TYPE_LABELS[activeFare] || "adult";
  const isSpecialCard = isSpecialRoundTripFlight(flight);
  const specialFlightNumberGroups =
    Array.isArray(flight.specialRoundTripFlightNumberGroups)
      ? flight.specialRoundTripFlightNumberGroups
      : [];

  const specialRouteGroups =
    Array.isArray(
      flight.specialRoundTripRouteGroups
    )
      ? flight.specialRoundTripRouteGroups
      : [];

  const specialSummaries = isSpecialCard
    ? (
      flight.specialRoundTripSummaries?.length
        ? flight.specialRoundTripSummaries
        : (() => {
          const split = splitSpecialRoundTripSegments(flight);

          return [
            split.outbound.length
              ? summarizeSegmentDirection(
                split.outbound,
                "Outbound",
                travelDate
              )
              : null,

            split.returnSegments.length
              ? summarizeSegmentDirection(
                split.returnSegments,
                "Return",
                travelDate
              )
              : null,
          ].filter(Boolean);
        })()
    ).map((summary, index) => {
      const authoritativeNumbers = Array.isArray(
        specialFlightNumberGroups[index]
      )
        ? specialFlightNumberGroups[index]
          .map(cleanFlightNumberDisplay)
          .filter(Boolean)
          .join(", ")
        : cleanFlightNumberDisplay(
          specialFlightNumberGroups[index] ||
          summary.flightNumbers ||
          ""
        );
      const physicalFlightCount =
        String(authoritativeNumbers)
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean)
          .length;

      const authoritativeStops =
        Math.max(
          0,
          physicalFlightCount - 1
        );

      const authoritativeRoute =
        specialRouteGroups[index] ||
        {};

      return {
        ...summary,

        origin:
          authoritativeRoute.origin ||
          summary.origin,

        destination:
          authoritativeRoute.destination ||
          summary.destination,

        flightNumbers:
          authoritativeNumbers,

        stops:
          authoritativeStops,

        stopText:
          authoritativeStops === 0
            ? "Non Stop"
            : authoritativeStops === 1
              ? "1 Stop"
              : `${authoritativeStops} Stops`,
      };
    })
    : [];
  const openSelectedSpecialPricing = () => {
    const selectedFare = selectedFareOption || flight.selectedFare || flight.fareOptions?.[flight.selectedFareIndex ?? 0] || flight.fareOptions?.[0] || null;
    setActiveModal("pricing");
    if (selectedFare && !flight.selectedFare) {
      flight.selectedFare = selectedFare;
      flight.selectedFareIndex = flight.fareOptions?.findIndex((fare) => fare === selectedFare) ?? flight.selectedFareIndex ?? 0;
    }
  };

  return (
    <>


      {activeModal === "pricing" && (
        <PricingModal
          flight={flight}
          travelDate={travelDate}
          onClose={closeAll}
          onLoginRequest={onLoginRequest}
          activeFare={activeFare}
          isInternational={isInternational}
          adults={adults}
          children={children}
          infants={infants}
          classType={classType}
          itineraryLegs={itineraryLegs}
        />
      )}
      {activeModal === "details" && (
        <FlightDetailsModal
          flight={flight}
          onClose={closeAll}
          onLoginRequest={onLoginRequest}
          onBookNow={isGroupBooking ? openGroupBooking : isSpecialCard ? openSelectedSpecialPricing : openPricing}
          activeFare={activeFare}
          isGroupBooking={isGroupBooking}
          cabinClass={classType}
          selectionMode={selectionMode}
          isSelectedLeg={isSelectedLeg}
          selectedFareOption={selectedFareOption}
          onSelectFare={() => {
            closeAll();
            handleCardSelect();
          }}
        />
      )}
      {activeModal === "group" && (
        <GroupBookingModal flight={flight} travelDate={travelDate} passengerCount={passengerCount} onClose={closeAll} />
      )}
      <div className={`flight-card-wrap ${hasBadge ? "has-offer" : ""}${selectionMode ? " is-selectable" : ""}${isSelectedLeg ? " is-selected-leg" : ""}`} {...(selectionMode ? {} : { "data-aos": "fade-up", "data-aos-delay": aosDelay, "data-aos-duration": "380" })}>
        {hasBadge && <div className="flight-card-offer-strip">🏷️ {flight.badge}</div>}
        <div className="flight-card-shell">
          <div className="flight-card-inner">
            <div className="flight-card-main">
              <div className="flight-card-airline">
                <AirlineLogo src={flight.airlineLogoUrl} alt={flight.airline} />
                <div style={{ minWidth: 0 }}>
                  <div className="flight-card-airline-name">{flight.airline}</div>
                  <div className="flight-card-airline-num">{flight.flightNumbers}</div>
                  <div className="flight-card-amenities">
                    <span className={available ? "" : "is-unavailable"}>{seatText}</span>
                    <span>{flight.fareOptions?.[0]?.meal || "Meal not provided"}</span>
                    <span>{baggageDisplay(flight.baggageCheckin)}</span>
                  </div>
                </div>
                <div style={{ marginLeft: "auto", display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-end" }}>
                  {flight.refundable ? (
                    <span style={tagStyle("#dcfce7", "#15803d")}>Refundable</span>
                  ) : flight.cancelFeeText && (
                    <span style={{ fontSize: 10.5, padding: "2px 8px", borderRadius: 4, fontWeight: 600, background: "#fef9ec", color: "#92400e" }}>
                      Cancel: {flight.cancelFeeText}
                    </span>
                  )}
                  {!available && <span style={tagStyle("#fee2e2", "#b91c1c")}>No seats</span>}
                  {flight.tag === "100% on time" && <span style={tagStyle("#dcfce7", "#15803d")}>🕐 {flight.tag}</span>}
                  {activeFare !== "regular" && (
                    <span style={tagStyle("#fef3c7", "#92400e")}>
                      {fareTypes.find(f => f.id === activeFare)?.label}
                    </span>
                  )}
                  {flight.holdAllowed === true && (
                    <span style={tagStyle("#e0f2fe", "#0369a1")}>🔒 Hold Payment Available</span>
                  )}
                </div>
              </div>

              {isSpecialCard && specialSummaries.length > 0 ? (
                <div className="special-rt-card-routes">
                  {specialSummaries.map((summary) => (
                    <div
                      key={summary.label}
                      className="special-rt-card-leg"
                    >
                      <div className="special-rt-card-leg-label">
                        {summary.label}
                      </div>

                      <div className="special-rt-card-leg-route">
                        {summary.origin} → {summary.destination}
                      </div>

                      <div className="special-rt-card-leg-flight">
                        {summary.flightNumbers}
                      </div>

                      <div className="special-rt-card-leg-time">
                        {summary.departTime || "--:--"}
                        <span> – </span>
                        {summary.arriveTime || "--:--"}
                      </div>

                      <div className="special-rt-card-leg-meta">
                        {summary.duration && (
                          <span>{summary.duration}</span>
                        )}

                        {summary.duration && summary.stopText && (
                          <span> · </span>
                        )}

                        <span>{summary.stopText}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flight-card-route">
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontFamily: "'Syne',sans-serif", fontSize: flight.departTime === "--:--" ? 11.5 : 19, fontWeight: 700, color: flight.departTime === "--:--" ? "#94a3b8" : "#0d1b3e", lineHeight: 1.1 }}>
                      {flight.departTime === "--:--" ? "Tap for timing" : flight.departTime}
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{flight.departAirport}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 600, color: "#64748b" }}>{flight.duration}</div>
                    <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 3 }}>
                      <div style={{ flex: 1, height: 1.5, background: "#cbd5e1" }} />
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#10b981", flexShrink: 0 }} />
                      <div style={{ flex: 1, height: 1.5, background: "#cbd5e1" }} />
                    </div>
                    <StopInfoPopup flight={flight} />
                    {formattedTravelDate && (
                      <div className="flight-card-travel-date">
                        <CalendarDays size={10} />{formattedTravelDate}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontFamily: "'Syne',sans-serif", fontSize: flight.arriveTime === "--:--" ? 11.5 : 19, fontWeight: 700, color: flight.arriveTime === "--:--" ? "#94a3b8" : "#0d1b3e", lineHeight: 1.1 }}>
                      {flight.arriveTime === "--:--" ? "Tap for timing" : (<>{flight.arriveTime}{flight.arriveNextDay && <sup style={{ fontSize: 9, color: "#dc2626", fontWeight: 700 }}>+1</sup>}</>)}
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{flight.arriveAirport}</div>
                  </div>
                </div>
              )}

              <div className="flight-card-price">
                {isGroupBooking ? (
                  <div>
                    <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 700, color: "#047857", lineHeight: 1.2 }}>
                      Group fare on request
                    </div>
                    <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 3, fontWeight: 600 }}>Select to get a WhatsApp quote</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 700, color: "#0d1b3e", lineHeight: 1 }}>
                      {fmt(price) ?? <span style={{ fontSize: 13, color: "#94a3b8" }}>Price on request</span>}
                    </div>
                    <div style={{ fontSize: 10.5, color: "#16a34a", marginTop: 2, fontWeight: 700 }}>/{paxLabel} · all incl.</div>
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!available) return;
                    if (selectionMode) {
                      handleCardSelect();
                      return;
                    }
                    isGroupBooking ? openGroupBooking() : openPricing();
                  }}
                  style={{
                    background: !available ? "#e2e8f0" : isSelectedLeg ? "linear-gradient(145deg,#047857,#065f46)" : "linear-gradient(145deg,#10b981,#047857)",
                    color: available ? "#fff" : "#64748b",
                    border: "none", borderRadius: 11, padding: "9px 16px",
                    fontFamily: "'Syne',sans-serif", fontSize: 12, fontWeight: 700,
                    cursor: available ? "pointer" : "not-allowed",
                    boxShadow: available ? "0 4px 14px rgba(16,185,129,0.28)" : "none",
                    transition: "all 0.18s", letterSpacing: "0.03em"
                  }}
                >
                  {!available
                    ? "UNAVAILABLE"
                    : selectionMode
                      ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          {isSelectedLeg && <Check size={12} strokeWidth={3} />}
                          {isSelectedLeg ? (selectedFareOption?.name || "Selected") : "Choose Fare"}
                        </span>
                      )
                      : (isGroupBooking ? "SELECT" : "VIEW PRICES")}               </button>
              </div>
            </div>

            {flight.discount && (
              <div className="flight-card-discount">
                <div style={{ width: 6, height: 6, background: "#d97706", borderRadius: "50%", flexShrink: 0 }} />
                {flight.discount}
              </div>
            )}

            <div className="flight-card-footer">
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                <button className="flight-card-compare-btn" onClick={(e) => { e.stopPropagation(); onCompareToggle?.(); }} disabled={!canCompare} /* unchanged */>
                  {isCompared
                    ? <><Check size={12} strokeWidth={3} style={{ display: "inline", verticalAlign: "-2px", marginRight: 4 }} />Added</>
                    : "+ Compare"}
                </button>
                {selectionMode && available && (
                  <button
                    className="flight-card-compare-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardSelect();
                    }}
                    style={{ background: isSelectedLeg ? "#f0fdf4" : undefined, borderColor: isSelectedLeg ? "rgba(16,185,129,0.4)" : undefined }}
                  >
                    {isSelectedLeg
                      ? <><Check size={12} strokeWidth={3} style={{ display: "inline", verticalAlign: "-2px", marginRight: 4 }} />Selected</>
                      : "Select"}
                  </button>
                )}
              </div>
              <button
                className="flight-card-details-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  openDetails();
                }}
              >
                Flight Details
              </button>
            </div>
          </div>
        </div>
        {showFareOptions && (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(13,27,62,0.6)", zIndex: 5000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
            onClick={(e) => { e.stopPropagation(); setShowFareOptions(false); }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ background: "#fff", borderRadius: 20, padding: 24, width: "min(920px, 96vw)", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 32px 80px rgba(0,0,0,0.35)" }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, color: "#0d1b3e" }}>Choose your fare</div>
                  <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 2 }}>{flight.airline} · {flight.departCode} → {flight.arriveCode}</div>
                </div>
                <button onClick={() => setShowFareOptions(false)} style={{ background: "#f1f5f9", border: "none", width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: 15 }}>✕</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
                {(flight.fareOptions || []).map((fare, i) => (
                  <FareOptionCard
                    key={i}
                    fare={fare}
                    selected={isSelectedLeg && selectedFareOption === fare}
                    pricePerLabel={`/${paxLabel}`}
                    onClick={() => {
                      onSelectLeg({
                        ...flight,

                        // Preserve the supplier fare for checkout calculations.
                        price: fare.basePrice ?? fare.price,

                        // Display the admin-adjusted fare.
                        displayPrice: fare.displayPrice ?? fare.price,

                        selectedFareIndex: i,

                        // Keep the selected fare's supplier price unchanged.
                        selectedFare: {
                          ...fare,
                          price: fare.basePrice ?? fare.price,
                          displayPrice: fare.displayPrice ?? fare.price,
                        },
                      });
                    }}
                  />
                ))}
              </div>
              {isSelectedLeg && selectedFareOption && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 18,
                    paddingTop: 16,
                    borderTop: "1px solid #e2e8f0",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowFareOptions(false);
                      openSelectedSpecialPricing();
                    }}
                    style={{
                      ...styles.bookBtn,
                      minWidth: 190,
                    }}
                  >
                    Select Passengers →
                  </button>
                </div>
              )}
            </div>
          </div>
        )} </div>


    </>
  );
}

// ── SearchBar ─────────────────────────────────────────────────────────────────
function SearchBar({
  meta, airports, airportsLoading, onSwap, onMetaChange, onDateChange, onSearch,
  formMessage, returnDateError, activeFare, onFareChange, loading, multiCityLegs,
  onAddLeg, onRemoveLeg, onUpdateLeg
}) {
  const pax = { adults: meta.adults, children: meta.children, infants: meta.infants, classType: meta.classType };
  const handlePaxChange = (next) => onMetaChange(next);
  const today = toIsoDate(new Date().toISOString().slice(0, 10));
  const departDate = toIsoDate(meta.departDate);
  const returnDate = toIsoDate(meta.returnDate, addDays(departDate, 1));
  const isOneWay = meta.tripType === "One Way";
  const isReturnTrip = isReturnTripType(meta.tripType);
  const fareIcons = { regular: <ShieldCheck size={12} />, student: <Backpack size={12} />, armed: <ShieldCheck size={12} />, senior: <Users size={12} />, doctor: <Handshake size={12} /> };
  return (
    <div className="flight-search-shell">
      <div className="flight-search-panel" data-aos="fade-down" data-aos-duration="500">
        <div className="flight-search-field flight-search-field--trip">

          <div className="flight-trip-radios" role="radiogroup" aria-label="Trip type">

            {[
              { id: "One Way", text: "One Way" },
              { id: "Round Trip", text: "Round Trip" },
              { id: "Special Round Trip", text: "Special Round Trip" },
              { id: "Multi City", text: "Multi City" },
            ].map((opt) => (
              <label key={opt.id} className={`flight-trip-radio ${meta.tripType === opt.id ? "is-active" : ""}`}>
                <input
                  type="radio"
                  name="tripType"
                  value={opt.id}
                  checked={meta.tripType === opt.id}
                  onChange={() => onMetaChange({ tripType: opt.id })}
                />
                <span>{opt.text}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flight-search-row">

          {/* <div className="flight-search-mark" aria-hidden="true"><Plane size={20} /></div> */}


          {meta.tripType === "Multi City" ? (
            <div className="multicity-fields">
              {multiCityLegs.map((leg, i) => (
                <div className="multicity-leg-row" key={leg.id}>
                  <span className="multicity-leg-num">{i + 1}</span>
                  <CityCombobox
                    label={`Leg ${i + 1} from`}
                    valueCode={leg.fromCode}
                    valueLabel={leg.from}
                    airports={airports}
                    loading={airportsLoading}
                    placeholder="From city"
                    onSelect={(c) =>
                      onUpdateLeg(leg.id, {
                        fromCode: c.code,
                        from: c.label,
                      })
                    }
                  />
                  <span className="multicity-arrow">→</span>
                  <CityCombobox
                    label={`Leg ${i + 1} to`}
                    valueCode={leg.toCode}
                    valueLabel={leg.to}
                    airports={airports}
                    loading={airportsLoading}
                    placeholder="To city"
                    onSelect={(c) =>
                      onUpdateLeg(leg.id, {
                        toCode: c.code,
                        to: c.label,
                      })
                    }
                  />
                  <FlightDateField
                    label="DEPART"
                    value={leg.departDate}
                    min={
                      i === 0
                        ? today
                        : addDays(multiCityLegs[i - 1].departDate, 1)
                    }
                    onChange={(value) => onUpdateLeg(leg.id, { departDate: value })}
                    compact
                  />
                  {multiCityLegs.length > 2 && (
                    <button type="button" className="multicity-remove-btn" onClick={() => onRemoveLeg(leg.id)}>✕</button>
                  )}
                </div>
              ))}
              {multiCityLegs.length < 5 && (
                <button type="button" className="multicity-add-btn" onClick={onAddLeg}>
                  + Add Another Flight
                </button>
              )}
            </div>
          ) : (
            <>

              <div className="flight-search-field flight-search-field--city">
                <span className="flight-field-icon"><MapPin size={13} /></span>
                <label>FROM</label>
                <CityCombobox
                  label="From city"
                  valueCode={meta.fromCode}
                  valueLabel={meta.from}
                  airports={airports}
                  loading={airportsLoading}
                  placeholder="Type city or airport"
                  onSelect={(c) =>
                    onMetaChange({
                      from: c.label,
                      fromCode: c.code,
                    })
                  }
                />
              </div>

              <button type="button" className="flight-search-swap" onClick={onSwap} aria-label="Swap cities"><span>⇄</span></button>

              <div className="flight-search-field flight-search-field--city flight-search-to-field">
                <span className="flight-field-icon"><MapPin size={13} /></span>
                <label>TO</label>
                <CityCombobox
                  label="To city"
                  valueCode={meta.toCode}
                  valueLabel={meta.to}
                  airports={airports}
                  loading={airportsLoading}
                  placeholder="Type city or airport"
                  onSelect={(c) =>
                    onMetaChange({
                      to: c.label,
                      toCode: c.code,
                    })
                  }
                />
              </div>

              <FlightDateField
                label="DEPART"
                value={departDate}
                min={today}
                onChange={(value) => onDateChange("departDate", value)}
              />

              {isReturnTrip && (
                <div className="flight-search-return-field">
                  <FlightDateField
                    label="RETURN"
                    value={returnDate}
                    min={departDate}
                    onChange={(value) => onDateChange("returnDate", value)}
                  />

                  {returnDateError && (
                    <div className="flight-date-error">
                      {returnDateError}
                    </div>
                  )}
                </div>
              )}

              <SearchPaxClassField pax={pax} onChange={handlePaxChange} />
            </>
          )}

          {meta.tripType === "Multi City" && (
            <SearchPaxClassField pax={pax} onChange={handlePaxChange} />
          )}
        </div>
        <div
          className={`flight-search-footer ${isReturnTrip
              ? "flight-search-footer--return"
              : "flight-search-footer--one-way"
            }`}
        >
          <span className="flight-fares-label">Special fares:</span>
          <div className="flight-fare-options">
            {fareTypes.map((f) => (
              <button type="button" key={f.id} onClick={() => onFareChange(f.id)} className={`flight-fare-pill ${activeFare === f.id ? "is-active" : ""}`}>
                <span>{fareIcons[f.id]}</span>{f.label}
              </button>
            ))}
          </div>
          {/* <label className="flight-hotel-dates"><input type="checkbox" /> Also find hotels for different dates</label> */}
          <div className="flight-search-benefits" aria-label="Booking benefits">
            <div><ShieldCheck size={15} /><span><strong>Best fares</strong><small>Guaranteed</small></span></div>
            <div><CreditCard size={15} /><span><strong>Easy booking</strong><small>Hassle-free</small></span></div>
            <div><Handshake size={15} /><span><strong>24/7 support</strong><small>We're here</small></span></div>
            <div><Lock size={15} /><span><strong>Secure payments</strong><small>100% Safe</small></span></div>
          </div>
          <button type="button" onClick={onSearch} disabled={loading} className="flight-search-button">
            {loading ? "Searching…" : "Search flights"}
          </button>
        </div>

        {formMessage && (
          <div className="flight-date-error">
            {formMessage}
          </div>
        )}

      </div>
    </div>
  );
}

// ── FilterSection ─────────────────────────────────────────────────────────────
function FilterSection({ title, onReset, children, initialOpen = true }) {
  const [open, setOpen] = useState(initialOpen);
  return (
    <div
      className="flights-filter-section"
      style={{
        background: "#fff",
        borderRadius: 16,
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 4px rgba(15,23,42,0.05)",
        overflow: "hidden"
      }}
    >
      <div style={{ padding: "12px 14px 9px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
        <div onClick={() => setOpen((o) => !o)} style={{ fontFamily: "'Syne',sans-serif", fontSize: 11.5, fontWeight: 700, color: "#0d1b3e", textTransform: "uppercase", letterSpacing: "0.06em", cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
          {title} <span style={{ fontSize: 10 }}>{open ? "▾" : "▸"}</span>
        </div>
        {onReset && <button className="fr-filter-reset" onClick={onReset}>Reset</button>}
      </div>
      {open && (
        <div className="flights-filter-section-body">
          {children}
        </div>
      )}
    </div>
  );
}

function CheckboxRow({ checked, onToggle, label, price }) {
  return (
    <div onClick={onToggle} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 14px", cursor: "pointer", background: checked ? "#f0fdf4" : "transparent", transition: "background 0.15s" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 15, height: 15, border: `2px solid ${checked ? "#10b981" : "#cbd5e1"}`, borderRadius: 4, background: checked ? "#10b981" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
          {checked && <Check size={10} strokeWidth={3} color="#fff" />}
        </div>
        <span style={{ fontSize: 12.5, color: "#0d1b3e" }}>{label}</span>
      </div>
      {price != null && <span style={{ fontSize: 11.5, color: "#64748b", fontWeight: 500 }}>{fmt(price)}</span>}
    </div>
  );
}

// ── Main FlightResults ────────────────────────────────────────────────────────
export default function FlightResults() {
  usePageMeta(
    "Book Cheap Domestic & International Flights – MakeBeeTrip",
    "Search and book cheap domestic and international flight tickets on MakeBeeTrip. Compare airline fares, choose seats and get instant e-tickets with 24/7 support.",
    "flight booking, cheap flights, domestic flights, international flights, air tickets, flight offers, compare flight fares, airline booking, book flights online"
  );
  useAOS();

  const location = useLocation();
  const navigate = useNavigate();
  const qp = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const today = useMemo(() => toIsoDate(new Date().toISOString().slice(0, 10)), []);
  const [returnFlights, setReturnFlights] = useState([]);
  const [selectedOutboundLeg, setSelectedOutboundLeg] = useState(null);
  const [selectedReturnLeg, setSelectedReturnLeg] = useState(null);
  const [selectedSpecialRoundTrip, setSelectedSpecialRoundTrip] = useState(null);
  const [returnDateError, setReturnDateError] = useState("");
  const canProceedRoundTrip = selectedOutboundLeg && selectedReturnLeg;
  const [multiCityLegs, setMultiCityLegs] = useState(() => {
    const rawLegs = qp.get("legs");

    if (!rawLegs) {
      return [
        {
  id: 1,
  fromCode: "",
  from: "",
  toCode: "",
  to: "",
  departDate: DEFAULT_DEPART_DATE,
},
{
  id: 2,
  fromCode: "",
  from: "",
  toCode: "",
  to: "",
  departDate: addDays(DEFAULT_DEPART_DATE, 1),
},
      ];
    }

    try {
      const parsedLegs = JSON.parse(rawLegs);

      if (!Array.isArray(parsedLegs) || parsedLegs.length === 0) {
        throw new Error("Invalid multi-city legs");
      }

      
      let previousDate = addDays(DEFAULT_DEPART_DATE, -1);

      return parsedLegs.map((leg, index) => {
        const fromCode = String(leg.fromCode || "").toUpperCase();
        const toCode = String(leg.toCode || "").toUpperCase();

        const minimumDate = addDays(previousDate, 1);
        const requestedDate = toIsoDate(leg.date, minimumDate);
        const departDate =
          requestedDate < minimumDate ? minimumDate : requestedDate;

        previousDate = departDate;

        return {
          id: index + 1,
          fromCode,
          from: leg.from || fromCode,
          toCode,
          to: leg.to || toCode,
          departDate,
        };
      });

    } catch (error) {
      console.warn("[Flights] Invalid multi-city legs from URL:", error);

      return [];
    }
  });
  const [multiCityResults, setMultiCityResults] = useState([]); // [{ leg, flights }]
  const [activeLegTab, setActiveLegTab] = useState(0);
  const [multiCitySelections, setMultiCitySelections] = useState({});
  // shape: { [legId]: flightObject }

  const selectMultiCityLeg = (legId, flight) => {
    if (!legId || !flight) return;

    const leg = multiCityLegs.find(
      (item) => item.id === legId
    );

    setMultiCitySelections((prev) => ({
      ...prev,
      [legId]: {
        ...flight,

        // Explicit leg information.
        multiCityLegId: legId,
        legIndex: leg
          ? multiCityLegs.findIndex(
            (item) => item.id === legId
          )
          : -1,

        travelDate:
          flight.travelDate ||
          leg?.departDate ||
          "",

        fromCode:
          flight.departCode ||
          flight.fromCode ||
          leg?.fromCode ||
          "",

        toCode:
          flight.arriveCode ||
          flight.toCode ||
          leg?.toCode ||
          "",
      },
    }));
  };

  const allMultiCityLegsSelected = multiCityLegs.every(
    (leg) => multiCitySelections[leg.id]
  );
  const [checkoutItinerary, setCheckoutItinerary] = useState(null);
  const buildCombinedFlight = useCallback((legs, label) => {
    const cleanLegs = legs.filter(Boolean);
    
    const getLegDisplayPrice = (leg) =>
      Number(
        leg?.selectedFare?.displayPricing?.payableTotal ??
        leg?.selectedFare?.displayPrice ??
        leg?.displayPricing?.payableTotal ??
        leg?.displayPrice ??
        leg?.selectedFare?.price ??
        leg?.price ??
        0
      );

    const getLegSupplierPrice = (leg) =>
      Number(
        leg?.selectedFare?.basePrice ??
        leg?.selectedFare?.price ??
        leg?.price ??
        0
      );

    // Keep supplier fare separate from the customer-facing price.
    const adultSupplierTotal = cleanLegs.reduce(
      (sum, leg) => sum + getLegSupplierPrice(leg),
      0
    );

    
// Sum all selected leg display prices first.
const summedDisplayTotal = cleanLegs.reduce(
  (sum, leg) => sum + getLegDisplayPrice(leg),
  0
);

// Return the fixed website fees already included in an individual leg.
const getDuplicateFixedFees = (leg) => {
  const pricing =
    leg?.selectedFare?.displayPricing ??
    leg?.displayPricing ??
    null;

  const charges =
    pricing?.breakdown ??
    pricing?.lines ??
    [];

  if (!Array.isArray(charges)) return 0;

  return charges.reduce((sum, charge) => {
    const basis = String(charge?.basis || "")
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

    const chargeType = String(charge?.chargeType || "")
      .toLowerCase();

    const isDuplicateFixedFee =
      chargeType === "fixed" &&
      (basis === "per_booking" ||
        basis === "per_passenger");

    if (!isDuplicateFixedFee) return sum;

    return sum + (Number(charge?.amount) || 0);
  }, 0);
};

// Keep booking-level and per-passenger fees on the first leg only.
// Remove those repeated fees from every subsequent leg.
const duplicateFixedFees = cleanLegs
  .slice(1)
  .reduce(
    (sum, leg) => sum + getDuplicateFixedFees(leg),
    0
  );

const adultTotal = Math.max(
  0,
  Math.round(
    (summedDisplayTotal - duplicateFixedFees) * 100
  ) / 100
);

const childSupplierTotal = cleanLegs.reduce((sum, leg) => {
  const fare = leg.selectedFare || leg.fareOptions?.[0] || {};
  const price = Number(
    fare.childBasePrice ??
    fare.childPrice ??
    leg.childBasePrice ??
    leg.childPrice ??
    Math.round(getLegSupplierPrice(leg) * 0.75)
  );

  return sum + (Number.isFinite(price) ? price : 0);
}, 0);

const childTotal = cleanLegs.reduce((sum, leg) => {
  const fare = leg.selectedFare || leg.fareOptions?.[0] || {};
  const price =
    fare.childDisplayPrice ??
    fare.displayPricing?.childPayableTotal ??
    fare.childPrice ??
    Math.round(getLegDisplayPrice(leg) * 0.75);

  return sum + (Number(price) || 0);
}, 0);

const infantSupplierTotal = cleanLegs.reduce((sum, leg) => {
  const fare = leg.selectedFare || leg.fareOptions?.[0] || {};
  const price = Number(
    fare.infantBasePrice ??
    fare.infantPrice ??
    leg.infantBasePrice ??
    leg.infantPrice ??
    Math.round(getLegSupplierPrice(leg) * 0.10)
  );

  return sum + (Number.isFinite(price) ? price : 0);
}, 0);

const infantTotal = cleanLegs.reduce((sum, leg) => {
  const fare = leg.selectedFare || leg.fareOptions?.[0] || {};
  const price =
    fare.infantDisplayPrice ??
    fare.displayPricing?.infantPayableTotal ??
    fare.infantPrice ??
    Math.round(getLegDisplayPrice(leg) * 0.10);

  return sum + (Number(price) || 0);
}, 0);

    const first = cleanLegs[0] || {};
    const last = cleanLegs[cleanLegs.length - 1] || first;
    return {
      ...first,
      id: `${label}-${cleanLegs.map((leg) => leg.id).join("-")}`,
      airline: first.airline,
      airlineLogoUrl: first.airlineLogoUrl,
      // Multi-City bookings must not be mislabeled as one_way downstream.
      // Only the multi-city combined flight is tagged; round-trip and special
      // round-trip labels keep their existing behaviour unchanged.
      ...(label === "multi-city" ? { tripType: "Multi City" } : {}),
      flightNumbers: cleanLegs.map((leg) => leg.flightNumbers).filter(Boolean).join(" + "),
                  fareOptions: [{
        name: "Combined fare",
        price: adultSupplierTotal,
childPrice: childSupplierTotal,
infantPrice: infantSupplierTotal,

displayPrice: adultTotal,
childDisplayPrice: childTotal,
infantDisplayPrice: infantTotal,
        baggage: (first.selectedFare || first.fareOptions?.[0])?.baggage,
        refundable: cleanLegs.every((leg) => leg.refundable),
        holdAllowed: aggregateCombinedHoldAllowed(cleanLegs),
        recommended: true,
      }],
      departAirport: first.departAirport,
      departCode: first.departCode,
      arriveAirport: first.arriveAirport,
      arriveCode: first.arriveCode,
      departTime: first.departTime,
      arriveTime: last.arriveTime,
      duration: `${cleanLegs.length} flight${cleanLegs.length > 1 ? "s" : ""}`,
      stops: Math.max(0, cleanLegs.length - 1),
      stopDetail: cleanLegs.length > 1
        ? `${cleanLegs.length} connecting flights`
        : "Non Stop",
      returnDate: cleanLegs[1]?.travelDate || null,
      legsDetail: cleanLegs.map((leg) => ({
        airline: leg.airline,
        flightNumbers: leg.flightNumbers,
        departCode: leg.departCode,
        arriveCode: leg.arriveCode,
        departTime: leg.departTime,
        arriveTime: leg.arriveTime,
        travelDate: leg.travelDate,
        price: leg.displayPrice ?? leg.price,
      })),
      price: adultSupplierTotal,
displayPrice: adultTotal,

            // Never reuse the first leg's pricing for a combined itinerary.
      // The combined fareOptions/displayPrice contain the complete journey total.
      ...(
        label === "round-trip" || label === "multi-city"
          ? {
              selectedFare: null,
              displayPricing: undefined,
            }
          : {}
      ),
      baggageCheckin: first.baggageCheckin,
      baggageCabin: first.baggageCabin,
      refundable: cleanLegs.every((leg) => leg.refundable),
      holdAllowed: aggregateCombinedHoldAllowed(cleanLegs),
    };
  }, []);

  const openCombinedCheckout = useCallback((legs, label) => {
    const cleanLegs = legs.filter(Boolean);
    if (cleanLegs.length < 2) return;
    setCheckoutItinerary({
      flight: buildCombinedFlight(cleanLegs, label),
      legs: cleanLegs,
      travelDate: cleanLegs[0]?.travelDate || DEFAULT_DEPART_DATE,
    });
  }, [buildCombinedFlight]);
  const buildDefaultLegSelection = useCallback((flight, travelDateIso) => {
    if (!flight) return null;

    const fareOption =
      flight.fareOptions?.find((f) => f.recommended) ||
      flight.fareOptions?.[0] ||
      null;

    
const supplierFare = fareOption
  ? (fareOption.basePrice ?? fareOption.price)
  : (flight.basePrice ?? flight.price);

// Prefer the admin-adjusted fare price. If the fare does not contain
// display pricing, fall back to the admin-adjusted flight-level price
// before using the supplier fare.
const adjustedDisplayPrice =
  fareOption?.displayPricing?.payableTotal ??
  fareOption?.displayPrice ??
  flight.displayPricing?.payableTotal ??
  flight.displayPrice ??
  fareOption?.price ??
  flight.price;


    return {
      ...flight,

      // Supplier price stays separate from the display price.
      price: supplierFare,
      displayPrice: adjustedDisplayPrice,

      selectedFareIndex:
        flight.fareOptions?.findIndex((f) => f === fareOption) ?? 0,

      
selectedFare: fareOption
  ? {
      ...fareOption,
      price: supplierFare,
      displayPrice: adjustedDisplayPrice,
      displayPricing: fareOption.displayPricing ?? flight.displayPricing ?? null,
    }
  : null,


      travelDate: travelDateIso,
    };
  }, []);
  const addMultiCityLeg = () => {
    if (multiCityLegs.length >= 5) return; // cap at 5 legs
    setMultiCityLegs((prev) => {
      const last = prev[prev.length - 1];
      return [
        ...prev,
        {
          id: Date.now(),
          fromCode: last?.toCode || "",
          from: last?.to || "",
          toCode: "",
          to: "",
          departDate: addDays(last?.departDate || DEFAULT_DEPART_DATE, 1),
        },
      ];
    });
  };

  const removeMultiCityLeg = (id) => {
    setMultiCityLegs((prev) => prev.length > 2 ? prev.filter((l) => l.id !== id) : prev);
  };

  const updateMultiCityLeg = (id, updates) => {
    setMultiCityLegs((prev) => {
      const index = prev.findIndex((l) => l.id === id);

      if (index === -1) return prev;

      const next = prev.map((l) =>
        l.id === id ? { ...l, ...updates } : l
      );

      if ("departDate" in updates && updates.departDate) {
        for (let i = index + 1; i < next.length; i += 1) {
          const previousDate = next[i - 1].departDate;

          if (
            previousDate &&
            (!next[i].departDate ||
              next[i].departDate <= previousDate)
          ) {
            next[i] = {
              ...next[i],
              departDate: addDays(previousDate, 1),
            };
          }
        }
      }

      return next;
    });
  };
  const [meta, setMeta] = useState(() => {
    const fromCode = (qp.get("fromCode") || "").toUpperCase();
    const toCode = (qp.get("toCode") || "").toUpperCase();

    const from = qp.get("from") || fromCode;
const to = qp.get("to") || toCode;
    const requestedDepartDate = toIsoDate(qp.get("departDate") || DEFAULT_DEPART_DATE);
    const departDate = requestedDepartDate < today ? today : requestedDepartDate;
    const requestedReturnDate = toIsoDate(qp.get("returnDate"), addDays(departDate, 1));
    const returnDate = requestedReturnDate > departDate ? requestedReturnDate : addDays(departDate, 1);
    return {
      from, fromCode, to, toCode,
      departDate,
      returnDate,
      tripType: qp.get("tripType") || defaultSearchMeta.tripType,
      adults: Number(qp.get("adults")) || 1,
      children: Number(qp.get("children")) || 0,
      infants: Number(qp.get("infants")) || 0,
      classType: qp.get("classType") || defaultSearchMeta.classType, // ADD
    };
  });

  const [rawFlights, setRawFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [searched, setSearched] = useState(false);
  const [airports, setAirports] = useState([]);
  const [airportsLoading, setAirportsLoading] = useState(false);
  const [airportsReady, setAirportsReady] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const abortRef = useRef(null);
  const searchInFlightRef = useRef(false);
  const datePriceLoadingRef = useRef(new Set());
  const initialSearchDoneRef = useRef(false);
  const [activeFare, setActiveFare] = useState(() => qp.get("fare") || "regular");
  const initialDepartDate = qp.get("departDate") || DEFAULT_DEPART_DATE;
  const [dateStripStart, setDateStripStart] = useState(() => initialDepartDate < today ? today : initialDepartDate);
  const [activeStripDate, setActiveStripDate] = useState(() => initialDepartDate < today ? today : initialDepartDate);
  const [datePriceMap, setDatePriceMap] = useState({});
  const datePriceMapRef = useRef({});
  const [activeSort, setActiveSort] = useState("cheapest");

  // Standard one-way results pagination.
  // Round-trip and multi-city keep their current selection flow unchanged.
  const FLIGHTS_PER_PAGE = 15;
  const [currentPage, setCurrentPage] = useState(1);
  const [outboundPage, setOutboundPage] = useState(1);
  const [returnPage, setReturnPage] = useState(1);
  const [multiCityPage, setMultiCityPage] = useState(1);

  const [checkedFilters, setCheckedFilters] = useState({});
  const [checkedAirlines, setCheckedAirlines] = useState({});
  const [checkedDepAirports, setCheckedDepAirports] = useState({});
  const [checkedArrAirports, setCheckedArrAirports] = useState({});
  const [activeStop, setActiveStop] = useState(null);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [maxPrice, setMaxPrice] = useState(100000);
  const [minPrice, setMinPrice] = useState(0);
  const [priceRangeMax, setPriceRangeMax] = useState(100000);
  const [selectedFlights, setSelectedFlights] = useState([]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);

  const filterStopsForAutoSelect = useCallback((list = []) => {
    let result = [...list];

    // Stops section
    if (activeStop === "nonstop") {
      result = result.filter((f) => matchesStopFilter(f, "nonstop"));
    } else if (activeStop === "onestop") {
      result = result.filter((f) => matchesStopFilter(f, "onestop"));
    } else if (activeStop === "twostop") {
      result = result.filter((f) => matchesStopFilter(f, "twostop"));
    }

    // Popular Filters section
    if (checkedFilters.nonstop) {
      result = result.filter((f) => matchesStopFilter(f, "nonstop"));
    }

    if (checkedFilters.onestop) {
      result = result.filter((f) => matchesStopFilter(f, "onestop"));
    }

    return result;
  }, [activeStop, checkedFilters]);

  // Mobile filter drawer toggle
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Departure time range filter (0–24 hrs)
  const [timeRange, setTimeRange] = useState([0, 24]);

  // Track whether we're in a "mobile" viewport (controls sidebar collapse behavior)
  const [isMobileFilters, setIsMobileFilters] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 960 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobileFilters(window.innerWidth <= 960);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const isInternational = useMemo(
    () => isInternationalRoute(meta.fromCode, meta.toCode, airports),
    [meta.fromCode, meta.toCode, airports]
  );
  // Count of currently active filters, shown as a badge on the mobile toggle
  const activeFilterCount = useMemo(() => {
    let count = 0;
    count += Object.values(checkedFilters).filter(Boolean).length;
    count += Object.values(checkedAirlines).filter(Boolean).length;
    count += Object.values(checkedDepAirports).filter(Boolean).length;
    count += Object.values(checkedArrAirports).filter(Boolean).length;
    if (activeStop) count += 1;
    if (timeRange[0] !== 0 || timeRange[1] !== 24) count += 1;
    return count;
  }, [checkedFilters, checkedAirlines, checkedDepAirports, checkedArrAirports, activeStop, timeRange]);

  // Airports: API is the only source of airport records.
useEffect(() => {
  let active = true;
  const controller = new AbortController();

  setAirportsLoading(true);

  fetch(`${API_BASE}/flight-search/airports`, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal: controller.signal,
  })
    .then(async (res) => {
      if (!res.ok) {
        throw new Error(`Airport API failed with status ${res.status}`);
      }

      return res.json();
    })
    .then((data) => {
      if (!active) return;

      if (!Array.isArray(data.airports)) {
        throw new Error("Invalid airport API response: airports must be an array");
      }

      // Do not silently substitute dummy records when the API returns no data.
      const next = data.airports
  .filter(
    (airport) =>
      airport &&
      typeof airport.code === "string" &&
      airport.code.trim() !== ""
  )
  .map((airport) => ({
    ...airport,
    code: airport.code.trim().toUpperCase(),
  }));

      setAirports(next);

      setMeta((current) => {
        const fromAirport = next.find(
          (airport) =>
            airport.code.toUpperCase() ===
            String(current.fromCode || "").toUpperCase()
        );

        const toAirport = next.find(
          (airport) =>
            airport.code.toUpperCase() ===
            String(current.toCode || "").toUpperCase()
        );

        return {
          ...current,
          from: fromAirport ? airportLabel(fromAirport) : current.from,
          to: toAirport ? airportLabel(toAirport) : current.to,
        };
      });

      if (next.length === 0) {
        setFormMessage("No airports were returned by the airport API.");
      }
    })
    .catch((error) => {
      if (!active || error.name === "AbortError") return;

      console.error("Failed to load airports:", error);
      setAirports([]);
      setFormMessage("Unable to load airports. Please try again.");
    })
    .finally(() => {
      if (active) {
        setAirportsLoading(false);
        setAirportsReady(true);
      }
    });

  return () => {
    active = false;
    controller.abort();
  };
}, []);

  const datePriceKey = useCallback((sm, date) =>
    [sm.fromCode, sm.toCode, date, sm.tripType, activeFare].join("|"), [activeFare]);

  const doSearch = useCallback(async (sm) => {
    const fromCode = String(sm.fromCode || "").trim().toUpperCase();
const toCode = String(sm.toCode || "").trim().toUpperCase();
    if (searchInFlightRef.current) {
      
      return;
    }

    if (!fromCode || !toCode) {
      setFormMessage("Please choose both departure and arrival cities from the suggestions.");
      setApiError("");
      setSearched(false);
      return;
    }

    if (fromCode === toCode) {
      setFormMessage("Departure and arrival city cannot be the same.");
      setApiError("");
      setSearched(false);
      return;
    }

    const isReturnTripSearch = isReturnTripType(sm.tripType);
    const isSpecialRoundTripSearch = isSpecialRoundTripType(sm.tripType);
    const bookingType = getSearchBookingType(sm.tripType);

    if (
      isReturnTripSearch &&
      (!sm.returnDate || sm.returnDate < sm.departDate)
    ) {
      setReturnDateError("Return date cannot be before the departure date.");
      setApiError("");
      setSearched(false);
      return;
    }

    setReturnDateError("");

    searchInFlightRef.current = true;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true); setApiError(""); setFormMessage(""); setSearched(true);

    const params = new URLSearchParams({
      fromCode, toCode,
      departDate: sm.departDate, tripType: sm.tripType,
      fare: activeFare,
      classType: sm.classType, // ADD
    });
    if (isReturnTripSearch) params.set("returnDate", sm.returnDate);
    params.set("adults", sm.adults || 1);
    params.set("children", sm.children || 0);
    params.set("infants", sm.infants || 0);
    navigate(`?${params.toString()}`, { replace: true },);

    try {
      const res = await fetch(`${API_BASE}/flight-search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fromCode,
toCode,
          departDate: sm.departDate,
          tripType: sm.tripType,
          Booking_Type: bookingType,
          bookingType,
          returnDate: isReturnTripSearch ? sm.returnDate : undefined,
          fare: activeFare,
          debugFindPassport: true,
          classType: toApiClassType(sm.classType),
          adults: sm.adults || 1,
          children: sm.children || 0,
          infants: sm.infants || 0,
        }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const supplierMessage =
          err.message ||
          err.error ||
          err.detail?.Response_Header?.Error_InnerException ||
          err.detail?.Response_Header?.Error_Desc ||
          err.detail?.ResponseHeader?.Error_InnerException ||
          err.detail?.ResponseHeader?.Error_Desc;
        throw new Error(supplierMessage || `Search failed with status ${res.status}`);
      }
      const data = await res.json();
      const allFlights = (data.flights || []).map((item) => ({
        ...item,
        tripType: item.tripType || sm.tripType,
      }));
      const routeMatches = (item, from, to, date) => {
        const itemFrom = String(item.departCode || "").toUpperCase();
        const itemTo = String(item.arriveCode || "").toUpperCase();
        const itemDate = String(item.travelDate || item.departureDate || "").slice(0, 10);
        return itemFrom === String(from || "").toUpperCase() &&
          itemTo === String(to || "").toUpperCase() &&
          (!date || !itemDate || itemDate === String(date).slice(0, 10));
      };
      const outboundFlightOptions = isReturnTripSearch && !isSpecialRoundTripSearch
        ? allFlights.filter((item) => routeMatches(item, sm.fromCode, sm.toCode, sm.departDate))
        : allFlights;
      const returnFlightOptions = isReturnTripSearch && !isSpecialRoundTripSearch
        ? allFlights.filter((item) => routeMatches(item, sm.toCode, sm.fromCode, sm.returnDate))
        : [];
      const flights = outboundFlightOptions.length ? outboundFlightOptions : allFlights;
      setRawFlights(flights);
      let autoOutbound = null;

      if (isSpecialRoundTripSearch) {
        const autoSpecialOptions = filterStopsForAutoSelect(flights);
        const cheapestSpecial = [...autoSpecialOptions]
          .filter((f) => Number(f.price) > 0)
          .sort((a, b) => Number(a.price) - Number(b.price))[0];

        setSelectedSpecialRoundTrip(
          cheapestSpecial
            ? buildDefaultLegSelection(cheapestSpecial, sm.departDate)
            : null
        );
        setSelectedOutboundLeg(null);
        setSelectedReturnLeg(null);
        setReturnFlights([]);
      } else if (isReturnTripSearch) {
        const autoOutboundOptions = filterStopsForAutoSelect(flights);

        const cheapestOutbound = [...autoOutboundOptions]
          .filter((f) => Number(f.price) > 0)
          .sort((a, b) => Number(a.price) - Number(b.price))[0];

        if (cheapestOutbound) {
          autoOutbound = buildDefaultLegSelection(
            cheapestOutbound,
            sm.departDate
          );

          setSelectedOutboundLeg(autoOutbound);
          setOutboundPage(1);
        } else {
          setSelectedOutboundLeg(null);
        }

        setSelectedReturnLeg(null);
      }
      if (isReturnTripSearch && !isSpecialRoundTripSearch && sm.returnDate) {
        try {
          const retFlights = returnFlightOptions;

          let validReturnFlights = retFlights;

          // Same-day round trip:
          // only allow return flights AFTER outbound arrival.
          if (
            autoOutbound &&
            sm.returnDate === sm.departDate
          ) {
            const outboundArrival = autoOutbound.arriveTime;

            if (outboundArrival) {
              const [arrH, arrM] = outboundArrival.split(":").map(Number);
              let arrivalMinutes = arrH * 60 + arrM;

              if (autoOutbound.arriveNextDay) {
                arrivalMinutes += 24 * 60;
              }

              validReturnFlights = retFlights.filter((flight) => {
                if (!flight.departTime) return false;

                const [depH, depM] = flight.departTime.split(":").map(Number);
                const departureMinutes = depH * 60 + depM;

                return departureMinutes > arrivalMinutes;
              });
            }
          }

          setReturnFlights(validReturnFlights);

          const autoReturnOptions =
            filterStopsForAutoSelect(validReturnFlights);

          const cheapestValidReturn = [...autoReturnOptions]
            .filter((f) => Number(f.price) > 0)
            .sort((a, b) => Number(a.price) - Number(b.price))[0];

          if (cheapestValidReturn) {
            setSelectedReturnLeg(
              buildDefaultLegSelection(
                cheapestValidReturn,
                sm.returnDate
              )
            );

            setReturnPage(1);
          } else {
            setSelectedReturnLeg(null);
          }
        } catch {
          setReturnFlights([]);
        }
      } else {
        setReturnFlights([]);
      }
      if (flights.length) {
        
const prices = flights
  .map((f) => {
    const recommendedFare =
      f.fareOptions?.find((fare) => fare.recommended) ||
      f.fareOptions?.[0] ||
      null;

    const value =
      f.displayPrice ??
      recommendedFare?.displayPrice ??
      f.price;

    const price = Number(value);

    return Number.isFinite(price) && price > 0
      ? price
      : null;
  })
  .filter((p) => p !== null);

        if (prices.length) {
          const realMax = Math.ceil(Math.max(...prices) * 1.1);
          const realMin = Math.floor(Math.min(...prices) * 0.95);
          setPriceRangeMax(realMax); setMaxPrice(realMax); setMinPrice(realMin);
        }
      }
      
setDatePriceMap((prev) => {
  const prices = flights
    .map((f) => {
      const recommendedFare =
        f.fareOptions?.find((fare) => fare.recommended) ||
        f.fareOptions?.[0] ||
        null;

      if (Number(f.displayPrice) > 0) {
        return Number(f.displayPrice);
      }

      if (Number(recommendedFare?.displayPrice) > 0) {
        return Number(recommendedFare.displayPrice);
      }

      return Number(f.price) > 0
        ? Math.round(Number(f.price) * mult)
        : null;
    })
    .filter((price) => Number(price) > 0);

  const next = {
    ...prev,
    [datePriceKey(sm, sm.departDate)]:
      prices.length ? Math.min(...prices) : null,
  };

  datePriceMapRef.current = next;
  return next;
});


    } catch (err) {
      if (err.name === "AbortError") return;
      setRawFlights([]);
      setApiError(
        err.message?.includes("fetch") || err.message?.includes("network")
          ? "Could not connect to flight search service. Please check your connection and try again."
          : `Search failed: ${err.message || "Unknown error"}. Please try again.`
      );
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
      }
      searchInFlightRef.current = false;
    }
  }, [activeFare, datePriceKey, navigate]);

  const doMultiCitySearch = useCallback(async () => {
    const todayIso = toIsoDate(new Date().toISOString().slice(0, 10));

    for (let i = 0; i < multiCityLegs.length; i += 1) {
      const leg = multiCityLegs[i];
      const fromCode = String(leg.fromCode || "").trim().toUpperCase();
const toCode = String(leg.toCode || "").trim().toUpperCase();

      
if (!fromCode || !toCode) {
  setFormMessage(
    `Please select departure and arrival city for Leg ${i + 1}.`
  );
  return;
}


      if (fromCode === toCode) {
        setFormMessage(
          `Departure and arrival city cannot be the same for Leg ${i + 1}.`
        );
        return;
      }

      if (!leg.departDate) {
        setFormMessage(
          `Please select a departure date for Leg ${i + 1}.`
        );
        return;
      }

      if (leg.departDate < todayIso) {
        setFormMessage(
          `Leg ${i + 1} departure date cannot be in the past.`
        );
        return;
      }

      if (
        i > 0 &&
        leg.departDate <= multiCityLegs[i - 1].departDate
      ) {
        setFormMessage(
          `Leg ${i + 1} departure date must be after Leg ${i}.`
        );
        return;
      }
    }
    setLoading(true); setApiError(""); setFormMessage(""); setSearched(true);

    try {
      // Booking_Type 3 backend flow: ONE call carries every leg and the
      // supplier answers with a single shared Search_Key. The response is
      // regrouped per leg into the same { leg, flights } rows this UI has
      // always consumed — selection, tabs and totals stay untouched.
      const res = await fetch(`${API_BASE}/flight-search/multi-city`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          legs: multiCityLegs.map((leg) => ({
  fromCode: String(leg.fromCode || "").trim().toUpperCase(),
  toCode: String(leg.toCode || "").trim().toUpperCase(),
  departDate: leg.departDate,
})),
          classType: toApiClassType(meta.classType),
          adults: meta.adults || 1, children: meta.children || 0, infants: meta.infants || 0,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setApiError(data?.message || data?.error || "Could not fetch multi-city flights. Please try again.");
        setMultiCityResults([]);
        return;
      }
      const results = (Array.isArray(data.legs) ? data.legs : [])
        .map((legResult) => {
          const leg = multiCityLegs[legResult.index];
          if (!leg) return null;
          return { leg, flights: Array.isArray(legResult.flights) ? legResult.flights : [] };
        })
        .filter(Boolean);
      setMultiCityResults(results);
      setActiveLegTab(0);
      setMultiCitySelections(() => {
  const next = {};

  results.forEach(({ leg, flights: legFlights }) => {
          const autoLegOptions =
            filterStopsForAutoSelect(legFlights);

                    const cheapest = [...autoLegOptions]
            .filter((f) => Number(f.displayPrice ?? f.price) > 0)
            .sort(
              (a, b) =>
                Number(a.displayPrice ?? a.price) -
                Number(b.displayPrice ?? b.price)
            )[0];
          if (cheapest) next[leg.id] = buildDefaultLegSelection(cheapest, leg.departDate);
        });
        return next;
      });
    } catch {
      setApiError("Could not fetch multi-city flights. Please try again.");
      setMultiCityResults([]);
    } finally {
      setLoading(false);
    }
  }, [multiCityLegs, activeFare, meta.adults, meta.children, meta.infants, meta.classType]);


  useEffect(() => {
    if (!airportsReady) return;
    if (__bookingInProgress) return;
    if (initialSearchDoneRef.current) return;

    initialSearchDoneRef.current = true;

    if (meta.tripType === "Multi City") {
      doMultiCitySearch();
      return;
    }

    doSearch(meta);
  }, [airportsReady, meta.tripType, doMultiCitySearch, doSearch]);


  useEffect(() => {
    if (true) return;
    if (!airportsReady) return;
    if (!meta.fromCode || !meta.toCode) return;
    if (meta.fromCode === meta.toCode) return;
    if (loading) return;
    if (__bookingInProgress) return;

    let cancelled = false;
    const dates = Array.from({ length: 7 }, (_, i) => addDays(dateStripStart, i));
    const missingDates = dates.filter((date) => {
      if (date === activeStripDate) return false;
      const key = [meta.fromCode, meta.toCode, date, meta.tripType || "One Way", activeFare].join("|");
      return !(key in datePriceMapRef.current) && !datePriceLoadingRef.current.has(key);
    });

    missingDates.forEach(async (date, batchIdx) => {
      await new Promise((r) => setTimeout(r, 6000 + batchIdx * 4000));
      if (cancelled || __bookingInProgress) return;

      const key = [meta.fromCode, meta.toCode, date, meta.tripType || "One Way", activeFare].join("|");
      datePriceLoadingRef.current.add(key);

      try {
        const res = await fetch(`${API_BASE}/flight-search`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fromCode: meta.fromCode,
            toCode: meta.toCode,
            departDate: date,
            tripType: meta.tripType || "One Way",
            fare: activeFare,
            classType: toApiClassType(meta.classType),
            adults: 1, children: 0, infants: 0,
          }),
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        
const prices = (data.flights || [])
  .map((f) => {
    const recommendedFare =
      f.fareOptions?.find((fare) => fare.recommended) ||
      f.fareOptions?.[0] ||
      null;

    if (Number(f.displayPrice) > 0) {
      return Number(f.displayPrice);
    }

    if (Number(recommendedFare?.displayPrice) > 0) {
      return Number(recommendedFare.displayPrice);
    }

    return Number(f.price) > 0
      ? Math.round(Number(f.price) * mult)
      : null;
  })
  .filter((price) => Number(price) > 0);

if (cancelled) return;

setDatePriceMap((prev) => {
  const next = {
    ...prev,
    [key]: prices.length ? Math.min(...prices) : null,
  };

  datePriceMapRef.current = next;
  return next;
});

      } catch {
        if (cancelled) return;
        setDatePriceMap((prev) => {
          const next = { ...prev, [key]: null };
          datePriceMapRef.current = next;
          return next;
        });
      } finally {
        datePriceLoadingRef.current.delete(key);
      }
    });

    return () => { cancelled = true; };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airportsReady, meta.fromCode, meta.toCode, meta.tripType, dateStripStart, loading, activeFare]);


  useEffect(() => {
    if (!loading && rawFlights.length > 0) setTimeout(() => window.AOS?.refresh(), 100);
  }, [loading, rawFlights]);

  const handleMetaChange = (u) => {
    setMeta((m) => ({
      ...m,
      ...u,
    }));
  };
  const handleSwap = () => setMeta((m) => ({ ...m, from: m.to, fromCode: m.toCode, to: m.from, toCode: m.fromCode }));

  const handleDateChange = (field, val) => {
    if (!val) return;
    setReturnDateError("");

    if (field === "departDate") {
      const validDate = val < today ? today : val;

      const nextReturn =
        meta.returnDate >= validDate
          ? meta.returnDate
          : validDate;

      setDateStripStart(validDate);
      setActiveStripDate(validDate);

      setMeta((m) => ({
        ...m,
        departDate: validDate,
        returnDate: nextReturn,
      }));

      return;
    }

    const depIso = toIsoDate(meta.departDate);

    const nextReturn =
      val < depIso
        ? depIso
        : val;

    setMeta((m) => ({
      ...m,
      returnDate: nextReturn,
    }));
  };

  const toggleFilter = (setter, id) => setter((p) => ({ ...p, [id]: !p[id] }));

  const resetAllFilters = () => {
    setCheckedFilters({}); setCheckedAirlines({}); setCheckedDepAirports({});
    setCheckedArrAirports({}); setActiveStop(null); setActiveSort("cheapest");
    setActiveFare("regular"); setMaxPrice(priceRangeMax);
  };

  const mult = 1;
  const paxLabel = PAX_TYPE_LABELS[activeFare] || "adult";

  const sidebarFilterPrices = useMemo(() => {
  if (!rawFlights.length) return {};
  

const priceOf = (flights) => {
  const prices = flights
    .map((f) => {
      const recommendedFare =
        f.fareOptions?.find((fare) => fare.recommended) ||
        f.fareOptions?.[0] ||
        null;

      const value =
        f.displayPrice ??
        recommendedFare?.displayPrice ??
        f.price;

      const price = Number(value);

      return Number.isFinite(price) && price > 0
        ? price
        : null;
    })
    .filter((price) => price !== null);

  return prices.length ? Math.min(...prices) : null;
};


    return {
      nonstop: priceOf(rawFlights.filter((f) => matchesStopFilter(f, "nonstop"))),
      onestop: priceOf(rawFlights.filter((f) => matchesStopFilter(f, "onestop"))),
      nearby: priceOf(rawFlights),
      refundable: priceOf(rawFlights.filter((f) => f.refundable)),
      morning: priceOf(rawFlights.filter((f) => getHour(f.departTime) < 12)),
      afternoon: priceOf(rawFlights.filter((f) => { const h = getHour(f.departTime); return h >= 12 && h < 17; })),
      evening: priceOf(rawFlights.filter((f) => getHour(f.departTime) >= 17)),
      airindiaexpress: priceOf(rawFlights.filter((f) => f.airlineLogo === "IX")),
    };
  }, [rawFlights, mult]);

  
const sidebarAirlinePrices = useMemo(() => {
  if (!rawFlights.length) return {};

  const map = {};

  rawFlights.forEach((f) => {
    const recommendedFare =
      f.fareOptions?.find((fare) => fare.recommended) ||
      f.fareOptions?.[0] ||
      null;

    const price =
      Number(f.displayPrice) > 0
        ? Number(f.displayPrice)
        : Number(recommendedFare?.displayPrice) > 0
          ? Number(recommendedFare.displayPrice)
          : Number(f.price) > 0
            ? Math.round(Number(f.price) * mult)
            : null;

    if (!(price > 0)) return;

    if (!map[f.airlineLogo] || price < map[f.airlineLogo]) {
      map[f.airlineLogo] = price;
    }
  });

  return map;
}, [rawFlights, mult]);



  const uniqueAirlines = useMemo(() => {
    if (!rawFlights.length) return [];
    return [...new Map(rawFlights.map((f) => [f.airlineLogo, { logo: f.airlineLogo, label: f.airline }])).values()];
  }, [rawFlights]);

  const airportNameByCode = useMemo(() => {
    const map = new Map();
    airports.forEach((a) => { if (a.code) map.set(a.code, airportLabel(a)); });
    return map;
  }, [airports]);

  const uniqueDepAirports = useMemo(() => {
    if (!rawFlights.length) return [];
    const byCode = new Map();
    rawFlights.forEach((f) => {
      if (!f.departCode || byCode.has(f.departCode)) return;
      byCode.set(f.departCode, {
        id: f.departCode.toLowerCase(),
        label: airportNameByCode.get(f.departCode) || f.departAirportFull || f.departCode,
        code: f.departCode,
      });
    });
    return [...byCode.values()];
  }, [rawFlights, airportNameByCode]);

  
const sidebarDepAirportPrices = useMemo(() => {
  if (!rawFlights.length) return {};

  const map = {};

  rawFlights.forEach((f) => {
    const recommendedFare =
      f.fareOptions?.find((fare) => fare.recommended) ||
      f.fareOptions?.[0] ||
      null;

    const price =
      Number(f.displayPrice) > 0
        ? Number(f.displayPrice)
        : Number(recommendedFare?.displayPrice) > 0
          ? Number(recommendedFare.displayPrice)
          : Number(f.price) > 0
            ? Math.round(Number(f.price) * mult)
            : null;

    if (!(price > 0)) return;

    if (!map[f.departCode] || price < map[f.departCode]) {
      map[f.departCode] = price;
    }
  });

  return map;
}, [rawFlights, mult]);


  const uniqueArrAirports = useMemo(() => {
    if (!rawFlights.length) return [];
    const byCode = new Map();
    rawFlights.forEach((f) => {
      if (!f.arriveCode || byCode.has(f.arriveCode)) return;
      byCode.set(f.arriveCode, {
        id: f.arriveCode.toLowerCase(),
        label: airportNameByCode.get(f.arriveCode) || f.arriveAirportFull || f.arriveCode,
        code: f.arriveCode,
      });
    });
    return [...byCode.values()];
  }, [rawFlights, airportNameByCode]);

  
const sidebarArrAirportPrices = useMemo(() => {
  if (!rawFlights.length) return {};

  const map = {};

  rawFlights.forEach((f) => {
    const recommendedFare =
      f.fareOptions?.find((fare) => fare.recommended) ||
      f.fareOptions?.[0] ||
      null;

    const price =
      Number(f.displayPrice) > 0
        ? Number(f.displayPrice)
        : Number(recommendedFare?.displayPrice) > 0
          ? Number(recommendedFare.displayPrice)
          : Number(f.price) > 0
            ? Math.round(Number(f.price) * mult)
            : null;

    if (!(price > 0)) return;

    if (!map[f.arriveCode] || price < map[f.arriveCode]) {
      map[f.arriveCode] = price;
    }
  });

  return map;
}, [rawFlights, mult]);


  const processFlights = useCallback((list) => {
    let l = list.map((f) => ({
      ...f,
      basePrice: f.basePrice ?? f.price,

      // Prefer the admin-adjusted price returned by the API.
      displayPrice:
        Number(f.displayPrice) > 0
          ? Number(f.displayPrice)
          : Number(f.price) > 0
            ? Math.round(Number(f.price) * mult)
            : null,

      fareOptions: Array.isArray(f.fareOptions)
        ? f.fareOptions.map((fare) => {
          const baseFare = fare.basePrice ?? fare.price;

          return {
            ...fare,
            basePrice: baseFare,

            // Keep the existing supplier-fare processing intact.
            price:
              Number(baseFare) > 0
                ? Math.round(Number(baseFare) * mult)
                : fare.price,

            // Use the admin-adjusted fare price when provided.
            displayPrice:
              Number(fare.displayPrice) > 0
                ? Number(fare.displayPrice)
                : Number(baseFare) > 0
                  ? Math.round(Number(baseFare) * mult)
                  : fare.price,
          };
        })
        : f.fareOptions,
    }));

    // The supplier occasionally returns the exact same flight (same id/flightKey)
    // more than once in a single search response. React needs unique keys, and
    // showing the same flight twice is confusing, so keep only the first copy.
    {
      const seenIds = new Set();
      const seenIdentity = new Set();
      l = l.filter((f) => {
        if (seenIds.has(f.id)) return false;
        seenIds.add(f.id);
        const hasUpfrontFare = (f.fareOptions || []).some((fo) => fo.isUpfront);
        const identityKey = [
          f.airlineLogo, f.flightNumbers, f.departTime, f.arriveTime,
          hasUpfrontFare ? "upfront" : "std",
        ].join("|");
        if (seenIdentity.has(identityKey)) return false;
        seenIdentity.add(identityKey);
        return true;
      });
    }

    l = l.filter((f) => f.displayPrice == null || f.displayPrice <= maxPrice);
    if (timeRange[0] > 0 || timeRange[1] < 24) {
      l = l.filter((f) => {
        if (!f.departTime || f.departTime === "--:--") return true;
        const h = getHour(f.departTime);
        return h >= timeRange[0] && h < timeRange[1];
      });
    }
    if (activeStop === "nonstop") l = l.filter((f) => matchesStopFilter(f, "nonstop"));
    else if (activeStop === "onestop") l = l.filter((f) => matchesStopFilter(f, "onestop"));
    else if (activeStop === "twostop") l = l.filter((f) => matchesStopFilter(f, "twostop"));

    const activeAirlines = Object.keys(checkedAirlines).filter((k) => checkedAirlines[k]);
    if (activeAirlines.length) l = l.filter((f) => activeAirlines.includes(f.airlineLogo));

    const depCodes = Object.keys(checkedDepAirports).filter((k) => checkedDepAirports[k]).map((id) => id.toUpperCase());
    if (depCodes.length) l = l.filter((f) => depCodes.includes(f.departCode));

    const arrCodes = Object.keys(checkedArrAirports).filter((k) => checkedArrAirports[k]).map((id) => id.toUpperCase());
    if (arrCodes.length) l = l.filter((f) => arrCodes.includes(f.arriveCode));

    const active = Object.keys(checkedFilters).filter((k) => checkedFilters[k]);
    active.forEach((id) => {
      if (id === "nonstop") l = l.filter((f) => matchesStopFilter(f, "nonstop"));
      if (id === "onestop") l = l.filter((f) => matchesStopFilter(f, "onestop"));
      if (id === "refundable") l = l.filter((f) => f.refundable);
      if (id === "morning") l = l.filter((f) => getHour(f.departTime) < 12);
      if (id === "afternoon") l = l.filter((f) => { const h = getHour(f.departTime); return h >= 12 && h < 17; });
      if (id === "evening") l = l.filter((f) => getHour(f.departTime) >= 17);
      if (id === "airindiaexpress") l = l.filter((f) => f.airlineLogo === "IX");
      if (id === "nearby") l = l.filter((f) => !["GZB", "NMB"].includes(f.departCode));
    });

    const priceSort = (a, b) => (a.displayPrice ?? Number.MAX_SAFE_INTEGER) - (b.displayPrice ?? Number.MAX_SAFE_INTEGER);
    if (activeSort === "cheapest") l.sort(priceSort);
    else if (activeSort === "nonstop") l.sort((a, b) => a.stops - b.stops || priceSort(a, b));
    else if (activeSort === "prefer") l.sort((a, b) => ((a.displayPrice ?? 999999) + a.stops * 500) - ((b.displayPrice ?? 999999) + b.stops * 500));
    else if (activeSort === "other") l.sort((a, b) => a.departTime.localeCompare(b.departTime));
    return l;
  }, [activeSort, activeStop, mult, checkedAirlines, checkedArrAirports, checkedDepAirports, checkedFilters, maxPrice, timeRange]);

  const sortedFlights = useMemo(() => processFlights(rawFlights), [rawFlights, processFlights]);

  const totalFlightPages = Math.max(1, Math.ceil(sortedFlights.length / FLIGHTS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalFlightPages);
  const paginatedFlights = useMemo(() => {
    const start = (safeCurrentPage - 1) * FLIGHTS_PER_PAGE;
    return sortedFlights.slice(start, start + FLIGHTS_PER_PAGE);
  }, [sortedFlights, safeCurrentPage]);

  const visibleFlightStart = sortedFlights.length
    ? (safeCurrentPage - 1) * FLIGHTS_PER_PAGE + 1
    : 0;
  const visibleFlightEnd = Math.min(safeCurrentPage * FLIGHTS_PER_PAGE, sortedFlights.length);
  const specialRoundTripSummary = useCallback((flight) => {
    const split = splitSpecialRoundTripSegments(flight);
    if (!split.outbound.length || !split.returnSegments.length) return [];
    return [
      summarizeSegmentDirection(split.outbound, "Outbound", meta.departDate),
      summarizeSegmentDirection(split.returnSegments, "Return", meta.returnDate),
    ];
  }, [meta.departDate, meta.returnDate]);

  const buildSpecialRoundTripDisplayLegs = useCallback((flight) => {
    const segments = Array.isArray(flight?.segments) ? flight.segments : [];
    if (segments.length < 2) return [];

    let outbound = segments.filter(
      (segment) =>
        segment.returnFlight === false ||
        String(segment.returnFlight).toLowerCase() === "false"
    );
    let returnSegments = segments.filter(
      (segment) =>
        segment.returnFlight === true ||
        String(segment.returnFlight).toLowerCase() === "true"
    );

    if (!outbound.length || !returnSegments.length) {
      outbound = segments.filter((segment) => Number(segment.legIndex) === 0);
      returnSegments = segments.filter((segment) => Number(segment.legIndex) === 1);
    }

    const makeLeg = (label, legSegments, legIndex, fallbackDate) => {
      const first = legSegments[0] || {};
      const last = legSegments[legSegments.length - 1] || first;
      const selectedFare = flight.selectedFare || flight.fareOptions?.[flight.selectedFareIndex ?? 0] || flight.fareOptions?.[0] || null;
      return {
        ...flight,
        specialRoundTripDisplayLeg: true,
        label,
        legIndex,
        id: `${flight.id || flight.flightKey || "special-rt"}-${label.toLowerCase()}`,
        departCode: first.origin || flight.departCode,
        arriveCode: last.destination || flight.arriveCode,
        departAirport: first.origin || flight.departAirport,
        arriveAirport: last.destination || flight.arriveAirport,
        departTime: sliceTime(first.departureDateTime) || flight.departTime,
        arriveTime: sliceTime(last.arrivalDateTime) || flight.arriveTime,
        travelDate: first.departureDateTime ? String(first.departureDateTime).slice(0, 10) : fallbackDate,
        flightNumbers: legSegments.map((segment) => segment.flightNo).filter(Boolean).join(", "),
        stops: Math.max(0, legSegments.length - 1),
        duration: legSegments.length > 1 ? `${legSegments.length - 1} stop${legSegments.length - 1 === 1 ? "" : "s"}` : "Non Stop",
        segmentId: first.segmentId ?? null,
        segments: legSegments,
        selectedFareIndex: flight.selectedFareIndex ?? 0,
        selectedFare,
        supplierRequestId: selectedFare?.supplierRequestId || flight.supplierRequestId || null,
      };
    };

    if (!outbound.length || !returnSegments.length) return [];
    const outboundSummary = summarizeSegmentDirection(outbound, "Outbound", meta.departDate);
    const returnSummary = summarizeSegmentDirection(returnSegments, "Return", meta.returnDate);
    return [
      { ...makeLeg("Outbound", outbound, 0, meta.departDate), duration: outboundSummary.duration, stops: outboundSummary.stops, stopDetail: outboundSummary.stopText },
      { ...makeLeg("Return", returnSegments, 1, meta.returnDate), duration: returnSummary.duration, stops: returnSummary.stops, stopDetail: returnSummary.stopText },
    ];
  }, [meta.departDate, meta.returnDate]);

  useEffect(() => {
    setCurrentPage(1);
    setOutboundPage(1);
    setReturnPage(1);
  }, [
    rawFlights,
    activeSort,
    activeFare,
    checkedFilters,
    checkedAirlines,
    checkedDepAirports,
    checkedArrAirports,
    activeStop,
    maxPrice,
    timeRange,
  ]);

  const goToFlightPage = (page) => {
    const nextPage = Math.min(Math.max(1, page), totalFlightPages);
    setCurrentPage(nextPage);
    requestAnimationFrame(() => {
      document.querySelector(".flights-results-head")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const sortedReturnFlights = useMemo(() => {
    const flights = processFlights(returnFlights);

    if (!selectedOutboundLeg) return flights;

    const outboundDate =
      selectedOutboundLeg.travelDate || meta.departDate;

    const returnDate = meta.returnDate;

    // Different day → all return flights for that date are valid.
    if (returnDate !== outboundDate) {
      return flights;
    }

    const outboundArrivalTime = selectedOutboundLeg.arriveTime;

    if (!outboundArrivalTime) {
      return flights;
    }

    const [arrHour, arrMinute] = outboundArrivalTime.split(":").map(Number);
    let outboundArrivalMinutes = arrHour * 60 + arrMinute;

    // If outbound arrives next day, no flight on the original departure
    // date can be a valid return.
    if (selectedOutboundLeg.arriveNextDay) {
      outboundArrivalMinutes += 24 * 60;
    }

    return flights.filter((flight) => {
      if (!flight.departTime) return false;

      const [depHour, depMinute] = flight.departTime.split(":").map(Number);
      const returnDepartureMinutes = depHour * 60 + depMinute;

      return returnDepartureMinutes > outboundArrivalMinutes;
    });
  }, [
    returnFlights,
    processFlights,
    selectedOutboundLeg,
    meta.departDate,
    meta.returnDate,
  ]);

  const sameFlight = (a, b) => {
    if (!a || !b) return false;

    const identity = (flight) =>
      String(
        firstDefined(
          flight.flightKey,
          flight.Flight_Key,
          flight.resultIndex,
          flight.flightId,
          flight.Flight_ID,
          flight.id,
          ""
        )
      );

    const aKey = identity(a);
    const bKey = identity(b);

    return Boolean(aKey && bKey && aKey === bKey);
  };

  const keepSelectedFlightFirst = (flights, selectedFlight) => {
    if (!selectedFlight) return flights;

    const selected = flights.find((f) =>
      sameFlight(f, selectedFlight)
    );

    // Do not force an old selection back into filtered results.
    if (!selected) return flights;

    return [
      selected,
      ...flights.filter((f) => !sameFlight(f, selected)),
    ];
  };

  // Round trip: when filters remove the currently selected outbound flight,
  // automatically select the first valid filtered/sorted outbound result.
  useEffect(() => {
    if (!isReturnTripType(meta.tripType) || isSpecialRoundTripType(meta.tripType)) return;
    if (loading) return;

    if (!sortedFlights.length) {
      if (selectedOutboundLeg) {
        setSelectedOutboundLeg(null);
      }
      return;
    }

    const selectedStillVisible =
      selectedOutboundLeg &&
      sortedFlights.some((flight) =>
        sameFlight(flight, selectedOutboundLeg)
      );

    if (selectedStillVisible) return;

    setSelectedOutboundLeg({
      ...sortedFlights[0],
      travelDate: meta.departDate,
    });

    setOutboundPage(1);
  }, [
    meta.tripType,
    meta.departDate,
    loading,
    sortedFlights,
    selectedOutboundLeg,
  ]);


  // Round trip: do the same independently for the return flight.
  useEffect(() => {
    if (!isReturnTripType(meta.tripType) || isSpecialRoundTripType(meta.tripType)) return;
    if (loading) return;

    if (!sortedReturnFlights.length) {
      if (selectedReturnLeg) {
        setSelectedReturnLeg(null);
      }
      return;
    }

    const selectedStillVisible =
      selectedReturnLeg &&
      sortedReturnFlights.some((flight) =>
        sameFlight(flight, selectedReturnLeg)
      );

    if (selectedStillVisible) return;

    setSelectedReturnLeg({
      ...sortedReturnFlights[0],
      travelDate: meta.returnDate,
    });

    setReturnPage(1);
  }, [
    meta.tripType,
    meta.returnDate,
    loading,
    sortedReturnFlights,
    selectedReturnLeg,
  ]);

  // Special round trip results are complete supplier itineraries, so keep one
  // selected combined result instead of maintaining separate outbound/return legs.
  useEffect(() => {
    if (!isSpecialRoundTripType(meta.tripType)) return;
    if (loading) return;

    if (!sortedFlights.length) {
      if (selectedSpecialRoundTrip) {
        setSelectedSpecialRoundTrip(null);
      }
      return;
    }

    const selectedStillVisible =
      selectedSpecialRoundTrip &&
      sortedFlights.some((flight) =>
        sameFlight(flight, selectedSpecialRoundTrip)
      );

    if (selectedStillVisible) return;

    setSelectedSpecialRoundTrip({
      ...sortedFlights[0],
      travelDate: meta.departDate,
    });
  }, [
    meta.tripType,
    meta.departDate,
    loading,
    sortedFlights,
    selectedSpecialRoundTrip,
  ]);

  const orderedOutboundFlights = useMemo(() => {
    return keepSelectedFlightFirst(
      sortedFlights,
      selectedOutboundLeg
    );
  }, [sortedFlights, selectedOutboundLeg]);

  const outboundTotalPages = Math.max(
    1,
    Math.ceil(orderedOutboundFlights.length / FLIGHTS_PER_PAGE)
  );

  const safeOutboundPage = Math.min(
    outboundPage,
    outboundTotalPages
  );

  const paginatedOutboundFlights = useMemo(() => {
    const start =
      (safeOutboundPage - 1) * FLIGHTS_PER_PAGE;

    return orderedOutboundFlights.slice(
      start,
      start + FLIGHTS_PER_PAGE
    );
  }, [orderedOutboundFlights, safeOutboundPage]);

  const orderedReturnFlights = useMemo(() => {
    return keepSelectedFlightFirst(
      sortedReturnFlights,
      selectedReturnLeg
    );
  }, [sortedReturnFlights, selectedReturnLeg]);

  const returnTotalPages = Math.max(
    1,
    Math.ceil(orderedReturnFlights.length / FLIGHTS_PER_PAGE)
  );

  const safeReturnPage = Math.min(
    returnPage,
    returnTotalPages
  );

  const paginatedReturnFlights = useMemo(() => {
    const start =
      (safeReturnPage - 1) * FLIGHTS_PER_PAGE;

    return orderedReturnFlights.slice(
      start,
      start + FLIGHTS_PER_PAGE
    );
  }, [orderedReturnFlights, safeReturnPage]);

  const activeLegResult = multiCityResults[activeLegTab];
  const sortedMultiCityFlights = useMemo(
    () => activeLegResult ? processFlights(activeLegResult.flights) : [],
    [activeLegResult, processFlights]
  );

  const activeMultiCitySelected =
    activeLegResult
      ? multiCitySelections[activeLegResult.leg.id]
      : null;

  const orderedMultiCityFlights = useMemo(() => {
    if (!activeMultiCitySelected) {
      return sortedMultiCityFlights;
    }

    const selected = sortedMultiCityFlights.find(
      (f) => sameFlight(f, activeMultiCitySelected)
    );

    if (!selected) return sortedMultiCityFlights;

    return [
      selected,
      ...sortedMultiCityFlights.filter(
        (f) => !sameFlight(f, selected)
      ),
    ];
  }, [
    sortedMultiCityFlights,
    activeMultiCitySelected,
  ]);

  const multiCityTotalPages = Math.max(
    1,
    Math.ceil(orderedMultiCityFlights.length / FLIGHTS_PER_PAGE)
  );

  const safeMultiCityPage = Math.min(
    multiCityPage,
    multiCityTotalPages
  );

  const paginatedMultiCityFlights = useMemo(() => {
    const start =
      (safeMultiCityPage - 1) * FLIGHTS_PER_PAGE;

    return orderedMultiCityFlights.slice(
      start,
      start + FLIGHTS_PER_PAGE
    );
  }, [orderedMultiCityFlights, safeMultiCityPage]);

  const getCompareId = useCallback((flight) => {
    if (!flight) return null;

    return (
      flight.compareId ||
      flight.id ||
      flight.flightId ||
      flight.key ||
      `${flight.airline || "flight"}-${flight.flightNumbers || flight.flightNumber || ""}-${flight.departTime || ""}-${flight.arriveTime || ""}`
    );
  }, []);

  const isSelected = useCallback(
    (id) =>
      selectedFlights.some(
        (f) => getCompareId(f) === id
      ),
    [selectedFlights, getCompareId]
  );

  const canAdd = selectedFlights.length < 3;

  const toggleCompare = useCallback(
    (flight) => {
      if (!flight) return;

      const compareId = getCompareId(flight);
      if (!compareId) return;

      setSelectedFlights((prev) => {
        const alreadySelected = prev.some(
          (f) => getCompareId(f) === compareId
        );

        if (alreadySelected) {
          return prev.filter(
            (f) => getCompareId(f) !== compareId
          );
        }

        if (prev.length >= 3) {
          return prev;
        }

        const compareItem = buildCompareItem({
          tripType: meta.tripType,
          flight,
          meta,
          searchContext: {
            fare: activeFare,
          },
        });

        if (!compareItem) {
          return prev;
        }

        return [...prev, compareItem];
      });
    },
    [activeFare, getCompareId, meta]
  );

  const openComparePage = useCallback(() => {
    if (selectedFlights.length < 2) {
      return;
    }

    const compareFlights = selectedFlights.slice(0, 3);

    const compareMeta = {
      tripType: meta.tripType,
      from: meta.from,
      fromCode: meta.fromCode,
      to: meta.to,
      toCode: meta.toCode,
      departDate: meta.departDate,
      returnDate: meta.returnDate,
      adults: Number(meta.adults || 1),
      children: Number(meta.children || 0),
      infants: Number(meta.infants || 0),
      classType: meta.classType || "",
      fare: activeFare || "regular",
      savedAt: Date.now(),
    };

    try {
      window.localStorage.setItem(
        COMPARE_STORAGE_KEY,
        JSON.stringify(compareFlights)
      );

      window.localStorage.setItem(
        COMPARE_META_KEY,
        JSON.stringify(compareMeta)
      );
    } catch {
      // Storage can fail in private browsing/quota-restricted environments.
      // Navigation should still work.
    }

    navigate("/compare");
  }, [activeFare, meta, navigate, selectedFlights]);

  const displayFilters = showAllFilters ? popularFilterDefs : popularFilterDefs.slice(0, 5);

  
const lowestFlightPrice = useMemo(() => {
  const prices = rawFlights
    .map((f) => {
      const recommendedFare =
        f.fareOptions?.find((fare) => fare.recommended) ||
        f.fareOptions?.[0] ||
        null;

      if (Number(f.displayPrice) > 0) {
        return Number(f.displayPrice);
      }

      if (Number(recommendedFare?.displayPrice) > 0) {
        return Number(recommendedFare.displayPrice);
      }

      return Number(f.price) > 0
        ? Math.round(Number(f.price) * mult)
        : null;
    })
    .filter((price) => Number(price) > 0);

  return prices.length ? Math.min(...prices) : null;
}, [rawFlights, mult]);

const dateStripOptions = useMemo(
  () =>
    Array.from({ length: 7 }, (_, i) => {
      const iso = addDays(dateStripStart, i);
      const cachedPrice = datePriceMap[datePriceKey(meta, iso)];

      const price =
        Number(cachedPrice) > 0
          ? Number(cachedPrice)
          : iso === activeStripDate
            ? lowestFlightPrice
            : null;

      return {
        iso,
        date: formatStripDate(iso),
        price,
      };
    }),
  [
    activeStripDate,
    datePriceKey,
    datePriceMap,
    dateStripStart,
    lowestFlightPrice,
    meta,
  ]
);


  const handleDateStripSelect = (option) => {
    setActiveStripDate(option.iso); setSelectedFlights([]);
    const nextMeta = { ...meta, departDate: option.iso };
    setMeta(nextMeta); doSearch(nextMeta);
  };

  const shiftDateStrip = (days) => setDateStripStart((cur) => {
    const next = addDays(cur, days);
    return next < today ? today : next;
  });

  return (
    <div className="flights-page">
      <SearchBar
        meta={meta} airports={airports} airportsLoading={airportsLoading}
        onSwap={handleSwap} onMetaChange={handleMetaChange}
        onDateChange={handleDateChange}
        onSearch={() => meta.tripType === "Multi City" ? doMultiCitySearch() : doSearch(meta)}
        formMessage={formMessage}
        returnDateError={returnDateError}
        activeFare={activeFare} onFareChange={setActiveFare}
        loading={loading}
        multiCityLegs={multiCityLegs}
        onAddLeg={addMultiCityLeg}
        onRemoveLeg={removeMultiCityLeg}
        onUpdateLeg={updateMultiCityLeg}
      />

      <div className="flights-layout">
        <div className={`flights-mobile-filter-bar${mobileFiltersOpen ? " is-open" : ""}`}>
          <button
            type="button"
            className="flights-mobile-filter-toggle"
            onClick={() => setMobileFiltersOpen((open) => !open)}
            aria-expanded={mobileFiltersOpen}
          >
            <BarChart3 size={15} />
            <span>{mobileFiltersOpen ? "Hide filters" : "Show filters"}</span>
            {activeFilterCount > 0 && <b>{activeFilterCount}</b>}
          </button>
          {activeFilterCount > 0 && (
            <button type="button" className="flights-mobile-filter-clear" onClick={resetAllFilters}>
              Clear
            </button>
          )}
        </div>

        {/* SIDEBAR */}
        <aside className={`flights-sidebar ${isMobileFilters && !mobileFiltersOpen ? "is-mobile-hidden" : ""}`}>
          <FilterSection title="Popular Filters" onReset={() => setCheckedFilters({})} initialOpen={!isMobileFilters}>
            <div style={{ padding: "5px 0" }}>
              {displayFilters.map((f) => (
                <CheckboxRow key={f.id} checked={!!checkedFilters[f.id]} onToggle={() => toggleFilter(setCheckedFilters, f.id)} label={f.label} price={sidebarFilterPrices[f.id]} />
              ))}
            </div>
            <div onClick={() => setShowAllFilters((s) => !s)} style={{ marginLeft: "5px", textAlign: "center", fontSize: "10px" }}>
              {showAllFilters ? "Show less ▲" : `Show more ▼ (${popularFilterDefs.length - 5} more)`}
            </div>
          </FilterSection>

          <FilterSection title="Stops" onReset={() => setActiveStop(null)} initialOpen={!isMobileFilters}>
            <div className="flight-stop-filter-pills" style={{ padding: "9px 14px", display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[{ id: "nonstop", label: "Non Stop" }, { id: "onestop", label: "1 Stop" }, { id: "twostop", label: "2+ Stops" }].map((s) => (
                <div key={s.id} className="flight-stop-filter-pill" onClick={() => setActiveStop((p) => (p === s.id ? null : s.id))}
                  style={{ padding: "5px 12px", borderRadius: 20, border: `1.5px solid ${activeStop === s.id ? "#10b981" : "#cbd5e1"}`, fontSize: 12, cursor: "pointer", fontWeight: activeStop === s.id ? 700 : 400, background: activeStop === s.id ? "#f0fdf4" : "transparent", color: activeStop === s.id ? "#047857" : "#475569", transition: "all 0.15s" }}>
                  {s.label}
                  {rawFlights.length > 0 && (
                    <span style={{ fontSize: 9.5, color: "#94a3b8", marginLeft: 3 }}>
                      ({rawFlights.filter((f) => matchesStopFilter(f, s.id)).length})
                    </span>
                  )}
                </div>
              ))}
            </div>
          </FilterSection>

          <FilterSection title="Price Range" onReset={() => setMaxPrice(priceRangeMax)} initialOpen={!isMobileFilters}>
            <div className="flight-price-range-filter" style={{ padding: "11px 14px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "#64748b", marginBottom: 7 }}>
                <span>{rawFlights.length ? fmt(minPrice) : "₹5,000"}</span>
                <span style={{ color: "#047857", fontWeight: 700 }}>{fmt(maxPrice)}</span>
              </div>
              <input type="range" min={minPrice || 0} max={priceRangeMax} step={Math.max(100, Math.round(priceRangeMax / 200))} value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value))} style={{ width: "100%", accentColor: "#10b981" }} />
              <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 5, textAlign: "center" }}>
                {rawFlights.length ? `${sortedFlights.length} of ${rawFlights.length} flights match` : "Search to see prices"}
              </div>
            </div>
          </FilterSection>
          <FilterSection title="Departure Time Range" onReset={() => setTimeRange([0, 24])} initialOpen={!isMobileFilters}>
            <div className="time-range-filter">
              <div className="time-range-labels">
                <span>{String(timeRange[0]).padStart(2, "0")}:00</span>
                <strong>{String(timeRange[1]).padStart(2, "0")}:00</strong>
              </div>
              <div className="time-range-track" style={{
                // Pass values to CSS instead of using inline styles on the fill div
                // (keeps React render cheap and moves layout logic to CSS)
                // eslint-disable-next-line react/forbid-dom-props
                '--time-range-start': `${timeRange[0]}`,
                // eslint-disable-next-line react/forbid-dom-props
                '--time-range-end': `${timeRange[1]}`,
              }}>
                <div className="time-range-fill" />
                <input
                  type="range" min={0} max={24} step={1}
                  value={timeRange[0]}
                  onChange={(e) => {
                    const v = Math.min(Number(e.target.value), timeRange[1] - 1);
                    setTimeRange([v, timeRange[1]]);
                  }}
                />
                <input
                  type="range" min={0} max={24} step={1}
                  value={timeRange[1]}
                  onChange={(e) => {
                    const v = Math.max(Number(e.target.value), timeRange[0] + 1);
                    setTimeRange([timeRange[0], v]);
                  }}
                />
              </div>
              <div className="time-range-ticks">
                <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>12am</span>
              </div>
              <div className="time-range-match">
                {rawFlights.length ? `${sortedFlights.length} of ${rawFlights.length} flights match` : "Search to see times"}
              </div>
            </div>
          </FilterSection>
          <FilterSection title="Airlines" onReset={() => setCheckedAirlines({})} initialOpen={!isMobileFilters}>
            <div style={{ padding: "5px 0" }}>
              {uniqueAirlines.length === 0
                ? <div style={{ padding: "11px 14px", fontSize: 12, color: "#94a3b8" }}>Search flights to see airlines</div>
                : uniqueAirlines.map((a) => (
                  <CheckboxRow key={a.logo} checked={!!checkedAirlines[a.logo]} onToggle={() => toggleFilter(setCheckedAirlines, a.logo)}
                    label={`${a.label} (${rawFlights.filter((f) => f.airlineLogo === a.logo).length})`}
                    price={sidebarAirlinePrices[a.logo]} />
                ))}
            </div>
          </FilterSection>

          <FilterSection title="Departure Airports" onReset={() => setCheckedDepAirports({})} initialOpen={!isMobileFilters}>
            <div style={{ padding: "5px 0" }}>
              {uniqueDepAirports.map((a) => (
                <CheckboxRow key={a.id} checked={!!checkedDepAirports[a.id]} onToggle={() => toggleFilter(setCheckedDepAirports, a.id)}
                  label={`${a.label} (${rawFlights.filter((f) => f.departCode === a.code).length})`}
                  price={sidebarDepAirportPrices[a.code]} />
              ))}
            </div>
          </FilterSection>
          <FilterSection title="Arrival Airports" onReset={() => setCheckedArrAirports({})} initialOpen={!isMobileFilters}>
            <div style={{ padding: "5px 0" }}>
              {uniqueArrAirports.length === 0
                ? <div style={{ padding: "11px 14px", fontSize: 12, color: "#94a3b8" }}>Search flights to see arrival airports</div>
                : uniqueArrAirports.map((a) => (
                  <CheckboxRow key={a.id} checked={!!checkedArrAirports[a.id]} onToggle={() => toggleFilter(setCheckedArrAirports, a.id)}
                    label={`${a.label} (${rawFlights.filter((f) => f.arriveCode === a.code).length})`}
                    price={sidebarArrAirportPrices[a.code]} />
                ))}
            </div>
          </FilterSection>
          <FilterSection title="Departure Time" onReset={() => setCheckedFilters((p) => { const next = { ...p };["morning", "afternoon", "evening"].forEach((k) => delete next[k]); return next; })} initialOpen={!isMobileFilters}>
            <div className="flight-departure-time-filter" style={{ padding: "9px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
              {[
                { id: "morning", label: "Morning", sub: "Before 12pm", icon: <Sunrise size={15} color="#f59e0b" /> },
                { id: "afternoon", label: "Afternoon", sub: "12pm – 5pm", icon: <Sun size={15} color="#f97316" /> },
                { id: "evening", label: "Evening", sub: "After 5pm", icon: <Sunset size={15} color="#8b5cf6" /> },
              ].map((slot) => {
                const count = rawFlights.filter((f) =>
                  slot.id === "morning" ? getHour(f.departTime) < 12 :
                    slot.id === "afternoon" ? getHour(f.departTime) >= 12 && getHour(f.departTime) < 17 :
                      getHour(f.departTime) >= 17
                ).length;
                return (
                  <div key={slot.id} className="flight-departure-time-option" onClick={() => toggleFilter(setCheckedFilters, slot.id)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", borderRadius: 10, border: `1.5px solid ${checkedFilters[slot.id] ? "#10b981" : "#e2e8f0"}`, background: checkedFilters[slot.id] ? "#f0fdf4" : "#f8fafc", cursor: "pointer", transition: "all 0.15s" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                      {slot.icon}
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: checkedFilters[slot.id] ? "#047857" : "#0d1b3e" }}>{slot.label}</div>
                        <div style={{ fontSize: 10, color: "#94a3b8" }}>{slot.sub} · {count} flights</div>
                      </div>
                    </div>
                    {sidebarFilterPrices[slot.id] != null && (
                      <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{fmt(sidebarFilterPrices[slot.id])}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </FilterSection>
        </aside>

        {/* Main results */}
        <main className="flights-results">
          {/* Date strip */}
          <div className="flights-date-strip" data-aos="fade-up" data-aos-delay="50">
            <div className="flights-date-head">
              <div className="flights-date-head-icon"><CalendarDays size={16} /></div>
              <div>
                <div className="flights-date-head-title">Choose your departure date</div>
                <div className="flights-date-head-sub">See lowest fares for nearby dates</div>
              </div>
              <div className="flights-date-picker-wrap" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  className="flights-date-picker" type="date" value={activeStripDate} min={today}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const val = e.target.value < today ? today : e.target.value;
                    setActiveStripDate(val); setDateStripStart(val); setSelectedFlights([]);
                    const nextMeta = { ...meta, departDate: val };
                    setMeta(nextMeta); doSearch(nextMeta);
                  }}
                  style={{ border: "1.5px solid rgba(16,185,129,0.22)", borderRadius: 10, padding: "8px 12px", fontFamily: "'Syne',sans-serif", fontSize: 13, color: "#0d1b3e", background: "#fff", outline: "none" }}
                />
              </div>
            </div>
            <div className="flights-date-navigation" style={{ display: "flex", alignItems: "center" }}>
              <button className="flights-date-nav-btn" type="button" onClick={() => shiftDateStrip(-7)} disabled={dateStripStart <= today}
                style={{ padding: "9px 10px", cursor: dateStripStart <= today ? "not-allowed" : "pointer", color: dateStripStart <= today ? "#cbd5e1" : "#64748b", background: "none", border: "none", fontSize: 20, flexShrink: 0 }}>&lt;</button>
              <div className="flights-date-list">
                {dateStripOptions.map((d) => {
                  const isActiveDate = d.iso === activeStripDate;
                  return (
                    <button type="button" className="flights-date-option" key={d.iso}
                      onClick={() => handleDateStripSelect(d)}
                      style={{ borderBottom: isActiveDate ? "3px solid #10b981" : "3px solid transparent", background: isActiveDate ? "rgba(16,185,129,0.06)" : "transparent" }}
                      aria-pressed={isActiveDate}>
                      {isActiveDate && d.price != null && <div className="flights-best-price">LOWEST</div>}
                      <div style={{ fontSize: 11, fontWeight: 600, color: isActiveDate ? "#047857" : "#64748b", whiteSpace: "nowrap" }}>{d.date}</div>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: isActiveDate ? "#047857" : "#0d1b3e", marginTop: 3, fontFamily: "'Syne',sans-serif" }}>
                        {d.price != null ? fmt(d.price) : <span style={{ color: "#94a3b8", fontSize: 11 }}>—</span>}
                      </div>
                      <div className="flights-date-low">{d.price ? `per ${paxLabel}` : ""}</div>
                    </button>
                  );
                })}
              </div>
              <button className="flights-date-nav-btn" type="button" onClick={() => shiftDateStrip(7)} style={{ padding: "9px 10px", cursor: "pointer", color: "#64748b", background: "none", border: "none", fontSize: 20, flexShrink: 0 }}>&gt;</button>
            </div>
          </div>

          {/* Results header */}
          <div className="flights-results-head" data-aos="fade-up" data-aos-delay="100">
            <div className="flights-results-title-row">
              <span className="flights-results-plane"><Plane size={16} /></span>
              <span style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 700 }}>
                Flights: {meta.fromCode} → {meta.toCode}
                {meta.departDate && (
                  <span className="flights-results-date" style={{ fontSize: 13, color: "#64748b", marginLeft: 8, fontWeight: 400 }}>
                    - {new Date(`${meta.departDate}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                )}
                {!loading && searched && (
                  <span className="flights-results-count" style={{ fontSize: 12.5, color: "#10b981", marginLeft: 6, fontWeight: 700 }}>
                    ({sortedFlights.length} found)
                  </span>
                )}
              </span>
            </div>
            <div className="flights-results-actions" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {activeFare !== "regular" && (
                <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 20, padding: "5px 12px", fontSize: 11.5, color: "#92400e", fontWeight: 700 }}>
                  Showing {fareTypes.find(f => f.id === activeFare)?.label} fares
                </div>
              )}
              <button onClick={resetAllFilters} style={{ border: "1px solid rgba(15,23,42,0.1)", background: "#fff", color: "#64748b", borderRadius: 20, padding: "7px 13px", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>Reset all</button>
            </div>
          </div>



          {/* Sort bar */}
          <div className="flights-sort-strip" data-aos="fade-up" data-aos-delay="120">
            {sortOptions.map((o) => (
              <div key={o.id} className={`flights-sort-option${activeSort === o.id ? " is-active" : ""}`} onClick={() => setActiveSort(o.id)}
                style={{ borderBottom: activeSort === o.id ? "3px solid #10b981" : "3px solid transparent", background: activeSort === o.id ? "rgba(16,185,129,0.05)" : "transparent" }}>
                <div className="flights-sort-icon" style={{ color: activeSort === o.id ? "#047857" : "#94a3b8" }}>{o.icon}</div>
                <div className="flights-sort-label" style={{ color: activeSort === o.id ? "#047857" : "#64748b" }}>{o.label}</div>
                {o.detail && <div style={{ fontSize: 10, color: activeSort === o.id ? "#10b981" : "#94a3b8", marginTop: 1 }}>{o.detail}</div>}
              </div>
            ))}
          </div>
          {loading && (
            <div className="flights-search-loader">
              <TravelLoader inline />
            </div>
          )}
          {meta.tripType === "Multi City" ? (
            <div>
              <div className="multicity-tabs">
                {multiCityResults.map((r, i) => (
                  <button
                    key={r.leg.id} type="button"
                    className={`multicity-tab ${activeLegTab === i ? "is-active" : ""}`}
                    onClick={() => setActiveLegTab(i)}
                  >
                    <Plane size={13} />
                    Flight {i + 1}: {r.leg.fromCode} → {r.leg.toCode}
                    <span className="multicity-tab-date">{formatStripDate(r.leg.departDate)}</span>
                    <span className="multicity-tab-count">{r.flights.length} found</span>
                  </button>
                ))}
              </div>

              <div className="flights-list flights-list--single">
                {/* {loading && [1, 2, 3].map((i) => <SkeletonCard key={i} />)} */}

                {!loading && !searched && (
                  <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "60px 20px" }}>
                    <div style={{ fontSize: 52, marginBottom: 14 }}>✈️</div>
                    <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 700, color: "#0d1b3e", marginBottom: 7 }}>Add your flights above</div>
                    <div style={{ fontSize: 13.5, color: "#64748b" }}>Set a city and date for each leg, then hit <strong>Search flights</strong>.</div>
                  </div>
                )}

                {!loading && apiError && (
                  <div style={{ gridColumn: "1 / -1", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 12, padding: "12px 18px", fontSize: 13, color: "#92400e", display: "flex", alignItems: "center", gap: 9 }}>
                    ⚠️ {apiError}
                    <button onClick={() => doMultiCitySearch()} style={{ marginLeft: "auto", background: "#d97706", color: "#fff", border: "none", borderRadius: 8, padding: "5px 13px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Retry</button>
                  </div>
                )}

                {!loading && searched && sortedMultiCityFlights.length === 0 && !apiError && (
                  <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "60px 20px" }}>
                    <div style={{ fontSize: 52, marginBottom: 14 }}>🔍</div>
                    <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 700, color: "#0d1b3e" }}>No flights found for this leg</div>
                  </div>
                )}

                {!loading && paginatedMultiCityFlights.map((f, i) => (
                  <FlightCard
                    key={f.id} flight={f} travelDate={activeLegResult?.leg.departDate}
                    passengerCount={meta.adults + meta.children + meta.infants}
                    adults={meta.adults}
                    children={meta.children}
                    infants={meta.infants}
                    classType={meta.classType}
                    isCompared={isSelected(f.id)} canCompare={isSelected(f.id) || canAdd}
                    onCompareToggle={() => toggleCompare(f)} activeFare={activeFare}
                    onLoginRequest={(mode = "login") => mode === "signup" ? setShowSignupModal(true) : setShowLoginModal(true)}
                    aosDelay={Math.min(i * 50, 150)}
                    isInternational={isInternational}
                    selectionMode={true}
                    isSelectedLeg={multiCitySelections[activeLegResult.leg.id]?.id === f.id}
                    selectedFareOption={
                      multiCitySelections[activeLegResult.leg.id]?.id === f.id
                        ? multiCitySelections[activeLegResult.leg.id].selectedFare
                        : null
                    }
                    onSelectLeg={(flight) => {
                      if (!activeLegResult?.leg?.id || !flight) {
                        return;
                      }

                      selectMultiCityLeg(
                        activeLegResult.leg.id,
                        {
                          ...flight,
                          travelDate:
                            activeLegResult.leg.departDate ||
                            flight.travelDate ||
                            "",
                          multiCityLegId: activeLegResult.leg.id,
                          legIndex: activeLegTab,
                        }
                      );

                      setMultiCityPage(1);
                    }}
                  />
                ))}
              </div>
            </div>
          ) : isSpecialRoundTripType(meta.tripType) ? (
            <div className="flights-list flights-list--single">
              {loading && [1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)}

              {!loading && apiError && (
                <div style={{ gridColumn: "1 / -1", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 12, padding: "12px 18px", fontSize: 13, color: "#92400e", display: "flex", alignItems: "center", gap: 9 }}>
                  ⚠️ {apiError}
                  <button onClick={() => doSearch(meta)} style={{ marginLeft: "auto", background: "#d97706", color: "#fff", border: "none", borderRadius: 8, padding: "5px 13px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Retry</button>
                </div>
              )}

              {!loading && !searched && (
                <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "60px 20px" }}>
                  <div style={{ fontSize: 52, marginBottom: 14 }}>✈️</div>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 700, color: "#0d1b3e", marginBottom: 7 }}>Ready to find your flight?</div>
                  <div style={{ fontSize: 13.5, color: "#64748b" }}>Choose your route above and hit <strong>Search flights</strong>.</div>
                </div>
              )}

              {!loading && searched && sortedFlights.length === 0 && !apiError && (
                <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "60px 20px" }}>
                  <div style={{ fontSize: 52, marginBottom: 14 }}>🔍</div>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 700, color: "#0d1b3e", marginBottom: 7 }}>No flights found</div>
                  <div style={{ fontSize: 13, color: "#64748b" }}>Try adjusting your filters, date, or route.</div>
                  <button onClick={resetAllFilters} style={{ marginTop: 18, background: "linear-gradient(145deg,#10b981,#047857)", color: "#fff", border: "none", borderRadius: 12, padding: "11px 26px", fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "'Syne',sans-serif" }}>Clear all filters</button>
                </div>
              )}

              {!loading && paginatedFlights.map((f, i) => (
                <div key={f.id} style={{ display: "grid", gap: 8 }}>
                  <FlightCard
                    flight={{ ...f, specialRoundTrip: true, bookingType: 2, tripType: "Special Round Trip" }}
                    travelDate={meta.departDate}
                    passengerCount={meta.adults + meta.children + meta.infants}
                    adults={meta.adults}
                    children={meta.children}
                    infants={meta.infants}
                    isCompared={isSelected(f.id)} canCompare={isSelected(f.id) || canAdd}
                    onCompareToggle={() => toggleCompare(f)} activeFare={activeFare}
                    onLoginRequest={(mode = "login") => mode === "signup" ? setShowSignupModal(true) : setShowLoginModal(true)}
                    aosDelay={Math.min((i % 2) * 60, 80)}
                    isInternational={isInternational}
                    selectionMode={true}
                    isSelectedLeg={sameFlight(selectedSpecialRoundTrip, f)}
                    selectedFareOption={
                      sameFlight(selectedSpecialRoundTrip, f)
                        ? selectedSpecialRoundTrip.selectedFare
                        : null
                    }
                    itineraryLegs={buildSpecialRoundTripDisplayLegs(
                      sameFlight(selectedSpecialRoundTrip, f)
                        ? selectedSpecialRoundTrip
                        : f
                    )}
                    onSelectLeg={(flight) => {
                      setSelectedSpecialRoundTrip({
                        ...flight,
                        travelDate: meta.departDate,
                      });

                      setCurrentPage(1);
                    }}
                  />
                </div>
              ))}

              {!loading && searched && sortedFlights.length > FLIGHTS_PER_PAGE && (
                <div className="flights-pagination" aria-label="Flight results pagination">
                  <div className="flights-pagination-summary">
                    Showing {visibleFlightStart}–{visibleFlightEnd} of {sortedFlights.length} flights
                  </div>

                  <div className="flights-pagination-controls">
                    <button
                      type="button"
                      className="flights-pagination-btn flights-pagination-btn--nav"
                      onClick={() => goToFlightPage(safeCurrentPage - 1)}
                      disabled={safeCurrentPage === 1}
                    >
                      <ChevronLeft size={15} />
                      Previous
                    </button>

                    <div className="flights-pagination-pages">
                      {Array.from({ length: totalFlightPages }, (_, index) => index + 1).map((page) => (
                        <button
                          key={page}
                          type="button"
                          className={`flights-pagination-btn flights-pagination-btn--page${safeCurrentPage === page ? " is-active" : ""}`}
                          onClick={() => goToFlightPage(page)}
                          aria-current={safeCurrentPage === page ? "page" : undefined}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      type="button"
                      className="flights-pagination-btn flights-pagination-btn--nav"
                      onClick={() => goToFlightPage(safeCurrentPage + 1)}
                      disabled={safeCurrentPage === totalFlightPages}
                    >
                      Next
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : isReturnTripType(meta.tripType) ? (
            <div className="flights-roundtrip-grid">
              <div className="flights-leg-column">
                <div className="flights-leg-heading">
                  <Plane size={14} /> Onward → {meta.fromCode} → {meta.toCode}
                  <span>{formatStripDate(meta.departDate)}</span>
                </div>
                <div className="flights-list flights-list--single">
                  {loading && [1, 2].map((i) => <SkeletonCard key={i} />)}
                  {!loading && paginatedOutboundFlights.map((f, i) => (
                    <FlightCard
                      key={f.id}
                      flight={f}
                      travelDate={meta.departDate}
                      passengerCount={meta.adults + meta.children + meta.infants}
                      adults={meta.adults}
                      children={meta.children}
                      infants={meta.infants}
                      classType={meta.classType}
                      isCompared={isSelected(f.id)} canCompare={isSelected(f.id) || canAdd}
                      onCompareToggle={() => toggleCompare(f)} activeFare={activeFare}
                      onLoginRequest={(mode = "login") => mode === "signup" ? setShowSignupModal(true) : setShowLoginModal(true)}
                      aosDelay={Math.min((i % 2) * 60, 80)}
                      isInternational={isInternational}
                      selectionMode={true}
                      isSelectedLeg={sameFlight(selectedOutboundLeg, f)}
                      selectedFareOption={
                        sameFlight(selectedOutboundLeg, f)
                          ? selectedOutboundLeg.selectedFare
                          : null
                      }
                      onSelectLeg={(flight) => {
                        setSelectedOutboundLeg({
                          ...flight,
                          travelDate: meta.departDate,
                        });

                        setOutboundPage(1);
                        setSelectedReturnLeg(null);
                      }}
                    />
                  ))}

                  {!loading &&
                    orderedOutboundFlights.length > FLIGHTS_PER_PAGE && (
                      <div className="flights-pagination">
                        <div className="flights-pagination-summary">
                          Showing{" "}
                          {(safeOutboundPage - 1) * FLIGHTS_PER_PAGE + 1}
                          –
                          {Math.min(
                            safeOutboundPage * FLIGHTS_PER_PAGE,
                            orderedOutboundFlights.length
                          )}{" "}
                          of {orderedOutboundFlights.length} flights
                        </div>

                        <div className="flights-pagination-controls">
                          <button
                            type="button"
                            className="flights-pagination-btn flights-pagination-btn--nav"
                            disabled={safeOutboundPage === 1}
                            onClick={() =>
                              setOutboundPage((p) => Math.max(1, p - 1))
                            }
                          >
                            <ChevronLeft size={15} />
                            Previous
                          </button>

                          <div className="flights-pagination-pages">
                            {Array.from(
                              { length: outboundTotalPages },
                              (_, i) => i + 1
                            ).map((page) => (
                              <button
                                key={page}
                                type="button"
                                className={`flights-pagination-btn flights-pagination-btn--page${safeOutboundPage === page ? " is-active" : ""
                                  }`}
                                onClick={() => setOutboundPage(page)}
                              >
                                {page}
                              </button>
                            ))}
                          </div>

                          <button
                            type="button"
                            className="flights-pagination-btn flights-pagination-btn--nav"
                            disabled={
                              safeOutboundPage === outboundTotalPages
                            }
                            onClick={() =>
                              setOutboundPage((p) =>
                                Math.min(outboundTotalPages, p + 1)
                              )
                            }
                          >
                            Next
                            <ChevronRight size={15} />
                          </button>
                        </div>
                      </div>
                    )}
                </div>
              </div>

              <div className="flights-leg-column">
                <div className="flights-leg-heading">
                  <Plane size={14} style={{ transform: "rotate(180deg)" }} /> Return → {meta.toCode} → {meta.fromCode}
                </div>
                <div className="flights-list flights-list--single">
                  {loading && [1, 2].map((i) => <SkeletonCard key={i} />)}
                  {!loading && paginatedReturnFlights.map((f, i) => (
                    <FlightCard
                      key={f.id} flight={f} travelDate={meta.returnDate}
                      passengerCount={meta.adults + meta.children + meta.infants}
                      adults={meta.adults}
                      children={meta.children}
                      infants={meta.infants}
                      isCompared={isSelected(f.id)}
                      canCompare={isSelected(f.id) || canAdd}
                      onCompareToggle={() => toggleCompare(f)}
                      activeFare={activeFare}
                      onLoginRequest={(mode = "login") => mode === "signup" ? setShowSignupModal(true) : setShowLoginModal(true)}
                      aosDelay={Math.min(i * 40, 160)}
                      isInternational={isInternational}
                      classType={meta.classType}
                      selectionMode={true}
                      isSelectedLeg={sameFlight(selectedReturnLeg, f)}
                      selectedFareOption={
                        sameFlight(selectedReturnLeg, f)
                          ? selectedReturnLeg.selectedFare
                          : null
                      }
                      onSelectLeg={(flight) => {
                        setSelectedReturnLeg({
                          ...flight,
                          travelDate: meta.returnDate,
                        });

                        setReturnPage(1);
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flights-list flights-list--single">
              {loading && [1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)}

              {!loading && apiError && (
                <div style={{ gridColumn: "1 / -1", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 12, padding: "12px 18px", fontSize: 13, color: "#92400e", display: "flex", alignItems: "center", gap: 9 }}>
                  ⚠️ {apiError}
                  <button onClick={() => doSearch(meta)} style={{ marginLeft: "auto", background: "#d97706", color: "#fff", border: "none", borderRadius: 8, padding: "5px 13px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Retry</button>
                </div>
              )}

              {!loading && !searched && (
                <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "60px 20px" }}>
                  <div style={{ fontSize: 52, marginBottom: 14 }}>✈️</div>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 700, color: "#0d1b3e", marginBottom: 7 }}>Ready to find your flight?</div>
                  <div style={{ fontSize: 13.5, color: "#64748b" }}>Choose your route above and hit <strong>Search flights</strong>.</div>
                </div>
              )}

              {!loading && searched && sortedFlights.length === 0 && !apiError && (
                <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "60px 20px" }}>
                  <div style={{ fontSize: 52, marginBottom: 14 }}>🔍</div>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 700, color: "#0d1b3e", marginBottom: 7 }}>No flights found</div>
                  <div style={{ fontSize: 13, color: "#64748b" }}>Try adjusting your filters, date, or route.</div>
                  <button onClick={resetAllFilters} style={{ marginTop: 18, background: "linear-gradient(145deg,#10b981,#047857)", color: "#fff", border: "none", borderRadius: 12, padding: "11px 26px", fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "'Syne',sans-serif" }}>Clear all filters</button>
                </div>
              )}

              {!loading && paginatedFlights.map((f, i) => (
                <FlightCard
                  key={f.id}
                  flight={f}
                  travelDate={meta.departDate}
                  passengerCount={meta.adults + meta.children + meta.infants}
                  adults={meta.adults}
                  children={meta.children}
                  infants={meta.infants}
                  isCompared={isSelected(f.id)} canCompare={isSelected(f.id) || canAdd}
                  onCompareToggle={() => toggleCompare(f)} activeFare={activeFare}
                  onLoginRequest={(mode = "login") => mode === "signup" ? setShowSignupModal(true) : setShowLoginModal(true)}
                  aosDelay={Math.min((i % 2) * 60, 80)}
                  isInternational={isInternational}
                />
              ))}

              {!loading && searched && sortedFlights.length > FLIGHTS_PER_PAGE && (
                <div className="flights-pagination" aria-label="Flight results pagination">
                  <div className="flights-pagination-summary">
                    Showing {visibleFlightStart}–{visibleFlightEnd} of {sortedFlights.length} flights
                  </div>

                  <div className="flights-pagination-controls">
                    <button
                      type="button"
                      className="flights-pagination-btn flights-pagination-btn--nav"
                      onClick={() => goToFlightPage(safeCurrentPage - 1)}
                      disabled={safeCurrentPage === 1}
                    >
                      <ChevronLeft size={15} />
                      Previous
                    </button>

                    <div className="flights-pagination-pages">
                      {Array.from({ length: totalFlightPages }, (_, index) => index + 1).map((page) => (
                        <button
                          key={page}
                          type="button"
                          className={`flights-pagination-btn flights-pagination-btn--page${safeCurrentPage === page ? " is-active" : ""}`}
                          onClick={() => goToFlightPage(page)}
                          aria-current={safeCurrentPage === page ? "page" : undefined}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      type="button"
                      className="flights-pagination-btn flights-pagination-btn--nav"
                      onClick={() => goToFlightPage(safeCurrentPage + 1)}
                      disabled={safeCurrentPage === totalFlightPages}
                    >
                      Next
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {(selectedFlights.length > 0 ||
        (isSpecialRoundTripType(meta.tripType) && selectedSpecialRoundTrip) ||
        (isReturnTripType(meta.tripType) && !isSpecialRoundTripType(meta.tripType) && (selectedOutboundLeg || selectedReturnLeg)) ||
        (meta.tripType === "Multi City" && Object.keys(multiCitySelections).length > 0)) && (
          <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 500, display: "flex", flexDirection: "column" }}>

            {selectedFlights.length > 0 && (
              <div style={{ background: "#047857", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", padding: "10px 20px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: "#fff", fontFamily: "'Syne',sans-serif" }}>Compare ({selectedFlights.length}/3):</span>
                {selectedFlights.map((f) => (
                  <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.08)", borderRadius: 20, padding: "5px 10px", fontSize: 12, color: "#fff" }}>
                    <span>{f.airline}</span>
                    <span style={{ color: "#fff", fontWeight: 700 }}>{fmt(f.displayPrice || f.price)}</span>
                    <button onClick={() => toggleCompare(f)} style={{ background: "none", border: "none", color: "#black", cursor: "pointer", fontSize: 14, lineHeight: 1 }}>✕</button>
                  </div>
                ))}
                {selectedFlights.length >= 2 && (
                  <button onClick={openComparePage} style={{ marginLeft: "auto", background: "linear-gradient(135deg,#10b981,#047857)", color: "#fff", border: "none", borderRadius: 10, padding: "8px 18px", fontWeight: 700, fontSize: 12.5, fontFamily: "'Syne',sans-serif", cursor: "pointer" }}>Compare Now →</button>
                )}
              </div>
            )}

            {isSpecialRoundTripType(meta.tripType) && selectedSpecialRoundTrip && (
              <div style={{ background: "rgba(13,27,62,0.95)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", padding: "14px 24px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", borderTop: "1px solid rgba(16,185,129,0.25)", boxShadow: "0 -8px 30px rgba(0,0,0,0.25)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14, padding: "8px 14px", minWidth: 240 }}>
                  <div>
                    <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Special Round Trip</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", fontFamily: "'Syne',sans-serif" }}>
                      {cleanFlightNumberDisplay(
                        selectedSpecialRoundTrip.flightNumbers ||
                        selectedSpecialRoundTrip.airline
                      )} · <span style={{ color: "#10b981" }}>
  {fmt(
    selectedSpecialRoundTrip.selectedFare?.displayPrice ??
    selectedSpecialRoundTrip.displayPrice ??
    selectedSpecialRoundTrip.selectedFare?.price ??
    selectedSpecialRoundTrip.price
  )}
</span>
                    </div>
                  </div>
                  <button onClick={() => setSelectedSpecialRoundTrip(null)} style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", fontSize: 15, lineHeight: 1, marginLeft: "auto" }}>✕</button>
                </div>
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Total</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", fontFamily: "'Syne',sans-serif" }}>
                      {fmt(
  selectedSpecialRoundTrip.selectedFare?.displayPrice ??
  selectedSpecialRoundTrip.displayPrice ??
  selectedSpecialRoundTrip.selectedFare?.price ??
  selectedSpecialRoundTrip.price
) || "—"}
                    </div>
                  </div>
                  <button
                    onClick={() => setCheckoutItinerary({
                      flight: {
                        ...selectedSpecialRoundTrip,
                        specialRoundTrip: true,
                        bookingType: 2,
                        tripType: "Special Round Trip",
                        selectedFareIndex: selectedSpecialRoundTrip.selectedFareIndex ?? 0,
                        selectedFare:
                          selectedSpecialRoundTrip.selectedFare ||
                          selectedSpecialRoundTrip.fareOptions?.[
                          selectedSpecialRoundTrip.selectedFareIndex ?? 0
                          ] ||
                          selectedSpecialRoundTrip.fareOptions?.[0] ||
                          null,
                      },
                      legs: buildSpecialRoundTripDisplayLegs(selectedSpecialRoundTrip),
                      travelDate: selectedSpecialRoundTrip.travelDate || meta.departDate,
                    })}
                    style={{ background: "linear-gradient(135deg,#10b981,#047857)", color: "#fff", border: "none", borderRadius: 12, padding: "12px 26px", fontWeight: 800, fontSize: 13.5, fontFamily: "'Syne',sans-serif", cursor: "pointer", boxShadow: "0 8px 24px rgba(16,185,129,0.35)" }}
                  >Book Now →</button>
                </div>
              </div>
            )}

            {isReturnTripType(meta.tripType) && !isSpecialRoundTripType(meta.tripType) && (selectedOutboundLeg || selectedReturnLeg) && (
              <div style={{ background: "rgba(13,27,62,0.95)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", padding: "14px 24px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", borderTop: "1px solid rgba(16,185,129,0.25)", boxShadow: "0 -8px 30px rgba(0,0,0,0.25)" }}>
                {[
                  { label: "Onward", leg: selectedOutboundLeg, onRemove: () => setSelectedOutboundLeg(null) },
                  { label: "Return", leg: selectedReturnLeg, onRemove: () => setSelectedReturnLeg(null) },
                ].map(({ label, leg, onRemove }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14, padding: "8px 14px", minWidth: 200 }}>
                    <div>
                      <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
                      {leg ? (
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", fontFamily: "'Syne',sans-serif" }}>
                          {leg.departCode} → {leg.arriveCode} · <span style={{ color: "#10b981" }}>{fmt(
  leg.selectedFare?.displayPricing?.payableTotal ??
  leg.selectedFare?.displayPrice ??
  leg.displayPricing?.payableTotal ??
  leg.displayPrice ??
  leg.selectedFare?.price ??
  leg.price
)}</span>
                        </div>
                      ) : (
                        <div style={{ fontSize: 12.5, color: "#94a3b8" }}>Select a {label.toLowerCase()} flight</div>
                      )}
                    </div>
                    {leg && <button onClick={onRemove} style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", fontSize: 15, lineHeight: 1, marginLeft: "auto" }}>✕</button>}
                  </div>
                ))}
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Total</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", fontFamily: "'Syne',sans-serif" }}>
                      
{fmt(
  (() => {
    const getLegPricing = (leg) =>
      leg?.selectedFare?.displayPricing ??
      leg?.displayPricing ??
      null;

    const getLegDisplayTotal = (leg) =>
      Number(
        leg?.selectedFare?.displayPricing?.payableTotal ??
        leg?.selectedFare?.displayPrice ??
        leg?.displayPricing?.payableTotal ??
        leg?.displayPrice ??
        leg?.selectedFare?.price ??
        leg?.price ??
        0
      );

    const outboundTotal = getLegDisplayTotal(selectedOutboundLeg);
    const returnTotal = getLegDisplayTotal(selectedReturnLeg);

    const returnPricing = getLegPricing(selectedReturnLeg);
    const returnCharges =
      returnPricing?.breakdown ??
      returnPricing?.lines ??
      [];

    // Remove the duplicate fixed website fee from the return leg.
    const duplicateFixedFee = returnCharges.reduce(
      (sum, charge) => {
        const basis = String(charge?.basis || "").toLowerCase();
        const chargeType = String(
          charge?.chargeType || ""
        ).toLowerCase();

        const isDuplicateFee =
          chargeType === "fixed" &&
          (basis === "per_booking" ||
            basis === "per_passenger");

        return isDuplicateFee
          ? sum + Number(charge?.amount || 0)
          : sum;
      },
      0
    );

    return Math.max(
      0,
      Math.round(
        (outboundTotal + returnTotal - duplicateFixedFee) * 100
      ) / 100
    );
  })()
) || "—"}

                    </div>
                  </div>
                  <button
                    disabled={!selectedOutboundLeg || !selectedReturnLeg}
                    onClick={() => openCombinedCheckout([selectedOutboundLeg, selectedReturnLeg], "round-trip")}
                    style={{ background: (!selectedOutboundLeg || !selectedReturnLeg) ? "#475569" : "linear-gradient(135deg,#10b981,#047857)", color: "#fff", border: "none", borderRadius: 12, padding: "12px 26px", fontWeight: 800, fontSize: 13.5, fontFamily: "'Syne',sans-serif", cursor: (!selectedOutboundLeg || !selectedReturnLeg) ? "not-allowed" : "pointer", boxShadow: (!selectedOutboundLeg || !selectedReturnLeg) ? "none" : "0 8px 24px rgba(16,185,129,0.35)" }}
                  >Book Now →</button>
                </div>
              </div>
            )}

            {meta.tripType === "Multi City" && Object.keys(multiCitySelections).length > 0 && (
              <div style={{ background: "rgba(13,27,62,0.95)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", padding: "14px 24px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", borderTop: "1px solid rgba(16,185,129,0.25)", boxShadow: "0 -8px 30px rgba(0,0,0,0.25)" }}>
                {multiCityLegs.map((leg, index) => {
                  const selected = multiCitySelections[leg.id];
                  return (
                    <div key={leg.id} style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14, padding: "8px 14px", minWidth: 200 }}>
                      <div>
                        <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Flight {index + 1}</div>
                        {selected ? (
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", fontFamily: "'Syne',sans-serif" }}>
                            {selected.departCode} → {selected.arriveCode} · <span style={{ color: "#10b981" }}>
  {fmt(
    selected.selectedFare?.displayPricing?.payableTotal ??
    selected.selectedFare?.displayPrice ??
    selected.displayPricing?.payableTotal ??
    selected.displayPrice ??
    selected.selectedFare?.price ??
    selected.price
  )}
</span>
                          </div>
                        ) : (
                          <div style={{ fontSize: 12.5, color: "#94a3b8" }}>Select flight {index + 1}</div>
                        )}
                      </div>
                      {selected && (
                        <button onClick={() => setMultiCitySelections((prev) => { const next = { ...prev }; delete next[leg.id]; return next; })} style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", fontSize: 15, lineHeight: 1, marginLeft: "auto" }}>✕</button>
                      )}
                    </div>
                  );
                })}
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Total</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", fontFamily: "'Syne',sans-serif" }}>
                      
{fmt(
  (() => {
    const getLegDisplayTotal = (selected) =>
      Number(
        selected?.selectedFare?.displayPricing?.payableTotal ??
        selected?.selectedFare?.displayPrice ??
        selected?.displayPricing?.payableTotal ??
        selected?.displayPrice ??
        selected?.selectedFare?.price ??
        selected?.price ??
        0
      );

    const getFixedWebsiteFees = (selected) => {
      const pricing =
        selected?.selectedFare?.displayPricing ??
        selected?.displayPricing ??
        null;

      const charges =
        pricing?.breakdown ??
        pricing?.lines ??
        [];

      if (!Array.isArray(charges)) return 0;

      return charges.reduce((total, charge) => {
        const basis = String(charge?.basis || "")
          .toLowerCase()
          .replace(/[\s-]+/g, "_");

        const chargeType = String(charge?.chargeType || "")
          .toLowerCase();

        if (
          chargeType === "fixed" &&
          ["per_booking", "per_passenger"].includes(basis)
        ) {
          return total + (Number(charge?.amount) || 0);
        }

        return total;
      }, 0);
    };

    const selectedLegs = multiCityLegs
      .map((leg) => multiCitySelections[leg.id])
      .filter(Boolean);

    const summedTotal = selectedLegs.reduce(
      (total, selected) => total + getLegDisplayTotal(selected),
      0
    );

    // The first selected leg retains these fees.
    // Remove duplicate copies from every subsequent leg.
    const duplicateFees = selectedLegs
      .slice(1)
      .reduce(
        (total, selected) => total + getFixedWebsiteFees(selected),
        0
      );

    return Math.max(
      0,
      Math.round((summedTotal - duplicateFees) * 100) / 100
    );
  })()
) || "—"}

                    </div>
                  </div>
                  <button
                    disabled={!allMultiCityLegsSelected}
                    onClick={() => openCombinedCheckout(multiCityLegs.map((leg) => multiCitySelections[leg.id]), "multi-city")}
                    style={{ background: !allMultiCityLegsSelected ? "#475569" : "linear-gradient(135deg,#10b981,#047857)", color: "#fff", border: "none", borderRadius: 12, padding: "12px 26px", fontWeight: 800, fontSize: 13.5, fontFamily: "'Syne',sans-serif", cursor: !allMultiCityLegsSelected ? "not-allowed" : "pointer", boxShadow: !allMultiCityLegsSelected ? "none" : "0 8px 24px rgba(16,185,129,0.35)" }}
                  >Book Now →</button>
                </div>
              </div>
            )}
          </div>
        )}
      {checkoutItinerary && (
        <PricingModal
          flight={checkoutItinerary.flight}
          itineraryLegs={checkoutItinerary.legs}
          travelDate={checkoutItinerary.travelDate}
          onClose={() => setCheckoutItinerary(null)}
          onLoginRequest={(mode = "login") => mode === "signup" ? setShowSignupModal(true) : setShowLoginModal(true)}
          activeFare={activeFare}
          isInternational={checkoutItinerary.legs.length
            ? checkoutItinerary.legs.some((leg) => isInternationalRoute(leg.departCode, leg.arriveCode, airports))
            : isInternationalRoute(checkoutItinerary.flight?.departCode, checkoutItinerary.flight?.arriveCode, airports)}
          adults={meta.adults}
          children={meta.children}
          infants={meta.infants}
          classType={meta.classType}
        />
      )}
      {showLoginModal && <Login onClose={() => setShowLoginModal(false)} onSwitchToSignup={() => { setShowLoginModal(false); setShowSignupModal(true); }} />}
      {showSignupModal && <Signup onClose={() => setShowSignupModal(false)} onSwitchToLogin={() => { setShowSignupModal(false); setShowLoginModal(true); }} />}
    </div>
  );
}

// ── Inline styles ─────────────────────────────────────────────────────────────
const styles = {
  modalOverlay: {
    position: "fixed", inset: 0,
    background: "rgba(13,27,62,0.82)",
    zIndex: 9999,
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 20,
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  },
  pricingModal: {
    background: "#fff", borderRadius: 22,
    width: "100%", maxWidth: 940, maxHeight: "94vh",
    overflow: "hidden", display: "flex", flexDirection: "column",
    boxShadow: "0 40px 100px rgba(13,27,62,0.45)",
  },
  pricingHeader: {
    padding: "20px 28px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    background: "linear-gradient(135deg, #10b981 0%, #047857 100%)",
    flexShrink: 0,
  },
  pricingTitle: { fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 800, color: "#fff" },
  pricingSubtitle: { fontSize: 12.5, color: "rgba(255,255,255,0.72)", marginTop: 3 },
  pricingBody: { flex: 1, overflowY: "auto", padding: "22px 28px" },
  fareGrid: { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 20, alignItems: "start" },
  fareCard: {
    borderRadius: 14, padding: 18, cursor: "pointer", position: "relative",
    transition: "all 0.22s cubic-bezier(0.22,1,0.36,1)",
    boxShadow: "0 1px 4px rgba(13,27,62,0.08)",
    display: "flex", flexDirection: "column",
  },
  recommendedBadge: {
    position: "absolute", top: -9, left: "50%", transform: "translateX(-50%)",
    background: "linear-gradient(135deg,#10b981,#047857)",
    color: "#fff", fontSize: 8.5, fontWeight: 800,
    padding: "2px 10px", borderRadius: 20, whiteSpace: "nowrap", letterSpacing: "0.06em",
  },
  fareName: { fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, color: "#0d1b3e", marginBottom: 4 },
  farePrice: { fontFamily: "'Syne',sans-serif", fontSize: 26, fontWeight: 800, color: "#0d1b3e", lineHeight: 1 },
  farePricePer: { fontSize: 11, color: "#5a6d92", marginBottom: 12 },
  fareDetails: { display: "flex", flexDirection: "column", gap: 5 },
  fareRow: { display: "flex", gap: 7, fontSize: 11.5, color: "#5a6d92", alignItems: "flex-start" },
  pricingSummary: {
    background: "linear-gradient(135deg,#ecfdf5,#d1fae5)",
    borderRadius: 12, padding: "16px 20px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    border: "1px solid rgba(16,185,129,0.24)",
  },
  summaryLabel: { fontSize: 10, fontWeight: 800, color: "#047857", textTransform: "uppercase", letterSpacing: "0.06em" },
  summaryPrice: { fontFamily: "'Syne',sans-serif", fontSize: 28, fontWeight: 800, color: "#0d1b3e" },
  pricingFooter: {
    padding: "16px 28px", borderTop: "1px solid #e5eaf2",
    display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center",
    background: "#f7f9fc", flexShrink: 0,
  },
  bookBtn: {
    background: "linear-gradient(135deg,#10b981 0%,#047857 100%)",
    color: "#fff", border: "none", borderRadius: 12,
    padding: "12px 26px",
    fontFamily: "'Syne',sans-serif", fontSize: 13.5, fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 8px 28px rgba(16,185,129,0.28), 0 2px 8px rgba(16,185,129,0.16)",
    transition: "all 0.18s cubic-bezier(0.22,1,0.36,1)",
    display: "flex", alignItems: "center", gap: 6,
  },
  backBtn: {
    background: "none", color: "#5a6d92",
    border: "1.5px solid #e0e9f0", borderRadius: 12,
    padding: "12px 20px", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
    transition: "all 0.15s", fontFamily: "'Plus Jakarta Sans',sans-serif",
  },
  closeBtn: {
    background: "rgba(255,255,255,0.14)", border: "none",
    color: "#fff", width: 34, height: 34, borderRadius: "50%",
    fontSize: 15, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "background 0.15s",
  },
  bookingSelectedFare: {
    background: "#ecfdf5", borderRadius: 11, padding: "12px 16px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: 18, fontSize: 13, color: "#0d1b3e",
    border: "1px solid rgba(16,185,129,0.20)",
  },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 },
  formField: { display: "flex", flexDirection: "column", gap: 5 },
  formLabel: { fontSize: 10, fontWeight: 800, color: "#5a6d92", textTransform: "uppercase", letterSpacing: "0.06em" },
  formInput: {
    border: "1.5px solid #e0e9f0", borderRadius: 10,
    padding: "10px 13px", fontSize: 13.5, color: "#0d1b3e",
    outline: "none", fontFamily: "'Plus Jakarta Sans',sans-serif",
    transition: "border-color 0.2s", background: "#fff",
  },
  agreeRow: { display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 18 },
};
